(function () {
  const instances = new Map();
  const TAU = Math.PI * 2;
  const BUILD_VERSION = '4.61.0-luminous-grid';
  const BUILD_SOURCE_BRANCH = 'codex/blog-wireframe-20260924';
  const BUILD_BASE_COMMIT = '8ff70b99';
  const CSS_ASSET_REVISION = document.querySelector('link[href*="moztech-floating-bubbles.css"]')?.href.match(/[?&]v=(\d+)/)?.[1] || 'local / unversioned';
  const HERO_LAYOUT = Object.freeze({
    wordmarkWidthFraction: Object.freeze({ mobile: 0.92, tablet: 0.88, desktop: 0.8 }),
    orbitWidthFraction: Object.freeze({ mobile: 0.88, tablet: 0.82, desktop: 0.54 }),
    maxOrbDiameter: Object.freeze({
      mobileWidthFraction: 0.335,
      mobileMaxPixels: 192,
      tabletWidthFraction: 0.265,
      tabletMaxPixels: 480,
      desktopWidthFraction: 0.155,
      desktopMaxPixels: 640,
      heightFraction: 0.29
    }),
    phoneToTabletWidth: Object.freeze({ start: 600, end: 900 }),
    tabletToDesktopWidth: Object.freeze({ start: 1024, end: 1280 }),
    portraitAspectBlend: Object.freeze({ start: 0.92, end: 1.08 }),
    portraitYScale: 1,
    // Includes the longest authored silhouette, not just the glass mesh.
    maxAuthoredSilhouetteDiameter: 1.82
  });
  const REFERENCE_ORBIT = Object.freeze({
    angularSpeed: 0.025,
    landscape: Object.freeze({
      tiltXDeg: 29,
      tiltZDeg: 19,
      radiusXRatio: 0.48,
      radiusZRatio: 0.4
    }),
    portrait: Object.freeze({
      tiltXDeg: 45.9,
      tiltZDeg: 43,
      radiusXRatio: 0.56,
      radiusZRatio: 0.46
    })
  });
  window._bubblesInstances = instances;

  class MoztechFloatingBubbles {
    constructor(section) {
      this.section = section;
      this.container = section;
      this.sectionId = section.dataset.sectionId;
      this.loadingStartTime = performance.now();
      
      this.timelineLogs = [];
      this.logTimelineEvent = (name) => {
        const ts = ((performance.now() - this.loadingStartTime) / 1000).toFixed(3);
        this.timelineLogs.push({
          event: name,
          timestamp_sec: parseFloat(ts)
        });
        console.log(`[BMS Timeline] ${name} at ${ts}s`);
        this.updateDebugDashboard();
      };
      
      this.logTimelineEvent('HTML critical CSS active');
      this.logTimelineEvent('DOM section mounted');
      this.logTimelineEvent('app constructor started');
      
      this.pointerListenersCount = 0;
      this.objects = Array.from(section.querySelectorAll('[data-mbs-object]'));
      this.videos = Array.from(section.querySelectorAll('video'));
      this.enabled = section.dataset.enableAnimation !== 'false' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.enableProductAsset = section.dataset.enableProductAsset === 'true';
      this.productFanImageUrl = section.dataset.productFanImage || '';
      this.productFanIsFallback = section.dataset.productFanIsFallback === 'true'
        || (section.dataset.productFanIsFallback == null
          && /\/moztech-fan-powerbank-poster-transparent\.png(?:[?#]|$)/.test(this.productFanImageUrl));
      this.productCapsuleImageUrl = section.dataset.productCapsuleImage || '';
      this.productChargerImageUrl = section.dataset.productChargerImage || '';
      this.productArkImageUrl = section.dataset.productArkImage || '';
      this.ribbonBrandImageUrl = section.dataset.ribbonBrandImage || '';
      this.visible = false;
      this.pointerX = 0;
      this.pointerY = 0;
      this.scroll = 0;
      this.raf = null;
      this.bubbles = [];
      this.basePlaneWidth = 10.5;
      this.mouseHoveredBubble = null;
      this.physicsReleaseUntil = 0;
      this.pointerIsDown = false;
      this.activePointerId = null;
      this.lastPointerSample = null;
      this.pointerVelocity = { x: 0, y: 0 };
      this.pointerSurfaceActive = false;
      this.touchTapCandidate = null;
      this.touchFocusBubble = null;
      this.touchFocusUntil = 0;
      this.touchHoldTimer = null;
      this.dragConstraint = null;
      this.physicsActive = false;
      this.physicsAwake = false;
      this.introStartTime = null;
      this.introProgress = 1;
      this.introPhase = 'idle';
      this.introActive = false;
      this.logoIntroOpacity = 1;
      this.logoIntroScaleX = 1;
      this.logoExitScaleX = 1;
      this.logoExitOpacity = 1;
      this.orbExitProgress = 0;
      this.orbExitOpacity = 1;
      this.wordmarkBaseScale = 1;
      this.heroExitProgress = 0;
      this.heroExitTarget = 0;
      this.transitionPathMetrics = null;
      this.transitionQueueDescriptors = null;

      if (window.moztechWebGLContextCount === undefined) {
        window.moztechWebGLContextCount = 0;
      }

      this.totalAssetsCount = 0;
      this.loadedAssetsCount = 0;
      this.failedTextureUrls = [];
      this.isFirstFrameRendered = false;
      this.isSceneReady = false;
      this.objectLayoutScale = 1;
      this.spacingPackScale = 1;
      this.deferredAssetLoaders = [];
      this.deferredAssetHandle = null;
      this.warmupFrameId = null;
      this.lastScrollActivity = 0;
      this.sectionDocumentTop = null;
      this.sectionDocumentHeight = 0;
      this.documentScrollRange = 1;

      this.onPointerMove = this.onPointerMove.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.applyFrame = this.applyFrame.bind(this);
      this.render = this.render.bind(this);

      this.init();
    }

    createStudioEnvironmentTexture(THREE) {
      const canvas = document.createElement('canvas');
      // Keep the same reflection field across cold loads and live resizes.
      // Viewport-dependent optical assets made mobile and desktop look like
      // different marbles and could not be corrected without rebuilding.
      canvas.width = 512;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;

      ctx.fillStyle = '#010204';
      ctx.fillRect(0, 0, width, height);

      const paintSoftbox = (x, y, radiusX, radiusY, colorStops) => {
        ctx.save();
        ctx.translate(x, y);
        ctx.scale(radiusX, radiusY);
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
        colorStops.forEach(([stop, color]) => gradient.addColorStop(stop, color));
        ctx.fillStyle = gradient;
        ctx.fillRect(-1, -1, 2, 2);
        ctx.restore();
      };

      // Broad studio cards produce the long curved reflections that make a
      // black glass sphere read as volume instead of a few painted highlights.
      paintSoftbox(width * 0.24, height * 0.18, width * 0.22, height * 0.1, [
        [0, 'rgba(255,255,255,0.98)'],
        [0.42, 'rgba(255,255,255,0.52)'],
        [1, 'rgba(255,255,255,0)']
      ]);
      paintSoftbox(width * 0.78, height * 0.48, width * 0.075, height * 0.3, [
        [0, 'rgba(255,255,255,0.9)'],
        [0.52, 'rgba(230,244,255,0.32)'],
        [1, 'rgba(255,255,255,0)']
      ]);
      paintSoftbox(width * 0.52, height * 0.82, width * 0.3, height * 0.1, [
        [0, 'rgba(43,185,237,0.42)'],
        [0.5, 'rgba(80,110,255,0.15)'],
        [1, 'rgba(43,185,237,0)']
      ]);
      paintSoftbox(width * 0.48, height * 0.1, width * 0.045, height * 0.045, [
        [0, 'rgba(255,255,255,1)'],
        [0.38, 'rgba(255,255,255,0.48)'],
        [1, 'rgba(255,255,255,0)']
      ]);

      const texture = new THREE.CanvasTexture(canvas);
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = true;
      texture.mapping = THREE.EquirectangularReflectionMapping;
      texture.encoding = THREE.sRGBEncoding;
      texture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
      texture.needsUpdate = true;
      return texture;
    }

    setupObserver() {
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.visible = entry.isIntersecting;
            if (this.visible) {
              this.onScroll();
              this.playVideos();
              this.requestFrame();
              if (this.isSceneReady) {
                this.startRenderLoop();
                this.startDeferredAssetLoads();
              }
            } else {
              this.pauseVideos();
            }
          });
        }, { threshold: 0.05 });
        this.observer.observe(this.section);
      } else {
        this.visible = true;
        this.playVideos();
        this.requestFrame();
      }
    }

    getResponsiveLayoutValue(values) {
      const profile = this.layoutProfile || this.getResponsiveLayoutProfile();
      return this.lerp(
        this.lerp(values.mobile, values.tablet, profile.phoneToTablet),
        values.desktop,
        profile.tabletToDesktop
      );
    }

    lerp(start, end, progress) {
      return start + (end - start) * progress;
    }

    smoothstep(edge0, edge1, value) {
      if (edge0 === edge1) return value < edge0 ? 0 : 1;
      const progress = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)));
      return progress * progress * (3 - 2 * progress);
    }

    getResponsiveLayoutProfile() {
      const width = Math.max(1, this.viewportWidth || window.innerWidth || 1440);
      const height = Math.max(1, this.viewportHeight || window.innerHeight || 900);
      const aspect = width / height;
      const phoneToTablet = this.smoothstep(
        HERO_LAYOUT.phoneToTabletWidth.start,
        HERO_LAYOUT.phoneToTabletWidth.end,
        width
      );
      const tabletToDesktop = this.smoothstep(
        HERO_LAYOUT.tabletToDesktopWidth.start,
        HERO_LAYOUT.tabletToDesktopWidth.end,
        width
      );
      const landscapeBlend = this.smoothstep(
        HERO_LAYOUT.portraitAspectBlend.start,
        HERO_LAYOUT.portraitAspectBlend.end,
        aspect
      );

      // Frame the same world-space belt rather than independently fitting its
      // objects and projected gaps. A portrait window sees a taller diagonal;
      // a wide window sees the shallow plane with two substantial arcs.
      // Smooth the framing transitions so live resize never changes the slots.
      const landscapeFrame = 7
        + this.smoothstep(1.1, 1.3, aspect)
        + this.smoothstep(1.5, 1.7, aspect)
        + 0.5 * this.smoothstep(1.9, 2.1, aspect);
      const sceneWidth = Math.max(
        this.lerp(5, landscapeFrame, landscapeBlend),
        // Keep the complete belt inside short/square windows, rather than
        // clipping the upper/lower orbs under the sticky navigation.
        this.lerp(6.4, 4, landscapeBlend) * aspect
      );
      const orbitObjectRadius = this.lerp(0.73, 0.6, landscapeBlend);
      const compositionBlend = tabletToDesktop * landscapeBlend;
      const maxOrbDiameterPixels = 2 * orbitObjectRadius * width / sceneWidth;

      return {
        width,
        height,
        aspect,
        phoneToTablet,
        tabletToDesktop,
        landscapeBlend,
        compositionBlend,
        sceneWidth,
        orbitObjectRadius,
        maxOrbDiameterPixels,
        portraitYScale: this.lerp(HERO_LAYOUT.portraitYScale, 1, landscapeBlend),
        name: tabletToDesktop >= 0.98
          ? 'desktop'
          : phoneToTablet <= 0.02
            ? 'mobile-portrait'
            : aspect < 1
              ? 'tablet-portrait'
              : 'tablet-landscape'
      };
    }

    getWordmarkWidthFraction() {
      return this.getResponsiveLayoutValue(HERO_LAYOUT.wordmarkWidthFraction);
    }

    getOrbitWidthFraction() {
      const profile = this.getResponsiveLayoutProfile();
      return 5 / profile.sceneWidth;
    }

    getResponsiveObjectScale() {
      const profile = this.getResponsiveLayoutProfile();
      // 0.715 is the authored radius of the standard MOZTECH glass asset.
      // Keep each asset's small silhouette differences, without another
      // foreground boost or far-arc reduction on top of perspective.
      return profile.orbitObjectRadius / 0.715;
    }

    getPixelRatioCap() {
      if (this.isMobile) return 1;
      if (this.isTablet) return 1.2;
      return 1.35;
    }

    getTargetFrameInterval() {
      const isActivelyInteracting = !this.isMobile && (this.pointerIsDown || this.mouseHoveredBubble);
      if (isActivelyInteracting) return 1000 / 60;
      const isPageScrolling = performance.now() - this.lastScrollActivity < 150;
      if (isPageScrolling) return this.isMobile ? (1000 / 24) : (1000 / 30);
      return this.isMobile ? (1000 / 30) : (1000 / 45);
    }

    getBubbleSilhouetteMultiplier(bubble) {
      if (bubble.orb_type === 'starburst' || bubble.orb_type === 'energy_starburst') return 1.18;
      if (bubble.orb_type === 'ribbon_wrapped') return 1.05;
      if (bubble.orb_type === 'organic_blob') return 1.28;
      if (bubble.orb_type === 'particle_grid') return 1.04;
      return 1;
    }

    getSpacingGapPixels() {
      return THREE.MathUtils.clamp(this.viewportWidth * (this.isMobile ? 0.012 : 0.0065), 7, 12);
    }

    usesProjectedOrbitSpacing() {
      // Screen-space packing destroys perspective overlap and changes angular
      // velocity on touch layouts. All viewports now use the same ordered 3D
      // belt; real contacts are still handled by the all-pair sphere solver.
      return false;
    }

    hasDesktopOrbitContact() {
      if (this.usesProjectedOrbitSpacing()) return false;
      for (let i = 0; i < this.bubbles.length; i++) {
        const first = this.bubbles[i];
        const firstRadius = this.getCollisionRadius(first);
        for (let j = i + 1; j < this.bubbles.length; j++) {
          const second = this.bubbles[j];
          const radius = firstRadius
            + this.getCollisionRadius(second);
          if (first.targetPos.distanceToSquared(second.targetPos) < radius * radius) return true;
        }
      }
      return false;
    }

    getCollisionRadius(bubble) {
      // The render scale already includes responsive, phase and depth scale.
      // One source of truth for wake-up, contacts, picking diagnostics.
      return bubble.baseRadius * bubble.group.scale.x
        * this.getBubbleSilhouetteMultiplier(bubble) * 0.96;
    }

    updateSpacingBudget() {
      if (!this.renderer || !this.camera || !this.bubbles.length) return;
      if (!this.usesProjectedOrbitSpacing()) {
        this.spacingPackScale = 1;
        this.spacingFrameScale = 1;
        this.spacingSolverIterations = 0;
        this._spacingSolution = null;
        return;
      }

      const canvas = this.renderer.domElement;
      const sampleCount = 256;
      let circumferencePixels = 0;
      let previousPoint = null;
      const arcLookup = [];

      for (let sampleIndex = 0; sampleIndex <= sampleCount; sampleIndex++) {
        const angle = (sampleIndex / sampleCount) * TAU;
        const sample = this.getOrbitPoint(angle);
        const projected = sample.position.clone().project(this.camera);
        const point = new THREE.Vector2(
          (projected.x * 0.5 + 0.5) * canvas.clientWidth,
          (-projected.y * 0.5 + 0.5) * canvas.clientHeight
        );
        if (previousPoint) circumferencePixels += point.distanceTo(previousPoint);
        arcLookup.push({ angle, arc: circumferencePixels });
        previousPoint = point;
      }

      let worstDiameterSum = 0;
      const phaseSamples = 48;
      for (let phaseIndex = 0; phaseIndex < phaseSamples; phaseIndex++) {
        const phaseOffset = (phaseIndex / phaseSamples) * TAU;
        let diameterSum = 0;
        this.bubbles.forEach((bubble) => {
          const theta = phaseOffset + bubble.startAngle;
          const orbitSample = this.getOrbitPoint(theta);
          const distance = this.camera.position.distanceTo(orbitSample.position);
          const frustumHeightAtDepth = 2 * Math.tan((this.camera.fov * Math.PI) / 360) * distance;
          const pixelsPerWorldUnit = canvas.clientHeight / frustumHeightAtDepth;
          const radiusWorld = bubble.baseRadius
            * bubble.baseScale
            * (this.objectLayoutScale || 1)
            * this.getOrbitScale(theta)
            * this.getBubbleSilhouetteMultiplier(bubble);
          diameterSum += radiusWorld * pixelsPerWorldUnit * 2
            * this.getProjectedPackingFactor(orbitSample.depthNorm);
        });
        worstDiameterSum = Math.max(worstDiameterSum, diameterSum);
      }

      // Chord distance is slightly shorter than distance measured along the
      // curved orbit. Reserve one stable safety margin for the entire group so
      // objects do not need to shrink independently as they pass MOZ / TECH.
      const curveSafety = 1.06;
      const gapBudget = this.getSpacingGapPixels() * this.bubbles.length;
      const availableCircumference = Math.max(1, (circumferencePixels / curveSafety) - gapBudget);
      this.spacingPackScale = THREE.MathUtils.clamp(
        availableCircumference / Math.max(1, worstDiameterSum),
        this.lerp(0.78, 0.84, this.getResponsiveLayoutProfile().phoneToTablet),
        1
      );
      this.orbitArcLookup = arcLookup;
      this.orbitCircumferencePixels = circumferencePixels;
      this.spacingWorstDiameterPixels = worstDiameterSum;
      this.spacingCurveSafety = curveSafety;
    }

    getOrbitArcAtAngle(theta) {
      const lookup = this.orbitArcLookup;
      const circumference = this.orbitCircumferencePixels || 0;
      if (!lookup || lookup.length < 2 || circumference <= 0) return 0;

      const normalized = ((theta % TAU) + TAU) % TAU;
      const scaledIndex = (normalized / TAU) * (lookup.length - 1);
      const lowerIndex = Math.floor(scaledIndex);
      const upperIndex = Math.min(lookup.length - 1, lowerIndex + 1);
      const progress = scaledIndex - lowerIndex;
      return THREE.MathUtils.lerp(lookup[lowerIndex].arc, lookup[upperIndex].arc, progress);
    }

    getProjectedPackingFactor(depthNorm) {
      // Only the projected silhouettes may overlap; real colliders and touch
      // input remain unchanged. Keep phone overlap small, with more layered
      // depth on tablets so larger objects are not immediately packed down.
      const profile = this.getResponsiveLayoutProfile();
      const depth = THREE.MathUtils.clamp(Math.abs(depthNorm), 0, 1);
      const tabletPacking = this.lerp(0.90, 0.79, depth);
      // Fade projected packing out before desktop takes over. Otherwise the
      // 1279→1280 resize would abruptly restore 10–15% of the sphere size.
      return this.lerp(0.93, tabletPacking, profile.phoneToTablet)
        * this.lerp(1, 0.7, profile.tabletToDesktop);
    }

    getOrbitAngleAtArc(arc) {
      const lookup = this.orbitArcLookup;
      const circumference = this.orbitCircumferencePixels || 0;
      if (!lookup || lookup.length < 2 || circumference <= 0) return 0;

      const normalizedArc = ((arc % circumference) + circumference) % circumference;
      let low = 0;
      let high = lookup.length - 1;
      while (low + 1 < high) {
        const middle = (low + high) >> 1;
        if (lookup[middle].arc <= normalizedArc) low = middle;
        else high = middle;
      }

      const lower = lookup[low];
      const upper = lookup[high];
      const span = Math.max(0.0001, upper.arc - lower.arc);
      return THREE.MathUtils.lerp(lower.angle, upper.angle, (normalizedArc - lower.arc) / span);
    }

    refreshOrbitMetrics(frustumWidth) {
      if (!this.camera) return;

      this.frustumWidth = frustumWidth || this.frustumWidth || this.basePlaneWidth || 5;
      const profile = this.getResponsiveLayoutProfile();
      this.layoutProfile = profile;
      this.orbitReferenceWidth = this.frustumWidth * this.getOrbitWidthFraction();
      this.orbitConfig = {
        tiltXDeg: this.lerp(REFERENCE_ORBIT.portrait.tiltXDeg, REFERENCE_ORBIT.landscape.tiltXDeg, profile.landscapeBlend),
        tiltZDeg: this.lerp(REFERENCE_ORBIT.portrait.tiltZDeg, REFERENCE_ORBIT.landscape.tiltZDeg, profile.landscapeBlend),
        radiusXRatio: this.lerp(REFERENCE_ORBIT.portrait.radiusXRatio, REFERENCE_ORBIT.landscape.radiusXRatio, profile.landscapeBlend),
        radiusZRatio: this.lerp(REFERENCE_ORBIT.portrait.radiusZRatio, REFERENCE_ORBIT.landscape.radiusZRatio, profile.landscapeBlend)
      };

      const tiltX = THREE.MathUtils.degToRad(this.orbitConfig.tiltXDeg);
      this.orbitRotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(
        tiltX,
        0,
        THREE.MathUtils.degToRad(this.orbitConfig.tiltZDeg),
        'XYZ'
      ));
      this.orbitMaxCameraDepth = Math.max(
        0.0001,
        this.orbitReferenceWidth * this.orbitConfig.radiusZRatio * Math.abs(Math.cos(tiltX))
      );

      this.cameraRight = this.cameraRight || new THREE.Vector3();
      this.cameraUp = this.cameraUp || new THREE.Vector3();
      this.cameraTowardViewer = this.cameraTowardViewer || new THREE.Vector3();
      this.cameraRight.set(1, 0, 0).applyQuaternion(this.camera.quaternion).normalize();
      this.cameraUp.set(0, 1, 0).applyQuaternion(this.camera.quaternion).normalize();
      this.camera.getWorldDirection(this.cameraTowardViewer).multiplyScalar(-1).normalize();
      this.transitionPathMetrics = null;
      this.transitionQueueDescriptors = null;
    }

    getOrbitPoint(theta) {
      if (!this.orbitRotation || !this.cameraRight) this.refreshOrbitMetrics();
      const orbitReferenceWidth = this.orbitReferenceWidth || this.logoWidth || this.basePlaneWidth || 5.0;

      let phase = (theta % TAU) / TAU;
      if (phase < 0) phase += 1.0;

      const orbitConfig = this.orbitConfig || (this.camera.aspect > 1 ? REFERENCE_ORBIT.landscape : REFERENCE_ORBIT.portrait);
      const localPoint = new THREE.Vector3(
        Math.cos(theta) * orbitReferenceWidth * orbitConfig.radiusXRatio,
        0,
        Math.sin(theta) * orbitReferenceWidth * orbitConfig.radiusZRatio
      );
      localPoint.applyQuaternion(this.orbitRotation);
      const portraitYScale = (this.layoutProfile || this.getResponsiveLayoutProfile()).portraitYScale;

      // The reference uses one XZ ellipse rotated as a rigid plane. Keeping Y at
      // zero before rotation guarantees a single non-self-intersecting belt.
      const depthNorm = THREE.MathUtils.clamp(localPoint.z / this.orbitMaxCameraDepth, -1, 1);

      return {
        phase,
        depthNorm,
        position: new THREE.Vector3(0, 0, 0)
          .addScaledVector(this.cameraRight, localPoint.x)
          .addScaledVector(this.cameraUp, localPoint.y * portraitYScale)
          .addScaledVector(this.cameraTowardViewer, localPoint.z)
      };
    }

    getOrbitScale(theta) {
      let angleDeg = THREE.MathUtils.radToDeg(theta) % 360;
      if (angleDeg < 0) angleDeg += 360;

      const easeInOutQuad = (value) => (
        value < 0.5
          ? 2 * value * value
          : 1 - Math.pow(-2 * value + 2, 2) / 2
      );
      // The side crossings are compact; both broad arcs remain substantial.
      // This creates changing silhouette gaps without repacking the centers.
      const sideScale = 0.5;
      let zoneProgress = null;

      if (angleDeg >= 135 && angleDeg <= 225) {
        zoneProgress = (angleDeg - 135) / 90;
      } else if (angleDeg >= 315) {
        zoneProgress = (angleDeg - 315) / 90;
      } else if (angleDeg <= 45) {
        zoneProgress = (angleDeg + 45) / 90;
      }

      if (zoneProgress === null) return 1;

      let edgeInfluence;
      if (zoneProgress < 0.4) {
        edgeInfluence = 1 - easeInOutQuad(zoneProgress / 0.4);
      } else if (zoneProgress <= 0.6) {
        edgeInfluence = 0;
      } else {
        edgeInfluence = easeInOutQuad((zoneProgress - 0.6) / 0.4);
      }

      return THREE.MathUtils.lerp(sideScale, 1, edgeInfluence);
    }

    getPresentationScale(depthNorm) {
      // Actual camera distance already supplies the near/far hierarchy.
      return 1;
    }

    shouldRunIntro() {
      return this.enabled && !this.isMobile && (!this.debugMode || this.debugMode === 'intro-animation');
    }

    replayIntro() {
      if (!this.shouldRunIntro()) return;
      this.introStartTime = performance.now();
      this.introProgress = 0;
      this.introPhase = 'converging';
      this.introActive = true;
      this.logoIntroOpacity = 0;
      this.logoIntroScaleX = 0.095;
      if (this.container) this.container.dataset.mbsIntro = this.introPhase;
    }

    normalizeOrbitAngle(angle) {
      return ((angle % TAU) + TAU) % TAU;
    }

    getTransitionPathMetrics() {
      if (this.transitionPathMetrics) return this.transitionPathMetrics;

      const findGateAngle = (targetX, targetY) => {
        const targetLength = Math.hypot(targetX, targetY) || 1;
        const normalizedTargetX = targetX / targetLength;
        const normalizedTargetY = targetY / targetLength;
        let bestAngle = 0;
        let bestScore = -Infinity;

        for (let sampleIndex = 0; sampleIndex < 192; sampleIndex++) {
          const angle = (sampleIndex / 192) * TAU;
          const position = this.getOrbitPoint(angle).position;
          const x = position.dot(this.cameraRight);
          const y = position.dot(this.cameraUp);
          const length = Math.hypot(x, y) || 1;
          const score = (x / length) * normalizedTargetX + (y / length) * normalizedTargetY;
          if (score > bestScore) {
            bestScore = score;
            bestAngle = angle;
          }
        }
        return bestAngle;
      };

      const probeAngle = findGateAngle(1, 0);
      const probePosition = this.getOrbitPoint(probeAngle).position;
      const probeNext = this.getOrbitPoint(probeAngle + 0.012).position;
      const probeX = probePosition.dot(this.cameraRight);
      const probeY = probePosition.dot(this.cameraUp);
      const tangentX = probeNext.dot(this.cameraRight) - probeX;
      const tangentY = probeNext.dot(this.cameraUp) - probeY;
      const cross = probeX * tangentY - probeY * tangentX;

      this.transitionPathMetrics = {
        // In camera space, a negative 2D cross product is clockwise.
        clockwiseSign: cross < 0 ? 1 : -1,
        leftLowerGateAngle: findGateAngle(-0.72, -0.7),
        rightUpperGateAngle: findGateAngle(0.72, 0.7)
      };
      return this.transitionPathMetrics;
    }

    getClockwiseOrbitDistance(fromAngle, toAngle) {
      const { clockwiseSign } = this.getTransitionPathMetrics();
      return this.normalizeOrbitAngle((toAngle - fromAngle) * clockwiseSign);
    }

    advanceClockwiseOrbitAngle(fromAngle, distance) {
      const { clockwiseSign } = this.getTransitionPathMetrics();
      return fromAngle + distance * clockwiseSign;
    }

    getCubicBezierPoint(p0, p1, p2, p3, progress) {
      const t = THREE.MathUtils.clamp(progress, 0, 1);
      const inverse = 1 - t;
      return p0.clone().multiplyScalar(inverse * inverse * inverse)
        .add(p1.clone().multiplyScalar(3 * inverse * inverse * t))
        .add(p2.clone().multiplyScalar(3 * inverse * t * t))
        .add(p3.clone().multiplyScalar(t * t * t));
    }

    getTransitionGatePose(gateKey) {
      const metrics = this.getTransitionPathMetrics();
      const gateAngle = gateKey === 'left-lower'
        ? metrics.leftLowerGateAngle
        : metrics.rightUpperGateAngle;
      const gatePosition = this.getOrbitPoint(gateAngle).position;
      const tangentPosition = this.getOrbitPoint(
        this.advanceClockwiseOrbitAngle(gateAngle, 0.018)
      ).position;
      const tangent = tangentPosition.clone().sub(gatePosition).normalize();
      return { gateAngle, gatePosition, tangent };
    }

    getIntroFeederPosition(gateKey, progress) {
      const { gatePosition, tangent } = this.getTransitionGatePose(gateKey);
      const wordmarkWidth = this.logoWidth || 5;
      const frustumWidth = this.frustumWidth || wordmarkWidth * 1.6;
      const frustumHeight = this.frustumHeight || wordmarkWidth;
      const isLeftLower = gateKey === 'left-lower';
      const offscreenPosition = gatePosition.clone()
        .addScaledVector(this.cameraRight, frustumWidth * (isLeftLower ? -0.56 : 0.56))
        .addScaledVector(this.cameraUp, frustumHeight * (isLeftLower ? -0.48 : 0.48))
        .addScaledVector(this.cameraTowardViewer, -wordmarkWidth * 0.14);
      const handle = wordmarkWidth * (this.isMobile ? 0.16 : 0.22);
      const firstControl = offscreenPosition.clone()
        .add(gatePosition.clone().sub(offscreenPosition).normalize().multiplyScalar(handle * 1.35));
      const secondControl = gatePosition.clone().addScaledVector(tangent, -handle);
      return this.getCubicBezierPoint(
        offscreenPosition,
        firstControl,
        secondControl,
        gatePosition,
        progress
      );
    }

    getExitTailPosition(progress) {
      const { gatePosition, tangent } = this.getTransitionGatePose('left-lower');
      const wordmarkWidth = this.logoWidth || 5;
      const frustumWidth = this.frustumWidth || wordmarkWidth * 1.6;
      const frustumHeight = this.frustumHeight || wordmarkWidth;
      const handle = wordmarkWidth * (this.isMobile ? 0.18 : 0.25);
      const offscreenPosition = gatePosition.clone()
        .addScaledVector(this.cameraRight, -frustumWidth * 0.7)
        .addScaledVector(this.cameraUp, -frustumHeight * 0.58)
        .addScaledVector(this.cameraTowardViewer, -wordmarkWidth * 0.18);
      const firstControl = gatePosition.clone().addScaledVector(tangent, handle);
      const secondControl = offscreenPosition.clone()
        .addScaledVector(this.cameraRight, wordmarkWidth * 0.2)
        .addScaledVector(this.cameraUp, wordmarkWidth * 0.12);
      return this.getCubicBezierPoint(
        gatePosition,
        firstControl,
        secondControl,
        offscreenPosition,
        progress
      );
    }

    getTransitionQueueDescriptor(index) {
      if (!this.transitionQueueDescriptors || this.transitionQueueDescriptors.length !== this.bubbles.length) {
        const metrics = this.getTransitionPathMetrics();
        const descriptors = this.bubbles.map((bubble, bubbleIndex) => {
          // Two balanced ordered feeders reproduce the reference's left-lower
          // and right-upper diagonal entrances without random per-orb paths.
          const gateKey = bubbleIndex % 2 === 0 ? 'left-lower' : 'right-upper';
          const gateAngle = gateKey === 'left-lower'
            ? metrics.leftLowerGateAngle
            : metrics.rightUpperGateAngle;
          return {
            index: bubbleIndex,
            gateKey,
            distance: this.getClockwiseOrbitDistance(gateAngle, bubble.startAngle || 0),
            order: 0
          };
        });

        ['left-lower', 'right-upper'].forEach((gateKey) => {
          descriptors
            .filter((descriptor) => descriptor.gateKey === gateKey)
            .sort((a, b) => a.distance - b.distance)
            .forEach((descriptor, order) => {
              descriptor.order = order;
            });
        });
        this.transitionQueueDescriptors = descriptors;
      }
      return this.transitionQueueDescriptors[index];
    }

    getIntroTransform(targetPosition, targetAngle, index, now) {
      if (!this.introActive || this.introStartTime === null) {
        return { position: targetPosition, scale: 1, opacity: 1 };
      }

      const duration = this.isMobile ? 1.58 : 1.76;
      const elapsed = (now - this.introStartTime) / 1000;
      const descriptor = this.getTransitionQueueDescriptor(index);
      const stagger = (this.isMobile ? 0.4 : 0.46)
        + descriptor.order * (this.isMobile ? 0.045 : 0.052);
      const progress = THREE.MathUtils.clamp(
        (elapsed - stagger) / Math.max(0.4, duration - stagger),
        0,
        1
      );
      const feederEnd = this.isMobile ? 0.38 : 0.34;
      let position;
      if (progress <= feederEnd) {
        const feederProgress = this.smoothstep(0, feederEnd, progress);
        position = this.getIntroFeederPosition(descriptor.gateKey, feederProgress);
      } else {
        const metrics = this.getTransitionPathMetrics();
        const gateAngle = descriptor.gateKey === 'left-lower'
          ? metrics.leftLowerGateAngle
          : metrics.rightUpperGateAngle;
        const orbitProgress = this.smoothstep(feederEnd, 1, progress);
        const orbitDistance = this.getClockwiseOrbitDistance(gateAngle, targetAngle);
        const transitionAngle = this.advanceClockwiseOrbitAngle(
          gateAngle,
          orbitDistance * orbitProgress
        );
        position = this.getOrbitPoint(transitionAngle).position;
      }

      // Reference orbs enter already substantial. The camera-depth scale does
      // the visual work; they no longer grow from tiny dots near the center.
      const easedScale = 1 - Math.pow(1 - progress, 3);
      const introScale = THREE.MathUtils.lerp(this.isMobile ? 0.78 : 0.72, 1, easedScale);
      const introOpacity = THREE.MathUtils.smoothstep(progress, 0.01, 0.12);
      this.introPhase = progress <= 0 ? 'waiting' : progress < 0.82 ? 'orbit-entry' : 'settling';
      this.introProgress = Math.max(this.introProgress, progress);
      if (elapsed >= duration) {
        this.introActive = false;
        this.introPhase = 'complete';
        position.copy(targetPosition);
        this.logoIntroOpacity = 1;
      }
      if (this.container) this.container.dataset.mbsIntro = this.introPhase;

      return {
        position,
        scale: elapsed >= duration ? 1 : introScale,
        opacity: elapsed >= duration ? 1 : introOpacity
      };
    }

    getExitTransform(orbitPosition, orbitAngle, progress) {
      if (progress <= 0.0001) {
        return {
          position: orbitPosition,
          angle: orbitAngle,
          depthNorm: this.getOrbitPoint(orbitAngle).depthNorm,
          tailProgress: 0,
          scale: 1,
          opacity: 1
        };
      }

      const metrics = this.getTransitionPathMetrics();
      const eased = this.smoothstep(0, 1, progress);
      const tailTravel = TAU * (this.isMobile ? 0.34 : 0.4);
      const totalTravel = TAU + tailTravel;
      const travelled = totalTravel * eased;
      const gateDistance = this.getClockwiseOrbitDistance(
        orbitAngle,
        metrics.leftLowerGateAngle
      );

      let position;
      let transitionAngle = orbitAngle;
      let tailProgress = 0;
      let depthNorm;
      if (travelled <= gateDistance) {
        transitionAngle = this.advanceClockwiseOrbitAngle(orbitAngle, travelled);
        const sample = this.getOrbitPoint(transitionAngle);
        position = sample.position;
        depthNorm = sample.depthNorm;
      } else {
        tailProgress = THREE.MathUtils.clamp(
          (travelled - gateDistance) / tailTravel,
          0,
          1
        );
        position = this.getExitTailPosition(this.smoothstep(0, 1, tailProgress));
        depthNorm = THREE.MathUtils.clamp(
          position.dot(this.cameraTowardViewer) / Math.max(0.0001, this.orbitMaxCameraDepth),
          -1,
          1
        );
        transitionAngle = metrics.leftLowerGateAngle;
      }

      return {
        position,
        angle: transitionAngle,
        depthNorm,
        tailProgress,
        scale: THREE.MathUtils.lerp(1, this.isMobile ? 0.9 : 0.86, tailProgress),
        opacity: 1 - this.smoothstep(0.78, 1, tailProgress)
      };
    }

    init() {
      if (!this.section || !this.sectionId || instances.has(this.sectionId)) return;

      this.logTimelineEvent('app init started');
      window.moztechLastInitTime = performance.now();
      this.heightBeforeInit = this.section.clientHeight || 0;
      this.layoutShiftCount = 0;
      this.layoutShiftSources = [];
      this.resizeObserverEvents = 0;

      // Track layout shifts dynamically in loading mode
      if (window.location.search.includes('debug=loading')) {
        try {
          const obs = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
              if (!entry.hadRecentInput) {
                this.layoutShiftCount++;
                console.log(`[BMS Performance] Layout Shift detected. Score: ${entry.value}`);
                if (entry.sources) {
                  entry.sources.forEach((src) => {
                    if (src.node) {
                      console.log(`  Shift Source Node:`, src.node);
                      this.layoutShiftSources.push(src.node.nodeName + (src.node.className ? '.' + src.node.className.split(' ').join('.') : ''));
                    }
                  });
                }
              }
            }
            this.updateDebugDashboard();
          });
          obs.observe({ type: 'layout-shift', buffered: true });
          this.performanceObserver = obs;
        } catch (e) {
          console.warn("[Moztech] PerformanceObserver not supported:", e);
        }
      }

      instances.set(this.sectionId, this);
      if (!window.moztechBubblesInstances) {
        window.moztechBubblesInstances = new Set();
      }
      window.moztechBubblesInstances.forEach(inst => {
        if (!inst.section || !document.body.contains(inst.section)) {
          try {
            inst.destroy();
          } catch (e) {}
          window.moztechBubblesInstances.delete(inst);
        }
      });
      window.moztechBubblesInstances.add(this);
      window.moztechWebGLContextCount++;
      this.setupObserver();
      this.visibilityHandler = () => {
        if (document.hidden) {
          this.pauseVideos();
          if (this.renderer) this.onPointerUp();
          if (this.animFrameId) cancelAnimationFrame(this.animFrameId);
          this.animFrameId = null;
        } else if (this.visible && this.isSceneReady) {
          this._lastFrameTime = performance.now();
          this.playVideos();
          this.startDeferredAssetLoads();
          this.startRenderLoop();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.pointerListenersCount += 2;
      this.onScroll();

      // Find canvas
      const canvas = this.container.querySelector('.mbs-webgl-canvas');
      if (!canvas) return;

      const dataEl = document.getElementById(`mbs-bubbles-data-${this.sectionId}`);
      let rawData = [];
      if (dataEl) {
        try {
          rawData = JSON.parse(dataEl.textContent);
        } catch (e) {
          console.error("[Moztech] Failed to parse bubbles block data:", e);
        }
      }

      // Three.js References
      const THREE = window.THREE;
      if (!THREE) return;
      this.logTimelineEvent('ThreeJS lib resolved');

      // Check debug parameters
      const params = new URLSearchParams(window.location.search);
      this.debugMode = params.get('debug') || '';
      this.container.dataset.mbsDebug = this.debugMode;

      // 1. Scene, Camera, and Renderer Setup
      this.scene = new THREE.Scene();
      
      const parentEl = canvas.parentElement || this.container;
      const width = (parentEl && parentEl.clientWidth) || canvas.clientWidth || window.innerWidth || 800;
      const height = (parentEl && parentEl.clientHeight) || canvas.clientHeight || window.innerHeight || 600;

      this.viewportWidth = width;
      this.viewportHeight = height;
      this.isMobile = width < HERO_LAYOUT.phoneToTabletWidth.start;
      this.isTablet = width >= HERO_LAYOUT.phoneToTabletWidth.start && width < HERO_LAYOUT.tabletToDesktopWidth.end;
      this.isDesktop = width >= HERO_LAYOUT.tabletToDesktopWidth.end;

      // A long-lens camera keeps the ring sculptural instead of exaggerating it
      // into a bow-tie while preserving the existing on-screen frustum size.
      this.camera = new THREE.PerspectiveCamera(12, width / height, 0.1, 100);
      this.camera.position.set(0, 0, 28);

      this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
      this.logTimelineEvent('renderer created');
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.getPixelRatioCap()));
      this.renderer.setSize(width, height, false);
      this.renderer.outputEncoding = THREE.sRGBEncoding;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.05;
      this.renderer.autoClear = false;

      // 2. Lighting Layer (Studio Profiles)
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.42);
      this.scene.add(ambientLight);

      const dirLight = new THREE.DirectionalLight(0xffffff, 2.0);
      dirLight.position.set(5, 8, 8);
      this.scene.add(dirLight);

      const accentSpot = new THREE.SpotLight(0x9fc8da, 1.8, 25, Math.PI / 4, 0.55, 1);
      accentSpot.position.set(-6, -6, 5);
      this.scene.add(accentSpot);

      const violetFill = new THREE.DirectionalLight(0x9e84d8, 0.7);
      violetFill.position.set(-4, 5, -3);
      this.scene.add(violetFill);

      // 3. High-DPI Vector Logo Textures
      const canvasWidth = 2048;
      const canvasHeight = Math.round(canvasWidth / 3.125);
      const textCanvas = document.createElement('canvas');
      textCanvas.width = canvasWidth;
      textCanvas.height = canvasHeight;
      const ctx = textCanvas.getContext('2d');

      const textTexture = new THREE.CanvasTexture(textCanvas);
      textTexture.minFilter = THREE.LinearMipmapLinearFilter;
      textTexture.magFilter = THREE.LinearFilter;
      textTexture.generateMipmaps = true;
      textTexture.encoding = THREE.sRGBEncoding;
      textTexture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
      this.sharedTextures = this.sharedTextures || [];
      const studioEnvironmentTexture = this.createStudioEnvironmentTexture(THREE);
      this.sharedTextures.push(studioEnvironmentTexture);
      this.logTimelineEvent('scene created');

      // Calculate frustum dimensions at Z = 0
      const frustumHeight = 2 * Math.tan((this.camera.fov * Math.PI) / 360) * this.camera.position.z;
      const aspect = width / height;
      const frustumWidth = frustumHeight * aspect;
      
      this.frustumHeight = frustumHeight;
      this.frustumWidth = frustumWidth;
      this.refreshOrbitMetrics(frustumWidth);
      
      // The wordmark is the stage, not a background watermark. Desktop uses a
      // broader launch-poster proportion while mobile keeps the proven fit.
      const logoWidthFraction = this.getWordmarkWidthFraction();
      this.basePlaneWidth = frustumWidth * logoWidthFraction;
      const planeHeight = this.basePlaneWidth / 3.125;
      
      this.logoWidth = this.basePlaneWidth;
      this.logoHeight = planeHeight;

      const textGeo = new THREE.PlaneGeometry(this.basePlaneWidth, planeHeight);

      // Logo Depth Prepass Material (using robust MeshBasicMaterial to prevent rendering black rectangle artifacts on some GPUs)
      const prepassMat = new THREE.MeshBasicMaterial({
        map: textTexture,
        transparent: true,
        alphaTest: 0.05,
        colorWrite: false,
        depthWrite: true
      });
      this.textMeshPrepass = new THREE.Mesh(textGeo, prepassMat);
      this.textMeshPrepass.position.set(0, 0, 0);
      this.textMeshPrepass.visible = false; // Hide initially to prevent black box artifacts
      this.scene.add(this.textMeshPrepass);

      // Visual Logo Material (force color to white)
      const visualMat = new THREE.MeshBasicMaterial({
        map: textTexture,
        color: 0xffffff,
        transparent: true,
        alphaTest: 0.05,
        colorWrite: true,
        depthWrite: false,
        depthTest: false
      });
      this.textMeshVisual = new THREE.Mesh(textGeo, visualMat);
      this.textMeshVisual.position.set(0, 0, 0);
      this.textMeshVisual.visible = false; // Hide until loaded
      this.scene.add(this.textMeshVisual);

      // Unified loader promise registry
      const activeLoadPromises = [];
      this.logTimelineEvent('texture loading started');
      const trackLoad = (promise, name) => {
        this.totalAssetsCount++;
        this.logTimelineEvent(`asset queue: ${name}`);
        return promise.then(
          () => {
            this.loadedAssetsCount++;
            this.logTimelineEvent(`asset ready: ${name} (${this.loadedAssetsCount}/${this.totalAssetsCount})`);
            this.updateDebugDashboard();
          },
          (err) => {
            this.loadedAssetsCount++;
            this.logTimelineEvent(`asset fail: ${name}`);
            this.updateDebugDashboard();
          }
        );
      };

      // Load Logo SVG with explicit cloned path fills to override browser defaults
      const svgEl = this.container.querySelector('.mbs-logo-svg');
      let logoPromise;
      if (svgEl) {
        logoPromise = new Promise((resolve) => {
          const svgClone = svgEl.cloneNode(true);
          svgClone.setAttribute('fill', '#ffffff');
          svgClone.querySelectorAll('path').forEach(p => {
            p.setAttribute('fill', '#ffffff');
            p.style.fill = '#ffffff';
          });

          const svgString = new XMLSerializer().serializeToString(svgClone);
          const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
          const URL = window.URL || window.webkitURL || window;
          const url = URL.createObjectURL(svgBlob);
          const img = new Image();
          img.onload = () => {
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
            ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
            textTexture.needsUpdate = true;
            URL.revokeObjectURL(url);
            
            this.textMeshVisual.visible = true; // Show now!
            resolve();
          };
          img.onerror = () => {
            console.warn("[Moztech] SVG image loading failed, drawing text fallback.");
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 300px Poppins, sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(this.headingText, canvasWidth / 2, canvasHeight / 2);
            textTexture.needsUpdate = true;
            this.textMeshVisual.visible = true; // Show fallback now!
            resolve();
          };
          img.src = url;
        });
      } else {
        logoPromise = new Promise((resolve) => {
          ctx.clearRect(0, 0, canvasWidth, canvasHeight);
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 300px Poppins, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(this.headingText, canvasWidth / 2, canvasHeight / 2);
          textTexture.needsUpdate = true;
          this.textMeshVisual.visible = true; // Show fallback now!
          resolve();
        });
      }

      this.isSvgLogoReady = false;
      const logoTrackPromise = logoPromise.then(() => {
        this.isSvgLogoReady = true;
      });
      activeLoadPromises.push(trackLoad(logoTrackPromise, 'Wordmark Logo'));

      // Print debug logs requested by the user
      console.log("[Moztech Orbit Debug Console]");
      console.log(`- Source Branch: ${BUILD_SOURCE_BRANCH}`);
      console.log(`- Base Commit: ${BUILD_BASE_COMMIT} (release commit recorded separately)`);
      console.log(`- Loaded JS Version: ${BUILD_VERSION}`);
      console.log(`- Loaded CSS CDN Revision: ${CSS_ASSET_REVISION}`);
      console.log("- Logo Material Color:", this.textMeshVisual.material.color.getHexString());
      console.log("- Logo Material Opacity:", this.textMeshVisual.material.opacity);
      console.log("- Renderer OutputEncoding:", this.renderer.outputEncoding);
      console.log("- Renderer ToneMapping:", this.renderer.toneMapping);
      console.log("- Canvas Pixel Ratio:", this.renderer.getPixelRatio());
      console.log("- debugMode:", this.debugMode);
      console.log("- mbs-debug-labels element:", document.getElementById('mbs-debug-labels'));

      // Create visible debug overlay dashboard if debug mode is active
      if (this.debugMode) {
        const dbDiv = document.createElement('div');
        dbDiv.className = 'mbs-debug-dashboard';
        dbDiv.style.cssText = 'position: absolute; top: 20px; left: 20px; z-index: 999; font-family: monospace; font-size: 11px; background: rgba(8, 12, 22, 0.95); color: #00ffcc; border: 1px solid #2bb9ed; padding: 16px 20px; border-radius: 8px; line-height: 1.6; box-shadow: 0 0 25px rgba(43, 185, 237, 0.4); pointer-events: auto; min-width: 280px; text-align: left; text-shadow: 0 0 5px rgba(0, 255, 200, 0.5);';
        this.container.appendChild(dbDiv);
        this.debugDashboard = dbDiv;
      }

      if (this.debugMode === 'orbit-spacing' || this.debugMode === 'orbit-spacing-depth') {
        const overlayCanvas = document.createElement('canvas');
        overlayCanvas.id = 'mbs-debug-overlay-canvas';
        overlayCanvas.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 100;';
        this.container.appendChild(overlayCanvas);
        this.debugOverlayCanvas = overlayCanvas;
        this.debugOverlayCtx = overlayCanvas.getContext('2d');
        const canvas = this.renderer.domElement;
        overlayCanvas.width = canvas.clientWidth || 800;
        overlayCanvas.height = canvas.clientHeight || 600;
      }

      if (this.debugMode === 'physics-bounds') {
        const outline = document.createElement('div');
        outline.id = 'mbs-debug-safe-outline';
        outline.style.cssText = 'position: absolute; border: 2.2px dashed #2bb9ed; border-radius: 8px; box-shadow: inset 0 0 25px rgba(43, 185, 237, 0.12); pointer-events: none; z-index: 15; box-sizing: border-box; transition: opacity 0.3s;';
        this.container.appendChild(outline);
        this.safeAreaOutline = outline;
      }

      // 4. Map settings to manual art-directed 3D coordinates
      const count = rawData.length || 10;
      const parsedConfig = [];

      if (rawData.length > 0) {
        rawData.forEach((item, idx) => {
          const sizeFactor = (item.size || 180) / 200.0;
          
          // Project percentage coordinates (0-100) directly to static 3D anchor points
          const x_3d = ((item.x_pos || 50) / 50.0 - 1.0) * (frustumWidth * 0.44);
          const y_3d = -((item.y_pos || 50) / 50.0 - 1.0) * (frustumHeight * 0.4);
          const z_depth = (item.z_depth || 0) / 10.0;

          let typePreset = item.orb_type || 'crystal_glass';
          // Backward compatibility mapping for old V2 blocks stored in merchant DB
          if (item.type && item.type !== 'art_directed_orb') {
            if (item.type === 'image_bubble') typePreset = 'product_image';
            else if (item.type === 'spiky_bubble') typePreset = 'starburst';
            else if (item.type === 'globe_bubble') typePreset = 'wireframe_globe';
            else if (item.type === 'code_bubble') typePreset = 'dark_glossy_data';
            else if (item.type === 'ring_bubble') typePreset = 'ribbon_wrapped';
            else if (item.type === 'morph_bubble') typePreset = 'organic_blob';
            else if (item.type === 'model_bubble') typePreset = 'crystal_glass';
          }

          // Assign mass based on type
          let mass = 1.0;
          if (typePreset === 'starburst') mass = 1.4;
          if (typePreset === 'dark_glossy_data') mass = 1.3;
          if (typePreset === 'ui_screenshot') mass = 1.25;
          if (typePreset === 'small_bubble') mass = 0.4;

          parsedConfig.push({
            id: item.id,
            orb_type: typePreset,
            title: item.title,
            description: item.description || "Designed with premium cyberpunk glass engineering.",
            link: item.link || "#",
            size: sizeFactor,
            anchorX: x_3d,
            anchorY: y_3d,
            anchorZ: z_depth,
            mass: mass,
            opacity: (item.opacity || 90) / 100.0,
            rotation_speed: (item.rotation_speed || 10) / 10.0,
            interaction_intensity: (item.interaction_intensity || 10) / 10.0,
            image_url: this.enableProductAsset ? item.image_url : "",
            product_images: Array.isArray(item.product_images)
              ? item.product_images.filter(Boolean)
              : [],
            model_url: this.enableProductAsset ? item.model_url : "",
            video_url: this.enableProductAsset ? item.video_url : "",
            floatSpeed: 0.12 + (idx % 5) * 0.04
          });
        });
      } else {
        // CURATED FALLBACK DIRECTLY ON GRAPHICS POSTER COMPOSITION
        const defaultList = [
          { orb_type: 'crystal_glass', size: 2.0, anchorX: -0.8, anchorY: -0.4, anchorZ: 0.2, mass: 1.0 },
          { orb_type: 'ui_screenshot', size: 1.45, anchorX: 0.6, anchorY: -0.8, anchorZ: 0.4, mass: 1.25 },
          { orb_type: 'white_chart_data', size: 1.35, anchorX: -0.5, anchorY: 0.5, anchorZ: -0.3, mass: 1.15 },
          { orb_type: 'dark_glossy_data', size: 1.15, anchorX: 0.4, anchorY: 0.6, anchorZ: 0.2, mass: 1.3 },
          { orb_type: 'wireframe_globe', size: 1.10, anchorX: -0.3, anchorY: -0.6, anchorZ: -0.2, mass: 0.95 },
          { orb_type: 'ribbon_wrapped', size: 1.20, anchorX: 0.8, anchorY: -0.2, anchorZ: -0.4, mass: 1.05 },
          { orb_type: 'starburst', size: 1.15, anchorX: -0.7, anchorY: 0.3, anchorZ: 0.3, mass: 1.4 },
          { orb_type: 'small_bubble', size: 0.55, anchorX: 0.3, anchorY: -0.5, anchorZ: -0.1, mass: 0.4 },
          { orb_type: 'small_bubble', size: 0.48, anchorX: -0.4, anchorY: 0.4, anchorZ: 0.1, mass: 0.3 },
          { orb_type: 'small_bubble', size: 0.52, anchorX: 0.5, anchorY: 0.2, anchorZ: -0.2, mass: 0.35 }
        ].map((item, idx) => {
          const typePreset = item.orb_type;
          return {
            ...item,
            orb_type: typePreset,
            opacity: 0.9,
            rotation_speed: 1.0,
            interaction_intensity: 1.0,
            floatSpeed: 0.12 + (idx % 5) * 0.04
          };
        });
        parsedConfig.push(...defaultList);
      }

      const slotTypes = [
        'crystal_glass',       // 1. Large hero glass sphere
        'dark_glossy_data',    // 2. Dark code/data sphere
        'white_chart_data',    // 3. White analytics/chart sphere
        'wireframe_globe',     // 4. Wireframe globe
        'ribbon_wrapped',      // 5. Ribbon-wrapped sphere
        'purple_energy',       // 6. Purple energy sphere
        'starburst',           // 7. Starburst/energy object
        'ui_screenshot',       // 8. Product/UI sphere
        'organic_blob',        // 9. Organic point-cloud sphere
        'particle_grid',       // 10. Reactive particle grid sphere
        'product_image',       // 11. MOZTECH product carousel sphere
      ];

      // Shopify Editions uses a deliberate hierarchy across eleven objects:
      // hero spheres, supporting spheres and compact accents. Keep that
      // hierarchy explicit so packing cannot flatten it.
      const slotSizes = [
        1.3, 1.32, 1.28, 1.26, 1.1, 1.28, 1.12, 1.3, 1.16, 1.2, 1.26
      ];

      // Equal WORLD angles are deliberately not equal screen-space gaps.
      // Preserve the fixed 11-object order and the existing starting quadrant.
      const slotPhaseDeg = slotTypes.map((_, index) => 96 - index * 360 / slotTypes.length);

      const isSimpleDebug = (
        this.debugMode === 'prototype-1' ||
        this.debugMode === 'prototype-2' ||
        this.debugMode === 'prototype-orbit' ||
        this.debugMode === 'prototype-orbit-angle' ||
        this.debugMode === 'orbit-calibration' ||
        this.debugMode === 'orbit-belt-calibration' ||
        this.debugMode === 'orbit-system-reset' ||
        this.debugMode === 'orbit-minimal' ||
        this.debugMode === 'orbit-scale-curve' ||
        this.debugMode === 'single-orbit-belt' ||
        this.debugMode === 'depth-staging' ||
        this.debugMode === 'single-orbit-layout' ||
        this.debugMode === 'depth-occlusion' ||
        this.debugMode === 'orbit-depth-phase' ||
        this.debugMode === 'orbit-depth-phase-12' ||
        this.debugMode === 'h-front-pass'
      );

      const mappedConfigs = [];
      // B11 has one canonical Theme Editor source: the four section-level
      // product fields rendered as root data attributes. Legacy per-block
      // values remain a fallback for older saved themes, but never override a
      // current section-level upload. The built-in fallback is not an upload:
      // it must not mask the merchant's first product image in a block.
      const sectionProductImages = [
        this.productFanIsFallback ? '' : this.productFanImageUrl,
        this.productCapsuleImageUrl,
        this.productChargerImageUrl,
        this.productArkImageUrl
      ];
      const authoredProductImageSources = Array.from({ length: 4 }, (_, productIndex) => {
        if (sectionProductImages[productIndex]) {
          return {
            id: 'section',
            orbType: 'product_image',
            url: sectionProductImages[productIndex]
          };
        }
        const legacySource = rawData.find((entry) => (
          entry
          && Array.isArray(entry.product_images)
          && Boolean(entry.product_images[productIndex])
        ));
        return legacySource
          ? {
            id: legacySource.id || '',
            orbType: legacySource.orb_type || '',
            url: legacySource.product_images[productIndex]
          }
          : { id: '', orbType: '', url: '' };
      });
      const authoredProductImages = authoredProductImageSources.map((source) => source.url);
      const typedProductFallback = rawData.find((entry) => (
        entry && entry.orb_type === 'product_image' && entry.image_url
      ));
      if (!authoredProductImages[0] && typedProductFallback) {
        authoredProductImages[0] = typedProductFallback.image_url;
        authoredProductImageSources[0] = {
          id: typedProductFallback.id || '',
          orbType: typedProductFallback.orb_type || '',
          url: typedProductFallback.image_url
        };
      }
      if (!authoredProductImages[0] && this.productFanImageUrl) {
        authoredProductImages[0] = this.productFanImageUrl;
        authoredProductImageSources[0] = {
          id: 'default',
          orbType: 'product_image',
          url: this.productFanImageUrl
        };
      }
      for (let idx = 0; idx < slotTypes.length; idx++) {
        const item = rawData[idx] || {};
        const typePreset = isSimpleDebug ? 'placeholder' : slotTypes[idx];
        const sizeFactor = slotSizes[idx];

        mappedConfigs.push({
          id: item.id || `slot-${idx}`,
          orb_type: typePreset,
          title: item.title || `Orb ${String.fromCharCode(65 + idx)}`,
          description: item.description || "Designed with premium cyberpunk glass engineering.",
          link: item.link || "#",
          // Authored size lives in design space. Responsive scale is projected
          // from the final viewport on every resize so cold load and live resize
          // cannot produce different orb diameters.
          size: sizeFactor,
          anchorX: 0,
          anchorY: 0,
          anchorZ: 0,
          mass: 1.0,
          opacity: 0.85,
          rotation_speed: 1.0,
          interaction_intensity: 1.0,
          image_url: this.enableProductAsset ? (item.image_url || "") : "",
          product_images: idx === 10 ? authoredProductImages.slice(0, 4) : [],
          product_image_sources: idx === 10
            ? authoredProductImageSources.map((source) => ({
              id: source.id,
              orbType: source.orbType
            }))
            : [],
          model_url: this.enableProductAsset ? (item.model_url || "") : "",
          video_url: this.enableProductAsset && item.type === 'video_bubble'
            ? (item.video_url || "")
            : "",
          orbitRadiusXMult: 0.46,
          orbitRadiusYMult: 0.70,
          orbitRadiusZMult: 0.08,
          orbitSpeed: Math.PI * 2 / 60,
          startAngle: THREE.MathUtils.degToRad(slotPhaseDeg[idx]),
          floatSpeed: Math.PI * 2 / 60
        });
      }

      let activeConfigs = mappedConfigs;

      if (this.debugMode === 'orbit-depth-phase') {
        const phaseSlots = [
          { type: 'yellow_solid', title: 'Yellow Sphere', deg: 0 },
          { type: 'red_solid', title: 'Red Sphere', deg: 90 },
          { type: 'green_solid', title: 'Green Sphere', deg: 180 },
          { type: 'blue_solid', title: 'Blue Sphere', deg: 270 }
        ];
        activeConfigs = phaseSlots.map((slot, idx) => {
          return {
            id: `depth-phase-slot-${idx}`,
            orb_type: slot.type,
            title: slot.title,
            description: `Slot for phase ${slot.deg} deg`,
            link: '#',
            size: 1.2,
            anchorX: 0,
            anchorY: 0,
            anchorZ: 0,
            mass: 1.0,
            opacity: 0.85,
            rotation_speed: 1.0,
            interaction_intensity: 1.0,
            image_url: "",
            model_url: "",
            video_url: "",
            orbitRadiusXMult: 0.46,
            orbitRadiusYMult: 0.70,
            orbitRadiusZMult: 0.08,
            orbitSpeed: Math.PI * 2 / 60,
            startAngle: THREE.MathUtils.degToRad(slot.deg),
            floatSpeed: Math.PI * 2 / 60
          };
        });
      }

      if (this.debugMode === 'h-front-pass') {
        const phaseSlots = [
          { title: 'Phase 0.00 (Front-Side)', phase: 0.00, size: 0.85 },
          { title: 'Phase 0.60 (Front Peak)', phase: 0.60, size: 1.28 },
          { title: 'Phase 0.40 (Deep Back)', phase: 0.40, size: 1.02 },
          { title: 'Phase 0.20 (Back Transition)', phase: 0.20, size: 0.90 }
        ];
        activeConfigs = phaseSlots.map((slot, idx) => {
          return {
            id: `h-front-pass-slot-${idx}`,
            orb_type: 'placeholder',
            title: slot.title,
            description: `Occlusion proof slot for phase ${slot.phase}`,
            link: '#',
            size: slot.size,
            anchorX: 0,
            anchorY: 0,
            anchorZ: 0,
            mass: 1.0,
            opacity: 0.85,
            rotation_speed: 0.0,
            interaction_intensity: 0.0,
            image_url: "",
            model_url: "",
            video_url: "",
            orbitRadiusXMult: 0.68,
            orbitRadiusYMult: 1.12,
            orbitRadiusZMult: 0.12,
            orbitSpeed: 0.0,
            startAngle: slot.phase * Math.PI * 2,
            floatSpeed: 0.0
          };
        });
      }

      if (this.debugMode === 'mozcare-transmission-lab') {
        activeConfigs = [{
          id: 'mozcare-transmission-lab-orb',
          orb_type: 'ribbon_wrapped',
          title: 'MOZ Care+ Transmission Glass',
          description: 'IOR refraction, wordmark transmission and studio reflections',
          link: '#',
          size: this.isMobile ? 1.5 : 1.9,
          anchorX: 0,
          anchorY: 0,
          anchorZ: 0,
          mass: 1.1,
          opacity: 1,
          rotation_speed: 0.72,
          interaction_intensity: 1,
          image_url: '',
          model_url: '',
          video_url: '',
          orbitRadiusXMult: 0,
          orbitRadiusYMult: 0,
          orbitRadiusZMult: 0,
          orbitSpeed: 0,
          startAngle: Math.PI / 2,
          floatSpeed: 0
        }];
      }

      if (this.debugMode === 'starburst-tentacle-lab') {
        activeConfigs = [{
          id: 'starburst-tentacle-lab-orb',
          orb_type: 'starburst',
          title: 'MOZTECH Reactive Starburst',
          description: 'Instanced filament sphere with cursor attraction and traveling light pulses',
          link: '#',
          size: this.isMobile ? 1.45 : 1.95,
          anchorX: 0,
          anchorY: 0,
          anchorZ: 0,
          mass: 1.4,
          opacity: 1,
          rotation_speed: 0.72,
          interaction_intensity: 1,
          image_url: '',
          model_url: '',
          video_url: '',
          orbitRadiusXMult: 0,
          orbitRadiusYMult: 0,
          orbitRadiusZMult: 0,
          orbitSpeed: 0,
          startAngle: Math.PI / 2,
          floatSpeed: 0
        }];
      }

      if (this.debugMode === 'two-orb-interaction-lab') {
        activeConfigs = [
          {
            id: 'reactive-line-lab-orb',
            orb_type: 'wireframe_globe',
            title: 'Reactive Arc Field',
            description: 'Surface-tangent needles race around the shell and slow on focus',
            link: '#',
            size: this.isMobile ? 1.28 : 1.72,
            anchorX: 0,
            anchorY: 0,
            anchorZ: 0,
            mass: 0.95,
            opacity: 1,
            rotation_speed: 0.82,
            interaction_intensity: 1,
            image_url: '',
            model_url: '',
            video_url: '',
            orbitSpeed: 0,
            startAngle: Math.PI / 2,
            floatSpeed: 0
          },
          {
            id: 'purple-gemini-lab-orb',
            orb_type: 'purple_energy',
            title: 'Gemini Glass Stardust',
            description: 'Rounded solid Gemini stars turn clockwise inside a dark lilac marble',
            link: '#',
            size: this.isMobile ? 1.28 : 1.72,
            anchorX: 0,
            anchorY: 0,
            anchorZ: 0,
            mass: 1.1,
            opacity: 1,
            rotation_speed: 0.82,
            interaction_intensity: 1,
            image_url: '',
            model_url: '',
            video_url: '',
            orbitSpeed: 0,
            startAngle: Math.PI / 2,
            floatSpeed: 0
          }
        ];
      }

      if (this.debugMode === 'grid-mozcare-lab') {
        activeConfigs = [
          {
            id: 'particle-grid-lab-orb',
            orb_type: 'particle_grid',
            title: 'Top-down Particle Grid',
            description: 'Height-sequenced point expansion over a depth-faded spherical grid',
            link: '#',
            size: this.isMobile ? 1.24 : 1.68,
            anchorX: 0,
            anchorY: 0,
            anchorZ: 0,
            mass: 0.95,
            opacity: 1,
            rotation_speed: 0.82,
            interaction_intensity: 1,
            image_url: '',
            model_url: '',
            video_url: '',
            orbitSpeed: 0,
            startAngle: Math.PI / 2,
            floatSpeed: 0
          },
          {
            id: 'mozcare-surface-ribbon-lab-orb',
            orb_type: 'ribbon_wrapped',
            title: 'MOZ Care+ Surface Ribbon',
            description: 'Dark gray transmission body with a surface-conforming branded band',
            link: '#',
            size: this.isMobile ? 1.24 : 1.68,
            anchorX: 0,
            anchorY: 0,
            anchorZ: 0,
            mass: 1.05,
            opacity: 1,
            rotation_speed: 0.82,
            interaction_intensity: 1,
            image_url: '',
            model_url: '',
            video_url: '',
            orbitSpeed: 0,
            startAngle: Math.PI / 2,
            floatSpeed: 0
          }
        ];
      }

      if (this.debugMode === 'prototype-3' || this.debugMode === 'object-style-sheet') {
        const types = [
          { type: 'crystal_glass', title: '1. MOZTECH Brand Glass', desc: 'Original extruded MOZTECH emblem with depth-tested orbiting typography' },
          { type: 'dark_glossy_data', title: '2. Rising Code Lens', desc: 'Upward code stream with a rising magnifier ripple inside cyan black glass' },
          { type: 'white_chart_data', title: '3. MOZTECH Power Analytics', desc: 'Branded Qi2 power and device-health data inside clear white glass' },
          { type: 'wireframe_globe', title: '4. Reactive Needle Field', desc: 'Surface-tangent needles race around the glass shell and slow into focus on hover' },
          { type: 'ribbon_wrapped', title: '5. MOZ Care+ Ribbon', desc: 'Clear black glass wrapped by a blue-violet branded belt' },
          { type: 'purple_energy', title: '6. Gemini Glass Stardust', desc: 'Three rounded solid stars turn clockwise inside a black-to-lilac glass marble' },
          { type: 'starburst', title: '7. Reactive Starburst', desc: 'Instanced shader filaments with cursor attraction and traveling pulses' },
          { type: 'ui_screenshot', title: '8. UI Dashboard', desc: 'Neutral interface surface inside smoked glass' },
          { type: 'organic_blob', title: '9. Organic Point Field', desc: 'Hover reveals asymmetric jelly pulses, skirt waves and local ripple ridges' },
          { type: 'particle_grid', title: '10. Particle Grid', desc: 'Top-down sequential point expansion over a depth-faded spherical grid' },
          { type: 'product_image', title: '11. Product Carousel Glass', desc: 'MOZTECH products enclosed by a clear interactive glass marble' }
        ];

        activeConfigs = types.map((t, idx) => {
          const col = idx % 4;
          const row = Math.floor(idx / 4);
          
          const x = (col - 1.5) * 2.8; // -4.2, -1.4, 1.4, 4.2
          const y = (1 - row) * 1.8;   // 1.8, 0, -1.8
          
          return {
            orb_type: t.type,
            title: t.title,
            description: t.desc,
            size: 0.9,
            anchorX: x,
            anchorY: y,
            anchorZ: 0.0,
            mass: 1.0,
            opacity: 0.9,
            rotation_speed: 1.0,
            interaction_intensity: 1.0,
            floatSpeed: 0.0,
            startAngle: 0.0,
            orbitRadiusX: 0.0,
            orbitRadiusY: 0.0,
            orbitTilt: 0.0,
            orbitSpeed: 0.0
          };
        });
      }

      // 5. Shopify's product marble is not a fogged alpha shell. It adds a
      // rough environment reflection and an extremely narrow Fresnel highlight
      // over crisp content. Reproduce that single-pass additive material here;
      // each orb still clones uniforms so hover/depth state cannot leak.
      const glassShaderDef = {
        vertexShader: `
          varying vec3 vWorldNormal;
          varying vec3 vWorldPosition;
          varying vec3 vViewNormal;
          varying vec3 vViewPosition;
          void main() {
            vec4 worldPosition = modelMatrix * vec4(position, 1.0);
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            vWorldNormal = normalize(mat3(modelMatrix) * normal);
            vWorldPosition = worldPosition.xyz;
            vViewNormal = normalize(normalMatrix * normal);
            vViewPosition = -mvPosition.xyz;
            gl_Position = projectionMatrix * mvPosition;
          }
        `,
        fragmentShader: `
          precision highp float;

          varying vec3 vWorldNormal;
          varying vec3 vWorldPosition;
          varying vec3 vViewNormal;
          varying vec3 vViewPosition;

          uniform sampler2D environmentTexture;
          uniform vec3 baseColor;
          uniform vec3 accentColor;
          uniform vec3 hoverColor;
          uniform float opacity;
          uniform float roughness;
          uniform float fresnelPower;
          uniform vec3 lightDir;
          uniform float depthFade;
          uniform float hover;
          uniform float frostStrength;
          uniform float rimStrength;
          uniform float directionalHalo;
          uniform float time;

          const float PI = 3.141592653589793;

          vec2 equirectUv(vec3 direction) {
            vec3 ray = normalize(direction);
            return vec2(
              atan(ray.z, ray.x) / (2.0 * PI) + 0.5,
              asin(clamp(ray.y, -1.0, 1.0)) / PI + 0.5
            );
          }

          vec3 dfgApprox(vec3 viewDirection, vec3 normal, vec3 specularColor) {
            float dotNV = clamp(dot(normal, viewDirection), 0.0, 1.0);
            const vec4 c0 = vec4(-1.0, -0.0275, -0.572, 0.022);
            const vec4 c1 = vec4(1.0, 0.0425, 1.04, -0.04);
            vec4 response = roughness * c0 + c1;
            float a004 = min(response.x * response.x, exp2(-9.28 * dotNV))
              * response.x + response.y;
            vec2 fab = vec2(-1.04, 1.04) * a004 + response.zw;
            return specularColor * fab.x + fab.y;
          }

          void main() {
            vec3 worldNormal = normalize(vWorldNormal);
            vec3 worldViewDirection = normalize(cameraPosition - vWorldPosition);
            vec3 viewNormal = normalize(vViewNormal);
            vec3 viewDirection = normalize(vViewPosition);
            float dotNV = clamp(dot(viewNormal, viewDirection), 0.0, 1.0);
            float edge = 1.0 - dotNV;

            vec3 reflectionDirection = reflect(-worldViewDirection, worldNormal);
            reflectionDirection = normalize(mix(
              reflectionDirection,
              worldNormal,
              roughness * roughness
            ));
            vec3 environment = texture2D(environmentTexture, equirectUv(reflectionDirection)).rgb;
            environment = pow(max(environment, vec3(0.0)), vec3(2.2));

            vec3 irradiance = dfgApprox(viewDirection, viewNormal, baseColor);
            irradiance *= irradiance;
            vec3 interactiveFresnel = mix(accentColor, hoverColor, hover * 0.42);
            vec3 fresnel = pow(edge, fresnelPower)
              * interactiveFresnel
              * rimStrength
              * (1.0 + hover * 0.16);

            // The official cubemap produces a broad lower crescent. Our small
            // procedural environment needs this camera-space reinforcement to
            // keep the same marble read over MOZTECH's blue-black backdrop.
            vec2 disk = viewNormal.xy;
            float radial = clamp(length(disk), 0.0, 1.0);
            vec2 diskDirection = radial > 0.001 ? disk / radial : vec2(0.0, 1.0);
            float innerArc = smoothstep(0.48, 0.8, radial);
            float outerArc = 1.0 - smoothstep(0.975, 1.0, radial);
            float lowerCrescent = innerArc * outerArc
              * pow(max(dot(diskDirection, normalize(vec2(-0.58, -0.82))), 0.0), 4.8);
            float sideCrescent = smoothstep(0.78, 0.91, radial)
              * (1.0 - smoothstep(0.985, 1.0, radial))
              * pow(max(dot(diskDirection, normalize(vec2(0.97, -0.22))), 0.0), 7.0);
            float keyGlint = pow(max(dot(
              reflectionDirection,
              normalize(vec3(-0.38, 0.78, 0.5))
            ), 0.0), mix(120.0, 54.0, roughness));
            float innerFresnelBand = smoothstep(0.54, 0.78, radial)
              * (1.0 - smoothstep(0.89, 0.965, radial));
            float outerFresnelBand = smoothstep(0.84, 0.972, radial);
            float razorRim = smoothstep(0.965, 0.996, radial);
            float upperKeyCrescent = smoothstep(0.38, 0.58, radial)
              * (1.0 - smoothstep(0.76, 0.9, radial))
              * pow(max(dot(diskDirection, normalize(vec2(-0.5, 0.866))), 0.0), 13.0);

            // Shopify's glass does not read as a uniform white outline. Its
            // volume comes from a broad lower halo, a shorter side reflection,
            // and one compact upper glint, with most of the silhouette left
            // transparent. Keep the legacy ring for non-targeted orb styles;
            // the six approved glass marbles opt into this directional mask.
            float lowerHaloDirection = pow(max(dot(
              diskDirection,
              normalize(vec2(-0.42, -0.907))
            ), 0.0), 2.35);
            float sideHaloDirection = pow(max(dot(
              diskDirection,
              normalize(vec2(0.94, -0.342))
            ), 0.0), 5.4);
            float crownHaloDirection = pow(max(dot(
              diskDirection,
              normalize(vec2(-0.58, 0.815))
            ), 0.0), 12.0);
            float haloDirection = clamp(
              lowerHaloDirection
              + sideHaloDirection * 0.52
              + crownHaloDirection * 0.46,
              0.0,
              1.0
            );
            float outerHaloMask = mix(1.0, haloDirection, directionalHalo);
            float innerHaloMask = mix(
              1.0,
              clamp(
                lowerHaloDirection * 0.72
                + sideHaloDirection * 0.28
                + crownHaloDirection * 0.34,
                0.0,
                1.0
              ),
              directionalHalo
            );

            vec3 color = environment * irradiance * (0.5 + hover * 0.08);
            color += fresnel * outerHaloMask;
            vec3 opticalWhite = vec3(0.94, 0.98, 1.0);
            color += opticalWhite * (
              innerFresnelBand * innerHaloMask * 0.12
              + outerFresnelBand * outerHaloMask * 0.2
              + razorRim * outerHaloMask * 0.16
              + lowerCrescent * 1.08
              + sideCrescent * 0.38
              + upperKeyCrescent * 0.42
              + keyGlint * 0.38
            ) * (0.84 + hover * 0.16);
            color *= 1.0 - frostStrength * 0.08;

            // The atmospheric blue/violet field is CSS behind the transparent
            // WebGL canvas. A near-opaque sphere alpha would composite the
            // canvas' transparent black over that field, even when the glass
            // color itself is dark. Keep the center transmissive and reserve
            // opacity for the Fresnel rim and reflected highlights.
            float reflectedLuminance = dot(color, vec3(0.2126, 0.7152, 0.0722));
            float glassAlpha = 0.05
              + pow(edge, 1.85) * outerHaloMask * 0.22
              + innerFresnelBand * innerHaloMask * 0.12
              + outerFresnelBand * outerHaloMask * 0.3
              + razorRim * outerHaloMask * 0.15
              + lowerCrescent * 0.48
              + sideCrescent * 0.2
              + upperKeyCrescent * 0.3
              + keyGlint * 0.28
              + frostStrength * 0.26
              + reflectedLuminance * 0.06;
            glassAlpha = clamp(glassAlpha, 0.045, 0.92) * opacity * depthFade;

            gl_FragColor = vec4(color * depthFade, glassAlpha);
            #include <tonemapping_fragment>
            #include <encodings_fragment>
          }
        `
      };

      const crystalGlassMat = new THREE.ShaderMaterial({
        vertexShader: glassShaderDef.vertexShader,
        fragmentShader: glassShaderDef.fragmentShader,
        uniforms: {
          environmentTexture: { value: studioEnvironmentTexture },
          baseColor: { value: new THREE.Color('#525252') },
          accentColor: { value: new THREE.Color('#939393') },
          hoverColor: { value: new THREE.Color('#d9dde0') },
          opacity: { value: 0.92 },
          roughness: { value: 0.5 },
          fresnelPower: { value: 15.0 },
          lightDir: { value: new THREE.Vector3(5.0, 8.0, 8.0).normalize() },
          depthFade: { value: 1 },
          hover: { value: 0 },
          frostStrength: { value: 0 },
          rimStrength: { value: 1 },
          directionalHalo: { value: 0 },
          time: { value: 0 }
        },
        transparent: true,
        depthWrite: false,
        blending: THREE.NormalBlending,
        side: THREE.FrontSide
      });

      const darkGlossyGlassMat = new THREE.ShaderMaterial({
        vertexShader: glassShaderDef.vertexShader,
        fragmentShader: glassShaderDef.fragmentShader,
        uniforms: {
          environmentTexture: { value: studioEnvironmentTexture },
          baseColor: { value: new THREE.Color('#525252') },
          accentColor: { value: new THREE.Color('#939393') },
          hoverColor: { value: new THREE.Color('#d9dde0') },
          opacity: { value: 0.92 },
          roughness: { value: 0.5 },
          fresnelPower: { value: 15.0 },
          lightDir: { value: new THREE.Vector3(5.0, 8.0, 8.0).normalize() },
          depthFade: { value: 1 },
          hover: { value: 0 },
          frostStrength: { value: 0 },
          rimStrength: { value: 1 },
          directionalHalo: { value: 0 },
          time: { value: 0 }
        },
        transparent: true,
        depthWrite: false,
        blending: THREE.NormalBlending,
        side: THREE.FrontSide
      });

      const transmissionVertexShader = `
        varying vec3 vWorldPosition;
        varying vec3 vWorldNormal;
        varying vec3 vViewPosition;

        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vec4 mvPosition = viewMatrix * worldPosition;
          vWorldPosition = worldPosition.xyz;
          vWorldNormal = normalize(mat3(modelMatrix) * normal);
          vViewPosition = -mvPosition.xyz;
          gl_Position = projectionMatrix * mvPosition;
        }
      `;

      const transmissionFragmentShader = `
        precision highp float;

        varying vec3 vWorldPosition;
        varying vec3 vWorldNormal;
        varying vec3 vViewPosition;

        uniform sampler2D uWordmarkTexture;
        uniform sampler2D uEnvironmentTexture;
        uniform vec2 uWordmarkSize;
        uniform vec2 uWordmarkTexel;
        uniform vec3 uWordmarkCenter;
        uniform vec3 uWordmarkRight;
        uniform vec3 uWordmarkUp;
        uniform vec3 uWordmarkNormal;
        uniform vec3 uBackgroundColor;
        uniform vec3 uTintColor;
        uniform vec3 uBodyColor;
        uniform float uIor;
        uniform float uRefractionStrength;
        uniform float uThickness;
        uniform float uRoughness;
        uniform float uChromaticAberration;
        uniform float uEnvironmentIntensity;
        uniform float uBodyStrength;
        uniform float uWhiteEdgeStrength;
        uniform float uDepthFade;
        uniform float uHover;
        uniform float uTime;

        const float PI = 3.141592653589793;

        float insideUnitSquare(vec2 uv) {
          return step(0.0, uv.x) * step(uv.x, 1.0)
            * step(0.0, uv.y) * step(uv.y, 1.0);
        }

        vec2 wordmarkUv(float materialIor, vec2 sampleOffset, out float validRay) {
          vec3 normal = normalize(vWorldNormal);
          vec3 incident = normalize(vWorldPosition - cameraPosition);
          vec3 refractedRay = refract(incident, normal, 1.0 / materialIor);
          vec3 transmissionRay = normalize(mix(incident, refractedRay, uRefractionStrength));
          float denominator = dot(transmissionRay, uWordmarkNormal);
          float travel = dot(uWordmarkCenter - vWorldPosition, uWordmarkNormal)
            / (abs(denominator) < 0.0001 ? -0.0001 : denominator);
          validRay = step(0.0, travel);
          vec3 hit = vWorldPosition + transmissionRay * max(travel, 0.0);
          vec3 relativeHit = hit - uWordmarkCenter;
          vec2 uv = vec2(
            dot(relativeHit, uWordmarkRight) / max(uWordmarkSize.x, 0.0001) + 0.5,
            dot(relativeHit, uWordmarkUp) / max(uWordmarkSize.y, 0.0001) + 0.5
          );
          return uv + sampleOffset;
        }

        vec4 sampleWordmark(float materialIor, float sampleRotation) {
          float validRay = 0.0;
          vec2 uv = wordmarkUv(materialIor, vec2(0.0), validRay);
          float spreadAmount = 1.0 + uRoughness * 42.0;
          vec2 spread = uWordmarkTexel * spreadAmount;
          vec2 directionA = vec2(cos(sampleRotation), sin(sampleRotation));
          vec2 directionB = vec2(-directionA.y, directionA.x);

          vec4 sampleColor = texture2D(uWordmarkTexture, uv) * 0.42;
          sampleColor += texture2D(uWordmarkTexture, uv + directionA * spread) * 0.145;
          sampleColor += texture2D(uWordmarkTexture, uv - directionA * spread) * 0.145;
          sampleColor += texture2D(uWordmarkTexture, uv + directionB * spread) * 0.145;
          sampleColor += texture2D(uWordmarkTexture, uv - directionB * spread) * 0.145;
          return sampleColor * insideUnitSquare(uv) * validRay;
        }

        vec2 equirectUv(vec3 direction) {
          vec3 ray = normalize(direction);
          return vec2(
            atan(ray.z, ray.x) / (2.0 * PI) + 0.5,
            asin(clamp(ray.y, -1.0, 1.0)) / PI + 0.5
          );
        }

        void main() {
          vec3 normal = normalize(vWorldNormal);
          vec3 viewDirection = normalize(cameraPosition - vWorldPosition);
          vec3 incident = -viewDirection;
          float ndv = clamp(dot(normal, viewDirection), 0.0, 1.0);
          float edge = 1.0 - ndv;

          float chroma = uChromaticAberration * (0.72 + edge * 0.58);
          vec4 redSample = sampleWordmark(uIor * (1.0 - chroma), 0.0);
          vec4 greenSample = sampleWordmark(uIor, 2.0943951);
          vec4 blueSample = sampleWordmark(uIor * (1.0 + chroma), 4.1887902);
          float wordmarkAlpha = max(redSample.a, max(greenSample.a, blueSample.a));
          vec3 refractedWordmark = vec3(redSample.r, greenSample.g, blueSample.b);
          vec3 transmission = mix(uBackgroundColor, refractedWordmark, wordmarkAlpha);

          float opticalPath = uThickness / max(ndv, 0.2);
          vec3 absorption = exp(-vec3(0.2, 0.11, 0.065) * opticalPath);
          transmission *= absorption * mix(vec3(1.0), uTintColor, 0.14);
          float bodyMask = uBodyStrength
            * (0.56 + ndv * 0.28)
            * (1.0 - wordmarkAlpha * 0.62);
          vec3 volumetricBody = mix(transmission, uBodyColor, clamp(bodyMask, 0.0, 0.86));

          vec3 reflectionDirection = reflect(incident, normal);
          vec3 environment = texture2D(uEnvironmentTexture, equirectUv(reflectionDirection)).rgb;
          float f0 = pow((uIor - 1.0) / (uIor + 1.0), 2.0);
          float fresnel = f0 + (1.0 - f0) * pow(edge, 5.0);
          float whiteEdge = smoothstep(0.78, 0.985, edge);
          float innerEdge = smoothstep(0.3, 0.76, edge) * (1.0 - whiteEdge);
          vec3 viewNormal = normalize(mat3(viewMatrix) * normal);
          vec2 disk = viewNormal.xy;
          float radial = clamp(length(disk), 0.0, 1.0);
          vec2 diskDirection = radial > 0.001 ? disk / radial : vec2(0.0, 1.0);
          float shellInnerBand = smoothstep(0.52, 0.77, radial)
            * (1.0 - smoothstep(0.9, 0.968, radial));
          float shellOuterBand = smoothstep(0.84, 0.972, radial);
          float shellRazorRim = smoothstep(0.965, 0.997, radial);
          float shellUpperGlint = smoothstep(0.36, 0.56, radial)
            * (1.0 - smoothstep(0.76, 0.9, radial))
            * pow(max(dot(diskDirection, normalize(vec2(-0.5, 0.866))), 0.0), 13.0);
          float shellLowerCrescent = smoothstep(0.48, 0.8, radial)
            * (1.0 - smoothstep(0.975, 1.0, radial))
            * pow(max(dot(diskDirection, normalize(vec2(-0.58, -0.82))), 0.0), 4.6);
          float shellLowerHalo = pow(max(dot(
            diskDirection,
            normalize(vec2(-0.42, -0.907))
          ), 0.0), 2.35);
          float shellSideHalo = pow(max(dot(
            diskDirection,
            normalize(vec2(0.94, -0.342))
          ), 0.0), 5.4);
          float shellCrownHalo = pow(max(dot(
            diskDirection,
            normalize(vec2(-0.58, 0.815))
          ), 0.0), 12.0);
          float shellOuterHaloMask = clamp(
            shellLowerHalo + shellSideHalo * 0.52 + shellCrownHalo * 0.46,
            0.0,
            1.0
          );
          float shellInnerHaloMask = clamp(
            shellLowerHalo * 0.72
            + shellSideHalo * 0.28
            + shellCrownHalo * 0.34,
            0.0,
            1.0
          );

          float keyHighlight = pow(max(dot(reflectionDirection, normalize(vec3(-0.32, 0.72, 0.62))), 0.0), 64.0);
          float fillHighlight = pow(max(dot(reflectionDirection, normalize(vec3(0.76, 0.18, 0.62))), 0.0), 32.0);
          float lowerCrescent = pow(max(dot(reflectionDirection, normalize(vec3(0.42, -0.7, 0.58))), 0.0), 9.5);
          float sideCrescent = pow(max(dot(reflectionDirection, normalize(vec3(-0.82, 0.04, 0.57))), 0.0), 13.0);
          float hoverBoost = 1.0 + uHover * 0.22;
          vec3 reflectedLight = environment * uEnvironmentIntensity
            * (0.14 + fresnel * 1.5 + innerEdge * 0.1)
            * hoverBoost;

          vec3 color = volumetricBody * (1.0 - fresnel * 0.52);
          color += reflectedLight;
          color += vec3(1.0) * (
            whiteEdge * shellOuterHaloMask * uWhiteEdgeStrength
            + innerEdge * shellInnerHaloMask * 0.08
            + shellInnerBand * shellInnerHaloMask * 0.1
            + shellOuterBand * shellOuterHaloMask * 0.18
            + shellRazorRim * shellOuterHaloMask * 0.16
            + shellUpperGlint * 0.4
            + shellLowerCrescent * 1.05
            + keyHighlight * 0.72
            + fillHighlight * 0.29
            + lowerCrescent * 0.68
            + sideCrescent * 0.3
          );
          color += vec3(1.0) * pow(edge, 7.0) * (0.006 + uHover * 0.012);

          // Outside the refracted wordmark, expose the real CSS atmosphere
          // behind the WebGL canvas. Over the wordmark, retain enough coverage
          // to prevent the undistorted mark from leaking through twice.
          float transmissionAlpha = mix(0.075, 0.9, wordmarkAlpha);
          transmissionAlpha += whiteEdge * shellOuterHaloMask * 0.58
            + innerEdge * shellInnerHaloMask * 0.1
            + shellInnerBand * shellInnerHaloMask * 0.1
            + shellOuterBand * shellOuterHaloMask * 0.26
            + shellRazorRim * shellOuterHaloMask * 0.16
            + shellUpperGlint * 0.3
            + shellLowerCrescent * 0.48
            + keyHighlight * 0.48
            + fillHighlight * 0.12
            + lowerCrescent * 0.3
            + sideCrescent * 0.18
            + bodyMask * 0.06;
          transmissionAlpha = clamp(transmissionAlpha, 0.06, 0.94) * uDepthFade;

          gl_FragColor = vec4(max(color * uDepthFade, vec3(0.0015)), transmissionAlpha);
        }
      `;

      const createPremiumTransmissionMaterial = (thickness) => {
        const material = new THREE.ShaderMaterial({
          vertexShader: transmissionVertexShader,
          fragmentShader: transmissionFragmentShader,
          uniforms: {
            uWordmarkTexture: { value: textTexture },
            uEnvironmentTexture: { value: studioEnvironmentTexture },
            uWordmarkSize: { value: new THREE.Vector2(this.logoWidth, this.logoHeight) },
            uWordmarkTexel: { value: new THREE.Vector2(1 / canvasWidth, 1 / canvasHeight) },
            uWordmarkCenter: { value: new THREE.Vector3(0, 0, 0) },
            uWordmarkRight: { value: new THREE.Vector3(1, 0, 0) },
            uWordmarkUp: { value: new THREE.Vector3(0, 1, 0) },
            uWordmarkNormal: { value: new THREE.Vector3(0, 0, 1) },
            uBackgroundColor: { value: new THREE.Color('#02060b') },
            uTintColor: { value: new THREE.Color('#b8d7e5') },
            uBodyColor: { value: new THREE.Color('#111318') },
            uIor: { value: 1.2 },
            uRefractionStrength: { value: 0.32 },
            uThickness: { value: thickness },
            uRoughness: { value: 0.09 },
            uChromaticAberration: { value: 0.006 },
            uEnvironmentIntensity: { value: 0.86 },
            uBodyStrength: { value: 0.54 },
            uWhiteEdgeStrength: { value: 0.68 },
            uDepthFade: { value: 1 },
            uHover: { value: 0 },
            uTime: { value: 0 }
          },
          transparent: true,
          depthWrite: false,
          depthTest: true,
          blending: THREE.NormalBlending,
          side: THREE.FrontSide
        });
        material.userData.mbsForceOpaque = false;
        material.userData.mbsPremiumTransmission = true;
        material.userData.mbsLocalRenderOrder = 0;
        return material;
      };

      const frostedGlassMat = new THREE.ShaderMaterial({
        vertexShader: glassShaderDef.vertexShader,
        fragmentShader: glassShaderDef.fragmentShader,
        uniforms: {
          environmentTexture: { value: studioEnvironmentTexture },
          baseColor: { value: new THREE.Color('#eef4ff') },
          accentColor: { value: new THREE.Color('#f8f9fa') },
          hoverColor: { value: new THREE.Color('#ffffff') },
          opacity: { value: 0.7 },
          roughness: { value: 0.72 },
          fresnelPower: { value: 4.0 },
          lightDir: { value: new THREE.Vector3(5.0, 8.0, 8.0).normalize() },
          depthFade: { value: 1 },
          hover: { value: 0 },
          frostStrength: { value: 0.88 },
          rimStrength: { value: 1.28 },
          directionalHalo: { value: 0 },
          time: { value: 0 }
        },
        transparent: true,
        depthWrite: false,
        blending: THREE.NormalBlending,
        side: THREE.FrontSide
      });

      const uiInactiveColor = new THREE.Color('#c7cbd0');
      const uiInactiveBright = new THREE.Color('#f2f3f4');
      const uiActiveColor = new THREE.Color('#2bb9ed');
      const uiInactiveDark = new THREE.Color('#363b42');
      const uiActiveDark = new THREE.Color('#0b6f93');
      const productInactiveColor = new THREE.Color('#8b9096');
      const mixCssColor = (from, to, amount) => {
        const t = THREE.MathUtils.clamp(amount, 0, 1);
        const red = Math.round(THREE.MathUtils.lerp(from[0], to[0], t));
        const green = Math.round(THREE.MathUtils.lerp(from[1], to[1], t));
        const blue = Math.round(THREE.MathUtils.lerp(from[2], to[2], t));
        return `rgb(${red}, ${green}, ${blue})`;
      };

      // 6. Instantiating Orb Object types
      this.textureLoader = new THREE.TextureLoader();
      this.gltfLoader = window.THREE.GLTFLoader ? new THREE.GLTFLoader() : null;
      if (this.gltfLoader && window.THREE.DRACOLoader) {
        const dracoLoader = new THREE.DRACOLoader();
        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.4.3/');
        this.gltfLoader.setDRACOLoader(dracoLoader);
      }

      activeConfigs.forEach((b, idx) => {
        const wordmarkWidth = this.logoWidth || this.basePlaneWidth || 5.0;
        const wordmarkHeight = this.logoHeight || (this.basePlaneWidth / 3.125) || 1.6;

        // Constrained Anchor Offsets (restricted to maximum per-object offsets)
        const maxOffsetX = wordmarkWidth * 0.08;
        const maxOffsetY = wordmarkHeight * 0.16;
        const maxOffsetZ = wordmarkWidth * 0.03;

        const clampedAnchorX = Math.max(-maxOffsetX, Math.min(maxOffsetX, b.anchorX || 0));
        const clampedAnchorY = Math.max(-maxOffsetY, Math.min(maxOffsetY, b.anchorY || 0));
        const clampedAnchorZ = Math.max(-maxOffsetZ, Math.min(maxOffsetZ, b.anchorZ || 0));
        const smallAnchorOffset = new THREE.Vector3(clampedAnchorX, clampedAnchorY, clampedAnchorZ);

        const startAngle = b.startAngle !== undefined
          ? b.startAngle
          : (idx / Math.max(activeConfigs.length, 1)) * TAU;
        const orbitRadiusXMult = b.orbitRadiusXMult !== undefined ? b.orbitRadiusXMult : (0.42 + (idx % 4) * 0.03);
        const orbitRadiusYMult = b.orbitRadiusYMult !== undefined ? b.orbitRadiusYMult : (0.55 + (idx % 3) * 0.08);
        const orbitRadiusZMult = b.orbitRadiusZMult !== undefined ? b.orbitRadiusZMult : (0.05 + (idx % 3) * 0.02);
        
        // Every target advances at the same speed. Per-object rotation_speed is
        // reserved for self-rotation so objects cannot overtake one another.
        const orbitSpeed = REFERENCE_ORBIT.angularSpeed;

        b.startAngle = startAngle;
        b.orbitRadiusXMult = orbitRadiusXMult;
        b.orbitRadiusYMult = orbitRadiusYMult;
        b.orbitRadiusZMult = orbitRadiusZMult;
        b.orbitSpeed = orbitSpeed;

        const initTheta = startAngle;
        const initSample = this.getOrbitPoint(initTheta);
        const initialDepthNorm = initSample.depthNorm;
        const initialIsForeground = initialDepthNorm > 0.04 || (
          Math.abs(initialDepthNorm) <= 0.04 && Math.cos(initTheta) >= 0
        );
        const initPos = initSample.position.clone();

        const group = new THREE.Group();
        group.position.copy(initPos);
        group.scale.setScalar(this.getOrbitScale(startAngle));
        group.userData.mbsPendingRenderables = [];

        this.scene.add(group);

        const baseRadius = b.size * 0.55;

        // Shopify's glass shell already supplies its own additive reflection.
        // A second expanded halo creates the exact full white ring the user
        // rejected and costs another transparent draw, so production uses none.
        let haloMesh = null;

        let innerUpdate = null;
        const interactionState = {
          hover: 0,
          hoverTarget: 0,
          selected: 0,
          selectedTarget: 0,
          pointerX: 0,
          pointerY: 0
        };

        if (this.debugMode === 'prototype-1' || this.debugMode === 'prototype-2' || this.debugMode === 'prototype-orbit' || this.debugMode === 'prototype-orbit-angle' || this.debugMode === 'orbit-calibration' || this.debugMode === 'orbit-belt-calibration' || this.debugMode === 'orbit-system-reset' || this.debugMode === 'orbit-minimal' || this.debugMode === 'orbit-scale-curve' || this.debugMode === 'single-orbit-belt' || this.debugMode === 'depth-staging' || this.debugMode === 'single-orbit-layout' || this.debugMode === 'depth-occlusion' || this.debugMode === 'orbit-depth-phase' || this.debugMode === 'orbit-depth-phase-12' || this.debugMode === 'orbit-spacing' || this.debugMode === 'orbit-spacing-depth') {
          // Render plain placeholder sphere with specific colors
          const sphereGeo = new THREE.SphereGeometry(baseRadius, 64, 64);
          
          let sphereColor = 0x7f8c8d;
          if (b.orb_type === 'red_solid') sphereColor = 0xff0000;
          else if (b.orb_type === 'yellow_solid') sphereColor = 0xffff00;
          else if (b.orb_type === 'blue_solid') sphereColor = 0x0000ff;
          else if (b.orb_type === 'green_solid') sphereColor = 0x00ff00;
          else if (this.debugMode === 'orbit-depth-phase-12') {
            const colors = [
              0xff0000, 0xffa500, 0xffff00, 0x00ff00, 0x00ffff, 0x0000ff,
              0x8a2be2, 0xff00ff, 0xffc0cb, 0xffffff, 0x7f8c8d, 0x008000
            ];
            sphereColor = colors[idx % colors.length];
          }

          const placeholderMat = new THREE.MeshStandardMaterial({
            color: sphereColor,
            roughness: 0.7,
            metalness: 0.2,
            flatShading: true,
            transparent: true,
            opacity: 0.85
          });
          const sphereMesh = new THREE.Mesh(sphereGeo, placeholderMat);
          group.add(sphereMesh);

          if (this.debugMode === 'single-orbit-layout') {
            const markerGeo = new THREE.SphereGeometry(0.04, 8, 8);
            const markerMat = new THREE.MeshBasicMaterial({ color: 0xff3333 });
            const markerMesh = new THREE.Mesh(markerGeo, markerMat);
            this.scene.add(markerMesh);
            b.centerMarker = markerMesh;
          }
          
          if (this.debugMode !== 'orbit-minimal' && this.debugMode !== 'orbit-scale-curve' && this.debugMode !== 'single-orbit-belt' && this.debugMode !== 'depth-staging' && this.debugMode !== 'single-orbit-layout' && this.debugMode !== 'depth-occlusion' && this.debugMode !== 'orbit-depth-phase' && this.debugMode !== 'orbit-depth-phase-12') {
            // Outer wireframe shell to emphasize shape/rotation
            const wireframeMat = new THREE.MeshBasicMaterial({
              color: 0x2bb9ed,
              wireframe: true,
              transparent: true,
              opacity: 0.3
            });
            const wireframeMesh = new THREE.Mesh(sphereGeo, wireframeMat);
            group.add(wireframeMesh);
            
            // Simple rotation for visual reference
            innerUpdate = (time) => {
              sphereMesh.rotation.y += 0.015 * b.rotation_speed;
              wireframeMesh.rotation.y -= 0.005 * b.rotation_speed;
            };
          } else {
            innerUpdate = (time) => {
              sphereMesh.rotation.y += 0.015 * b.rotation_speed;
            };
          }
        }
        // 0.1 Custom 3D Model Bubble (GLTF/GLB)
        else if (b.model_url && this.gltfLoader) {
          const sphereGeo = new THREE.SphereGeometry(baseRadius, 64, 64);
          const glassMesh = new THREE.Mesh(sphereGeo, crystalGlassMat.clone());
          group.add(glassMesh);

          const rotGroup = new THREE.Group();
          group.add(rotGroup);

          const gltfPromise = new Promise((resolve) => {
            this.gltfLoader.load(b.model_url, (gltf) => {
              const model = gltf.scene;
              model.traverse((child) => {
                if (child.isMesh) {
                  child.castShadow = true;
                  child.receiveShadow = true;
                  if (child.material) {
                    child.material.roughness = Math.min(child.material.roughness, 0.15);
                    child.material.metalness = Math.max(child.material.metalness, 0.8);
                  }
                }
              });

              const box = new THREE.Box3().setFromObject(model);
              const center = box.getCenter(new THREE.Vector3());
              const size = box.getSize(new THREE.Vector3());
              model.position.sub(center);

              const maxDim = Math.max(size.x, size.y, size.z);
              const scaleFactor = (baseRadius * 1.3) / maxDim;
              model.scale.setScalar(scaleFactor);

              rotGroup.add(model);
              model.traverse((child) => {
                if (!child.isMesh && !child.isPoints && !child.isLine) return;
                if (group.userData.mbsRegisterRenderable) {
                  group.userData.mbsRegisterRenderable(child);
                } else {
                  group.userData.mbsPendingRenderables.push(child);
                }
              });
              resolve();
            }, undefined, (err) => {
              console.error("GLTF load failed for bubble:", b.title, err);
              this.failedTextureUrls.push(b.model_url);
              resolve();
            });
          });
          activeLoadPromises.push(trackLoad(gltfPromise, `Model: ${b.title || '3D GLB'}`));

          innerUpdate = (time, hover) => {
            rotGroup.rotation.y += (0.004 + hover * 0.014) * b.rotation_speed;
            rotGroup.rotation.x += (0.0015 + hover * 0.004) * b.rotation_speed;
            rotGroup.scale.setScalar(1 + hover * 0.08);
            glassMesh.material.uniforms.hover.value = hover;
          };
        }
        // 0.2 Custom Video Texture Bubble
        else if (b.video_url) {
          const video = document.createElement('video');
          video.preload = 'none';
          video.loop = true;
          video.muted = true;
          video.playsInline = true;
          video.crossOrigin = "anonymous";
          video.setAttribute('webkit-playsinline', 'true');

          const loadVideo = () => trackLoad(new Promise((resolve) => {
            let settled = false;
            const finish = () => {
              if (settled) return;
              settled = true;
              resolve();
            };
            video.onloadedmetadata = finish;
            video.onerror = () => {
              console.warn("Video metadata failed to load:", b.video_url);
              this.failedTextureUrls.push(b.video_url);
              finish();
            };
            video.preload = 'metadata';
            video.src = b.video_url;
            video.load();
            video.play().catch(() => {});
            setTimeout(finish, 2500);
          }), `Video: ${b.title || 'Stream'}`);
          this.deferredAssetLoaders.push(loadVideo);

          const videoTexture = new THREE.VideoTexture(video);
          videoTexture.minFilter = THREE.LinearFilter;
          videoTexture.magFilter = THREE.LinearFilter;
          videoTexture.encoding = THREE.sRGBEncoding;

          const sphereGeo = new THREE.SphereGeometry(baseRadius, 64, 64);
          const videoMat = new THREE.MeshBasicMaterial({
            map: videoTexture,
            transparent: true,
            opacity: b.opacity,
            side: THREE.DoubleSide
          });
          const videoMesh = new THREE.Mesh(sphereGeo, videoMat);
          videoMesh.visible = false; // Hide until video frame has loaded to avoid black sphere flashing
          group.add(videoMesh);

          video.addEventListener('playing', () => {
            videoMesh.visible = true;
          });

          const sphereGeoOuter = new THREE.SphereGeometry(baseRadius * 1.01, 64, 64);
          const outerGlassMesh = new THREE.Mesh(sphereGeoOuter, crystalGlassMat.clone());
          group.add(outerGlassMesh);

          innerUpdate = (time, hover) => {
            videoMesh.rotation.y += (0.004 + hover * 0.012) * b.rotation_speed;
            videoMesh.scale.setScalar(1 + hover * 0.025);
            outerGlassMesh.material.uniforms.hover.value = hover;
          };
        }
        // 1. Crystal Glass Sphere
        else if (b.orb_type === 'crystal_glass') {
          const sphereGeo = new THREE.SphereGeometry(
            baseRadius,
            this.isMobile ? 40 : 56,
            this.isMobile ? 28 : 40
          );
          // B01 uses the same wordmark-aware transmission model as the
          // premium product glass. The former generic shell only reflected the
          // studio map, which made the crystal read as white spikes painted on
          // a dark marble instead of a transparent volume.
          const crystalMaterial = createPremiumTransmissionMaterial(baseRadius * 1.62);
          crystalMaterial.uniforms.uBackgroundColor.value.set('#02070b');
          crystalMaterial.uniforms.uTintColor.value.set('#c6e2ec');
          crystalMaterial.uniforms.uBodyColor.value.set('#03070b');
          crystalMaterial.uniforms.uIor.value = 1.27;
          crystalMaterial.uniforms.uRefractionStrength.value = 0.37;
          crystalMaterial.uniforms.uRoughness.value = 0.045;
          crystalMaterial.uniforms.uChromaticAberration.value = 0.014;
          crystalMaterial.uniforms.uEnvironmentIntensity.value = 1.08;
          crystalMaterial.uniforms.uBodyStrength.value = 0.1;
          crystalMaterial.uniforms.uWhiteEdgeStrength.value = 0.92;
          // This object's studio key is below-left. Keep the shared glass
          // factory intact: other orbs have separately art-directed lighting.
          crystalMaterial.fragmentShader = crystalMaterial.fragmentShader
            .replace('vec3(-0.32, 0.72, 0.62)', 'vec3(-0.62, -0.58, 0.68)')
            .replace('vec3(0.42, -0.7, 0.58)', 'vec3(-0.48, -0.74, 0.56)')
            .replace('shellUpperGlint * 0.4', 'shellUpperGlint * 0.22');
          crystalMaterial.userData.mbsLocalRenderOrder = 7;
          crystalMaterial.userData.mbsCrystalGlass = true;
          const glassMesh = new THREE.Mesh(sphereGeo, crystalMaterial);
          glassMesh.userData.mbsLocalRenderOrder = 7;
          group.add(glassMesh);

          // The background now displays only lettering. Its non-rendering SVG
          // definition still supplies B01's unchanged 3D emblem and cutouts.
          // The fallback supports the previous section during CDN propagation.
          const emblemPath = this.container.querySelector('.mbs-logo-svg [data-mbs-emblem] path')
            || this.container.querySelector('.mbs-logo-svg > path');
          const brandObject = new THREE.Group();
          group.add(brandObject);
          const brandFocusUniform = { value: 0 };
          if (emblemPath) {
            const contourData = (emblemPath.getAttribute('d') || '').match(/[^zZ]+[zZ]/g) || [];
            const contours = contourData.map((data, index) => {
              const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
              // The third contour begins with a relative move from contour 2.
              const absoluteData = index === 2
                ? data.replace(/^\s*m[\d.,\s-]+?(?=[a-zA-Z])/, 'M116.938,175.124')
                : data;
              path.setAttribute('d', absoluteData);
              const length = path.getTotalLength();
              const points = [];
              const samples = index === 0 ? 192 : 144;
              for (let step = 0; step < samples; step++) {
                const point = path.getPointAtLength(length * step / samples);
                points.push(new THREE.Vector2(
                  (point.x - 88.4) * baseRadius * 0.0105,
                  (128 - point.y) * baseRadius * 0.0105
                ));
              }
              return points;
            });
            if (contours.length) {
              const emblemShape = new THREE.Shape(contours[0]);
              contours.slice(1).forEach((points) => emblemShape.holes.push(new THREE.Path(points)));
              const emblemGeometry = new THREE.ExtrudeGeometry(emblemShape, {
                depth: baseRadius * 0.15,
                bevelEnabled: true,
                bevelThickness: baseRadius * 0.015,
                bevelSize: baseRadius * 0.01,
                bevelSegments: 3,
                steps: 1,
                curveSegments: 12
              });
              emblemGeometry.translate(0, 0, -baseRadius * 0.075);
              const emblemFace = new THREE.MeshPhysicalMaterial({
                color: new THREE.Color('#2bb9ed').convertSRGBToLinear(),
                metalness: 0.34, roughness: 0.24,
                clearcoat: 0.38, clearcoatRoughness: 0.1,
                envMapIntensity: 1.1
              });
              // A view-fixed studio key shades the actual extruded faces and
              // bevels as they turn. No scene light, extra pass, or shadow map
              // is needed, and other objects retain their existing lighting.
              emblemFace.onBeforeCompile = (shader) => {
                shader.uniforms.uBrandFocus = brandFocusUniform;
                shader.fragmentShader = 'uniform float uBrandFocus;\n' + shader.fragmentShader;
                shader.fragmentShader = shader.fragmentShader.replace(
                  '#include <tonemapping_fragment>',
                  `
                    vec3 brandKeyDirection = normalize(vec3(-0.62, -0.58, 0.68));
                    float brandKey = max(dot(normal, brandKeyDirection), 0.0);
                    vec3 brandHalfVector = normalize(brandKeyDirection + normalize(vViewPosition));
                    float brandBevel = pow(max(dot(normal, brandHalfVector), 0.0), 38.0);
                    gl_FragColor.rgb = gl_FragColor.rgb * (0.64 + brandKey * 0.28)
                      + diffuseColor.rgb * brandKey * (0.18 + uBrandFocus * 0.1)
                      + vec3(0.7, 0.9, 1.0) * brandBevel * (0.34 + uBrandFocus * 0.16);
                    #include <tonemapping_fragment>
                  `
                );
              };
              emblemFace.customProgramCacheKey = () => 'moztech-emblem-lower-left-studio-v1';
              // Keep one material per renderable: the shared scene lifecycle
              // adjusts per-object depth fade and disposes materials directly.
              const emblem = new THREE.Mesh(emblemGeometry, emblemFace);
              emblem.userData.mbsLocalRenderOrder = 4;
              brandObject.add(emblem);
            }
          }

          // Lettering follows a narrow latitude band on the glass surface,
          // not a smaller cylindrical hoop suspended around the emblem.
          // The small radial offset prevents coplanar shimmer; alpha-tested
          // depth keeps the back lettering behind the solid 3D emblem.
          const brandCanvas = document.createElement('canvas');
          brandCanvas.width = 2048;
          brandCanvas.height = 192;
          const brandCtx = brandCanvas.getContext('2d');
          brandCtx.fillStyle = '#ffffff';
          brandCtx.font = '600 126px Poppins, sans-serif';
          brandCtx.textAlign = 'center';
          brandCtx.textBaseline = 'middle';
          brandCtx.fillText('MOZTECH  •  LESS LIMITS  •', 1024, 99, 1940);
          const brandTexture = new THREE.CanvasTexture(brandCanvas);
          brandTexture.encoding = THREE.sRGBEncoding;
          brandTexture.wrapS = THREE.RepeatWrapping;
          brandTexture.minFilter = THREE.LinearMipmapLinearFilter;
          brandTexture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
          const brandRingMaterial = new THREE.MeshBasicMaterial({
            map: brandTexture,
            color: 0xffffff,
            transparent: true,
            alphaTest: 0.12,
            depthWrite: true,
            side: THREE.DoubleSide,
            toneMapped: false
          });
          const brandRingPivot = new THREE.Group();
          brandRingPivot.rotation.set(0.18, 0, -0.38);
          const brandRingGeometry = new THREE.SphereGeometry(
            baseRadius * 1.004, this.isMobile ? 64 : 96, 6,
            0, TAU, Math.PI / 2 - 0.18, 0.36
          );
          const brandRing = new THREE.Mesh(
            brandRingGeometry,
            brandRingMaterial
          );
          brandRing.userData.mbsLocalRenderOrder = 4;
          brandRing.userData.mbsBrandSurfaceLettering = true;
          brandRingPivot.add(brandRing);
          group.add(brandRingPivot);

          group.userData.mbsBrand = {
            geometry: 'original-svg-extrusion',
            ring: 'depth-tested-spherical-surface-type',
            hover: 'single-turn-spring-grow-local-contact',
            lighting: 'lower-left-key-extruded-face-and-bevel',
            surfaceRadius: baseRadius * 1.004,
            hoverRadialGap: baseRadius * 0.205,
            textureUploads: 'initial-only'
          };
          let brandLastTime = 0;
          let brandFocus = 0;
          let brandFocusVelocity = 0;
          let brandTurn = 0;
          let brandTurnVelocity = 0;
          let brandTurnTarget = 0;
          let brandTouchActive = false;
          const brandContact = new THREE.Vector3(0, 0, 1);
          const brandContactTarget = new THREE.Vector3(0, 0, 1);
          innerUpdate = (time, hover, pointerX, pointerY, localHit) => {
            const delta = Math.min(0.05, Math.max(0, time - brandLastTime));
            brandLastTime = time;
            if (!brandTouchActive && hover > 0.08) {
              brandTouchActive = true;
              // A single deliberate turn on entry, not a hover-speed spinner.
              // Re-entry while the turn is underway does not stack flips.
              if (Math.abs(brandTurnTarget - brandTurn) < 0.12) brandTurnTarget += TAU;
            } else if (brandTouchActive && hover < 0.02) {
              brandTouchActive = false;
            }
            if (localHit && hover > 0.01) {
              brandContactTarget.copy(localHit).normalize();
            } else {
              brandContactTarget.set(0, 0, 1);
            }
            brandContact.lerp(brandContactTarget, 1 - Math.exp(-delta * 7));
            // Small fixed substeps keep the spring identical at 30/60/120Hz
            // and prevent a slow frame from flinging the emblem out of glass.
            const springSteps = Math.max(1, Math.ceil(delta / (1 / 120)));
            const springDt = delta / springSteps;
            for (let step = 0; step < springSteps; step++) {
              brandFocusVelocity += ((hover - brandFocus) * 72 - brandFocusVelocity * 15) * springDt;
              brandFocus += brandFocusVelocity * springDt;
              brandTurnVelocity += ((brandTurnTarget - brandTurn) * 42 - brandTurnVelocity * 12.6) * springDt;
              brandTurn += brandTurnVelocity * springDt;
            }
            if (Math.abs(brandTurnTarget - brandTurn) < 0.0001 && Math.abs(brandTurnVelocity) < 0.0001) {
              brandTurn = 0;
              brandTurnTarget = 0;
              brandTurnVelocity = 0;
            }
            const focus = THREE.MathUtils.clamp(brandFocus, 0, 1.04);
            brandObject.rotation.y = brandTurn - 0.18 + Math.sin(time * 0.5) * 0.25
              + brandContact.x * focus * 0.34;
            brandObject.rotation.x = Math.sin(time * 0.23) * 0.045 - brandContact.y * focus * 0.26;
            brandObject.rotation.z = brandContact.x * brandContact.y * focus * -0.1;
            brandObject.scale.setScalar(1 + focus * 0.18);
            brandFocusUniform.value = focus;
            // Lift the latitude band radially, without scaling its letter
            // height. At focus even the top/bottom edges sit > 18% of a
            // radius clear of the shell. Uniform scaling obscured this gap
            // by enlarging the letters together with their orbit.
            const ringLift = 1 + focus * 0.205;
            brandRing.scale.set(ringLift, 1, ringLift);
            brandRing.rotation.y -= delta * 0.26 * (1 + focus * 2) * b.rotation_speed;
            brandRingPivot.rotation.x = 0.18 + Math.sin(time * 0.21) * 0.07 - brandContact.y * focus * 0.08;
            brandRingPivot.rotation.z = -0.38 + brandContact.x * focus * 0.06;
            crystalMaterial.uniforms.uHover.value = focus;
            crystalMaterial.uniforms.uTime.value = time;
            crystalMaterial.uniforms.uRoughness.value = THREE.MathUtils.lerp(0.045, 0.028, hover);
            crystalMaterial.uniforms.uRefractionStrength.value = THREE.MathUtils.lerp(0.37, 0.43, hover);
            crystalMaterial.uniforms.uChromaticAberration.value = THREE.MathUtils.lerp(0.014, 0.02, hover);
            crystalMaterial.uniforms.uEnvironmentIntensity.value = THREE.MathUtils.lerp(1.08, 1.24, focus);
            crystalMaterial.uniforms.uWhiteEdgeStrength.value = THREE.MathUtils.lerp(0.92, 1.04, focus);
            group.userData.mbsBrand.focus = focus;
            group.userData.mbsBrand.emblemScale = brandObject.scale.x;
            group.userData.mbsBrand.turnRemaining = brandTurnTarget - brandTurn;
            group.userData.mbsBrand.contactX = brandContact.x;
            group.userData.mbsBrand.contactY = brandContact.y;
          };
        }
        
        // 2. Dark Glossy Code/Data Sphere: one crisp atlas, GPU-scrolled upward.
        else if (b.orb_type === 'dark_glossy_data') {
          const shellGeometry = new THREE.SphereGeometry(
            baseRadius,
            48,
            48
          );
          const codeShellMaterial = darkGlossyGlassMat.clone();
          codeShellMaterial.uniforms.baseColor.value.set('#080a0d');
          codeShellMaterial.uniforms.accentColor.value.set('#e7ebee');
          codeShellMaterial.uniforms.hoverColor.value.set('#2bb9ed');
          codeShellMaterial.uniforms.opacity.value = 0.9;
          codeShellMaterial.uniforms.roughness.value = 0.18;
          codeShellMaterial.uniforms.frostStrength.value = 0;
          codeShellMaterial.uniforms.fresnelPower.value = 6.8;
          codeShellMaterial.uniforms.rimStrength.value = 1.18;
          codeShellMaterial.uniforms.directionalHalo.value = 1;
          codeShellMaterial.userData.mbsLocalRenderOrder = 7;
          const shellMesh = new THREE.Mesh(shellGeometry, codeShellMaterial);
          shellMesh.userData.mbsLocalRenderOrder = 7;
          group.add(shellMesh);

          const codeCanvas = document.createElement('canvas');
          // Power-of-two source stays crisp at oblique angles on WebGL1/2.
          // It is painted once; hover/scroll never triggers another upload.
          const codeTextureSize = 1024;
          codeCanvas.width = codeTextureSize;
          codeCanvas.height = codeTextureSize;
          const codeCtx = codeCanvas.getContext('2d');
          let codeLastTime = 0;
          const codeScrollUniform = { value: 0 };
          const codeIdleColor = new THREE.Color(0xffffff);
          const codeHoverColor = new THREE.Color(0x16d9f2);
          const codeTexture = new THREE.CanvasTexture(codeCanvas);
          codeTexture.wrapS = THREE.RepeatWrapping;
          codeTexture.wrapT = THREE.RepeatWrapping;
          codeTexture.minFilter = THREE.LinearMipmapLinearFilter;
          codeTexture.magFilter = THREE.LinearFilter;
          codeTexture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
          codeTexture.generateMipmaps = true;
          codeTexture.encoding = THREE.sRGBEncoding;

          const innerVolumeMaterial = new THREE.MeshBasicMaterial({
            color: 0x060709,
            transparent: true,
            opacity: 0.2,
            side: THREE.BackSide,
            depthWrite: false,
            toneMapped: false
          });
          const innerVolume = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * 0.86, 40, 28),
            innerVolumeMaterial
          );
          innerVolume.userData.mbsLocalRenderOrder = 1;
          group.add(innerVolume);

          const codeSphere = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * 0.91, 56, 36),
            new THREE.MeshBasicMaterial({
              map: codeTexture,
              color: 0xffffff,
              transparent: true,
              opacity: 0.94,
              side: THREE.FrontSide,
              depthWrite: false,
              toneMapped: false
            })
          );
          codeSphere.userData.mbsLocalRenderOrder = 3;
          group.add(codeSphere);

          // A second, very narrow optical layer samples the same code texture
          // with local magnification. Its band rises from the bottom to the top
          // only while focused, producing the requested water-lens fusion at
          // the edge without drawing a fake permanent white outline.
          const codeLensMaterial = new THREE.ShaderMaterial({
            uniforms: {
              uCodeMap: { value: codeTexture },
              uTime: { value: 0 },
              uHover: { value: 0 },
              uCodeScroll: codeScrollUniform,
              uDepthFade: { value: 1 }
            },
            vertexShader: `
              varying vec2 vUv;
              varying vec3 vNormal;
              varying vec3 vViewPosition;

              void main() {
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                vUv = uv;
                vNormal = normalize(normalMatrix * normal);
                vViewPosition = -mvPosition.xyz;
                gl_Position = projectionMatrix * mvPosition;
              }
            `,
            fragmentShader: `
              precision highp float;

              uniform sampler2D uCodeMap;
              uniform float uTime;
              uniform float uHover;
              uniform float uCodeScroll;
              uniform float uDepthFade;

              varying vec2 vUv;
              varying vec3 vNormal;
              varying vec3 vViewPosition;

              void main() {
                // Wrap just outside the UV range so each pass visibly enters
                // at the lower edge, rises through the code and exits at top.
                float bandCenter = fract(uTime * 0.19) * 1.28 - 0.14;
                float bandDistance = abs(vUv.y - bandCenter);
                float lensBody = exp(-bandDistance * bandDistance * 320.0);
                float waterLip = exp(-pow(bandDistance - 0.038, 2.0) * 2600.0);
                float band = clamp(lensBody + waterLip * 0.46, 0.0, 1.0);

                vec2 centeredUv = vUv - vec2(0.5);
                float magnification = 1.0 - band * uHover * 0.12;
                vec2 magnifiedUv = centeredUv * magnification + vec2(0.5);
                magnifiedUv.y -= band * uHover * 0.012;
                // Match the base map's GPU transform, including full-atlas
                // repeat. The lens magnifies the same moving source rows.
                magnifiedUv.y -= uCodeScroll;
                vec4 codeSample = texture2D(uCodeMap, magnifiedUv);

                float facing = max(dot(normalize(vNormal), normalize(vViewPosition)), 0.0);
                float edge = 1.0 - facing;
                float magnifierEdge = pow(edge, 4.2) * band;
                vec2 diskDirection = vUv - vec2(0.5);
                diskDirection /= max(length(diskDirection), 0.0001);
                float lowerCrescent = pow(max(dot(
                  diskDirection,
                  normalize(vec2(-0.48, -0.88))
                ), 0.0), 5.0) * pow(edge, 2.6);
                vec3 cyan = vec3(0.086, 0.851, 0.949);
                vec3 opticalWhite = vec3(1.0);
                vec3 codeTint = mix(vec3(1.0), cyan, uHover * 0.94);
                vec3 color = mix(codeSample.rgb * codeTint, opticalWhite, band * 0.24);
                color += cyan * (magnifierEdge * 0.42 + lowerCrescent * band * 0.22);
                color += opticalWhite * waterLip * 0.5;
                float alpha = uHover * (
                  codeSample.a * lensBody * 0.42
                  + magnifierEdge * 0.74
                  + waterLip * mix(0.26, 0.66, edge)
                ) * uDepthFade;
                gl_FragColor = vec4(color * uDepthFade, clamp(alpha, 0.0, 0.82));
              }
            `,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            side: THREE.FrontSide,
            blending: THREE.NormalBlending
          });
          codeLensMaterial.userData.mbsLocalRenderOrder = 5;
          const codeLensSphere = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * 0.925, this.isMobile ? 40 : 56, this.isMobile ? 28 : 36),
            codeLensMaterial
          );
          codeLensSphere.userData.mbsLocalRenderOrder = 5;
          group.add(codeLensSphere);

          const codeRows = [
            [['// MOZTECH / RUNTIME', 'muted']],
            [['const ', 'syntax'], ['frame = clock();', 'bright']],
            [['const ', 'syntax'], ['mode = adaptive;', 'bright']],
            [['if ', 'syntax'], ['(device.connected) {', 'bright']],
            [['  sync(device.status);', 'bright']],
            [['  queue.render(frame);', 'bright']],
            [['}', 'muted']],
            [['{% ', 'muted'], ['for block in blocks', 'syntax'], [' %}', 'muted']],
            [["  render 'power-module'", 'bright']],
            [['{% ', 'muted'], ['endfor', 'syntax'], [' %}', 'muted']],
            [['system.', 'muted'], ['ready', 'bright'], [' = true;', 'syntax']],
            [['engine.', 'muted'], ['connect();', 'bright']],
            [['await ', 'syntax'], ['nextFrame();', 'bright']],
            [['return ', 'syntax'], ['render(scene);', 'bright']],
            [['// PRECISE BY DESIGN', 'muted']],
            [['export ', 'syntax'], ['{ createRuntime };', 'bright']]
          ];

          const codeColors = { bright: '#f6f7f8', syntax: '#e2e5e8', muted: '#aeb4bc' };
          const codeRowHeight = codeTextureSize / codeRows.length;
          codeCtx.font = '600 36px ui-monospace, SFMono-Regular, Menlo, monospace';
          codeCtx.textAlign = 'left';
          codeCtx.textBaseline = 'alphabetic';
          codeCtx.shadowBlur = 0;
          const drawCodeRow = (segments, x, y, alpha) => {
            let cursorX = x;
            codeCtx.globalAlpha = alpha;
            for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex++) {
              const [text, tone] = segments[segmentIndex];
              codeCtx.fillStyle = codeColors[tone] || codeColors.bright;
              codeCtx.fillText(text, cursorX, y);
              cursorX += codeCtx.measureText(text).width;
            }
          };
          // Sixteen complete rows fill one repeat exactly. A wrap advances to
          // the next source row instead of resetting the same text every line.
          for (let column = 0; column < 2; column++) {
            codeCtx.save();
            codeCtx.beginPath();
            codeCtx.rect(column * 512, 0, 512, codeTextureSize);
            codeCtx.clip();
            for (let row = 0; row < codeRows.length; row++) {
              drawCodeRow(codeRows[(row + column * 8) % codeRows.length],
                column * 512 + 28, row * codeRowHeight + 43, column === 0 ? 0.96 : 0.86);
            }
            codeCtx.restore();
          }
          codeCtx.globalAlpha = 1;

          innerUpdate = (time, hover) => {
            const delta = Math.min(0.05, Math.max(0, time - codeLastTime));
            codeLastTime = time;
            codeScrollUniform.value = (codeScrollUniform.value
              + delta * (36 + hover * 30) * Math.max(0, b.rotation_speed) / codeTextureSize) % 1;
            // CanvasTexture flipY maps larger v to the top of the sphere.
            // Sampling v - phase moves each unchanged source row upward.
            codeTexture.offset.y = -codeScrollUniform.value;
            codeSphere.material.color.copy(codeIdleColor).lerp(codeHoverColor, hover * 0.94);
            codeSphere.rotation.y += delta * (0.12 - hover * 0.085) * b.rotation_speed;
            codeSphere.rotation.x = Math.sin(time * 0.22) * 0.075;
            codeSphere.rotation.z = -0.055 + Math.sin(time * 0.16) * 0.018;
            codeSphere.scale.setScalar(1 + hover * 0.045);
            codeLensSphere.rotation.copy(codeSphere.rotation);
            codeLensSphere.scale.copy(codeSphere.scale);
            codeLensMaterial.uniforms.uTime.value = time;
            codeLensMaterial.uniforms.uHover.value = hover;
            innerVolumeMaterial.opacity = 0.2 + hover * 0.05;
            codeShellMaterial.uniforms.hover.value = hover * 0.45;
            group.userData.codeGlassLayers = 4;
            group.userData.codeFrostStrength = codeShellMaterial.uniforms.frostStrength.value;
            group.userData.codeTextureSize = codeTextureSize;
            group.userData.codeUiCoverage = 0.72;
            group.userData.codeMotion = 'continuous-upward';
            group.userData.codeTextureUploads = 'initial-only';
            group.userData.codeScrollPhase = codeScrollUniform.value;
            group.userData.codeLensMotion = 'bottom-to-top-water-magnifier';
          };
        }

        // 4. Reactive surface needle field. The flights follow the sphere's
        // tangent curvature with only a restrained shell inset, so they read as
        // snow-like needles skimming glass rather than chords crossing a core.
        // Focus slows the shared clock and thickens each dot/tail pair.
        else if (b.orb_type === 'wireframe_globe') {
          const arcGroup = new THREE.Group();
          const arcSegments = this.isMobile ? 16 : 22;
          const arcCount = this.isMobile ? 420 : 760;
          const dotCount = arcCount;
          const verticesPerArc = (arcSegments + 1) * 2;
          const basePositions = new Float32Array(verticesPerArc * 3);
          const baseUvs = new Float32Array(verticesPerArc * 2);
          const baseIndices = new Uint16Array(arcSegments * 6);

          for (let segment = 0; segment <= arcSegments; segment++) {
            const t = segment / arcSegments;
            const vertexOffset = segment * 2;
            basePositions[(vertexOffset + 0) * 3 + 0] = -1;
            basePositions[(vertexOffset + 0) * 3 + 1] = t;
            basePositions[(vertexOffset + 1) * 3 + 0] = 1;
            basePositions[(vertexOffset + 1) * 3 + 1] = t;
            baseUvs[(vertexOffset + 0) * 2 + 0] = 0;
            baseUvs[(vertexOffset + 0) * 2 + 1] = t;
            baseUvs[(vertexOffset + 1) * 2 + 0] = 1;
            baseUvs[(vertexOffset + 1) * 2 + 1] = t;

            if (segment < arcSegments) {
              const indexOffset = segment * 6;
              baseIndices[indexOffset + 0] = vertexOffset + 0;
              baseIndices[indexOffset + 1] = vertexOffset + 1;
              baseIndices[indexOffset + 2] = vertexOffset + 2;
              baseIndices[indexOffset + 3] = vertexOffset + 2;
              baseIndices[indexOffset + 4] = vertexOffset + 1;
              baseIndices[indexOffset + 5] = vertexOffset + 3;
            }
          }

          const arcGeometry = new THREE.InstancedBufferGeometry();
          arcGeometry.setAttribute('position', new THREE.BufferAttribute(basePositions, 3));
          arcGeometry.setAttribute('uv', new THREE.BufferAttribute(baseUvs, 2));
          arcGeometry.setIndex(new THREE.BufferAttribute(baseIndices, 1));

          const startPoint = new Float32Array(arcCount * 3);
          const midPoint1 = new Float32Array(arcCount * 3);
          const midPoint2 = new Float32Array(arcCount * 3);
          const endPoint = new Float32Array(arcCount * 3);
          const arcSeed = new Float32Array(arcCount);
          const arcShade = new Float32Array(arcCount);
          const arcWidth = new Float32Array(arcCount);
          let randomState = 0x8f3b6a2d;
          const seededRandom = () => {
            randomState = (Math.imul(randomState, 1664525) + 1013904223) >>> 0;
            return randomState / 4294967296;
          };
          const randomSphereDirection = () => {
            const z = seededRandom() * 2 - 1;
            const angle = seededRandom() * TAU;
            const ring = Math.sqrt(Math.max(0, 1 - z * z));
            return new THREE.Vector3(Math.cos(angle) * ring, z, Math.sin(angle) * ring);
          };
          const startDirection = new THREE.Vector3();
          const endDirection = new THREE.Vector3();
          const rotationAxis = new THREE.Vector3();
          const helperAxis = new THREE.Vector3();
          const tangentA = new THREE.Vector3();
          const tangentB = new THREE.Vector3();
          const control = new THREE.Vector3();
          const startPosition = new THREE.Vector3();
          const endPosition = new THREE.Vector3();
          const surfaceRadius = baseRadius * 0.985;

          for (let index = 0; index < arcCount; index++) {
            startDirection.copy(randomSphereDirection()).normalize();
            helperAxis.set(
              0,
              Math.abs(startDirection.y) > 0.86 ? 0 : 1,
              Math.abs(startDirection.y) > 0.86 ? 1 : 0
            );
            tangentA.crossVectors(startDirection, helperAxis).normalize();
            tangentB.crossVectors(startDirection, tangentA).normalize();
            const axisAngle = seededRandom() * TAU;
            rotationAxis.copy(tangentA).multiplyScalar(Math.cos(axisAngle))
              .addScaledVector(tangentB, Math.sin(axisAngle))
              .normalize();
            // Keep enough length to read as a flying needle, but stop before a
            // path becomes a diameter-like chord through the sphere.
            const arcLength = 0.7 + seededRandom() * 1.3;
            endDirection.copy(startDirection).applyAxisAngle(rotationAxis, arcLength).normalize();

            startPosition.copy(startDirection).multiplyScalar(surfaceRadius);
            endPosition.copy(endDirection).multiplyScalar(surfaceRadius);
            startPosition.toArray(startPoint, index * 3);
            endPosition.toArray(endPoint, index * 3);

            // Great-circle controls establish a UV-like tangent path. The
            // vertex shader adds the slight shell inset consistently along the
            // trajectory so the line can feel embedded without collapsing into
            // the centre of the ball.
            control.copy(startDirection)
              .applyAxisAngle(rotationAxis, arcLength * 0.33)
              .addScaledVector(tangentB, (seededRandom() - 0.5) * 0.045)
              .normalize()
              .multiplyScalar(surfaceRadius);
            control.toArray(midPoint1, index * 3);
            control.copy(startDirection)
              .applyAxisAngle(rotationAxis, arcLength * 0.67)
              .addScaledVector(tangentA, (seededRandom() - 0.5) * 0.045)
              .normalize()
              .multiplyScalar(surfaceRadius);
            control.toArray(midPoint2, index * 3);

            arcSeed[index] = (index + seededRandom()) / arcCount;
            arcShade[index] = seededRandom();
            arcWidth[index] = 0.76 + seededRandom() * 0.62;
          }

          arcGeometry.setAttribute('aStartPoint', new THREE.InstancedBufferAttribute(startPoint, 3));
          arcGeometry.setAttribute('aMidPoint1', new THREE.InstancedBufferAttribute(midPoint1, 3));
          arcGeometry.setAttribute('aMidPoint2', new THREE.InstancedBufferAttribute(midPoint2, 3));
          arcGeometry.setAttribute('aEndPoint', new THREE.InstancedBufferAttribute(endPoint, 3));
          arcGeometry.setAttribute('aSeed', new THREE.InstancedBufferAttribute(arcSeed, 1));
          arcGeometry.setAttribute('aShade', new THREE.InstancedBufferAttribute(arcShade, 1));
          arcGeometry.setAttribute('aWidth', new THREE.InstancedBufferAttribute(arcWidth, 1));
          arcGeometry.instanceCount = arcCount;
          arcGeometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), baseRadius * 1.35);

          const arcMaterial = new THREE.ShaderMaterial({
            uniforms: {
              uTime: { value: 0 },
              uHover: { value: 0 },
              uThickness: { value: baseRadius * 0.003 },
              uExpansion: { value: 1 },
              uSurfaceRadius: { value: surfaceRadius },
              uDrift: { value: baseRadius * 0.007 },
              uDepthFade: { value: 1 }
            },
            vertexShader: `
              precision highp float;

              attribute vec3 aStartPoint;
              attribute vec3 aMidPoint1;
              attribute vec3 aMidPoint2;
              attribute vec3 aEndPoint;
              attribute float aSeed;
              attribute float aShade;
              attribute float aWidth;

              uniform float uTime;
              uniform float uHover;
              uniform float uThickness;
              uniform float uExpansion;
              uniform float uSurfaceRadius;
              uniform float uDrift;

              varying float vT;
              varying float vFacing;
              varying float vShade;
              varying float vSeed;
              varying float vTravelSpeed;

              const float PI = 3.141592653589793;
              const float TAU = 6.283185307179586;

              vec3 cubicBezier(vec3 p0, vec3 p1, vec3 p2, vec3 p3, float t) {
                float inverseT = 1.0 - t;
                return inverseT * inverseT * inverseT * p0
                  + 3.0 * inverseT * inverseT * t * p1
                  + 3.0 * inverseT * t * t * p2
                  + t * t * t * p3;
              }

              vec3 cubicBezierTangent(vec3 p0, vec3 p1, vec3 p2, vec3 p3, float t) {
                float inverseT = 1.0 - t;
                return 3.0 * inverseT * inverseT * (p1 - p0)
                  + 6.0 * inverseT * t * (p2 - p1)
                  + 3.0 * t * t * (p3 - p2);
              }

              void main() {
                float t = uv.y;
                float side = uv.x * 2.0 - 1.0;
                vec3 rawCurve = cubicBezier(aStartPoint, aMidPoint1, aMidPoint2, aEndPoint, t);
                vec3 radial = normalize(rawCurve);
                float centreEnvelope = sin(t * PI);
                float shellInset = centreEnvelope * mix(0.008, 0.03, aShade);
                vec3 pointOnCurve = radial * uSurfaceRadius * (1.0 - shellInset);
                float surfaceDrift = sin(uTime * 0.7 + aSeed * TAU + t * TAU * 1.7)
                  * uDrift * (0.35 + aSeed * 0.65);
                pointOnCurve = pointOnCurve * uExpansion + radial * surfaceDrift;
                vec3 tangentOnCurve = normalize(cubicBezierTangent(
                  aStartPoint,
                  aMidPoint1,
                  aMidPoint2,
                  aEndPoint,
                  t
                ));

                vec4 pointInView = modelViewMatrix * vec4(pointOnCurve, 1.0);
                vec3 tangentInView = normalize(mat3(modelViewMatrix) * tangentOnCurve);
                vec3 lookDirection = normalize(pointInView.xyz);
                vec3 ribbonOffsetRaw = cross(lookDirection, tangentInView);
                vec3 ribbonOffset = ribbonOffsetRaw / max(length(ribbonOffsetRaw), 0.0001);
                float objectScale = length(modelMatrix[0].xyz);
                float endTaper = smoothstep(0.0, 0.06, t) * (1.0 - smoothstep(0.9, 1.0, t));
                // A visible width response accompanies time dilation; the
                // tapered ribbons remain surface needles, never solid rods.
                float focusWidth = mix(1.0, 2.15, uHover);
                float width = uThickness * aWidth * objectScale * focusWidth * endTaper;
                vec3 adjustedPosition = pointInView.xyz + ribbonOffset * side * width;

                vec3 radialInView = normalize(mat3(modelViewMatrix) * radial);
                vT = t;
                vFacing = clamp(radialInView.z * 0.5 + 0.5, 0.0, 1.0);
                vShade = aShade;
                vSeed = aSeed;
                vTravelSpeed = mix(0.34, 0.62, aShade);
                gl_Position = projectionMatrix * vec4(adjustedPosition, 1.0);
              }
            `,
            fragmentShader: `
              precision highp float;

              uniform float uTime;
              uniform float uHover;
              uniform float uDepthFade;

              varying float vT;
              varying float vFacing;
              varying float vShade;
              varying float vSeed;
              varying float vTravelSpeed;

              void main() {
                float depthAlpha = mix(0.12, 1.0, smoothstep(0.08, 0.92, vFacing));
                float arcFade = smoothstep(0.0, 0.08, vT) * (1.0 - smoothstep(0.86, 1.0, vT));
                float lifeProgress = fract(vSeed + uTime * vTravelSpeed);
                float activeWindow = mix(0.58, 0.78, fract(vSeed * 31.731));
                float isActive = 1.0 - step(activeWindow, lifeProgress);
                float travelHead = clamp(lifeProgress / activeWindow, 0.0, 1.0);
                float trailRandom = fract(vSeed * 19.173);
                float idleTrailLength = mix(0.6, 0.88, trailRandom);
                float focusTrailLength = mix(0.68, 0.94, trailRandom);
                float trailLength = mix(idleTrailLength, focusTrailLength, uHover);
                float trailStart = travelHead - trailLength;
                float trailingEdge = smoothstep(trailStart - 0.02, trailStart + 0.038, vT);
                float leadingEdge = 1.0 - smoothstep(travelHead - 0.028, travelHead + 0.01, vT);
                float lifeEnvelope = smoothstep(0.0, 0.055, travelHead)
                  * (1.0 - smoothstep(0.9, 1.0, travelHead));
                float needleMask = trailingEdge * leadingEdge * isActive * lifeEnvelope;
                float trajectoryPresence = mix(0.01, 1.0, needleMask);
                float idleLuminance = mix(0.62, 0.96, vShade);
                vec3 color = mix(vec3(idleLuminance), vec3(1.0), uHover * 0.92);
                float alpha = arcFade * trajectoryPresence * depthAlpha * mix(0.68, 0.8, uHover)
                  * mix(0.64, 1.0, vShade) * uDepthFade;
                gl_FragColor = vec4(color * uDepthFade, alpha);
              }
            `,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            side: THREE.DoubleSide,
            blending: THREE.NormalBlending
          });
          arcMaterial.userData.mbsLocalRenderOrder = 2;
          arcMaterial.userData.mbsReactiveArcField = true;

          const arcMesh = new THREE.Mesh(arcGeometry, arcMaterial);
          arcMesh.frustumCulled = false;
          arcMesh.userData.mbsLocalRenderOrder = 2;
          arcGroup.add(arcMesh);

          const dotGeometry = new THREE.BufferGeometry();
          const dotStartPoint = new Float32Array(dotCount * 3);
          const dotMidPoint1 = new Float32Array(dotCount * 3);
          const dotMidPoint2 = new Float32Array(dotCount * 3);
          const dotEndPoint = new Float32Array(dotCount * 3);
          const dotSeed = new Float32Array(dotCount);
          const dotShade = new Float32Array(dotCount);
          const dotTravelSpeed = new Float32Array(dotCount);
          const dotSize = new Float32Array(dotCount);
          const dotPositions = new Float32Array(dotCount * 3);

          for (let index = 0; index < dotCount; index++) {
            const sourceIndex = index % arcCount;
            dotStartPoint.set(startPoint.subarray(sourceIndex * 3, sourceIndex * 3 + 3), index * 3);
            dotMidPoint1.set(midPoint1.subarray(sourceIndex * 3, sourceIndex * 3 + 3), index * 3);
            dotMidPoint2.set(midPoint2.subarray(sourceIndex * 3, sourceIndex * 3 + 3), index * 3);
            dotEndPoint.set(endPoint.subarray(sourceIndex * 3, sourceIndex * 3 + 3), index * 3);
            dotSeed[index] = arcSeed[sourceIndex];
            dotShade[index] = arcShade[sourceIndex];
            dotTravelSpeed[index] = 0.34 + arcShade[sourceIndex] * 0.28;
            dotSize[index] = 0.62 + seededRandom() * 0.66;
          }

          dotGeometry.setAttribute('position', new THREE.BufferAttribute(dotPositions, 3));
          dotGeometry.setAttribute('aStartPoint', new THREE.BufferAttribute(dotStartPoint, 3));
          dotGeometry.setAttribute('aMidPoint1', new THREE.BufferAttribute(dotMidPoint1, 3));
          dotGeometry.setAttribute('aMidPoint2', new THREE.BufferAttribute(dotMidPoint2, 3));
          dotGeometry.setAttribute('aEndPoint', new THREE.BufferAttribute(dotEndPoint, 3));
          dotGeometry.setAttribute('aSeed', new THREE.BufferAttribute(dotSeed, 1));
          dotGeometry.setAttribute('aShade', new THREE.BufferAttribute(dotShade, 1));
          dotGeometry.setAttribute('aTravelSpeed', new THREE.BufferAttribute(dotTravelSpeed, 1));
          dotGeometry.setAttribute('aSize', new THREE.BufferAttribute(dotSize, 1));
          dotGeometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), baseRadius * 1.35);

          const dotMaterial = new THREE.ShaderMaterial({
            uniforms: {
              uTime: { value: 0 },
              uHover: { value: 0 },
              uExpansion: { value: 1 },
              uSurfaceRadius: { value: surfaceRadius },
              uPointSize: { value: baseRadius * 0.013 },
              uViewportHeight: { value: 900 },
              uDrift: { value: baseRadius * 0.007 },
              uDepthFade: { value: 1 }
            },
            vertexShader: `
              precision highp float;

              attribute vec3 aStartPoint;
              attribute vec3 aMidPoint1;
              attribute vec3 aMidPoint2;
              attribute vec3 aEndPoint;
              attribute float aSeed;
              attribute float aShade;
              attribute float aTravelSpeed;
              attribute float aSize;

              uniform float uTime;
              uniform float uHover;
              uniform float uExpansion;
              uniform float uSurfaceRadius;
              uniform float uPointSize;
              uniform float uDrift;
              uniform float uViewportHeight;

              varying float vOpacity;
              varying float vFacing;

              const float PI = 3.141592653589793;

              vec3 cubicBezier(vec3 p0, vec3 p1, vec3 p2, vec3 p3, float t) {
                float inverseT = 1.0 - t;
                return inverseT * inverseT * inverseT * p0
                  + 3.0 * inverseT * inverseT * t * p1
                  + 3.0 * inverseT * t * t * p2
                  + t * t * t * p3;
              }

              void main() {
                float lifeProgress = fract(aSeed + uTime * aTravelSpeed);
                float activeWindow = mix(0.58, 0.78, fract(aSeed * 31.731));
                float isActive = 1.0 - step(activeWindow, lifeProgress);
                float t = clamp(lifeProgress / activeWindow, 0.0, 1.0);
                vec3 rawCurve = cubicBezier(aStartPoint, aMidPoint1, aMidPoint2, aEndPoint, t);
                float centreEnvelope = sin(t * PI);
                float shellInset = centreEnvelope * mix(0.008, 0.03, aShade);
                vec3 pointOnCurve = normalize(rawCurve) * uSurfaceRadius
                  * (1.0 - shellInset) * uExpansion;
                // The head uses the exact same surface displacement as its
                // ribbon. Otherwise a hovering point detaches from its tail.
                pointOnCurve += normalize(rawCurve) * sin(uTime * 0.7
                  + aSeed * 6.283185307179586 + t * 6.283185307179586 * 1.7)
                  * uDrift * (0.35 + aSeed * 0.65);
                vec4 mvPosition = modelViewMatrix * vec4(pointOnCurve, 1.0);
                float objectScale = length(modelMatrix[0].xyz);
                gl_PointSize = clamp(
                  uPointSize * aSize * objectScale * mix(1.0, 1.65, uHover)
                    * (0.5 * uViewportHeight * projectionMatrix[1][1]) / max(-mvPosition.z, 0.5),
                  1.25,
                  8.0
                );
                vec3 radialInView = normalize(mat3(modelViewMatrix) * normalize(pointOnCurve));
                vFacing = clamp(radialInView.z * 0.5 + 0.5, 0.0, 1.0);
                float lifeEnvelope = smoothstep(0.0, 0.055, t)
                  * (1.0 - smoothstep(0.9, 1.0, t));
                vOpacity = isActive * lifeEnvelope * (0.78 + 0.22 * sin((t + aSeed) * 18.8495559));
                gl_Position = projectionMatrix * mvPosition;
              }
            `,
            fragmentShader: `
              precision highp float;

              uniform float uHover;
              uniform float uDepthFade;
              varying float vOpacity;
              varying float vFacing;

              void main() {
                float distanceToCenter = length(gl_PointCoord - vec2(0.5));
                float disc = 1.0 - smoothstep(0.18, 0.5, distanceToCenter);
                float depthAlpha = mix(0.22, 1.0, smoothstep(0.08, 0.9, vFacing));
                float alpha = disc * depthAlpha * vOpacity * mix(0.72, 1.0, uHover) * uDepthFade;
                gl_FragColor = vec4(vec3(1.0) * uDepthFade, alpha);
              }
            `,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            blending: THREE.AdditiveBlending
          });
          dotMaterial.userData.mbsLocalRenderOrder = 3;
          const movingDots = new THREE.Points(dotGeometry, dotMaterial);
          movingDots.frustumCulled = false;
          movingDots.renderOrder = 3;
          arcGroup.add(movingDots);

          const volumeMaterial = new THREE.ShaderMaterial({
            uniforms: {
              uHover: { value: 0 },
              uDepthFade: { value: 1 }
            },
            vertexShader: `
              varying vec3 vNormal;
              varying vec3 vViewPosition;
              void main() {
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                vNormal = normalize(normalMatrix * normal);
                vViewPosition = -mvPosition.xyz;
                gl_Position = projectionMatrix * mvPosition;
              }
            `,
            fragmentShader: `
              uniform float uHover;
              uniform float uDepthFade;
              varying vec3 vNormal;
              varying vec3 vViewPosition;
              void main() {
                float edge = 1.0 - max(dot(normalize(vNormal), normalize(vViewPosition)), 0.0);
                float outer = pow(edge, 4.6);
                float inner = smoothstep(0.35, 0.74, edge) * (1.0 - smoothstep(0.82, 1.0, edge));
                vec3 color = mix(vec3(0.72), vec3(1.0), uHover * 0.88);
                float alpha = (outer * mix(0.1, 0.26, uHover) + inner * 0.025) * uDepthFade;
                gl_FragColor = vec4(color * uDepthFade, alpha);
              }
            `,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            blending: THREE.AdditiveBlending,
            side: THREE.FrontSide
          });
          volumeMaterial.userData.mbsLocalRenderOrder = 1;
          const volumeMesh = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * 0.985, 40, 28),
            volumeMaterial
          );
          volumeMesh.userData.mbsLocalRenderOrder = 1;
          arcGroup.add(volumeMesh);

          const hitMaterial = new THREE.MeshBasicMaterial({
            colorWrite: false,
            transparent: true,
            opacity: 0,
            depthWrite: false,
            depthTest: false
          });
          hitMaterial.userData.mbsBaseOpacity = 0;
          const hitMesh = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * 1.09, 16, 12),
            hitMaterial
          );
          hitMesh.userData.mbsLocalRenderOrder = 0;
          arcGroup.add(hitMesh);

          arcGroup.userData.arcCount = arcCount;
          arcGroup.userData.dotCount = dotCount;
          arcGroup.userData.arcThickness = 1;
          arcGroup.userData.arcExpansion = 1;
          arcGroup.userData.arcLengthRangeRad = [0.7, 2.0];
          arcGroup.userData.arcSurfaceMode = 'tangent-shell-uv';
          // Radius fractions before the subtle animated surface drift.
          arcGroup.userData.arcSurfaceDepthRange = [0.985 * 0.97, 0.985];
          arcGroup.userData.visibleTrailRange = [0.6, 0.94];
          group.add(arcGroup);

          let arcClock = 0;
          let arcLastTime = 0;
          let arcMotionSpeed = this.isMobile ? 1.55 : 1.85;
          innerUpdate = (time, hover, pointerX, pointerY) => {
            const delta = Math.min(0.05, Math.max(0, time - arcLastTime));
            arcLastTime = time;
            const hoverEase = THREE.MathUtils.smoothstep(hover, 0.04, 0.92);
            // Longer surface trails remain readable at a brisk idle speed;
            // focus dilates their time, not their curved trajectories.
            const idleMotionSpeed = this.isMobile ? 1.55 : 1.85;
            const focusedMotionSpeed = this.isMobile ? 0.12 : 0.14;
            const targetMotionSpeed = THREE.MathUtils.lerp(
              idleMotionSpeed,
              focusedMotionSpeed,
              hoverEase
            );
            arcMotionSpeed += (targetMotionSpeed - arcMotionSpeed)
              * (1 - Math.exp(-delta * 7.5));
            arcClock += delta * arcMotionSpeed;
            const rotationSpeedScale = THREE.MathUtils.lerp(1, 0.5, hoverEase);
            arcGroup.rotation.y += delta * 0.25 * b.rotation_speed * rotationSpeedScale;
            arcGroup.rotation.z -= delta * 0.05 * b.rotation_speed * rotationSpeedScale;
            arcGroup.rotation.x = THREE.MathUtils.lerp(
              arcGroup.rotation.x,
              pointerY * 0.075,
              Math.min(1, delta * 4.2)
            );
            const expansion = 1 + hover * 0.025;
            arcMaterial.uniforms.uTime.value = arcClock;
            arcMaterial.uniforms.uHover.value = hover;
            arcMaterial.uniforms.uExpansion.value = expansion;
            dotMaterial.uniforms.uTime.value = arcClock;
            dotMaterial.uniforms.uHover.value = hover;
            dotMaterial.uniforms.uExpansion.value = expansion;
            dotMaterial.uniforms.uViewportHeight.value = this.renderer.domElement.height;
            volumeMaterial.uniforms.uHover.value = hover;
            arcGroup.userData.arcThickness = 1 + hover * 1.15;
            arcGroup.userData.arcExpansion = expansion;
            arcGroup.userData.arcBrightness = 0.22 + hover * 0.45;
            arcGroup.userData.arcMotionSpeed = arcMotionSpeed;
            arcGroup.userData.arcMotionMode = hoverEase > 0.7 ? 'hover-slow' : 'idle-fast';
            arcGroup.userData.motionTarget = 'wireframe_globe';
          };
        }

        // 3. White analytics sphere: a real animated UI surface wrapped around
        // the sphere instead of a flat card floating inside it.
        else if (b.orb_type === 'white_chart_data') {
          const chartCanvas = document.createElement('canvas');
          const chartTextureSize = this.isMobile ? 384 : 512;
          chartCanvas.width = chartTextureSize;
          chartCanvas.height = chartTextureSize;
          const chartCtx = chartCanvas.getContext('2d');
          chartCtx.scale(chartTextureSize / 1024, chartTextureSize / 1024);
          let chartLastDrawTime = -Infinity;
          let chartLastTime = 0;
          const chartTexture = new THREE.CanvasTexture(chartCanvas);
          chartTexture.minFilter = THREE.LinearFilter;
          chartTexture.magFilter = THREE.LinearFilter;
          chartTexture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
          chartTexture.generateMipmaps = false;
          chartTexture.encoding = THREE.sRGBEncoding;

          const chartGlassMaterial = crystalGlassMat.clone();
          chartGlassMaterial.uniforms.baseColor.value.set('#d8eef5');
          chartGlassMaterial.uniforms.accentColor.value.set('#ffffff');
          chartGlassMaterial.uniforms.hoverColor.value.set('#2bb9ed');
          chartGlassMaterial.uniforms.opacity.value = 0.68;
          chartGlassMaterial.uniforms.roughness.value = 0.18;
          chartGlassMaterial.uniforms.frostStrength.value = 0;
          chartGlassMaterial.uniforms.fresnelPower.value = 7.2;
          chartGlassMaterial.uniforms.rimStrength.value = 1.08;
          chartGlassMaterial.uniforms.directionalHalo.value = 1;
          chartGlassMaterial.userData.mbsLocalRenderOrder = 7;
          const outerGlass = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius, 32, 32),
            chartGlassMaterial
          );
          outerGlass.userData.mbsLocalRenderOrder = 7;
          group.add(outerGlass);

          const chartSphere = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * 0.94, 40, 28),
            new THREE.MeshBasicMaterial({
              map: chartTexture,
              color: 0xffffff,
              toneMapped: false
            })
          );
          chartSphere.userData.mbsLocalRenderOrder = 3;
          group.add(chartSphere);

          const drawChartRoundedRect = (x, y, width, height, radius) => {
            const r = Math.min(radius, width * 0.5, height * 0.5);
            chartCtx.beginPath();
            chartCtx.moveTo(x + r, y);
            chartCtx.lineTo(x + width - r, y);
            chartCtx.quadraticCurveTo(x + width, y, x + width, y + r);
            chartCtx.lineTo(x + width, y + height - r);
            chartCtx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
            chartCtx.lineTo(x + r, y + height);
            chartCtx.quadraticCurveTo(x, y + height, x, y + height - r);
            chartCtx.lineTo(x, y + r);
            chartCtx.quadraticCurveTo(x, y, x + r, y);
            chartCtx.closePath();
          };

          innerUpdate = (time, hover) => {
            const delta = Math.min(0.05, Math.max(0, time - chartLastTime));
            chartLastTime = time;
            const textureInterval = this.isMobile ? 1 / 8 : 1 / 10;
            if (time - chartLastDrawTime >= textureInterval) {
              chartLastDrawTime = time;
              const chartAccent = mixCssColor([42, 130, 158], [43, 185, 237], hover);
              const chartInk = mixCssColor([29, 45, 54], [8, 55, 72], hover * 0.7);
              const chartBackground = chartCtx.createLinearGradient(0, 0, 1024, 1024);
              chartBackground.addColorStop(0, '#fbfcfb');
              chartBackground.addColorStop(0.52, '#f4f8f8');
              chartBackground.addColorStop(1, '#e8f8fd');
              chartCtx.fillStyle = chartBackground;
              chartCtx.fillRect(0, 0, 1024, 1024);

              // Keep the primary identity and metrics near the equator where
              // spherical UV projection remains readable at production size.
              chartCtx.textAlign = 'center';
              chartCtx.fillStyle = '#15303a';
              chartCtx.font = '700 32px Poppins, sans-serif';
              chartCtx.fillText('MOZTECH / POWER LAB', 512, 214);
              chartCtx.fillStyle = '#2bb9ed';
              chartCtx.fillRect(434, 234, 156, 6);

              const livePulse = 0.72 + Math.sin(time * 2.2) * 0.18;
              chartCtx.fillStyle = `rgba(43, 185, 237, ${0.16 + livePulse * 0.1})`;
              drawChartRoundedRect(418, 260, 188, 48, 24);
              chartCtx.fill();
              chartCtx.fillStyle = chartAccent;
              chartCtx.font = '700 22px Poppins, sans-serif';
              chartCtx.fillText('●  LIVE QI2', 512, 293);

              const outputWatts = 15 + Math.sin(time * 0.7) * 0.16;
              const deviceHealth = 98.4 + Math.sin(time * 0.48) * 0.34;
              chartCtx.fillStyle = chartInk;
              chartCtx.font = '700 76px Poppins, sans-serif';
              chartCtx.fillText(`${outputWatts.toFixed(1)} W`, 374, 398);
              chartCtx.font = '700 58px Poppins, sans-serif';
              chartCtx.fillText(`${deviceHealth.toFixed(1)}%`, 746, 394);
              chartCtx.fillStyle = '#66818c';
              chartCtx.font = '600 21px Poppins, sans-serif';
              chartCtx.fillText('ADAPTIVE OUTPUT', 374, 432);
              chartCtx.fillText('DEVICE HEALTH', 746, 432);

              chartCtx.strokeStyle = 'rgba(49, 105, 124, 0.15)';
              chartCtx.lineWidth = 2;
              for (let y = 490; y <= 670; y += 45) {
                chartCtx.beginPath();
                chartCtx.moveTo(112, y);
                chartCtx.lineTo(912, y);
                chartCtx.stroke();
              }

              const graphPoints = [];
              for (let x = 112; x <= 912; x += 16) {
                const normalized = (x - 112) / 800;
                const y = 598
                  - Math.sin(normalized * Math.PI * 3.25 + time * (0.38 + hover * 0.42)) * 64
                  - Math.cos(normalized * Math.PI * 7.4 - time * 0.16) * 20;
                graphPoints.push([x, y]);
              }

              const areaGradient = chartCtx.createLinearGradient(0, 480, 0, 700);
              areaGradient.addColorStop(0, `rgba(43, 185, 237, ${0.2 + hover * 0.08})`);
              areaGradient.addColorStop(1, 'rgba(43, 185, 237, 0)');
              chartCtx.beginPath();
              chartCtx.moveTo(graphPoints[0][0], 690);
              graphPoints.forEach(([x, y]) => chartCtx.lineTo(x, y));
              chartCtx.lineTo(graphPoints[graphPoints.length - 1][0], 690);
              chartCtx.closePath();
              chartCtx.fillStyle = areaGradient;
              chartCtx.fill();

              chartCtx.strokeStyle = chartAccent;
              chartCtx.lineWidth = 10;
              chartCtx.lineCap = 'round';
              chartCtx.lineJoin = 'round';
              chartCtx.beginPath();
              graphPoints.forEach(([x, y], pointIndex) => {
                if (pointIndex === 0) chartCtx.moveTo(x, y);
                else chartCtx.lineTo(x, y);
              });
              chartCtx.stroke();

              const markerIndex = Math.floor(
                ((time * (0.16 + hover * 0.12)) % 1) * (graphPoints.length - 1)
              );
              const marker = graphPoints[markerIndex];
              chartCtx.shadowBlur = 20;
              chartCtx.shadowColor = '#2bb9ed';
              chartCtx.fillStyle = '#ffffff';
              chartCtx.beginPath();
              chartCtx.arc(marker[0], marker[1], 11 + hover * 4, 0, TAU);
              chartCtx.fill();
              chartCtx.lineWidth = 7;
              chartCtx.strokeStyle = '#2bb9ed';
              chartCtx.stroke();
              chartCtx.shadowBlur = 0;

              const chipData = [
                ['THERMAL', `${(34.2 + Math.sin(time * 0.3) * 0.3).toFixed(1)}°C`],
                ['LINK', 'QI2 LOCK'],
                ['MODE', 'ADAPTIVE']
              ];
              chartCtx.font = '700 19px Poppins, sans-serif';
              chipData.forEach(([label, value], chipIndex) => {
                const x = 146 + chipIndex * 250;
                chartCtx.fillStyle = chipIndex === 1
                  ? `rgba(43, 185, 237, ${0.14 + hover * 0.08})`
                  : 'rgba(17, 54, 67, 0.07)';
                drawChartRoundedRect(x, 736, 220, 74, 22);
                chartCtx.fill();
                chartCtx.fillStyle = '#5f7a84';
                chartCtx.fillText(label, x + 110, 764);
                chartCtx.fillStyle = chartInk;
                chartCtx.fillText(value, x + 110, 794);
              });
              chartCtx.textAlign = 'left';
              chartTexture.needsUpdate = true;
            }
            chartSphere.rotation.y += delta * (0.18 + hover * 0.48) * b.rotation_speed;
            chartSphere.scale.setScalar(1 + hover * 0.026);
            outerGlass.material.uniforms.hover.value = hover;
            group.userData.chartTextureSize = chartTextureSize;
            group.userData.chartBrand = 'MOZTECH POWER LAB';
            group.userData.chartMetrics = ['adaptive-output', 'device-health', 'thermal', 'qi2-link'];
          };
        }

        // 8. Product interface sphere with a wrapped, animated diagnostic UI.
        else if (b.orb_type === 'ui_screenshot') {
          const uiShellMaterial = darkGlossyGlassMat.clone();
          uiShellMaterial.uniforms.baseColor.value.set('#0a141c');
          uiShellMaterial.uniforms.accentColor.value.set('#c8f3ff');
          uiShellMaterial.uniforms.hoverColor.value.set('#f7fcff');
          uiShellMaterial.uniforms.opacity.value = 0.84;
          uiShellMaterial.uniforms.roughness.value = 0.24;
          uiShellMaterial.uniforms.frostStrength.value = 0;
          uiShellMaterial.uniforms.fresnelPower.value = 7.4;
          uiShellMaterial.uniforms.rimStrength.value = 0.94;
          uiShellMaterial.uniforms.directionalHalo.value = 1;
          uiShellMaterial.userData.mbsLocalRenderOrder = 7;
          const shellMesh = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius, 32, 32),
            uiShellMaterial
          );
          shellMesh.userData.mbsLocalRenderOrder = 7;
          group.add(shellMesh);

          const uiCanvas = document.createElement('canvas');
          const uiTextureSize = this.isMobile ? 384 : 512;
          uiCanvas.width = uiTextureSize;
          uiCanvas.height = uiTextureSize;
          const uiCtx = uiCanvas.getContext('2d');
          uiCtx.scale(uiTextureSize / 1024, uiTextureSize / 1024);
          let uiLastDrawTime = -Infinity;
          let uiLastTime = 0;
          const uiTexture = new THREE.CanvasTexture(uiCanvas);
          uiTexture.minFilter = THREE.LinearFilter;
          uiTexture.magFilter = THREE.LinearFilter;
          uiTexture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
          uiTexture.generateMipmaps = false;
          uiTexture.encoding = THREE.sRGBEncoding;

          const uiSphere = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * 0.94, 40, 28),
            new THREE.MeshBasicMaterial({
              map: uiTexture,
              color: 0xffffff,
              transparent: true,
              opacity: 0.9,
              depthWrite: false,
              toneMapped: false
            })
          );
          group.add(uiSphere);

          innerUpdate = (time, hover) => {
            const delta = Math.min(0.05, Math.max(0, time - uiLastTime));
            uiLastTime = time;
            const textureInterval = 1 / 8;
            if (time - uiLastDrawTime >= textureInterval) {
              uiLastDrawTime = time;
              const uiAccent = mixCssColor([43, 185, 237], [238, 250, 255], hover * 0.88);
              const uiAccentSoft = mixCssColor([171, 211, 224], [255, 255, 255], hover);
              uiCtx.clearRect(0, 0, 1024, 1024);
              const uiBackdrop = uiCtx.createLinearGradient(0, 0, 1024, 1024);
              uiBackdrop.addColorStop(0, `rgba(3, 9, 13, ${0.18 - hover * 0.035})`);
              uiBackdrop.addColorStop(0.5, `rgba(6, 15, 21, ${0.3 - hover * 0.05})`);
              uiBackdrop.addColorStop(1, `rgba(2, 7, 11, ${0.14 - hover * 0.025})`);
              uiCtx.globalAlpha = 1;
              uiCtx.fillStyle = uiBackdrop;
              uiCtx.fillRect(0, 0, 1024, 1024);

              uiCtx.textAlign = 'left';
              uiCtx.fillStyle = '#f7fbfd';
              uiCtx.font = '600 34px Poppins, sans-serif';
              uiCtx.fillText('MOZTECH', 64, 82);
              uiCtx.fillStyle = uiAccent;
              uiCtx.font = '600 19px Poppins, sans-serif';
              uiCtx.fillText('● LIVE SYSTEM', 250, 81);
              uiCtx.fillStyle = 'rgba(200, 216, 224, 0.72)';
              uiCtx.font = '500 20px Poppins, sans-serif';
              uiCtx.fillText('MODULAR ENERGY CONTROL', 64, 122);
              uiCtx.fillStyle = uiAccent;
              uiCtx.globalAlpha = 0.74;
              uiCtx.fillRect(64, 151, 896, 3);
              uiCtx.globalAlpha = 1;

              const gauges = [
                { x: 256, value: 0.74 + Math.sin(time * 0.7) * 0.05, label: 'THERMAL' },
                { x: 768, value: 0.88 + Math.cos(time * 0.5) * 0.03, label: 'POWER FLOW' }
              ];
              gauges.forEach((gauge) => {
                uiCtx.strokeStyle = 'rgba(120, 143, 154, 0.2)';
                uiCtx.lineWidth = 24;
                uiCtx.beginPath();
                uiCtx.arc(gauge.x, 404, 146, Math.PI * 0.72, Math.PI * 2.28);
                uiCtx.stroke();
                uiCtx.strokeStyle = uiAccent;
                uiCtx.lineCap = 'round';
                uiCtx.beginPath();
                uiCtx.arc(gauge.x, 404, 146, Math.PI * 0.72, Math.PI * (0.72 + gauge.value * 1.56));
                uiCtx.stroke();
                uiCtx.lineCap = 'butt';

                for (let tick = 0; tick <= 18; tick += 1) {
                  const tickAngle = Math.PI * (0.72 + tick / 18 * 1.56);
                  const tickLength = tick % 3 === 0 ? 14 : 8;
                  const innerRadius = 176;
                  uiCtx.strokeStyle = tick / 18 <= gauge.value
                    ? 'rgba(228, 248, 255, 0.72)'
                    : 'rgba(156, 178, 188, 0.24)';
                  uiCtx.lineWidth = tick % 3 === 0 ? 3 : 2;
                  uiCtx.beginPath();
                  uiCtx.moveTo(
                    gauge.x + Math.cos(tickAngle) * innerRadius,
                    404 + Math.sin(tickAngle) * innerRadius
                  );
                  uiCtx.lineTo(
                    gauge.x + Math.cos(tickAngle) * (innerRadius + tickLength),
                    404 + Math.sin(tickAngle) * (innerRadius + tickLength)
                  );
                  uiCtx.stroke();
                }

                uiCtx.fillStyle = '#ffffff';
                uiCtx.font = '600 76px Poppins, sans-serif';
                uiCtx.textAlign = 'center';
                uiCtx.fillText(`${Math.round(gauge.value * 100)}`, gauge.x, 428);
                uiCtx.fillStyle = 'rgba(199, 216, 224, 0.78)';
                uiCtx.font = '500 21px Poppins, sans-serif';
                uiCtx.fillText(gauge.label, gauge.x, 474);
              });

              uiCtx.textAlign = 'left';
              uiCtx.strokeStyle = 'rgba(155, 181, 193, 0.24)';
              uiCtx.lineWidth = 2;
              uiCtx.strokeRect(64, 650, 896, 242);
              uiCtx.fillStyle = 'rgba(210, 226, 233, 0.68)';
              uiCtx.font = '500 18px Poppins, sans-serif';
              uiCtx.fillText('ENERGY RESPONSE / 24H', 84, 692);
              uiCtx.strokeStyle = uiAccentSoft;
              uiCtx.lineWidth = 6;
              uiCtx.beginPath();
              for (let x = 72; x <= 952; x += 14) {
                const y = 800 + Math.sin((x - 64) * 0.018 + time * (1.1 + hover * 0.35)) * 52
                  + Math.sin((x - 64) * 0.05) * 15;
                if (x === 72) uiCtx.moveTo(x, y);
                else uiCtx.lineTo(x, y);
              }
              uiCtx.stroke();

              uiTexture.needsUpdate = true;
            }
            const uiRotationSpeed = THREE.MathUtils.lerp(0.21, 0.055, hover);
            uiSphere.rotation.y += delta * uiRotationSpeed * b.rotation_speed;
            uiSphere.scale.setScalar(1 + hover * 0.02);
            shellMesh.material.uniforms.hover.value = hover;
            shellMesh.material.uniforms.roughness.value = THREE.MathUtils.lerp(0.24, 0.18, hover);
            group.userData.uiReadability = hover;
            group.userData.uiMotionMode = hover > 0.65 ? 'inspect-slow' : 'idle-orbit';
          };
        }

        // 11. MOZTECH product carousel. The product remains inside a refractive
        // glass volume while the cart/count is rendered as crisp surface UI.
        else if (b.orb_type === 'product_image') {
          const productGroup = new THREE.Group();
          const contentRoot = new THREE.Group();
          productGroup.add(contentRoot);
          const productLightPointer = new THREE.Vector2();
          const productLightTarget = new THREE.Vector2();

          const productGlassMaterial = createPremiumTransmissionMaterial(baseRadius * 1.48);
          productGlassMaterial.uniforms.uBackgroundColor.value.set('#02070b');
          productGlassMaterial.uniforms.uTintColor.value.set('#78d9ff');
          productGlassMaterial.uniforms.uBodyColor.value.set('#07131a');
          productGlassMaterial.uniforms.uIor.value = 1.22;
          productGlassMaterial.uniforms.uRefractionStrength.value = 0.34;
          productGlassMaterial.uniforms.uRoughness.value = 0.052;
          productGlassMaterial.uniforms.uChromaticAberration.value = 0.012;
          productGlassMaterial.uniforms.uEnvironmentIntensity.value = 1.2;
          productGlassMaterial.uniforms.uBodyStrength.value = 0.09;
          productGlassMaterial.uniforms.uWhiteEdgeStrength.value = 0.82;
          // Product-only studio setup: a broad lower-left softbox, a dark
          // opposite shoulder and a restrained upper fill. Leave every other
          // glass object and the shared environment untouched.
          productGlassMaterial.uniforms.uProductLightPointer = { value: productLightPointer };
          productGlassMaterial.fragmentShader = productGlassMaterial.fragmentShader
            .replace('uniform float uTime;', 'uniform float uTime;\n        uniform vec2 uProductLightPointer;')
            .replace(
              'vec3 color = volumetricBody * (1.0 - fresnel * 0.52);',
              `vec2 productSoftboxUv = (disk + vec2(0.51, 0.55) - uProductLightPointer * vec2(0.045, 0.035)) / vec2(0.34, 0.24);
              float productSoftbox = exp(-dot(productSoftboxUv, productSoftboxUv));
              float productShoulder = smoothstep(0.0, 0.85, disk.x + disk.y * 0.6) * (1.0 - fresnel);
              vec3 color = volumetricBody * (1.0 - fresnel * 0.52) * (1.0 - productShoulder * 0.18);`
            )
            .replace('shellUpperGlint * 0.4', 'shellUpperGlint * 0.19')
            .replace('keyHighlight * 0.72', 'keyHighlight * 0.32')
            .replace('fillHighlight * 0.29', 'fillHighlight * 0.16')
            .replace('shellLowerCrescent * 1.05', 'shellLowerCrescent * 1.22 + productSoftbox * (0.28 + uHover * 0.08)')
            .replace('shellLowerCrescent * 0.48', 'shellLowerCrescent * 0.52 + productSoftbox * 0.2');
          productGlassMaterial.userData.mbsLocalRenderOrder = 7;
          productGlassMaterial.userData.mbsProductGlass = true;
          const glassMesh = new THREE.Mesh(
            new THREE.SphereGeometry(
              baseRadius,
              this.isMobile ? 48 : 72,
              this.isMobile ? 36 : 54
            ),
            productGlassMaterial
          );
          glassMesh.userData.mbsLocalRenderOrder = 7;
          productGroup.add(glassMesh);

          // A soft interior light volume separates a transparent PNG from the
          // black stage without drawing a visible card around it. Shopify's
          // reference reads as an object suspended in glass because its
          // product, reflection and foreground cart occupy different depths.
          const productGlowCanvas = document.createElement('canvas');
          productGlowCanvas.width = 192;
          productGlowCanvas.height = 192;
          const productGlowContext = productGlowCanvas.getContext('2d');
          const productGlowGradient = productGlowContext.createRadialGradient(68, 122, 3, 86, 104, 92);
          productGlowGradient.addColorStop(0, 'rgba(225, 240, 246, 0.28)');
          productGlowGradient.addColorStop(0.36, 'rgba(173, 202, 215, 0.1)');
          productGlowGradient.addColorStop(0.72, 'rgba(43, 185, 237, 0.025)');
          productGlowGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
          productGlowContext.fillStyle = productGlowGradient;
          productGlowContext.fillRect(0, 0, 192, 192);
          const productGlowTexture = new THREE.CanvasTexture(productGlowCanvas);
          productGlowTexture.minFilter = THREE.LinearFilter;
          productGlowTexture.magFilter = THREE.LinearFilter;
          productGlowTexture.generateMipmaps = false;
          productGlowTexture.encoding = THREE.sRGBEncoding;
          const productGlowMaterial = new THREE.MeshBasicMaterial({
            map: productGlowTexture,
            transparent: true,
            opacity: 0.72,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
            depthTest: true,
            toneMapped: false
          });
          productGlowMaterial.userData.mbsBaseOpacity = 0.72;
          productGlowMaterial.userData.mbsLocalRenderOrder = 2;
          const productGlow = new THREE.Mesh(
            new THREE.PlaneGeometry(baseRadius * 1.42, baseRadius * 1.42),
            productGlowMaterial
          );
          productGlow.position.set(-baseRadius * 0.12, -baseRadius * 0.12, -baseRadius * 0.16);
          productGlow.userData.mbsLocalRenderOrder = 2;
          contentRoot.add(productGlow);

          const createFallbackProduct = (productIndex) => {
            const fallback = new THREE.Group();
            const bodyMaterial = new THREE.MeshStandardMaterial({
              color: productIndex === 0 ? 0xf3f4f4 : 0x20252b,
              roughness: 0.25,
              metalness: 0.28,
              transparent: true,
              opacity: 1
            });
            bodyMaterial.userData.mbsFrameOpacity = 1;
            const accentMaterial = new THREE.MeshStandardMaterial({
              color: 0x9aa1a8,
              emissive: 0x07131a,
              roughness: 0.18,
              metalness: 0.68,
              transparent: true,
              opacity: 1
            });
            accentMaterial.userData.mbsFrameOpacity = 1;

            if (productIndex === 0) {
              const handle = new THREE.Mesh(
                new THREE.BoxGeometry(baseRadius * 0.25, baseRadius * 0.75, baseRadius * 0.18),
                bodyMaterial
              );
              handle.position.y = -baseRadius * 0.12;
              fallback.add(handle);
              const fanHead = new THREE.Mesh(
                new THREE.CylinderGeometry(baseRadius * 0.34, baseRadius * 0.34, baseRadius * 0.18, 44),
                bodyMaterial
              );
              fanHead.rotation.x = Math.PI / 2;
              fanHead.position.y = baseRadius * 0.28;
              fallback.add(fanHead);
              const fanRing = new THREE.Mesh(
                new THREE.TorusGeometry(baseRadius * 0.24, baseRadius * 0.035, 10, 56),
                accentMaterial
              );
              fanRing.position.set(0, baseRadius * 0.28, baseRadius * 0.1);
              fallback.add(fanRing);
            } else if (productIndex === 1) {
              const capsuleBody = new THREE.Mesh(
                new THREE.CylinderGeometry(baseRadius * 0.32, baseRadius * 0.32, baseRadius * 0.5, 32),
                bodyMaterial
              );
              fallback.add(capsuleBody);
              [-0.25, 0.25].forEach((capY) => {
                const cap = new THREE.Mesh(
                  new THREE.SphereGeometry(baseRadius * 0.32, 28, 18),
                  bodyMaterial
                );
                cap.position.y = capY * baseRadius;
                fallback.add(cap);
              });
              const display = new THREE.Mesh(
                new THREE.TorusGeometry(baseRadius * 0.18, baseRadius * 0.028, 10, 48),
                accentMaterial
              );
              display.position.z = baseRadius * 0.31;
              fallback.add(display);
            } else if (productIndex === 2) {
              const chargerBody = new THREE.Mesh(
                new THREE.BoxGeometry(baseRadius * 0.72, baseRadius * 0.72, baseRadius * 0.34),
                bodyMaterial
              );
              fallback.add(chargerBody);
              const chargeRing = new THREE.Mesh(
                new THREE.TorusGeometry(baseRadius * 0.2, baseRadius * 0.035, 10, 48),
                accentMaterial
              );
              chargeRing.position.z = baseRadius * 0.18;
              fallback.add(chargeRing);
            } else {
              const powerBankBody = new THREE.Mesh(
                new THREE.BoxGeometry(baseRadius * 0.58, baseRadius * 0.82, baseRadius * 0.2),
                bodyMaterial
              );
              powerBankBody.rotation.z = -0.1;
              fallback.add(powerBankBody);
              const display = new THREE.Mesh(
                new THREE.PlaneGeometry(baseRadius * 0.22, baseRadius * 0.11),
                accentMaterial
              );
              display.position.set(0, baseRadius * 0.18, baseRadius * 0.105);
              display.rotation.z = -0.1;
              fallback.add(display);
            }
            fallback.scale.setScalar(0.78);
            return fallback;
          };

          const productFrames = [];
          const fallbackProductUrls = [
            this.productFanImageUrl,
            this.productCapsuleImageUrl,
            this.productChargerImageUrl,
            this.productArkImageUrl
          ];
          const productUrls = Array.isArray(b.product_images) && b.product_images.some(Boolean)
            ? fallbackProductUrls.map((fallbackUrl, productIndex) => b.product_images[productIndex] || fallbackUrl)
            : fallbackProductUrls;

          productUrls.forEach((url, productIndex) => {
            const frame = new THREE.Group();
            frame.userData.productFrameIndex = productIndex;
            const fallback = createFallbackProduct(productIndex);
            frame.add(fallback);
            frame.userData.opacity = 0;
            frame.userData.opacityDirty = true;
            contentRoot.add(frame);
            productFrames.push(frame);

            if (!url) return;
            const loadProductTexture = () => new Promise((resolve) => {
              this.textureLoader.load(url, (texture) => {
                if (!this.renderer || !this.section || !document.body.contains(this.section)) {
                  texture.dispose();
                  resolve();
                  return;
                }
                texture.encoding = THREE.sRGBEncoding;
                texture.premultiplyAlpha = true;
                texture.minFilter = THREE.LinearMipmapLinearFilter;
                texture.magFilter = THREE.LinearFilter;
                texture.generateMipmaps = true;
                texture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
                texture.needsUpdate = true;

                const imageWidth = texture.image && texture.image.width ? texture.image.width : 1;
                const imageHeight = texture.image && texture.image.height ? texture.image.height : 1;
                let imageAspect = imageWidth / Math.max(1, imageHeight);

                // Transparent merchant PNGs often contain large empty margins.
                // Measure a small alpha proxy and crop through the texture
                // transform so the visible product—not its source canvas—sets
                // the sphere fit. Cross-origin images safely keep full bounds.
                try {
                  const alphaProbe = document.createElement('canvas');
                  const probeMax = 192;
                  const probeScale = Math.min(1, probeMax / Math.max(imageWidth, imageHeight));
                  alphaProbe.width = Math.max(1, Math.round(imageWidth * probeScale));
                  alphaProbe.height = Math.max(1, Math.round(imageHeight * probeScale));
                  const alphaContext = alphaProbe.getContext('2d', { willReadFrequently: true });
                  alphaContext.drawImage(texture.image, 0, 0, alphaProbe.width, alphaProbe.height);
                  const alphaPixels = alphaContext.getImageData(0, 0, alphaProbe.width, alphaProbe.height).data;
                  let minAlphaX = alphaProbe.width;
                  let minAlphaY = alphaProbe.height;
                  let maxAlphaX = -1;
                  let maxAlphaY = -1;
                  for (let alphaY = 0; alphaY < alphaProbe.height; alphaY++) {
                    for (let alphaX = 0; alphaX < alphaProbe.width; alphaX++) {
                      const alpha = alphaPixels[(alphaY * alphaProbe.width + alphaX) * 4 + 3];
                      if (alpha < 12) continue;
                      minAlphaX = Math.min(minAlphaX, alphaX);
                      minAlphaY = Math.min(minAlphaY, alphaY);
                      maxAlphaX = Math.max(maxAlphaX, alphaX);
                      maxAlphaY = Math.max(maxAlphaY, alphaY);
                    }
                  }
                  if (maxAlphaX >= minAlphaX && maxAlphaY >= minAlphaY) {
                    const padX = Math.max(2, Math.round((maxAlphaX - minAlphaX + 1) * 0.045));
                    const padY = Math.max(2, Math.round((maxAlphaY - minAlphaY + 1) * 0.045));
                    minAlphaX = Math.max(0, minAlphaX - padX);
                    minAlphaY = Math.max(0, minAlphaY - padY);
                    maxAlphaX = Math.min(alphaProbe.width - 1, maxAlphaX + padX);
                    maxAlphaY = Math.min(alphaProbe.height - 1, maxAlphaY + padY);
                    const cropWidth = maxAlphaX - minAlphaX + 1;
                    const cropHeight = maxAlphaY - minAlphaY + 1;
                    texture.repeat.set(cropWidth / alphaProbe.width, cropHeight / alphaProbe.height);
                    texture.offset.set(minAlphaX / alphaProbe.width, 1 - (maxAlphaY + 1) / alphaProbe.height);
                    imageAspect = cropWidth / Math.max(1, cropHeight);
                    texture.needsUpdate = true;
                  }
                } catch (alphaError) {
                  // Shopify CDN images normally expose CORS headers. A custom
                  // external URL may not; in that case the full texture is a
                  // correct, non-blocking fallback.
                }

                const maxWidth = baseRadius * (productIndex === 0 ? 1.3 : 1.24);
                const maxHeight = baseRadius * (productIndex === 0 ? 1.42 : 1.22);
                let planeWidth = maxWidth;
                let planeHeight = planeWidth / imageAspect;
                if (planeHeight > maxHeight) {
                  planeHeight = maxHeight;
                  planeWidth = planeHeight * imageAspect;
                }
                const imageMaterial = new THREE.MeshBasicMaterial({
                  map: texture,
                  color: 0xffffff,
                  transparent: true,
                  side: THREE.DoubleSide,
                  depthWrite: false,
                  depthTest: true,
                  alphaTest: 0.012,
                  premultipliedAlpha: true,
                  toneMapped: false
                });
                imageMaterial.userData.mbsBaseOpacity = 1;
                imageMaterial.userData.mbsLocalRenderOrder = 4;
                imageMaterial.userData.mbsFrameOpacity = 1;
                // Merchant images retain their baked material lighting. This
                // gentle studio exposure gradient adds directional contrast,
                // not a fabricated 3D normal map or a bright blue duplicate.
                imageMaterial.onBeforeCompile = (shader) => {
                  shader.uniforms.uProductLightPointer = { value: productLightPointer };
                  shader.vertexShader = shader.vertexShader
                    .replace('#include <common>', '#include <common>\nvarying vec2 vProductLightUv;')
                    .replace('#include <uv_vertex>', '#include <uv_vertex>\nvProductLightUv = uv;');
                  shader.fragmentShader = shader.fragmentShader
                    .replace('#include <common>', '#include <common>\nvarying vec2 vProductLightUv;\nuniform vec2 uProductLightPointer;')
                    .replace('#include <color_fragment>', `#include <color_fragment>
                      vec2 studioUv = vProductLightUv - uProductLightPointer * 0.045;
                      float studioLift = exp(-dot((studioUv - vec2(0.23, 0.18)) * vec2(1.8, 1.6), (studioUv - vec2(0.23, 0.18)) * vec2(1.8, 1.6)));
                      float studioShade = smoothstep(0.35, 1.35, studioUv.x * 0.75 + studioUv.y * 0.7);
                      diffuseColor.rgb *= 0.93 + studioLift * 0.13 - studioShade * 0.075;`);
                };
                imageMaterial.customProgramCacheKey = () => 'moztech-product-lower-left-studio-458';
                const imageMesh = new THREE.Mesh(
                  new THREE.PlaneGeometry(planeWidth, planeHeight),
                  imageMaterial
                );
                imageMesh.position.z = baseRadius * 0.09;
                imageMesh.userData.mbsLocalRenderOrder = 4;
                frame.add(imageMesh);
                const imageDepthMaterial = new THREE.MeshBasicMaterial({
                  map: texture,
                  color: 0x030508,
                  transparent: true,
                  opacity: 0.24,
                  side: THREE.DoubleSide,
                  depthWrite: false,
                  depthTest: true,
                  alphaTest: 0.012,
                  premultipliedAlpha: true,
                  blending: THREE.NormalBlending,
                  toneMapped: false
                });
                imageDepthMaterial.userData.mbsBaseOpacity = 0.24;
                imageDepthMaterial.userData.mbsLocalRenderOrder = 3;
                imageDepthMaterial.userData.mbsFrameOpacity = 0.24;
                imageDepthMaterial.onBeforeCompile = (shader) => {
                  shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>', `
                    #ifdef USE_MAP
                      vec2 shadowSpread = vec2(0.008);
                      float shadowAlpha = texture2D(map, vUv).a * 0.36;
                      shadowAlpha += texture2D(map, vUv + vec2(shadowSpread.x, 0.0)).a * 0.16;
                      shadowAlpha += texture2D(map, vUv - vec2(shadowSpread.x, 0.0)).a * 0.16;
                      shadowAlpha += texture2D(map, vUv + vec2(0.0, shadowSpread.y)).a * 0.16;
                      shadowAlpha += texture2D(map, vUv - vec2(0.0, shadowSpread.y)).a * 0.16;
                      diffuseColor.a *= shadowAlpha;
                    #endif
                  `);
                };
                imageDepthMaterial.customProgramCacheKey = () => 'moztech-product-soft-shadow-458';
                const imageDepthMesh = new THREE.Mesh(
                  new THREE.PlaneGeometry(planeWidth, planeHeight),
                  imageDepthMaterial
                );
                imageDepthMesh.position.set(baseRadius * 0.055, baseRadius * 0.05, -baseRadius * 0.035);
                imageDepthMesh.scale.setScalar(0.98);
                imageDepthMesh.userData.mbsLocalRenderOrder = 3;
                frame.add(imageDepthMesh);
                if (group.userData.mbsRegisterRenderable) {
                  group.userData.mbsRegisterRenderable(imageMesh);
                  group.userData.mbsRegisterRenderable(imageDepthMesh);
                } else {
                  group.userData.mbsPendingRenderables.push(imageMesh);
                  group.userData.mbsPendingRenderables.push(imageDepthMesh);
                }
                fallback.visible = false;
                frame.userData.opacityDirty = true;
                resolve();
              }, undefined, () => resolve());
            });
            const trackedProductLoad = () => trackLoad(
              loadProductTexture(),
              `Product carousel image ${productIndex + 1}`
            );

            // Product PNGs are enhancement assets. The synchronous geometry
            // fallback keeps the first frame complete while every image waits
            // for idle time, so the carousel can never delay hero readiness.
            this.deferredAssetLoaders.push(trackedProductLoad);
          });

          const cartGroup = new THREE.Group();
          const cartCanvas = document.createElement('canvas');
          cartCanvas.width = 256;
          cartCanvas.height = 256;
          const cartContext = cartCanvas.getContext('2d');
          const cartTexture = new THREE.CanvasTexture(cartCanvas);
          cartTexture.minFilter = THREE.LinearFilter;
          cartTexture.magFilter = THREE.LinearFilter;
          cartTexture.generateMipmaps = false;
          cartTexture.encoding = THREE.sRGBEncoding;
          const drawCartBadge = (count) => {
            cartContext.clearRect(0, 0, 256, 256);
            cartContext.strokeStyle = '#f5f8fa';
            cartContext.fillStyle = '#f5f8fa';
            cartContext.lineWidth = 11;
            cartContext.lineCap = 'round';
            cartContext.lineJoin = 'round';
            cartContext.beginPath();
            cartContext.moveTo(34, 61);
            cartContext.lineTo(65, 61);
            cartContext.lineTo(91, 157);
            cartContext.lineTo(188, 157);
            cartContext.lineTo(216, 86);
            cartContext.lineTo(75, 86);
            cartContext.stroke();
            [111, 174].forEach((wheelX) => {
              cartContext.beginPath();
              cartContext.arc(wheelX, 188, 13, 0, TAU);
              cartContext.fill();
            });

            cartContext.beginPath();
            cartContext.arc(207, 48, 35, 0, TAU);
            cartContext.fillStyle = '#f7fafb';
            cartContext.fill();
            cartContext.lineWidth = 5;
            cartContext.strokeStyle = 'rgba(207, 217, 224, 0.88)';
            cartContext.stroke();
            cartContext.fillStyle = '#05090d';
            cartContext.font = '700 44px Poppins, -apple-system, sans-serif';
            cartContext.textAlign = 'center';
            cartContext.textBaseline = 'middle';
            cartContext.fillText(String(count), 207, 52);
            cartTexture.needsUpdate = true;
          };
          drawCartBadge(1);
          const cartMaterial = new THREE.MeshBasicMaterial({
            map: cartTexture,
            color: 0xdfe5e9,
            transparent: true,
            opacity: 0.88,
            depthWrite: false,
            depthTest: true,
            premultipliedAlpha: true,
            toneMapped: false
          });
          cartMaterial.userData.mbsBaseOpacity = 0.88;
          cartMaterial.userData.mbsLocalRenderOrder = 8;
          const cartSurface = new THREE.Mesh(
            new THREE.PlaneGeometry(baseRadius * 0.48, baseRadius * 0.48),
            cartMaterial
          );
          cartSurface.userData.mbsLocalRenderOrder = 8;
          cartSurface.userData.mbsCartSurfaceUi = true;
          cartGroup.add(cartSurface);
          cartGroup.position.set(baseRadius * 0.42, baseRadius * 0.4, baseRadius * 0.69);
          cartGroup.scale.setScalar(0.98);
          contentRoot.add(cartGroup);

          const setFrameOpacity = (frame, opacity) => {
            if (!frame.userData.opacityDirty && Math.abs(frame.userData.opacity - opacity) < 0.002) return;
            frame.userData.opacity = opacity;
            frame.userData.opacityDirty = false;
            frame.traverse((child) => {
              if (!child.material) return;
              const materials = Array.isArray(child.material) ? child.material : [child.material];
              materials.forEach((material) => {
                if (material.userData.mbsFrameOpacity === undefined) {
                  // Async textures can finish while their frame is hidden. A
                  // transient opacity of zero must never become the permanent
                  // base opacity for later carousel cycles.
                  material.userData.mbsFrameOpacity = 1;
                }
                material.transparent = true;
                material.opacity = material.userData.mbsFrameOpacity * opacity;
                if (material.uniforms && material.uniforms.uOpacity) {
                  material.uniforms.uOpacity.value = material.userData.mbsFrameOpacity * opacity;
                }
              });
            });
          };

          productGroup.userData.productCount = productFrames.length;
          productGroup.userData.activeProductIndex = 0;
          productGroup.userData.glassEdgeStrength = 0.78;
          productGroup.userData.productImageSources = b.product_image_sources || [];
          productGroup.userData.glassMaterial = 'wordmark-transmission';
          productGroup.userData.contentDepthLayers = 3;
          productGroup.userData.cartEntryDirection = 'right-to-left';
          productGroup.userData.cycleSequence = [1, 2, 3, 4, 1];
          productGroup.userData.productReveal = 'shrink-swap-spring-emergence';
          productGroup.userData.productLighting = 'lower-left-softbox';
          group.add(productGroup);

          const productRevealDuration = 1;
          const productExitDuration = 0.4;
          const productCycleDuration = 2.85;
          // A small, bounded overshoot gives a tangible arrival while the
          // previous product shrinks away instead of dissolving at full size.
          const productRevealEase = (progress) => {
            const p = THREE.MathUtils.clamp(progress, 0, 1) - 1;
            return 1 + 2.2 * p * p * p + 1.2 * p * p;
          };
          const productExitEase = (progress) => {
            const p = THREE.MathUtils.clamp(progress, 0, 1);
            return 1 - (2.2 * p * p * p - 1.2 * p * p);
          };
          let productClock = 0;
          let productLastTime = null;
          let cartBadgeCount = 1;
          innerUpdate = (time, hover, pointerX, pointerY, localHit) => {
            // Keep the merchandise cycle tied to elapsed time even when an
            // idle browser throttles requestAnimationFrame. The former 50 ms
            // cap stretched a merchandise loop to nearly a minute on a
            // five-fps background tab.
            const delta = productLastTime === null ? 0 : Math.min(0.24, Math.max(0, time - productLastTime));
            productLastTime = time;
            productClock += delta * b.rotation_speed;
            const cycleIndex = Math.floor(productClock / productCycleDuration);
            const activeIndex = cycleIndex % productFrames.length;
            const nextBadgeCount = activeIndex + 1;
            if (nextBadgeCount !== cartBadgeCount) {
              cartBadgeCount = nextBadgeCount;
              drawCartBadge(cartBadgeCount);
            }
            const localTime = productClock - cycleIndex * productCycleDuration;
            const revealProgress = THREE.MathUtils.clamp(localTime / productRevealDuration, 0, 1);
            const exitProgress = THREE.MathUtils.clamp(
              (localTime - productCycleDuration + productExitDuration) / productExitDuration, 0, 1
            );
            const revealScale = Math.max(0.001, exitProgress > 0
              ? productExitEase(exitProgress) : productRevealEase(revealProgress));
            const transition = revealProgress * revealProgress * (3 - 2 * revealProgress);

            productFrames.forEach((frame, frameIndex) => {
              const isActive = frameIndex === activeIndex;
              frame.visible = isActive;
              if (isActive) {
                frame.position.set(0, -baseRadius * (1 - Math.min(revealScale, 1)) * 0.07,
                  baseRadius * (0.03 - (1 - Math.min(revealScale, 1)) * 0.18));
                frame.scale.setScalar(revealScale);
                frame.rotation.z = -0.07 * (1 - transition);
                setFrameOpacity(frame, 1);
              } else {
                setFrameOpacity(frame, 0);
              }
            });

            // The cart and its count are the moving foreground UI. Each new
            // count enters from the right, settles over the product, then the
            // 4 -> 1 loop restarts from the same edge as the product emerges.
            cartGroup.position.x = THREE.MathUtils.lerp(
              baseRadius * 0.72,
              baseRadius * 0.4,
              transition
            );
            cartGroup.position.y = baseRadius * 0.4;

            const studioBlend = 1 - Math.exp(-delta * 4);
            if (localHit && hover > 0.001) {
              const localLength = Math.max(localHit.length(), 0.001);
              productLightTarget.set(localHit.x / localLength * hover, localHit.y / localLength * hover);
            } else {
              productLightTarget.set(pointerX * hover, pointerY * hover);
            }
            productLightPointer.lerp(productLightTarget, studioBlend);
            contentRoot.rotation.x = THREE.MathUtils.lerp(contentRoot.rotation.x,
              Math.sin(time * 0.7) * 0.055 + productLightPointer.y * 0.12, studioBlend);
            contentRoot.rotation.y = THREE.MathUtils.lerp(contentRoot.rotation.y,
              Math.sin(time * 0.46) * 0.085 - productLightPointer.x * 0.16, studioBlend);
            contentRoot.position.y = Math.sin(time * 0.82) * baseRadius * 0.02;
            productGlow.position.x = -baseRadius * 0.12 - productLightPointer.x * baseRadius * 0.025;
            productGlow.position.y = -baseRadius * 0.12 + productLightPointer.y * baseRadius * 0.018;
            productGlow.scale.setScalar(1 + hover * 0.08);
            productGlowMaterial.opacity = 0.58 + hover * 0.1;
            glassMesh.rotation.y += delta * (0.025 + hover * 0.035);
            productGlassMaterial.uniforms.uHover.value = hover;
            productGlassMaterial.uniforms.uTime.value = time;
            productGlassMaterial.uniforms.uRoughness.value = THREE.MathUtils.lerp(0.052, 0.032, hover);
            productGlassMaterial.uniforms.uRefractionStrength.value = THREE.MathUtils.lerp(0.34, 0.4, hover);
            productGlassMaterial.uniforms.uChromaticAberration.value = THREE.MathUtils.lerp(0.012, 0.018, hover);
            productGlassMaterial.uniforms.uEnvironmentIntensity.value = THREE.MathUtils.lerp(1.2, 1.34, hover);
            productGlassMaterial.uniforms.uWhiteEdgeStrength.value = THREE.MathUtils.lerp(0.82, 0.94, hover);
            cartMaterial.color.copy(uiInactiveColor).lerp(uiInactiveBright, hover);
            cartMaterial.opacity = 0.88 + hover * 0.12;
            cartMaterial.userData.mbsBaseOpacity = cartMaterial.opacity;
            cartGroup.scale.setScalar(0.98 + hover * 0.14);
            cartGroup.position.z = baseRadius * (0.69 + hover * 0.045);
            productGroup.userData.activeProductIndex = activeIndex;
            productGroup.userData.cartBadgeCount = cartBadgeCount;
            productGroup.userData.productRevealScale = revealScale;
            productGroup.userData.productCyclePhase = exitProgress > 0 ? 'shrink' : revealProgress < 1 ? 'reveal' : 'hold';
            productGroup.userData.glassEdgeStrength = 0.78 + hover * 0.18;
          };
        }

        // 5. MOZ Care+ branded transmission sphere. The band is a spherical
        // surface strip rather than a cylinder, so it conforms to the glass
        // instead of hovering beyond it. A colorless depth prepass hides only
        // the rear half of that strip.
        else if (b.orb_type === 'ribbon_wrapped') {
          const occluderMaterial = new THREE.MeshBasicMaterial({
            colorWrite: false,
            depthWrite: true,
            depthTest: true,
            side: THREE.FrontSide
          });
          occluderMaterial.userData.mbsForceOpaque = true;
          occluderMaterial.userData.mbsLocalRenderOrder = 0;
          const beltOccluder = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * 0.992, 64, 48),
            occluderMaterial
          );
          beltOccluder.userData.mbsLocalRenderOrder = 0;
          group.add(beltOccluder);

          const ribbonGlassMat = createPremiumTransmissionMaterial(baseRadius * 1.42);
          ribbonGlassMat.uniforms.uBackgroundColor.value.set('#050608');
          ribbonGlassMat.uniforms.uTintColor.value.set('#8fdfff');
          ribbonGlassMat.uniforms.uBodyColor.value.set('#070a0f');
          ribbonGlassMat.uniforms.uBodyStrength.value = 0.5;
          ribbonGlassMat.uniforms.uWhiteEdgeStrength.value = 0.94;
          ribbonGlassMat.uniforms.uEnvironmentIntensity.value = 1.48;
          ribbonGlassMat.uniforms.uRoughness.value = 0.03;
          ribbonGlassMat.uniforms.uIor.value = 1.22;
          ribbonGlassMat.uniforms.uRefractionStrength.value = 0.35;
          ribbonGlassMat.uniforms.uChromaticAberration.value = 0.011;
          const outerGlass = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius, 56, 40),
            ribbonGlassMat
          );
          outerGlass.userData.mbsLocalRenderOrder = 1;
          group.add(outerGlass);

          const bandCanvas = document.createElement('canvas');
          bandCanvas.width = 1024;
          bandCanvas.height = 128;
          const bandCtx = bandCanvas.getContext('2d');
          let brandLogoImage = null;
          let brandLogoWhiteCanvas = null;

          const drawBrandBand = () => {
            const width = bandCanvas.width;
            const height = bandCanvas.height;
            bandCtx.clearRect(0, 0, width, height);

            // Repeat the first color at both ends to keep the cylindrical seam
            // invisible while the band rotates.
            const gradient = bandCtx.createLinearGradient(0, 0, width, 0);
            gradient.addColorStop(0, '#0c6fa8');
            gradient.addColorStop(0.14, '#2bb9ed');
            gradient.addColorStop(0.3, '#25b4e8');
            gradient.addColorStop(0.46, '#286ed8');
            gradient.addColorStop(0.6, '#765ee5');
            gradient.addColorStop(0.74, '#2bb9ed');
            gradient.addColorStop(0.9, '#126cae');
            gradient.addColorStop(1, '#0c6fa8');
            bandCtx.fillStyle = gradient;
            bandCtx.fillRect(0, 0, width, height);

            const gloss = bandCtx.createLinearGradient(0, 0, 0, height);
            gloss.addColorStop(0, 'rgba(255,255,255,0.2)');
            gloss.addColorStop(0.14, 'rgba(255,255,255,0.06)');
            gloss.addColorStop(0.5, 'rgba(255,255,255,0)');
            gloss.addColorStop(0.8, 'rgba(190,220,255,0.12)');
            gloss.addColorStop(1, 'rgba(4,32,61,0.38)');
            bandCtx.fillStyle = gloss;
            bandCtx.fillRect(0, 0, width, height);

            bandCtx.fillStyle = 'rgba(255,255,255,0.18)';
            bandCtx.fillRect(0, height * 0.075, width, Math.max(2, height * 0.012));
            bandCtx.fillStyle = 'rgba(18,55,108,0.52)';
            bandCtx.fillRect(0, height * 0.91, width, Math.max(2, height * 0.014));

            const logoHeight = height * 0.34;
            const requestedLogoWidth = brandLogoImage
              ? logoHeight * brandLogoImage.naturalWidth / Math.max(brandLogoImage.naturalHeight, 1)
              : height * 2.6;
            const repeatCount = Math.max(1, Math.round(width / (requestedLogoWidth + height * 0.24)));
            const logoStep = width / repeatCount;
            const logoWidth = Math.min(requestedLogoWidth, logoStep - height * 0.18);
            const logoY = (height - logoHeight) / 2;

            for (let x = -logoStep * 0.36; x < width + logoStep; x += logoStep) {
              if (brandLogoImage && brandLogoWhiteCanvas) {
                bandCtx.drawImage(brandLogoWhiteCanvas, x, logoY, logoWidth, logoHeight);
              } else {
                bandCtx.save();
                bandCtx.font = `700 ${Math.round(height * 0.29)}px Poppins, Arial, sans-serif`;
                bandCtx.textBaseline = 'middle';
                bandCtx.shadowColor = 'rgba(0,24,58,0.42)';
                bandCtx.shadowBlur = Math.max(2, height * 0.035);
                bandCtx.fillStyle = '#ffffff';
                bandCtx.fillText('MOZ Care+', x, height / 2);
                bandCtx.restore();
              }
            }

            bandCtx.fillStyle = 'rgba(255,255,255,0.78)';
            bandCtx.fillRect(0, 0, width, Math.max(2, height * 0.014));
            bandCtx.fillStyle = 'rgba(7,38,86,0.62)';
            bandCtx.fillRect(0, height - Math.max(2, height * 0.018), width, Math.max(2, height * 0.018));
            bandTexture.needsUpdate = true;
          };

          const bandTexture = new THREE.CanvasTexture(bandCanvas);
          bandTexture.wrapS = THREE.RepeatWrapping;
          bandTexture.wrapT = THREE.ClampToEdgeWrapping;
          bandTexture.minFilter = THREE.LinearMipmapLinearFilter;
          bandTexture.magFilter = THREE.LinearFilter;
          bandTexture.generateMipmaps = true;
          bandTexture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
          bandTexture.encoding = THREE.sRGBEncoding;
          drawBrandBand();

          if (this.ribbonBrandImageUrl) {
            // This optional brand image must never hold the hero loading gate.
            // The matching text fallback is already rendered synchronously.
            const image = new Image();
            image.crossOrigin = 'anonymous';
            image.onload = () => {
              if (!this.renderer) return;
              brandLogoImage = image;
              const logoCanvas = document.createElement('canvas');
              logoCanvas.width = Math.max(1, image.naturalWidth);
              logoCanvas.height = Math.max(1, image.naturalHeight);
              const logoContext = logoCanvas.getContext('2d');
              logoContext.drawImage(image, 0, 0);
              logoContext.globalCompositeOperation = 'source-in';
              logoContext.fillStyle = '#ffffff';
              logoContext.fillRect(0, 0, logoCanvas.width, logoCanvas.height);
              logoContext.globalCompositeOperation = 'source-over';
              brandLogoWhiteCanvas = logoCanvas;
              drawBrandBand();
            };
            image.src = this.ribbonBrandImageUrl;
          }

          const bandHeight = baseRadius * 0.43;
          const bandRadius = baseRadius * 1.003;
          const bandHalfAngle = Math.asin((bandHeight * 0.5) / bandRadius);
          const bandGeo = new THREE.SphereGeometry(
            bandRadius,
            72,
            8,
            0,
            TAU,
            Math.PI / 2 - bandHalfAngle,
            bandHalfAngle * 2
          );
          // The belt is printed brand artwork, not a second white clearcoat.
          // Keep its ink saturation independent of the glass environment.
          const bandMat = new THREE.MeshBasicMaterial({
            map: bandTexture,
            color: 0xffffff,
            side: THREE.FrontSide,
            toneMapped: false,
            transparent: false,
            depthWrite: true,
            depthTest: true
          });
          bandMat.onBeforeCompile = (shader) => {
            shader.vertexShader = 'varying vec3 vBandNormal;\n' + shader.vertexShader;
            shader.vertexShader = shader.vertexShader.replace('#include <begin_vertex>',
              '#include <begin_vertex>\nvBandNormal = normalize(normalMatrix * normal);');
            shader.fragmentShader = 'varying vec3 vBandNormal;\n' + shader.fragmentShader;
            shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>',
              '#include <map_fragment>\ndiffuseColor.rgb *= 0.72 + 0.28 * abs(normalize(vBandNormal).z);');
          };
          bandMat.customProgramCacheKey = () => 'moz-care-saturated-print-v1';
          bandMat.userData.mbsForceOpaque = true;
          bandMat.userData.mbsLocalRenderOrder = 2;
          const bandMesh = new THREE.Mesh(bandGeo, bandMat);
          bandMesh.userData.mbsLocalRenderOrder = 2;

          const bandSpinner = new THREE.Group();
          bandSpinner.add(bandMesh);

          const bandTilt = new THREE.Group();
          const baseTilt = THREE.MathUtils.degToRad(-27);
          const bandBaseColor = new THREE.Color('#ffffff');
          const bandHoverTint = new THREE.Color('#eefaff');
          bandTilt.rotation.set(baseTilt, baseTilt, baseTilt);
          bandTilt.add(bandSpinner);
          bandTilt.userData.surfaceGap = bandRadius / baseRadius - 1;
          bandTilt.userData.surfaceConforming = true;
          bandTilt.userData.externalHalo = false;
          group.add(bandTilt);

          let ribbonLastTime = 0;
          innerUpdate = (time, hover, pointerX, pointerY) => {
            const delta = Math.min(0.05, Math.max(0, time - ribbonLastTime));
            ribbonLastTime = time;
            bandSpinner.rotation.y = (bandSpinner.rotation.y + delta * (0.24 + hover * 0.78) * b.rotation_speed) % TAU;
            bandTilt.rotation.x = THREE.MathUtils.lerp(
              bandTilt.rotation.x,
              baseTilt + pointerY * 0.075,
              Math.min(1, delta * 5.5)
            );
            bandTilt.rotation.z = THREE.MathUtils.lerp(
              bandTilt.rotation.z,
              baseTilt - pointerX * 0.065,
              Math.min(1, delta * 5.5)
            );
            bandTilt.scale.setScalar(1);
            bandMat.color.copy(bandBaseColor).lerp(bandHoverTint, hover * 0.16);
            ribbonGlassMat.uniforms.uHover.value = hover;
            ribbonGlassMat.uniforms.uTime.value = time;
            ribbonGlassMat.uniforms.uChromaticAberration.value = THREE.MathUtils.lerp(0.011, 0.015, hover);
            ribbonGlassMat.uniforms.uEnvironmentIntensity.value = THREE.MathUtils.lerp(1.48, 1.62, hover);
            bandTilt.userData.bandRotation = bandSpinner.rotation.y;
            bandTilt.userData.bodyStrength = ribbonGlassMat.uniforms.uBodyStrength.value;
            bandTilt.userData.whiteEdgeStrength = ribbonGlassMat.uniforms.uWhiteEdgeStrength.value;
            bandTilt.userData.chromaticAberration = ribbonGlassMat.uniforms.uChromaticAberration.value;
          };
        }

        // 8. Frosted Glass Sphere
        else if (b.orb_type === 'transparent_frosted') {
          const sphereGeo = new THREE.SphereGeometry(baseRadius, 64, 64);
          const frostedMesh = new THREE.Mesh(sphereGeo, frostedGlassMat.clone());
          group.add(frostedMesh);

          const opticalGroup = new THREE.Group();
          const opticalMaterial = new THREE.MeshBasicMaterial({
            color: 0xf5f7f8,
            transparent: true,
            opacity: 0.22,
            depthWrite: false
          });
          const horizontalRing = new THREE.Mesh(
            new THREE.TorusGeometry(baseRadius * 0.5, baseRadius * 0.018, 8, 64),
            opticalMaterial
          );
          horizontalRing.rotation.x = Math.PI / 2;
          opticalGroup.add(horizontalRing);
          const diagonalRing = horizontalRing.clone();
          diagonalRing.rotation.set(Math.PI / 3, 0.4, -0.5);
          opticalGroup.add(diagonalRing);
          group.add(opticalGroup);

          innerUpdate = (time, hover) => {
            opticalGroup.rotation.y += (0.0018 + hover * 0.009) * b.rotation_speed;
            opticalGroup.scale.setScalar(0.92 + hover * 0.12);
            opticalMaterial.opacity = 0.22 + hover * 0.22;
            opticalMaterial.color.copy(uiInactiveBright).lerp(uiActiveColor, hover);
            frostedMesh.material.uniforms.hover.value = hover;
          };
        }

        // 6. Gemini violet volume. Curved, beveled Gemini glyphs occupy
        // different depths inside a dark animated nebula, while restrained
        // white dust and an asymmetric glass rim preserve the sphere volume.
        else if (b.orb_type === 'purple_energy') {
          const purpleGroup = new THREE.Group();

          const glassMaterial = new THREE.ShaderMaterial({
            uniforms: {
              uTime: { value: 0 },
              uHover: { value: 0 },
              uDepthFade: { value: 1 },
              uBackLayer: { value: 0 }
            },
            vertexShader: `
              varying vec3 vLocalPosition;
              varying vec3 vNormal;
              varying vec3 vViewPosition;

              void main() {
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                vLocalPosition = normalize(position);
                vNormal = normalize(normalMatrix * normal);
                vViewPosition = -mvPosition.xyz;
                gl_Position = projectionMatrix * mvPosition;
              }
            `,
            fragmentShader: `
              precision highp float;

              uniform float uTime;
              uniform float uHover;
              uniform float uDepthFade;
              uniform float uBackLayer;
              varying vec3 vLocalPosition;
              varying vec3 vNormal;
              varying vec3 vViewPosition;

              void main() {
                vec3 normal = normalize(vNormal);
                vec3 viewDirection = normalize(vViewPosition);
                float ndv = abs(dot(normal, viewDirection));
                float fresnel = pow(1.0 - ndv, 2.65);
                float leftRim = fresnel * smoothstep(
                  -0.08,
                  0.88,
                  -vLocalPosition.x * 0.72 - vLocalPosition.y * 0.5
                );
                float rightRim = fresnel * smoothstep(
                  0.18,
                  0.9,
                  vLocalPosition.x * 0.8 - vLocalPosition.y * 0.22
                );
                float lowerRim = fresnel * smoothstep(
                  0.28,
                  0.92,
                  -vLocalPosition.y - abs(vLocalPosition.x) * 0.22
                );
                vec3 keyDirection = normalize(vec3(-0.46, 0.78, 0.48));
                float keyGlint = pow(max(dot(normal, keyDirection), 0.0), 38.0);
                vec3 broadKeyDirection = normalize(vec3(-0.58, 0.62, 0.53));
                float broadGlassGlint = pow(max(dot(normal, broadKeyDirection), 0.0), 10.0);
                vec3 shoulderDirection = normalize(vec3(0.72, -0.26, 0.64));
                float shoulderGlint = pow(max(dot(normal, shoulderDirection), 0.0), 13.0)
                  * smoothstep(-0.7, 0.52, vLocalPosition.x - vLocalPosition.y * 0.25);
                float innerRefraction = pow(1.0 - ndv, 1.18)
                  * (0.58 + 0.42 * sin(vLocalPosition.y * 4.2 - vLocalPosition.x * 2.7 + uTime * 0.1));
                float drift = 0.5 + 0.5 * sin(
                  vLocalPosition.y * 7.0 - vLocalPosition.x * 4.0 + uTime * 0.12
                );
                // A marble has a broad refractive shoulder below its edge,
                // not a uniform outline. Keep this lighting in view space so
                // the cloud can turn without carrying a painted highlight.
                vec2 disk = normal.xy;
                float radius = length(disk);
                vec2 diskDirection = disk / max(radius, 0.001);
                // A refractive caustic lives INSIDE the glass wall. Separate
                // its soft violet shoulder from the sharper pearl reflection
                // instead of drawing a white outline at the silhouette.
                // Explicit squares avoid GLSL pow(negative, 2.0), whose result
                // is undefined on some mobile/Safari GPU implementations.
                float leftDistance = (radius - 0.88) / 0.09;
                float rightDistance = (radius - 0.95) / 0.052;
                float innerDistance = (radius - 0.82) / 0.14;
                float pearlDistance = (radius - 0.971) / 0.016;
                float leftBand = exp(-leftDistance * leftDistance);
                float rightBand = exp(-rightDistance * rightDistance);
                float edgeTaper = 1.0 - smoothstep(0.988, 1.0, radius);
                float leftShoulder = leftBand * edgeTaper * pow(max(dot(
                  diskDirection, normalize(vec2(-0.55, -0.83))
                ), 0.0), 4.2);
                float rightShoulder = rightBand * edgeTaper * pow(max(dot(
                  diskDirection, normalize(vec2(0.98, -0.12))
                ), 0.0), 7.0);
                float innerViolet = exp(-innerDistance * innerDistance)
                  * (leftShoulder * 0.5 + rightShoulder * 0.35);

                vec3 blackGlass = vec3(0.006, 0.004, 0.018);
                vec3 violetGlass = vec3(0.24, 0.025, 0.68);
                vec3 lilacRim = vec3(0.4, 0.065, 1.0);
                vec3 pearlRim = vec3(0.98, 0.9, 1.0);
                float verticalGradient = smoothstep(-0.92, 0.78, vLocalPosition.y);
                vec3 color = mix(
                  blackGlass,
                  violetGlass,
                  0.12 + drift * 0.08 + verticalGradient * 0.1
                );
                color += lilacRim * leftRim * (0.28 + uHover * 0.04);
                color += mix(lilacRim, pearlRim, 0.25) * rightRim * (0.34 + uHover * 0.05);
                color += lilacRim * lowerRim * (0.18 + uHover * 0.025);
                color += pearlRim * keyGlint * (0.38 + uHover * 0.055);
                color += mix(lilacRim, pearlRim, 0.66) * broadGlassGlint
                  * (0.2 + uHover * 0.035);
                color += mix(violetGlass, pearlRim, 0.78) * shoulderGlint
                  * (0.22 + uHover * 0.04);
                color += mix(violetGlass, pearlRim, 0.58) * innerRefraction
                  * (0.1 + uHover * 0.035);
                // Keep the residual all-around Fresnel violet, never white;
                // the readable silhouette comes from the directional lobes.
                color += lilacRim * fresnel * 0.56;
                // White only at the two off-axis reflections; the rest of the
                // circumference stays deep violet and optically clear.
                float pearlArc = pow(max(dot(diskDirection,
                  normalize(vec2(0.99, 0.02))), 0.0), 18.0)
                  * exp(-pearlDistance * pearlDistance) * edgeTaper;
                color += mix(violetGlass, pearlRim, 0.48) * leftShoulder * 1.65;
                color += mix(lilacRim, pearlRim, 0.57) * rightShoulder * 1.6;
                color += violetGlass * innerViolet * 0.8;
                color += pearlRim * pearlArc * (2.2 + uHover * 0.18);

                float alpha = 0.018
                  + fresnel * 0.22
                  + leftRim * 0.16
                  + rightRim * 0.18
                  + lowerRim * 0.08
                  + keyGlint * 0.16
                  + broadGlassGlint * 0.11
                  + shoulderGlint * 0.13
                  + innerRefraction * 0.07
                  + leftShoulder * 0.6
                  + rightShoulder * 0.65
                  + pearlArc * 0.85;
                color *= mix(1.0, 0.72, uBackLayer);
                alpha *= mix(1.0, 0.42, uBackLayer);
                alpha = clamp(alpha + uHover * 0.035, 0.0, 0.9) * uDepthFade;
                gl_FragColor = vec4(color * uDepthFade, alpha);
                #include <tonemapping_fragment>
                #include <encodings_fragment>
              }
            `,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            blending: THREE.NormalBlending,
            side: THREE.FrontSide
          });
          glassMaterial.userData.mbsLocalRenderOrder = 7;
          glassMaterial.userData.mbsPurpleGlass = true;
          const sphereGeo = new THREE.SphereGeometry(
            baseRadius * 0.99,
            this.isMobile ? 40 : 64,
            this.isMobile ? 32 : 48
          );
          const mainMesh = new THREE.Mesh(sphereGeo, glassMaterial);
          mainMesh.userData.mbsLocalRenderOrder = 7;
          purpleGroup.add(mainMesh);

          // A restrained back-face reflection gives the clear shell an inner
          // wall. Without it the opaque nebula reads as a painted purple ball
          // instead of a volume suspended inside glass.
          const backGlassMaterial = glassMaterial.clone();
          backGlassMaterial.side = THREE.BackSide;
          backGlassMaterial.uniforms.uBackLayer.value = 1;
          backGlassMaterial.userData.mbsLocalRenderOrder = 6;
          backGlassMaterial.userData.mbsPurpleGlass = true;
          const backGlassMesh = new THREE.Mesh(sphereGeo, backGlassMaterial);
          backGlassMesh.userData.mbsLocalRenderOrder = 6;
          purpleGroup.add(backGlassMesh);

          // The opaque-enough core uses layered value noise rather than two
          // sine bands, so broad dark pockets and lilac wisps drift at
          // different rates without reading as a smaller ball inside a shell.
          const volumeStepCount = this.isMobile ? 4 : 6;
          const purpleCoreMaterial = new THREE.ShaderMaterial({
            uniforms: {
              uTime: { value: 0 },
              uHover: { value: 0 },
              uDepthFade: { value: 1 }
            },
            vertexShader: `
              varying vec3 vLocalPosition;
              varying vec3 vLocalViewDirection;
              varying vec3 vNormal;
              varying vec3 vViewPosition;
              void main() {
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                vLocalPosition = normalize(position);
                vec3 V = normalize(-mvPosition.xyz);
                // Inverse rotation without GLSL inverse()/transpose(), for
                // WebGL 1. The orb has uniform scale, removed by normalize.
                vLocalViewDirection = normalize(vec3(
                  dot(modelViewMatrix[0].xyz, V),
                  dot(modelViewMatrix[1].xyz, V),
                  dot(modelViewMatrix[2].xyz, V)
                ));
                vNormal = normalize(normalMatrix * normal);
                vViewPosition = -mvPosition.xyz;
                gl_Position = projectionMatrix * mvPosition;
              }
            `,
            fragmentShader: `
              precision highp float;
              uniform float uTime;
              uniform float uHover;
              uniform float uDepthFade;
              varying vec3 vLocalPosition;
              varying vec3 vLocalViewDirection;
              varying vec3 vNormal;
              varying vec3 vViewPosition;

              float hash31(vec3 point) {
                point = fract(point * 0.1031);
                point += dot(point, point.yzx + 33.33);
                return fract((point.x + point.y) * point.z);
              }

              float noise3(vec3 point) {
                vec3 cell = floor(point);
                vec3 local = fract(point);
                local = local * local * (3.0 - 2.0 * local);
                return mix(
                  mix(
                    mix(hash31(cell + vec3(0.0, 0.0, 0.0)), hash31(cell + vec3(1.0, 0.0, 0.0)), local.x),
                    mix(hash31(cell + vec3(0.0, 1.0, 0.0)), hash31(cell + vec3(1.0, 1.0, 0.0)), local.x),
                    local.y
                  ),
                  mix(
                    mix(hash31(cell + vec3(0.0, 0.0, 1.0)), hash31(cell + vec3(1.0, 0.0, 1.0)), local.x),
                    mix(hash31(cell + vec3(0.0, 1.0, 1.0)), hash31(cell + vec3(1.0, 1.0, 1.0)), local.x),
                    local.y
                  ),
                  local.z
                );
              }

              float fbm(vec3 point) {
                float value = 0.0;
                float amplitude = 0.54;
                for (int octave = 0; octave < 3; octave++) {
                  value += amplitude * noise3(point);
                  point = point * 2.03 + vec3(7.1, 3.7, 5.4);
                  amplitude *= 0.5;
                }
                return value;
              }

              void main() {
                vec3 slowDrift = vec3(uTime * 0.045, -uTime * 0.028, uTime * 0.036);
                vec3 counterDrift = vec3(-uTime * 0.022, uTime * 0.04, -uTime * 0.03);
                vec3 entry = normalize(vLocalPosition);
                vec3 ray = normalize(vLocalViewDirection);
                float chord = max(0.0, dot(entry, ray));
                float volumeDensity = 0.0;
                float volumeWisp = 0.0;
                for (int stepIndex = 0; stepIndex < ${volumeStepCount}; stepIndex++) {
                  float rayPosition = (float(stepIndex) + 0.5) / float(${volumeStepCount});
                  // Integrate the actual camera ray through the local volume.
                  // Clouds now reveal different depths as the marble rolls.
                  vec3 samplePoint = entry - ray * (2.0 * chord * rayPosition);
                  float broadCloud = fbm(samplePoint * 2.35 + slowDrift);
                  float fineCloud = fbm(samplePoint * 4.65 + counterDrift);
                  float density = smoothstep(
                    0.48,
                    0.79,
                    broadCloud * 0.72 + fineCloud * 0.31
                  );
                  float centerWeight = 1.0 - abs(rayPosition - 0.5) * 0.48;
                  volumeDensity += density * centerWeight / float(${volumeStepCount});
                  volumeWisp += smoothstep(
                    0.69,
                    0.94,
                    fineCloud + broadCloud * 0.18
                  ) / float(${volumeStepCount});
                }
                float volumeNebula = smoothstep(0.045, 0.43, volumeDensity * 1.62);
                float brightWisp = smoothstep(0.025, 0.3, volumeWisp);
                float projectedBroad = fbm(
                  vec3(vLocalPosition.xy * 2.8, vLocalPosition.z * 1.25)
                  + slowDrift * 0.72
                );
                float projectedFine = fbm(
                  vec3(vLocalPosition.yx * 5.1, -vLocalPosition.z * 1.8)
                  + counterDrift * 0.58
                );
                float projectedCloud = smoothstep(
                  0.43,
                  0.8,
                  projectedBroad * 0.76 + projectedFine * 0.29
                );
                float nebula = pow(clamp(volumeNebula * 0.62 + projectedCloud * 0.72, 0.0, 1.0), 1.55);
                float ndv = clamp(dot(normalize(vNormal), normalize(vViewPosition)), 0.0, 1.0);
                float centerDepth = smoothstep(0.02, 0.82, ndv);
                // Keep genuinely dark pockets, but transmit violet through
                // the rest of the volume instead of stacking black absorption.
                // Clearer midtones let the existing directional shell lights
                // read as glass surrounding clouds, not an opaque dark planet.
                vec3 voidViolet = vec3(0.004, 0.002, 0.018);
                vec3 deepViolet = vec3(0.015, 0.008, 0.065);
                vec3 cloudViolet = vec3(0.12, 0.028, 0.38);
                vec3 lilacWisp = vec3(0.29, 0.15, 0.58);
                vec3 color = mix(voidViolet, deepViolet, 0.38 + centerDepth * 0.22);
                color = mix(color, cloudViolet, nebula * (0.62 + uHover * 0.015));
                color += lilacWisp * brightWisp * 0.19;
                color *= mix(0.76, 1.15, smoothstep(0.08, 0.74, projectedCloud));
                color *= mix(0.82, 1.14, pow(centerDepth, 0.72));
                // Retain enough purple absorption to resist the cyan stage,
                // while leaving the internal space legible at rest and hover.
                float alpha = (0.36 + centerDepth * 0.34 + nebula * 0.065) * uDepthFade;
                gl_FragColor = vec4(color * uDepthFade, alpha);
                #include <tonemapping_fragment>
                #include <encodings_fragment>
              }
            `,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            blending: THREE.NormalBlending,
            side: THREE.FrontSide
          });
          purpleCoreMaterial.userData.mbsLocalRenderOrder = 1;
          const purpleCore = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * 0.975, 40, 32),
            purpleCoreMaterial
          );
          purpleCore.userData.mbsLocalRenderOrder = 1;
          purpleGroup.add(purpleCore);

          const createGeminiGeometry = (radius, depth) => {
            const angularSegments = this.isMobile ? 56 : 88;
            const radialRings = this.isMobile ? 9 : 14;
            // Shopify's Gemini marks read as inflated rounded squares rotated
            // 45 degrees: four soft corners with a continuous inward waist
            // on each side. The thin vertical silhouette comes from Y rotation,
            // never from giving one mark a permanently narrow shape.
            const outlinePoints = [];
            for (let segmentIndex = 0; segmentIndex < angularSegments; segmentIndex++) {
              const angle = Math.PI / 2 - segmentIndex / angularSegments * TAU;
              const waistWave = Math.abs(Math.sin(angle * 2));
              const outlineRadius = radius * (
                1 - 0.4 * Math.pow(waistWave, 1.4)
              );
              outlinePoints.push(new THREE.Vector2(
                Math.cos(angle) * outlineRadius,
                Math.sin(angle) * outlineRadius
              ));
            }
            const vertexPositions = [];
            const indices = [];

            const addLensSurface = (surfaceSign) => {
              const centerIndex = vertexPositions.length / 3;
              vertexPositions.push(0, 0, surfaceSign * depth);
              const ringStarts = [];

              for (let ringIndex = 1; ringIndex <= radialRings; ringIndex++) {
                const radialProgress = ringIndex / radialRings;
                const ringStart = vertexPositions.length / 3;
                ringStarts.push(ringStart);
                for (let segmentIndex = 0; segmentIndex < angularSegments; segmentIndex++) {
                  const outlinePoint = outlinePoints[segmentIndex];
                  // One continuous pillow face, followed by a rounded lip.
                  // Using only normalized contour radius for the whole dome
                  // created four ridges/specular petals (a cross) in its center.
                  const distanceSquared = outlinePoint.lengthSq() * radialProgress * radialProgress / (radius * radius);
                  const dome = Math.sqrt(Math.max(0, 1 - distanceSquared * 0.8));
                  const rimBlend = 1 - Math.pow(radialProgress, 6);
                  const bulge = 0.13 + 0.87 * dome * rimBlend;
                  vertexPositions.push(
                    outlinePoint.x * radialProgress,
                    outlinePoint.y * radialProgress,
                    surfaceSign * depth * bulge
                  );
                }
              }

              const firstRingStart = ringStarts[0];
              for (let segmentIndex = 0; segmentIndex < angularSegments; segmentIndex++) {
                const nextSegment = (segmentIndex + 1) % angularSegments;
                if (surfaceSign > 0) {
                  indices.push(centerIndex, firstRingStart + segmentIndex, firstRingStart + nextSegment);
                } else {
                  indices.push(centerIndex, firstRingStart + nextSegment, firstRingStart + segmentIndex);
                }
              }

              for (let ringIndex = 1; ringIndex < radialRings; ringIndex++) {
                const innerStart = ringStarts[ringIndex - 1];
                const outerStart = ringStarts[ringIndex];
                for (let segmentIndex = 0; segmentIndex < angularSegments; segmentIndex++) {
                  const nextSegment = (segmentIndex + 1) % angularSegments;
                  const innerCurrent = innerStart + segmentIndex;
                  const innerNext = innerStart + nextSegment;
                  const outerCurrent = outerStart + segmentIndex;
                  const outerNext = outerStart + nextSegment;
                  if (surfaceSign > 0) {
                    indices.push(innerCurrent, outerCurrent, outerNext);
                    indices.push(innerCurrent, outerNext, innerNext);
                  } else {
                    indices.push(innerCurrent, outerNext, outerCurrent);
                    indices.push(innerCurrent, innerNext, outerNext);
                  }
                }
              }

              return ringStarts[ringStarts.length - 1];
            };

            const frontOuterStart = addLensSurface(1);
            const backOuterStart = addLensSurface(-1);
            // The clockwise outline reverses the two lens faces, but the
            // closing wall below already has outward winding.
            for (let index = 0; index < indices.length; index += 3) {
              const swap = indices[index + 1];
              indices[index + 1] = indices[index + 2];
              indices[index + 2] = swap;
            }
            // Close the shallow lens with a real side wall. This keeps every
            // star solid and light-reactive while it turns edge-on on hover.
            for (let segmentIndex = 0; segmentIndex < angularSegments; segmentIndex++) {
              const nextSegment = (segmentIndex + 1) % angularSegments;
              const frontCurrent = frontOuterStart + segmentIndex;
              const frontNext = frontOuterStart + nextSegment;
              const backCurrent = backOuterStart + segmentIndex;
              const backNext = backOuterStart + nextSegment;
              indices.push(frontCurrent, backNext, backCurrent);
              indices.push(frontCurrent, frontNext, backNext);
            }

            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute(
              'position',
              new THREE.Float32BufferAttribute(vertexPositions, 3)
            );
            geometry.setIndex(indices);
            geometry.computeVertexNormals();
            geometry.computeBoundingSphere();
            return geometry;
          };

          // A single off-axis softbox is shared by the three true 3D glyphs.
          // Lighting uses their transformed surface normals (not four painted
          // quadrants). The stage's several spotlights made four pale lobes,
          // flattening the center into a cross; this local BRDF preserves a
          // dark convex face and a traveling pearl reflection during yaw.
          const geminiMaterials = [0, 1, 2].map((materialIndex) => {
            const material = new THREE.ShaderMaterial({
              uniforms: {
                uHover: { value: 0 },
                uDepthFade: { value: 1 },
                uTint: { value: new THREE.Color().setRGB(0.28 + materialIndex * 0.025, 0.1 + materialIndex * 0.01, 0.52 + materialIndex * 0.03) }
              },
              vertexShader: `
                varying vec3 vNormal;
                varying vec3 vViewPosition;
                void main() {
                  vec4 mv = modelViewMatrix * vec4(position, 1.0);
                  vNormal = normalize(normalMatrix * normal);
                  vViewPosition = -mv.xyz;
                  gl_Position = projectionMatrix * mv;
                }
              `,
              fragmentShader: `
                uniform float uHover;
                uniform float uDepthFade;
                uniform vec3 uTint;
                varying vec3 vNormal;
                varying vec3 vViewPosition;
                void main() {
                  vec3 N = normalize(vNormal);
                  vec3 V = normalize(vViewPosition);
                  // Front-side studio key: the old negative Z key lit mostly
                  // the back/edge of each glyph and left its convex face flat.
                  vec3 L = normalize(vec3(-0.74, -0.46, 0.49));
                  vec3 H = normalize(V + L);
                  float nv = max(dot(N, V), 0.001);
                  float nl = max(dot(N, L), 0.0);
                  float nh = max(dot(N, H), 0.0);
                  float fresnel = pow(1.0 - nv, 2.3);
                  float softbox = pow(nh, 17.0);
                  float broadReflection = pow(nh, 4.5);
                  float oppositeFill = pow(max(dot(N, normalize(vec3(0.92, 0.24, 0.35))), 0.0), 2.0);
                  vec3 pearl = vec3(0.99, 0.87, 1.0);
                  vec3 color = uTint * (0.27 + nl * 0.82 + oppositeFill * 0.16);
                  color += pearl * broadReflection * 0.12;
                  color += pearl * softbox * (0.9 + uHover * 0.25);
                  color += mix(uTint, pearl, 0.75) * fresnel * (0.22 + nl * 0.78);
                  gl_FragColor = vec4(color, 0.98 * uDepthFade);
                  #include <tonemapping_fragment>
                  #include <encodings_fragment>
                }
              `,
              transparent: true, depthWrite: true, depthTest: true,
              side: THREE.FrontSide
            });
            material.userData.mbsLocalRenderOrder = 3;
            return material;
          });
          const geminiGroup = new THREE.Group();
          geminiGroup.position.set(0, -baseRadius * 0.055, baseRadius * 0.035);
          const stars = [];
          const geminiGlowMaterials = [];
          [
            [0.12, -0.28, 0.46, 0.3, -0.22],
            [-0.3, 0.16, 0.28, 0.23, 0.28],
            [0.17, 0.46, 0.06, 0.15, -0.28]
          ].forEach(([x, y, z, size, rotationY], starIndex) => {
            const star = new THREE.Mesh(
              createGeminiGeometry(baseRadius * size, baseRadius * size * 0.34),
              geminiMaterials[starIndex]
            );
            star.position.set(x * baseRadius, y * baseRadius, z * baseRadius);
            const uprightZ = [0.015, -0.03, 0.02][starIndex];
            star.rotation.set(-0.065 + starIndex * 0.03, rotationY, uprightZ);
            star.userData.basePosition = star.position.clone();
            star.userData.baseRotationX = star.rotation.x;
            star.userData.baseRotationY = rotationY;
            star.userData.baseRotationZ = uprightZ;
            star.userData.yawPhase = starIndex * 1.84;
            star.userData.hoverYaw = 0;
            star.userData.focus = 0;
            star.userData.mbsLocalRenderOrder = 3;
            const glowMaterial = new THREE.MeshBasicMaterial({
              color: starIndex === 1 ? 0xd5b3ff : 0xb985ff,
              transparent: true,
              opacity: 0.045,
              depthWrite: false,
              depthTest: true,
              side: THREE.BackSide,
              blending: THREE.AdditiveBlending
            });
            glowMaterial.userData.mbsBaseOpacity = 0.045;
            glowMaterial.userData.mbsLocalRenderOrder = 2;
            const glowMesh = new THREE.Mesh(star.geometry, glowMaterial);
            glowMesh.scale.setScalar(1.075);
            glowMesh.userData.mbsLocalRenderOrder = 2;
            star.add(glowMesh);
            geminiGlowMaterials.push(glowMaterial);
            geminiGroup.add(star);
            stars.push(star);
          });
          purpleGroup.add(geminiGroup);

          const dustCount = this.isMobile ? 220 : 360;
          const dustPositions = new Float32Array(dustCount * 3);
          const dustSeed = new Float32Array(dustCount);
          const dustSize = new Float32Array(dustCount);
          let dustRandomState = 0xc2b2ae35;
          const dustRandom = () => {
            dustRandomState = (Math.imul(dustRandomState, 1664525) + 1013904223) >>> 0;
            return dustRandomState / 4294967296;
          };

          for (let index = 0; index < dustCount; index++) {
            const z = dustRandom() * 2 - 1;
            const angle = dustRandom() * TAU;
            const ring = Math.sqrt(Math.max(0, 1 - z * z));
            const radial = baseRadius * (0.2 + Math.cbrt(dustRandom()) * 0.7);
            dustPositions[index * 3 + 0] = Math.cos(angle) * ring * radial;
            dustPositions[index * 3 + 1] = z * radial;
            dustPositions[index * 3 + 2] = Math.sin(angle) * ring * radial;
            dustSeed[index] = dustRandom();
            dustSize[index] = 0.42 + dustRandom() * 0.92;
          }

          const dustGeometry = new THREE.BufferGeometry();
          dustGeometry.setAttribute('position', new THREE.BufferAttribute(dustPositions, 3));
          dustGeometry.setAttribute('aSeed', new THREE.BufferAttribute(dustSeed, 1));
          dustGeometry.setAttribute('aSize', new THREE.BufferAttribute(dustSize, 1));
          dustGeometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), baseRadius * 1.05);

          const dustMaterial = new THREE.ShaderMaterial({
            uniforms: {
              uTime: { value: 0 },
              uHover: { value: 0 },
              uPointer: { value: new THREE.Vector3(0, 0, 1) },
              uPointSize: { value: baseRadius * 0.008 },
              uViewportHeight: { value: 900 },
              uDepthFade: { value: 1 }
            },
            vertexShader: `
              precision highp float;

              attribute float aSeed;
              attribute float aSize;
              uniform float uTime;
              uniform float uHover;
              uniform vec3 uPointer;
              uniform float uPointSize;
              uniform float uViewportHeight;

              varying float vOpacity;
              varying float vColorMix;

              const float TAU = 6.283185307179586;

              mat2 rotate2d(float angle) {
                float sine = sin(angle);
                float cosine = cos(angle);
                return mat2(cosine, -sine, sine, cosine);
              }

              void main() {
                vec3 dustPosition = position;
                // uTime is an integrated clock. Switching hover must not jump
                // the phase of every particle to a different point in space.
                float rotation = uTime * 0.11 * (0.72 + aSeed * 0.56);
                dustPosition.xz = rotate2d(rotation) * dustPosition.xz;
                vec3 radial = normalize(dustPosition);
                vec3 helper = abs(radial.y) > 0.86 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 1.0, 0.0);
                vec3 tangent = normalize(cross(radial, helper));
                float shimmer = sin(uTime * (0.7 + aSeed * 0.8) + aSeed * TAU);
                float bounce = sin(uTime * (1.5 + aSeed * 1.7) + aSeed * 31.0);
                dustPosition += radial * shimmer * length(position) * 0.06;
                dustPosition += tangent * bounce * length(position) * 0.055;
                float pointerInfluence = pow(max(dot(radial, normalize(uPointer)), 0.0), 7.0) * uHover;
                dustPosition += tangent * pointerInfluence * length(position) * 0.3;
                dustPosition *= 1.0 + uHover * (0.055 + aSeed * 0.055);

                vec4 mvPosition = modelViewMatrix * vec4(dustPosition, 1.0);
                float objectScale = length(modelMatrix[0].xyz);
                gl_PointSize = clamp(
                  uPointSize * aSize * objectScale * mix(1.0, 1.25, uHover)
                    * (0.5 * uViewportHeight * projectionMatrix[1][1]) / max(-mvPosition.z, 0.5),
                  1.0,
                  6.2
                );
                vOpacity = 0.38 + 0.62 * (0.5 + 0.5 * shimmer);
                vColorMix = aSeed;
                gl_Position = projectionMatrix * mvPosition;
              }
            `,
            fragmentShader: `
              precision highp float;

              uniform float uHover;
              uniform float uDepthFade;
              varying float vOpacity;
              varying float vColorMix;

              void main() {
                float distanceToCenter = length(gl_PointCoord - vec2(0.5));
                float core = 1.0 - smoothstep(0.08, 0.36, distanceToCenter);
                float glow = 1.0 - smoothstep(0.12, 0.5, distanceToCenter);
                vec3 lilac = vec3(0.74, 0.55, 1.0);
                vec3 pearl = vec3(1.0, 0.96, 1.0);
                vec3 color = mix(lilac, pearl, 0.54 + vColorMix * 0.34 + uHover * 0.06);
                float alpha = (core + glow * 0.38) * vOpacity * mix(0.62, 0.94, uHover) * uDepthFade;
                gl_FragColor = vec4(color * uDepthFade, alpha);
              }
            `,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            blending: THREE.AdditiveBlending
          });
          dustMaterial.userData.mbsLocalRenderOrder = 2;
          const stardust = new THREE.Points(dustGeometry, dustMaterial);
          stardust.frustumCulled = false;
          stardust.renderOrder = 2;
          purpleGroup.add(stardust);

          const hitMaterial = new THREE.MeshBasicMaterial({
            colorWrite: false,
            transparent: true,
            opacity: 0,
            depthWrite: false,
            depthTest: false
          });
          hitMaterial.userData.mbsBaseOpacity = 0;
          const hitMesh = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * 1.07, 16, 12),
            hitMaterial
          );
          hitMesh.userData.mbsLocalRenderOrder = 0;
          purpleGroup.add(hitMesh);

          purpleGroup.userData.stardustCount = dustCount;
          purpleGroup.userData.geminiSymbolCount = stars.length;
          purpleGroup.userData.geminiRotation = 0;
          purpleGroup.userData.nebulaLayers = 2;
          purpleGroup.userData.geminiDepthSpread = 0.465;
          purpleGroup.userData.geminiGeometry = 'inflated-rounded-square-gemini';
          purpleGroup.userData.geminiGlow = 'directional-lilac-aura';
          purpleGroup.userData.glassRimMode = 'directional-no-outline';
          purpleGroup.userData.marbleGradient = 'clear-violet-with-dark-nebula-pockets';
          purpleGroup.userData.glassCaustics = 'inner-lilac-shoulder-pearl-crescent';
          group.add(purpleGroup);

          let purpleLastTime = 0;
          let purpleAge = 0;
          let dustClock = 0;
          let nebulaYaw = 0;
          let purpleTiltX = 0;
          let purpleTiltZ = 0;
          const purpleViewTilt = new THREE.Quaternion();
          const purpleViewEuler = new THREE.Euler();
          innerUpdate = (time, hover, pointerX, pointerY) => {
            const delta = Math.min(0.05, Math.max(0, time - purpleLastTime));
            purpleLastTime = time;
            purpleAge += delta;
            dustClock += delta * (1 + hover * 0.7);
            glassMaterial.uniforms.uTime.value = time;
            glassMaterial.uniforms.uHover.value = hover;
            backGlassMaterial.uniforms.uTime.value = time;
            backGlassMaterial.uniforms.uHover.value = hover;
            purpleCoreMaterial.uniforms.uTime.value = time;
            purpleCoreMaterial.uniforms.uHover.value = hover;
            dustMaterial.uniforms.uTime.value = dustClock;
            dustMaterial.uniforms.uHover.value = hover;
            dustMaterial.uniforms.uViewportHeight.value = this.renderer.domElement.height;
            dustMaterial.uniforms.uPointer.value.set(pointerX, -pointerY, 1).normalize();

            // Integrate rotation so entering/leaving hover cannot jump the
            // volume to a different orientation after a long idle session.
            nebulaYaw = (nebulaYaw + delta * (0.026 + hover * 0.012)) % TAU;
            purpleCore.rotation.y = nebulaYaw;
            purpleCore.rotation.z = Math.sin(time * 0.115) * 0.075;

            purpleTiltX = THREE.MathUtils.lerp(
              purpleTiltX,
              pointerY * 0.045,
              Math.min(1, delta * 3.5)
            );
            purpleTiltZ = THREE.MathUtils.lerp(
              purpleTiltZ,
              -pointerX * 0.035,
              Math.min(1, delta * 3.5)
            );
            // Inherit physical drag/contact rotation. Cancelling the parent
            // quaternion made the stars/clouds act like a camera-facing decal.
            // The parent already returns to its upright rest orientation.
            purpleViewEuler.set(purpleTiltX, 0, purpleTiltZ);
            purpleViewTilt.setFromEuler(purpleViewEuler);
            purpleGroup.quaternion.copy(purpleViewTilt);
            // Keep the constellation upright. On hover every rounded solid
            // glyph turns clockwise around its own vertical axis; the group
            // never becomes a flat icon rotating in the screen plane.
            geminiGroup.rotation.x = THREE.MathUtils.lerp(
              geminiGroup.rotation.x,
              pointerY * hover * 0.018,
              Math.min(1, delta * 5.5)
            );
            geminiGroup.rotation.y = THREE.MathUtils.lerp(
              geminiGroup.rotation.y,
              pointerX * hover * 0.025,
              Math.min(1, delta * 5.5)
            );
            geminiGroup.rotation.z = THREE.MathUtils.lerp(
              geminiGroup.rotation.z,
              0,
              Math.min(1, delta * 7)
            );
            stars.forEach((star, starIndex) => {
              const basePosition = star.userData.basePosition;
              star.position.copy(basePosition);
              star.userData.focus += (hover - star.userData.focus)
                * (1 - Math.exp(-delta * (6.5 - starIndex * 0.8)));
              const focus = star.userData.focus;
              const entry = THREE.MathUtils.smoothstep(purpleAge, 0.08 + starIndex * 0.1, 0.95 + starIndex * 0.1);
              star.position.z += baseRadius * (focus * 0.055 - (1 - entry) * 0.18);
              star.position.y += baseRadius * Math.sin(time * 0.62 + starIndex * 1.7) * 0.012;
              const pulse = 1
                + Math.sin(time * 0.72 + starIndex * 1.7) * (0.012 + hover * 0.014)
                + hover * 0.018;
              star.scale.setScalar(pulse * (0.78 + entry * 0.22));
              star.rotation.x = star.userData.baseRotationX;
              star.rotation.z = star.userData.baseRotationZ;
              // Continuous phase, including hover-out: multiplying accumulated
              // yaw by hover previously unwound the glyph back to its start.
              const spinSpeed = 0.9 + starIndex * 0.08 + focus * 2.1;
              star.userData.spinSpeed = spinSpeed;
              star.userData.hoverYaw = (star.userData.hoverYaw
                + delta * spinSpeed) % TAU;
              star.rotation.y = star.userData.baseRotationY
                - star.userData.hoverYaw;
              const starMaterial = geminiMaterials[starIndex];
              starMaterial.uniforms.uHover.value = hover;
              // Light is strongest as the convex face turns toward the key,
              // never a uniformly glowing silhouette independent of rotation.
              const lightFacing = Math.pow(Math.max(0, Math.sin(star.rotation.y - 0.5)), 2);
              geminiGlowMaterials[starIndex].opacity = 0.02
                + lightFacing * (0.055 + hover * 0.07);
            });
            purpleGroup.userData.geminiRotation = stars.map((star) => star.rotation.y);
            purpleGroup.userData.geminiSpinSpeeds = stars.map((star) => star.userData.spinSpeed);
            purpleGroup.userData.geminiMotion = 'continuous-local-yaw-with-body-roll';
            purpleGroup.userData.stardustActivity = 1 + hover * 0.58;
            purpleGroup.userData.glassEdgeStrength = 1.16 + hover * 0.09;
          };
        }

        // 7. Reactive starburst. The reference object is a dense sphere of
        // camera-facing filament ribbons, not a solid core with coarse cones.
        // A single instanced draw call keeps hundreds of independently moving
        // strands practical inside the Shopify hero.
        else if (b.orb_type === 'starburst' || b.orb_type === 'energy_starburst') {
          const starburstGroup = new THREE.Group();
          const filamentSegments = this.isMobile ? 8 : 12;
          const filamentCount = this.isMobile ? 224 : 384;
          const verticesPerFilament = (filamentSegments + 1) * 2;
          const basePositions = new Float32Array(verticesPerFilament * 3);
          const baseUvs = new Float32Array(verticesPerFilament * 2);
          const baseIndices = new Uint16Array(filamentSegments * 6);

          for (let segment = 0; segment <= filamentSegments; segment++) {
            const t = segment / filamentSegments;
            const vertexOffset = segment * 2;

            basePositions[(vertexOffset + 0) * 3 + 0] = -1;
            basePositions[(vertexOffset + 0) * 3 + 1] = t;
            basePositions[(vertexOffset + 1) * 3 + 0] = 1;
            basePositions[(vertexOffset + 1) * 3 + 1] = t;

            baseUvs[(vertexOffset + 0) * 2 + 0] = 0;
            baseUvs[(vertexOffset + 0) * 2 + 1] = t;
            baseUvs[(vertexOffset + 1) * 2 + 0] = 1;
            baseUvs[(vertexOffset + 1) * 2 + 1] = t;

            if (segment < filamentSegments) {
              const indexOffset = segment * 6;
              baseIndices[indexOffset + 0] = vertexOffset + 0;
              baseIndices[indexOffset + 1] = vertexOffset + 1;
              baseIndices[indexOffset + 2] = vertexOffset + 2;
              baseIndices[indexOffset + 3] = vertexOffset + 2;
              baseIndices[indexOffset + 4] = vertexOffset + 1;
              baseIndices[indexOffset + 5] = vertexOffset + 3;
            }
          }

          const filamentGeometry = new THREE.InstancedBufferGeometry();
          filamentGeometry.setAttribute('position', new THREE.BufferAttribute(basePositions, 3));
          filamentGeometry.setAttribute('uv', new THREE.BufferAttribute(baseUvs, 2));
          filamentGeometry.setIndex(new THREE.BufferAttribute(baseIndices, 1));

          const midPoint1 = new Float32Array(filamentCount * 3);
          const midPoint2 = new Float32Array(filamentCount * 3);
          const endPoint = new Float32Array(filamentCount * 3);
          const filamentSeed = new Float32Array(filamentCount);
          const filamentShade = new Float32Array(filamentCount);
          const filamentWidth = new Float32Array(filamentCount);
          const goldenAngle = Math.PI * (3 - Math.sqrt(5));
          let randomState = 0x6d2b79f5;
          const seededRandom = () => {
            randomState = (Math.imul(randomState, 1664525) + 1013904223) >>> 0;
            return randomState / 4294967296;
          };
          const direction = new THREE.Vector3();
          const tangentA = new THREE.Vector3();
          const tangentB = new THREE.Vector3();
          const helperAxis = new THREE.Vector3();

          for (let index = 0; index < filamentCount; index++) {
            const y = 1 - (index / Math.max(1, filamentCount - 1)) * 2;
            const ringRadius = Math.sqrt(Math.max(0, 1 - y * y));
            const angle = index * goldenAngle + (seededRandom() - 0.5) * 0.14;
            direction.set(
              Math.cos(angle) * ringRadius,
              y,
              Math.sin(angle) * ringRadius
            ).normalize();

            helperAxis.set(0, Math.abs(direction.y) > 0.88 ? 0 : 1, Math.abs(direction.y) > 0.88 ? 1 : 0);
            tangentA.crossVectors(direction, helperAxis).normalize();
            tangentB.crossVectors(direction, tangentA).normalize();

            const lengthSeed = seededRandom();
            const length = baseRadius * (0.76 + lengthSeed * 0.28);
            const bendStrength = length * (0.006 + seededRandom() * 0.018);
            const bendAngle = seededRandom() * TAU;
            const bend = tangentA.clone().multiplyScalar(Math.cos(bendAngle) * bendStrength)
              .add(tangentB.clone().multiplyScalar(Math.sin(bendAngle) * bendStrength));
            const first = direction.clone().multiplyScalar(length * 0.33).addScaledVector(bend, 0.78);
            const second = direction.clone().multiplyScalar(length * 0.67).addScaledVector(bend, -0.44);
            const end = direction.clone().multiplyScalar(length);

            first.toArray(midPoint1, index * 3);
            second.toArray(midPoint2, index * 3);
            end.toArray(endPoint, index * 3);
            filamentSeed[index] = seededRandom();
            filamentShade[index] = seededRandom();
            filamentWidth[index] = 0.86 + seededRandom() * 0.64;
          }

          filamentGeometry.setAttribute('aMidPoint1', new THREE.InstancedBufferAttribute(midPoint1, 3));
          filamentGeometry.setAttribute('aMidPoint2', new THREE.InstancedBufferAttribute(midPoint2, 3));
          filamentGeometry.setAttribute('aEndPoint', new THREE.InstancedBufferAttribute(endPoint, 3));
          filamentGeometry.setAttribute('aSeed', new THREE.InstancedBufferAttribute(filamentSeed, 1));
          filamentGeometry.setAttribute('aShade', new THREE.InstancedBufferAttribute(filamentShade, 1));
          filamentGeometry.setAttribute('aWidth', new THREE.InstancedBufferAttribute(filamentWidth, 1));
          filamentGeometry.instanceCount = filamentCount;
          filamentGeometry.computeBoundingSphere();
          filamentGeometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), baseRadius * 1.48);

          const filamentMaterial = new THREE.ShaderMaterial({
            uniforms: {
              uTime: { value: 0 },
              uHover: { value: 0 },
              uPointer: { value: new THREE.Vector3(0, 0, baseRadius) },
              uThickness: { value: baseRadius * (this.isMobile ? 0.0064 : 0.0058) },
              uSway: { value: baseRadius * (this.isMobile ? 0.022 : 0.028) },
              uDepthFade: { value: 1 },
              uAccentColor: { value: new THREE.Color('#f7f8f9') }
            },
            vertexShader: `
              precision highp float;

              attribute vec3 aMidPoint1;
              attribute vec3 aMidPoint2;
              attribute vec3 aEndPoint;
              attribute float aSeed;
              attribute float aShade;
              attribute float aWidth;

              uniform float uTime;
              uniform float uHover;
              uniform vec3 uPointer;
              uniform float uThickness;
              uniform float uSway;

              varying float vT;
              varying float vFacing;
              varying float vShade;
              varying float vSeed;
              varying float vHoverInfluence;
              varying float vFilamentSide;

              const float PI = 3.141592653589793;
              const float TAU = 6.283185307179586;

              vec3 cubicBezier(vec3 p0, vec3 p1, vec3 p2, vec3 p3, float t) {
                float inverseT = 1.0 - t;
                return inverseT * inverseT * inverseT * p0
                  + 3.0 * inverseT * inverseT * t * p1
                  + 3.0 * inverseT * t * t * p2
                  + t * t * t * p3;
              }

              vec3 cubicBezierTangent(vec3 p0, vec3 p1, vec3 p2, vec3 p3, float t) {
                float inverseT = 1.0 - t;
                return 3.0 * inverseT * inverseT * (p1 - p0)
                  + 6.0 * inverseT * t * (p2 - p1)
                  + 3.0 * t * t * (p3 - p2);
              }

              void main() {
                float t = uv.y;
                float side = uv.x * 2.0 - 1.0;
                vec3 startPoint = vec3(0.0);
                vec3 filamentDirection = normalize(aEndPoint);
                vec3 helperAxis = abs(filamentDirection.y) > 0.88
                  ? vec3(0.0, 0.0, 1.0)
                  : vec3(0.0, 1.0, 0.0);
                vec3 tangentA = normalize(cross(filamentDirection, helperAxis));
                vec3 tangentB = normalize(cross(filamentDirection, tangentA));
                float phase = uTime * 0.42 + aSeed * TAU;
                float filamentLength = length(aEndPoint);
                vec3 sway = (
                  tangentA * sin(phase) + tangentB * cos(phase * 0.83 + aSeed * 2.7)
                ) * uSway * (0.42 + aSeed * 0.58);

                vec3 point1 = aMidPoint1 + sway * 0.72;
                vec3 point2 = aMidPoint2 - sway * 0.48;
                vec3 point3 = (aEndPoint + sway * 0.16)
                  * (1.0 + uHover * (0.045 + aSeed * 0.035));

                vec3 pointerDirection = normalize(uPointer);
                float cursorProximity = pow(max(dot(filamentDirection, pointerDirection), 0.0), 4.5);
                float hoverInfluence = uHover * cursorProximity;
                vec3 cursorTarget = pointerDirection * filamentLength * (1.06 + uHover * 0.18);
                point1 = mix(point1, cursorTarget * 0.34, hoverInfluence * 0.42);
                point2 = mix(point2, cursorTarget * 0.7, hoverInfluence * 0.68);
                point3 = mix(point3, cursorTarget, hoverInfluence * 0.92);

                vec3 pointOnCurve = cubicBezier(startPoint, point1, point2, point3, t);
                vec3 tangentOnCurve = normalize(cubicBezierTangent(startPoint, point1, point2, point3, t));
                vec4 pointInView = modelViewMatrix * vec4(pointOnCurve, 1.0);
                vec3 tangentInView = normalize(mat3(modelViewMatrix) * tangentOnCurve);
                vec3 lookDirection = normalize(pointInView.xyz);
                vec3 ribbonOffsetRaw = cross(lookDirection, tangentInView);
                vec3 ribbonOffset = ribbonOffsetRaw / max(length(ribbonOffsetRaw), 0.0001);
                float objectScale = length(modelMatrix[0].xyz);
                float centerTaper = mix(0.64, 1.0, smoothstep(0.025, 0.22, t));
                // Preserve a rod-like, softly capped tip. A strong taper made
                // the burst read as sharp needles instead of layered rods.
                float tipTaper = 1.0 - smoothstep(0.84, 1.0, t) * 0.16;
                float strandWidth = uThickness * aWidth * objectScale * centerTaper * tipTaper
                  * (1.0 + uHover * 0.2 + hoverInfluence * 0.3);
                vec3 adjustedPosition = pointInView.xyz + ribbonOffset * side * strandWidth;

                vec3 directionInView = normalize(mat3(modelViewMatrix) * filamentDirection);
                vT = t;
                vFacing = clamp(directionInView.z * 0.5 + 0.5, 0.0, 1.0);
                vShade = aShade;
                vSeed = aSeed;
                vHoverInfluence = hoverInfluence;
                vFilamentSide = side;
                gl_Position = projectionMatrix * vec4(adjustedPosition, 1.0);
              }
            `,
            fragmentShader: `
              precision highp float;

              uniform float uTime;
              uniform float uHover;
              uniform float uDepthFade;
              uniform vec3 uAccentColor;

              varying float vT;
              varying float vFacing;
              varying float vShade;
              varying float vSeed;
              varying float vHoverInfluence;
              varying float vFilamentSide;

              void main() {
                float startIntensity = mix(0.12, 0.5, vShade);
                float endIntensity = mix(0.68, 0.98, fract(vShade * 2.731 + 0.17));
                float filamentIntensity = mix(startIntensity, endIntensity, pow(vT, 0.78));
                float pulsePosition = mod(uTime * 0.28 + vSeed * 0.34, 1.38);
                float pulse = smoothstep(0.085, 0.0, abs(vT - pulsePosition))
                  * (1.0 - step(1.0, pulsePosition));
                vec3 baseColor = vec3(filamentIntensity);
                float accentMix = uHover * (0.045 + vHoverInfluence * 0.62);
                vec3 color = mix(baseColor, uAccentColor, accentMix);
                color += vec3(1.0) * pulse * 0.38;
                // One bright flank and a graphite flank read as a metal rod,
                // rather than a uniformly lit flat ribbon or fuzzy spike.
                float roundProfile = sqrt(max(0.0, 1.0 - vFilamentSide * vFilamentSide));
                float sideGlint = pow(max(0.0, roundProfile * 0.82
                  + vFilamentSide * 0.57), 8.0);
                color *= 0.63 + roundProfile * 0.32 + sideGlint * 0.38;

                float centerFade = smoothstep(0.012, 0.18, vT);
                float tipSoftening = 1.0 - smoothstep(0.96, 1.0, vT) * 0.18;
                float depthAlpha = mix(0.16, 1.0, smoothstep(0.08, 0.92, vFacing));
                float alpha = centerFade * tipSoftening * depthAlpha * mix(0.64, 0.98, vShade);
                alpha *= uDepthFade;
                gl_FragColor = vec4(color * uDepthFade, alpha);
              }
            `,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            side: THREE.DoubleSide,
            blending: THREE.NormalBlending
          });
          filamentMaterial.userData.mbsStarburstTentacles = true;
          filamentMaterial.userData.mbsLocalRenderOrder = 2;

          const filamentMesh = new THREE.Mesh(filamentGeometry, filamentMaterial);
          filamentMesh.frustumCulled = false;
          filamentMesh.userData.mbsLocalRenderOrder = 2;
          starburstGroup.add(filamentMesh);

          const coreMaterial = new THREE.MeshPhysicalMaterial({
            color: 0x111318,
            metalness: 0.72,
            roughness: 0.2,
            clearcoat: 1,
            clearcoatRoughness: 0.12,
            transparent: true,
            opacity: 0.5,
            depthWrite: false
          });
          coreMaterial.userData.mbsBaseOpacity = 0.5;
          const coreMesh = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * 0.105, 20, 16),
            coreMaterial
          );
          coreMesh.userData.mbsLocalRenderOrder = 1;
          starburstGroup.add(coreMesh);

          const hitMaterial = new THREE.MeshBasicMaterial({
            colorWrite: false,
            transparent: true,
            opacity: 0,
            depthWrite: false,
            depthTest: false
          });
          hitMaterial.userData.mbsBaseOpacity = 0;
          const hitMesh = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * 1.18, 16, 12),
            hitMaterial
          );
          hitMesh.userData.mbsLocalRenderOrder = 0;
          starburstGroup.add(hitMesh);

          starburstGroup.userData.filamentCount = filamentCount;
          starburstGroup.userData.filamentSegments = filamentSegments;
          group.add(starburstGroup);

          let starburstLastTime = 0;
          const starburstInverseRotation = new THREE.Quaternion();
          innerUpdate = (time, hover, pointerX, pointerY, localHit) => {
            const delta = Math.min(0.05, Math.max(0, time - starburstLastTime));
            starburstLastTime = time;
            const idleRotation = 1 - hover * 0.88;
            starburstGroup.rotation.y += delta * 0.16 * b.rotation_speed * idleRotation;
            starburstGroup.rotation.z += delta * 0.055 * b.rotation_speed * idleRotation;
            starburstGroup.rotation.x = THREE.MathUtils.lerp(
              starburstGroup.rotation.x,
              pointerY * 0.06,
              Math.min(1, delta * 3.2)
            );
            filamentMaterial.uniforms.uTime.value = time;
            filamentMaterial.uniforms.uHover.value = hover;
            if (localHit) {
              // The shared hit is bubble-local; this inner group also spins.
              // Undo that rotation so the attracted strands sit under the ray.
              starburstInverseRotation.copy(starburstGroup.quaternion).invert();
              filamentMaterial.uniforms.uPointer.value.copy(localHit)
                .applyQuaternion(starburstInverseRotation)
                .normalize().multiplyScalar(baseRadius);
            }
            coreMaterial.color.copy(uiInactiveDark).lerp(uiInactiveColor, hover * 0.36);
          };
        }

        // 10. Structured particle grid. A height-ordered point attribute drives
        // a focus wave from the north pole to the south pole while the grid and
        // point layers share the same spherical surface.
        else if (b.orb_type === 'particle_grid') {
          const gridGroup = new THREE.Group();
          const sphereRadius = baseRadius * 0.985;
          const longitudeCount = 24;
          const latitudeCount = 11;
          const linePositions = [];
          const lineTangents = [];
          const lineSides = [];
          const lineIndices = [];
          const pointPositions = [];
          const pointSweepPhase = [];
          const pointSeed = [];
          const pointSize = [];
          let gridRandomState = 0x4e6f7274;
          const gridRandom = () => {
            gridRandomState = (Math.imul(gridRandomState, 1664525) + 1013904223) >>> 0;
            return gridRandomState / 4294967296;
          };
          const sphericalPoint = (theta, phi) => new THREE.Vector3(
            Math.sin(theta) * Math.cos(phi) * sphereRadius,
            Math.cos(theta) * sphereRadius,
            Math.sin(theta) * Math.sin(phi) * sphereRadius
          );

          // One continuous strip per arc, with shared vertices and analytic
          // tangents. Unlike per-cell quads, joints cannot leave square caps or
          // overlap bright spots as the sphere rotates. Four samples per grid
          // cell keep the wires on the sphere rather than cutting chord corners.
          const pushGridArc = (steps, sample) => {
            const firstVertex = lineSides.length;
            for (let step = 0; step <= steps; step++) {
              const { point, tangent } = sample(step / steps);
              for (const side of [-1, 1]) {
                linePositions.push(point.x, point.y, point.z);
                lineTangents.push(tangent.x, tangent.y, tangent.z);
                lineSides.push(side);
              }
              if (step < steps) {
                const vertex = firstVertex + step * 2;
                lineIndices.push(vertex, vertex + 1, vertex + 2, vertex + 1, vertex + 3, vertex + 2);
              }
            }
          };
          for (let longitude = 0; longitude < longitudeCount; longitude++) {
            const phi = longitude / longitudeCount * TAU;
            pushGridArc(latitudeCount * 4, (progress) => {
              const theta = progress * Math.PI;
              return {
                point: sphericalPoint(theta, phi),
                tangent: new THREE.Vector3(
                  Math.cos(theta) * Math.cos(phi), -Math.sin(theta), Math.cos(theta) * Math.sin(phi)
                )
              };
            });
          }
          for (let latitude = 1; latitude < latitudeCount; latitude++) {
            const theta = latitude / latitudeCount * Math.PI;
            pushGridArc(longitudeCount * 4, (progress) => {
              const phi = progress * TAU;
              return {
                point: sphericalPoint(theta, phi),
                tangent: new THREE.Vector3(-Math.sin(phi), 0, Math.cos(phi))
              };
            });
          }

          const pushGridPoint = (theta, phi, sweepPhase) => {
            const point = sphericalPoint(theta, phi);
            pointPositions.push(point.x, point.y, point.z);
            pointSweepPhase.push(sweepPhase);
            pointSeed.push(gridRandom());
            pointSize.push(0.76 + gridRandom() * 0.58);
          };

          pushGridPoint(0, 0, 0);
          for (let latitude = 1; latitude < latitudeCount; latitude++) {
            const theta = latitude / latitudeCount * Math.PI;
            for (let longitude = 0; longitude < longitudeCount; longitude++) {
              pushGridPoint(theta, longitude / longitudeCount * TAU, latitude / latitudeCount);
            }
          }
          pushGridPoint(Math.PI, 0, 1);

          const lineGeometry = new THREE.BufferGeometry();
          lineGeometry.setAttribute(
            'position',
            new THREE.Float32BufferAttribute(linePositions, 3)
          );
          lineGeometry.setAttribute('aTangent', new THREE.Float32BufferAttribute(lineTangents, 3));
          lineGeometry.setAttribute('aSide', new THREE.Float32BufferAttribute(lineSides, 1));
          lineGeometry.setIndex(lineIndices);
          lineGeometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), baseRadius * 1.08);

          const lineMaterial = new THREE.ShaderMaterial({
            uniforms: {
              uViewport: { value: new THREE.Vector2(1, 1) },
              uPixelRatio: { value: 1 },
              uLineWidth: { value: baseRadius * 0.009 },
              uDepthFade: { value: 1 }
            },
            vertexShader: `
              precision highp float;
              attribute vec3 aTangent;
              attribute float aSide;
              uniform vec2 uViewport;
              uniform float uPixelRatio;
              uniform float uLineWidth;
              varying float vFacing;
              varying float vSide;
              varying float vHalfWidth;
              void main() {
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                vec3 radialInView = normalize(mat3(modelViewMatrix) * normalize(position));
                vFacing = clamp(radialInView.z * 0.5 + 0.5, 0.0, 1.0);
                vec4 clipPosition = projectionMatrix * mvPosition;
                vec4 clipTangent = projectionMatrix * vec4(mat3(modelViewMatrix) * aTangent, 0.0);
                vec2 screenTangent = (clipTangent.xy * clipPosition.w - clipPosition.xy * clipTangent.w) * uViewport;
                vec2 normal = vec2(-screenTangent.y, screenTangent.x) / max(length(screenTangent), 0.0001);
                float objectScale = length(modelMatrix[0].xyz);
                float projectedWidth = uLineWidth * objectScale * 0.5 * uViewport.y
                  * projectionMatrix[1][1] / max(-mvPosition.z, 0.5);
                float width = clamp(projectedWidth, 0.72 * uPixelRatio, 2.4 * uPixelRatio);
                vHalfWidth = width * 0.5;
                vSide = aSide;
                // One physical pixel of coverage padding gives a smooth edge
                // without an extra blur pass or platform-dependent lineWidth.
                clipPosition.xy += normal * aSide * (width + 1.0) / uViewport * clipPosition.w;
                gl_Position = clipPosition;
              }
            `,
            fragmentShader: `
              precision highp float;
              uniform float uDepthFade;
              varying float vFacing;
              varying float vSide;
              varying float vHalfWidth;
              void main() {
                float edgeDistance = abs(vSide) * (vHalfWidth + 0.5);
                float coverage = 1.0 - smoothstep(max(0.0, vHalfWidth - 0.5), vHalfWidth + 0.5, edgeDistance);
                float depthAlpha = mix(0.075, 0.54, smoothstep(0.08, 0.9, vFacing));
                float alpha = coverage * depthAlpha * uDepthFade;
                gl_FragColor = vec4(vec3(0.90) * uDepthFade, alpha);
              }
            `,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            side: THREE.DoubleSide,
            blending: THREE.NormalBlending
          });
          lineMaterial.userData.mbsLocalRenderOrder = 1;
          const gridLines = new THREE.Mesh(lineGeometry, lineMaterial);
          gridLines.frustumCulled = false;
          gridLines.userData.mbsLocalRenderOrder = 1;
          gridGroup.add(gridLines);

          const pointGeometry = new THREE.BufferGeometry();
          pointGeometry.setAttribute(
            'position',
            new THREE.Float32BufferAttribute(pointPositions, 3)
          );
          pointGeometry.setAttribute(
            'aSweepPhase',
            new THREE.Float32BufferAttribute(pointSweepPhase, 1)
          );
          pointGeometry.setAttribute(
            'aSeed',
            new THREE.Float32BufferAttribute(pointSeed, 1)
          );
          pointGeometry.setAttribute(
            'aSize',
            new THREE.Float32BufferAttribute(pointSize, 1)
          );
          pointGeometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), baseRadius * 1.12);

          const pointMaterial = new THREE.ShaderMaterial({
            uniforms: {
              uTime: { value: 0 },
              uHover: { value: 0 },
              uSweep: { value: 0 },
              uPointSize: { value: baseRadius * 0.015 },
              uMaxPointSize: { value: this.isMobile ? 12 : 16 },
              uViewportHeight: { value: 900 },
              uPixelRatio: { value: 1 },
              uDepthFade: { value: 1 }
            },
            vertexShader: `
              precision highp float;

              attribute float aSweepPhase;
              attribute float aSeed;
              attribute float aSize;

              uniform float uTime;
              uniform float uHover;
              uniform float uSweep;
              uniform float uPointSize;
              uniform float uMaxPointSize;
              uniform float uViewportHeight;
              uniform float uPixelRatio;

              varying float vOpacity;
              varying float vFacing;
              varying float vWave;

              void main() {
                float waveDistance = abs(aSweepPhase - uSweep);
                float primaryWave = exp(-pow(waveDistance / 0.145, 2.0));
                float trailingWave = exp(-pow((aSweepPhase - (uSweep - 0.18)) / 0.28, 2.0)) * 0.58;
                float wave = clamp(primaryWave + trailingWave, 0.0, 1.0) * uHover;
                vec3 radial = normalize(position);
                float microPulse = sin(uTime * 1.4 + aSeed * 6.28318530718) * 0.035 * uHover;
                vec3 animatedPosition = position * (1.0 + wave * 0.052 + microPulse);
                vec4 mvPosition = modelViewMatrix * vec4(animatedPosition, 1.0);
                float objectScale = length(modelMatrix[0].xyz);
                float pointExpansion = 1.0 + wave * 4.0;
                gl_PointSize = clamp(
                  uPointSize * aSize * objectScale * pointExpansion
                    * (0.5 * uViewportHeight * projectionMatrix[1][1]) / max(-mvPosition.z, 0.5),
                  1.75 * uPixelRatio,
                  uMaxPointSize * uPixelRatio
                );
                vec3 radialInView = normalize(mat3(modelViewMatrix) * radial);
                vFacing = clamp(radialInView.z * 0.5 + 0.5, 0.0, 1.0);
                vOpacity = 0.96 + wave * 0.04;
                vWave = wave;
                gl_Position = projectionMatrix * mvPosition;
              }
            `,
            fragmentShader: `
              precision highp float;

              uniform float uHover;
              uniform float uDepthFade;
              varying float vOpacity;
              varying float vFacing;
              varying float vWave;

              void main() {
                float distanceToCenter = length(gl_PointCoord - vec2(0.5));
                float disc = 1.0 - smoothstep(0.32, 0.5, distanceToCenter);
                float halo = 1.0 - smoothstep(0.1, 0.5, distanceToCenter);
                float depthAlpha = mix(0.22, 1.0, smoothstep(0.08, 0.92, vFacing));
                float interactionGlow = vWave * 0.68;
                float alpha = (disc + halo * interactionGlow) * depthAlpha * vOpacity
                  * uDepthFade;
                gl_FragColor = vec4(vec3(uDepthFade), alpha);
              }
            `,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            blending: THREE.AdditiveBlending
          });
          pointMaterial.userData.mbsLocalRenderOrder = 2;
          const gridPoints = new THREE.Points(pointGeometry, pointMaterial);
          gridPoints.frustumCulled = false;
          gridPoints.renderOrder = 2;
          gridGroup.add(gridPoints);

          const hitMaterial = new THREE.MeshBasicMaterial({
            colorWrite: false,
            transparent: true,
            opacity: 0,
            depthWrite: false,
            depthTest: false
          });
          hitMaterial.userData.mbsBaseOpacity = 0;
          const hitMesh = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * 1.06, 16, 12),
            hitMaterial
          );
          hitMesh.userData.mbsLocalRenderOrder = 0;
          gridGroup.add(hitMesh);

          gridGroup.rotation.set(Math.PI / 4, 0, Math.PI / 4);
          gridGroup.userData.pointCount = pointSweepPhase.length;
          gridGroup.userData.longitudeCount = longitudeCount;
          gridGroup.userData.latitudeCount = latitudeCount;
          gridGroup.userData.lineTreatment = 'continuous-antialiased-surface-ribbons';
          gridGroup.userData.lineTriangleCount = lineIndices.length / 3;
          gridGroup.userData.sweepProgress = 0;
          group.add(gridGroup);

          let gridLastTime = 0;
          let gridSweepClock = 0;
          let gridIdleSweepClock = 0;
          let gridWasFocused = false;
          innerUpdate = (time, hover) => {
            const delta = Math.min(0.05, Math.max(0, time - gridLastTime));
            gridLastTime = time;
            const isFocused = hover > 0.12;
            if (isFocused && !gridWasFocused) gridSweepClock = 0;
            gridWasFocused = isFocused;
            gridIdleSweepClock += delta * 0.11;
            if (hover > 0.01) gridSweepClock += delta * 0.46;
            else gridSweepClock = 0;
            const sweepProgress = isFocused
              ? gridSweepClock % 1.35
              : gridIdleSweepClock % 1.35;
            const idleHint = hover <= 0.01
              ? 0.18 + Math.sin(time * 0.9) * 0.03
              : 0;
            const visualFocus = Math.max(hover, idleHint);

            gridGroup.rotation.y = (gridGroup.rotation.y + delta * 0.25 * b.rotation_speed) % TAU;
            const canvas = this.renderer.domElement;
            const pixelRatio = this.renderer.getPixelRatio();
            lineMaterial.uniforms.uViewport.value.set(Math.max(1, canvas.width), Math.max(1, canvas.height));
            lineMaterial.uniforms.uPixelRatio.value = pixelRatio;
            pointMaterial.uniforms.uViewportHeight.value = canvas.height;
            pointMaterial.uniforms.uPixelRatio.value = pixelRatio;
            pointMaterial.uniforms.uTime.value = time;
            pointMaterial.uniforms.uHover.value = visualFocus;
            pointMaterial.uniforms.uSweep.value = sweepProgress;
            gridGroup.userData.sweepProgress = sweepProgress;
            gridGroup.userData.focus = hover;
            gridGroup.userData.maxPointScale = 1 + visualFocus * 4.0;
            gridGroup.userData.idleHint = idleHint > 0;
            gridGroup.userData.pointerFocus = false;
            gridGroup.userData.sweepDirection = 'top-to-bottom';
          };
        }

        // 9. Organic point field. Displacement runs entirely on the GPU so the
        // dense dotted surface can breathe and pull toward the pointer smoothly.
        else if (b.orb_type === 'organic_blob') {
          const organicLatitudeBands = this.isMobile ? 52 : 76;
          const organicAngularStep = Math.PI / organicLatitudeBands;
          const organicRadius = baseRadius * 0.98;
          const organicPositions = [0, organicRadius, 0];
          const organicNormals = [0, 1, 0];
          // Keep arc-length spacing comparable at every latitude. A UV sphere
          // repeats its pole/seam vertices and packs the polar dots together.
          for (let ring = 1; ring < organicLatitudeBands; ring++) {
            const latitude = ring * organicAngularStep;
            const radial = Math.sin(latitude);
            const y = Math.cos(latitude);
            const pointsInRing = Math.max(6, Math.round(TAU * radial / organicAngularStep));
            const ringOffset = (ring % 2) * 0.5;
            for (let point = 0; point < pointsInRing; point++) {
              const longitude = (point + ringOffset) * TAU / pointsInRing;
              const x = Math.cos(longitude) * radial;
              const z = Math.sin(longitude) * radial;
              organicPositions.push(x * organicRadius, y * organicRadius, z * organicRadius);
              organicNormals.push(x, y, z);
            }
          }
          organicPositions.push(0, -organicRadius, 0);
          organicNormals.push(0, -1, 0);
          const morphGeo = new THREE.BufferGeometry();
          morphGeo.setAttribute('position', new THREE.Float32BufferAttribute(organicPositions, 3));
          morphGeo.setAttribute('normal', new THREE.Float32BufferAttribute(organicNormals, 3));
          morphGeo.computeBoundingSphere();
          const pointMaterial = new THREE.ShaderMaterial({
            uniforms: {
              uTime: { value: 0 },
              uHover: { value: 0 },
              uPointer: { value: new THREE.Vector3(0, 0, 1) },
              uPointSize: { value: baseRadius * 0.018 },
              uViewportHeight: { value: 900 },
              uDepthFade: { value: 1 }
            },
            vertexShader: `
              precision highp float;

              uniform float uTime;
              uniform float uHover;
              uniform vec3 uPointer;
              uniform float uPointSize;
              uniform float uViewportHeight;

              varying float vFacing;
              // Independent rotating-gradient simplex field. Each lattice
              // gradient evolves continuously, so folds travel and reform even
              // without input; this is not a static lobe shape being rotated.
              vec3 organicGradient(vec3 cell, float time) {
                vec3 seed = fract(sin(vec3(
                  dot(cell, vec3(127.1, 311.7, 74.7)),
                  dot(cell, vec3(269.5, 183.3, 246.1)),
                  dot(cell, vec3(113.5, 271.9, 124.6))
                )) * 43758.5453);
                vec3 axisA = normalize(seed * 2.0 - 1.0 + vec3(0.001));
                vec3 helper = abs(axisA.y) < 0.85
                  ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
                vec3 axisB = normalize(cross(axisA, helper));
                float phase = time * (1.25 + seed.z * 0.35);
                return axisA * cos(phase) + axisB * sin(phase);
              }

              float organicCorner(vec3 cell, vec3 offset, float time) {
                // Compact support must end before a tetrahedron drops a
                // corner; 0.6 leaves a small jump at simplex boundaries.
                float weight = max(0.5 - dot(offset, offset), 0.0);
                return weight * weight * weight * dot(organicGradient(cell, time), offset);
              }

              float organicNoise(vec3 samplePosition, float time) {
                // Tetrahedral interpolation avoids grid-shaped waves and has
                // only four contributions per sample (WebGL 1 compatible).
                vec3 cell = floor(samplePosition + dot(samplePosition, vec3(1.0 / 3.0)));
                vec3 a = samplePosition - cell + dot(cell, vec3(1.0 / 6.0));
                // Resolve equal coordinates deterministically: a step-vector
                // rank duplicates corners at a three-way tie (visible seams).
                vec3 first;
                vec3 second;
                if (a.x >= a.y) {
                  if (a.y >= a.z) {
                    first = vec3(1.0, 0.0, 0.0); second = vec3(1.0, 1.0, 0.0);
                  } else if (a.x >= a.z) {
                    first = vec3(1.0, 0.0, 0.0); second = vec3(1.0, 0.0, 1.0);
                  } else {
                    first = vec3(0.0, 0.0, 1.0); second = vec3(1.0, 0.0, 1.0);
                  }
                } else {
                  if (a.y < a.z) {
                    first = vec3(0.0, 0.0, 1.0); second = vec3(0.0, 1.0, 1.0);
                  } else if (a.x < a.z) {
                    first = vec3(0.0, 1.0, 0.0); second = vec3(0.0, 1.0, 1.0);
                  } else {
                    first = vec3(0.0, 1.0, 0.0); second = vec3(1.0, 1.0, 0.0);
                  }
                }
                vec3 b = a - first + 1.0 / 6.0;
                vec3 c = a - second + 1.0 / 3.0;
                vec3 d = a - 0.5;
                return 28.0 * (
                  organicCorner(cell, a, time)
                  + organicCorner(cell + first, b, time)
                  + organicCorner(cell + second, c, time)
                  + organicCorner(cell + 1.0, d, time)
                );
              }

              vec3 organicSurface(vec3 direction) {
                vec3 pointer = normalize(uPointer);
                float proximity = 1.0 - smoothstep(0.0, 1.5, length(direction - pointer));
                float influence = proximity * clamp(uHover, 0.0, 1.0);
                // One broad, continuous field: a second octave made the
                // membrane look crumpled, particularly during contact. Move
                // the existing field through the patch without adding ripples.
                // The idle clock never stops or restarts when touched.
                vec3 domain = direction * 1.25
                  + pointer * influence * 3.0;
                float broadFold = organicNoise(domain + vec3(2.3, -1.7, 0.8), uTime);
                float displacement = broadFold * 0.26;
                return direction * (1.0 + displacement) + pointer * influence * 0.10;
              }

              void main() {
                vec3 direction = normalize(position);
                vec3 surface = organicSurface(direction);
                vec3 displaced = surface * length(position);
                vec4 mvPosition = modelViewMatrix * vec4(displaced, 1.0);
                vec3 normalInView = normalize(normalMatrix * direction);
                vec3 viewDirection = normalize(-mvPosition.xyz);
                float facing = clamp(dot(normalInView, viewDirection), -1.0, 1.0);
                float objectScale = length(modelMatrix[0].xyz);
                gl_PointSize = clamp(
                  uPointSize * objectScale
                    * (0.5 * uViewportHeight * projectionMatrix[1][1]) / max(-mvPosition.z, 0.48),
                  1.5,
                  6.0
                );
                vFacing = facing;
                gl_Position = projectionMatrix * mvPosition;
              }
            `,
            fragmentShader: `
              precision highp float;

              uniform float uDepthFade;
              varying float vFacing;

              void main() {
                vec2 bead = gl_PointCoord - vec2(0.5);
                float distanceToCenter = length(bead);
                float disc = 1.0 - smoothstep(0.44, 0.5, distanceToCenter);
                if (disc <= 0.001) discard;
                // Clear white points: the compressed rows at a moving fold
                // supply the brighter ridges, not a painted metallic hotspot.
                float frontLight = smoothstep(-0.15, 0.0, vFacing);
                float alpha = 0.52 * frontLight;
                gl_FragColor = vec4(
                  vec3(1.0), disc * alpha * uDepthFade
                );
                #include <tonemapping_fragment>
                #include <encodings_fragment>
              }
            `,
            toneMapped: false,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            blending: THREE.NormalBlending
          });
          pointMaterial.userData.mbsOrganicPointField = true;
          pointMaterial.userData.mbsLocalRenderOrder = 3;
          const morphPoints = new THREE.Points(morphGeo, pointMaterial);
          morphPoints.frustumCulled = false;
          morphPoints.userData.mbsLocalRenderOrder = 3;
          group.add(morphPoints);

          const coreMesh = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * 0.44, 20, 14),
            new THREE.MeshPhysicalMaterial({
              color: 0x060708,
              roughness: 0.68,
              metalness: 0.08,
              envMap: studioEnvironmentTexture,
              envMapIntensity: 0.16,
              transparent: true,
              opacity: 0.1,
              depthWrite: false
            })
          );
          coreMesh.material.userData.mbsBaseOpacity = 0.1;
          coreMesh.userData.mbsLocalRenderOrder = 1;
          group.add(coreMesh);

          const hitMesh = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * 1.28, 16, 12),
            new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
          );
          group.add(hitMesh);

          let organicLastTime = 0;
          let organicClock = 0;
          let organicFocus = 0;
          const organicInverseRotation = new THREE.Quaternion();
          const organicPointerTarget = new THREE.Vector3(0, 0, 1);
          innerUpdate = (time, hover, pointerX, pointerY, localHit) => {
            const delta = Math.min(0.05, Math.max(0, time - organicLastTime));
            organicLastTime = time;
            // The measured reference develops and releases contact over ~3s,
            // not a sub-second spring. Exact exponential following is monotonic
            // and keeps the same response at different render rates.
            const previousFocus = organicFocus;
            organicFocus += (hover - organicFocus) * (1 - Math.exp(-delta));
            organicClock += delta;
            morphPoints.rotation.y += delta * 0.25 * b.rotation_speed;
            pointMaterial.uniforms.uTime.value = organicClock;
            pointMaterial.uniforms.uHover.value = Math.max(0, Math.min(1, organicFocus));
            pointMaterial.uniforms.uViewportHeight.value = this.renderer.domElement.height;
            if (localHit) {
              organicInverseRotation.copy(morphPoints.quaternion).invert();
              organicPointerTarget.copy(localHit)
                .applyQuaternion(organicInverseRotation).normalize();
            }
            pointMaterial.uniforms.uPointer.value.lerp(organicPointerTarget,
              1 - Math.exp(-delta * 2.0)).normalize();
            coreMesh.scale.setScalar(1 + Math.sin(organicClock * 0.7) * 0.028 + organicFocus * 0.04);
            coreMesh.material.userData.mbsBaseOpacity = 0.1 - organicFocus * 0.02;
            group.userData.organicPointCount = morphGeo.attributes.position.count;
            group.userData.organicPointerPull = organicFocus;
            group.userData.organicMotionSpeed = 1;
            group.userData.organicLighting = 'white-dot-fold-overlap';
            group.userData.organicRipple = 'soft-single-field-contact';
            group.userData.organicContactVelocity = delta > 0 ? (organicFocus - previousFocus) / delta : 0;
          };
        }

        // Defensive fallback for unknown legacy block types. It is not part of
        // the eleven-slot production composition.
        else {
          const sphereGeo = new THREE.SphereGeometry(baseRadius, 32, 32);
          const accentGlassMaterial = crystalGlassMat.clone();
          accentGlassMaterial.uniforms.baseColor.value.set('#101820');
          accentGlassMaterial.uniforms.opacity.value = 0.62;
          accentGlassMaterial.uniforms.frostStrength.value = 0.015;
          accentGlassMaterial.uniforms.fresnelPower.value = 5.4;
          accentGlassMaterial.uniforms.rimStrength.value = 0.76;
          const glassMesh = new THREE.Mesh(sphereGeo, accentGlassMaterial);
          group.add(glassMesh);

          const accentCore = new THREE.Mesh(
            new THREE.SphereGeometry(baseRadius * (idx % 2 === 0 ? 0.24 : 0.16), 24, 24),
            new THREE.MeshStandardMaterial({
              color: idx % 2 === 0 ? 0xf1f2f3 : 0x2bb9ed,
              emissive: idx % 2 === 0 ? 0x050607 : 0x03131a,
              metalness: 0.72,
              roughness: 0.12
            })
          );
          accentCore.position.set(baseRadius * 0.16, baseRadius * -0.1, baseRadius * 0.22);
          group.add(accentCore);

          innerUpdate = (time, hover) => {
            const pulse = 1 + Math.sin(time * 1.7 + idx) * 0.06 + hover * 0.18;
            accentCore.scale.setScalar(pulse);
            accentCore.rotation.y += 0.006 + hover * 0.018;
            accentCore.material.color.copy(uiInactiveBright).lerp(uiActiveColor, hover);
            accentCore.material.emissive.copy(uiInactiveDark).lerp(uiActiveDark, hover * 0.68);
            glassMesh.material.uniforms.hover.value = hover;
          };
        }



        // Visual helper for the one shared production orbit.
        let pathLineMesh = null;
        const isDynamicOrbitDebug = (this.debugMode === 'prototype-orbit-angle' || this.debugMode === 'orbit-angle' || this.debugMode === 'orbit-calibration' || this.debugMode === 'orbit-members' || this.debugMode === 'orbit-belt-calibration' || this.debugMode === 'orbit-no-physics' || this.debugMode === 'orbit-system-reset');
        const showsOrbitPath = (
          this.debugMode === 'orbit-paths' ||
          this.debugMode === 'prototype-1' ||
          this.debugMode === 'prototype-orbit' ||
          this.debugMode === 'single-orbit-layout' ||
          this.debugMode === 'single-orbit-belt' ||
          this.debugMode === 'depth-staging' ||
          this.debugMode === 'depth-occlusion' ||
          isDynamicOrbitDebug
        );
        if (showsOrbitPath && idx === 0) {
          const pathPoints = [];
          const segments = 128;
          for (let i = 0; i <= segments; i++) {
            pathPoints.push(this.getOrbitPoint((i / segments) * TAU).position);
          }
          const pathGeo = new THREE.BufferGeometry().setFromPoints(pathPoints);
          const pathMat = new THREE.LineBasicMaterial({
            color: 0x00ffcc,
            transparent: true,
            opacity: 0.6
          });
          this.sharedPathLine = new THREE.LineLoop(pathGeo, pathMat);
          this.scene.add(this.sharedPathLine);
          pathLineMesh = this.sharedPathLine;

          if (
            this.debugMode === 'single-orbit-layout' ||
            this.debugMode === 'single-orbit-belt' ||
            this.debugMode === 'depth-staging' ||
            this.debugMode === 'depth-occlusion'
          ) {
            this.slotMarkers = [];
            const markerGeo = new THREE.SphereGeometry(0.04, 16, 16);
            const markerMat = new THREE.MeshBasicMaterial({
              color: 0xff3366,
              transparent: true,
              opacity: 0.7
            });
            for (let i = 0; i < 11; i++) {
              const markerMesh = new THREE.Mesh(markerGeo, markerMat);
              this.scene.add(markerMesh);
              this.slotMarkers.push(markerMesh);
            }
          }
        }

        // Visual collider bounds helper wireframe
        if (this.debugMode === 'physics-colliders' || this.debugMode === 'prototype-2') {
          const collGeo = new THREE.SphereGeometry(baseRadius * 1.05, 16, 16);
          const collMat = new THREE.MeshBasicMaterial({ color: 0xffaa00, wireframe: true, transparent: true, opacity: 0.35 });
          const collMesh = new THREE.Mesh(collGeo, collMat);
          collMesh.scale.setScalar(this.getBubbleSilhouetteMultiplier({ orb_type: b.orb_type }) * 0.96 / 1.05);
          group.add(collMesh);
        }

        // Store bubble descriptors with full kinematics physics properties
        const bubbleDescriptor = {
          group,
          orb_type: b.orb_type,
          orbitEnabled: true,
          title: b.title || `Orb_${idx + 1}`,
          description: b.description || "Designed with premium cyberpunk glass engineering.",
          link: b.link || "#",
          size: b.size,
          baseRadius,
          anchorX: b.anchorX,
          anchorY: b.anchorY,
          anchorZ: b.anchorZ,
          startAngle,
          orbitRadiusXMult,
          orbitRadiusYMult,
          orbitRadiusZMult,
          orbitSpeed,
          mass: b.mass || 1.0,
          velocity: new THREE.Vector3(0, 0, 0),
          force: new THREE.Vector3(0, 0, 0),
          targetPos: initPos.clone(),
          depthNorm: initialDepthNorm,
          isForeground: initialIsForeground,
          smallAnchorOffset,
          baseScale: (
            this.debugMode === 'single-orbit-layout' ||
            this.debugMode === 'single-orbit-belt' ||
            this.debugMode === 'depth-staging' ||
            this.debugMode === 'depth-occlusion' ||
            this.debugMode === 'orbit-depth-phase' ||
            this.debugMode === 'orbit-depth-phase-12' ||
            this.debugMode === 'orbit-spacing' ||
            this.debugMode === 'orbit-spacing-depth'
          )
            ? [1.00, 0.72, 0.68, 0.50, 0.46, 0.42, 0.36, 0.34, 0.26, 0.24, 0.16][idx % 11]
            : 1.0,
          opacity: b.opacity,
          floatSpeed: b.floatSpeed,
          interaction_intensity: b.interaction_intensity,
          rotation_speed: b.rotation_speed,
          interactionState,
          haloMesh,
          angularVelocity: new THREE.Vector3(),
          restOrientation: group.quaternion.clone(),
          model_url: b.model_url,
          video_url: b.video_url,
          seedX: Math.random() * 10,
          seedY: Math.random() * 10,
          seedZ: Math.random() * 10,
          pathLine: pathLineMesh,
          centerMarker: b.centerMarker,
          update: innerUpdate
        };
        this.bubbles.push(bubbleDescriptor);

        // Cache renderables once. Walking every nested group on every animation
        // frame caused avoidable main-thread churn on the homepage.
        bubbleDescriptor.renderables = [];
        bubbleDescriptor.renderGroups = [];
        const registerRenderable = (child) => {
          if (child.isGroup && !bubbleDescriptor.renderGroups.includes(child)) {
            bubbleDescriptor.renderGroups.push(child);
          }
          if (child.isMesh) child._bubbleRef = bubbleDescriptor;
          if (
            (child.isMesh || child.isPoints || child.isLine)
            && !bubbleDescriptor.renderables.includes(child)
          ) {
            bubbleDescriptor.renderables.push(child);
          }
        };
        group.traverse(registerRenderable);
        group.userData.mbsRegisterRenderable = registerRenderable;
        group.userData.mbsPendingRenderables.forEach(registerRenderable);
        group.userData.mbsPendingRenderables.length = 0;
      });

      this.updateSpacingBudget();

      // Build floating HTML/CSS debug tag elements inside DOM container
      let debugLabelsContainer = document.getElementById('mbs-debug-labels');
      if (!debugLabelsContainer) {
        debugLabelsContainer = document.createElement('div');
        debugLabelsContainer.id = 'mbs-debug-labels';
        debugLabelsContainer.className = 'mbs-debug-overlay-container';
        debugLabelsContainer.style.cssText = 'position: absolute; inset: 0; pointer-events: none; z-index: 20; overflow: hidden;';
        const canvasWrap = this.container.querySelector('.mbs-canvas') || this.container.querySelector('.mbs-canvas-wrap');
        if (canvasWrap) {
          canvasWrap.appendChild(debugLabelsContainer);
        }
      }
      if (debugLabelsContainer && (this.debugMode === 'object-labels' || this.debugMode === 'prototype-1' || this.debugMode === 'prototype-2' || this.debugMode === 'prototype-3' || this.debugMode === 'object-style-sheet' || this.debugMode === 'prototype-orbit' || this.debugMode === 'prototype-orbit-angle' || this.debugMode === 'orbit-angle' || this.debugMode === 'orbit-calibration' || this.debugMode === 'orbit-members' || this.debugMode === 'orbit-belt-calibration' || this.debugMode === 'scale-check' || this.debugMode === 'orbit-no-physics' || this.debugMode === 'orbit-system-reset' || this.debugMode === 'single-orbit-belt' || this.debugMode === 'depth-staging' || this.debugMode === 'single-orbit-layout' || this.debugMode === 'depth-occlusion' || this.debugMode === 'orbit-depth-phase' || this.debugMode === 'orbit-depth-phase-12' || this.debugMode === 'orbit-spacing' || this.debugMode === 'orbit-spacing-depth')) {
        debugLabelsContainer.innerHTML = '';
        this.bubbles.forEach((b, idx) => {
          const div = document.createElement('div');
          div.id = `mbs-debug-label-${idx}`;
          div.style.position = 'absolute';
          div.style.transform = 'translate(-50%, -100%)';
          debugLabelsContainer.appendChild(div);
        });
      }

      // 7. Interactive Spring Physics Pointer Event setup
      this.mouse2D = new THREE.Vector2();
      this.raycaster = new THREE.Raycaster();
      this.draggedBubble = null;

      this.pointerDownHandler = this.onPointerDown.bind(this);
      this.pointerMoveHandler = this.onPointerMove.bind(this);
      this.pointerUpHandler = this.onPointerUp.bind(this);
      this.pointerLeaveHandler = this.onPointerLeave.bind(this);
      this.pointerCaptureLostHandler = (event) => {
        if (event.pointerId === this.activePointerId) this.onPointerUp(event);
      };
      this.pointerBlurHandler = () => this.onPointerUp();
      this.touchMoveHandler = (event) => {
        // A normal swipe is never cancelled. Only a stationary press-and-hold
        // opts into dragging; touch-action stays pan-y for ordinary browsing.
        if (event.touches && event.touches.length > 1) {
          this.onPointerUp();
          return;
        }
        if (this.dragConstraint && this.dragConstraint.pointerType === 'touch' && event.cancelable) {
          event.preventDefault();
        }
      };

      canvas.addEventListener('pointerdown', this.pointerDownHandler, { passive: false });
      canvas.addEventListener('pointermove', this.pointerMoveHandler, { passive: false });
      canvas.addEventListener('pointerup', this.pointerUpHandler, { passive: false });
      canvas.addEventListener('pointercancel', this.pointerUpHandler, { passive: false });
      canvas.addEventListener('pointerleave', this.pointerLeaveHandler, { passive: true });
      canvas.addEventListener('lostpointercapture', this.pointerCaptureLostHandler);
      canvas.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
      window.addEventListener('blur', this.pointerBlurHandler);

      this.currentScroll = window.scrollY;
      this.targetScroll = window.scrollY;
      this.scrollProgress = 0;

      // Force canvas and poster opacity styles initially for a flicker-free gate
      if (this.renderer && this.renderer.domElement) {
        this.renderer.domElement.style.opacity = '0';
        this.renderer.domElement.style.pointerEvents = 'none';
        this.renderer.domElement.style.transition = 'opacity 420ms cubic-bezier(0.16, 1, 0.3, 1)';
      }
      const poster = this.container.querySelector('.mbs-canvas-poster');
      if (poster) {
        poster.style.opacity = '1';
        poster.style.pointerEvents = 'auto';
        poster.style.transition = 'opacity 360ms cubic-bezier(0.16, 1, 0.3, 1)';
      }

      const canvasEl = this.renderer ? this.renderer.domElement : null;
      const resizeParentEl = (canvasEl && canvasEl.parentElement) || this.container;

      this.onResize();
      this.heightAfterInit = this.section.clientHeight || 0;

      if (typeof ResizeObserver !== 'undefined' && resizeParentEl) {
        let resizeTimeout = null;
        let lastWidth = resizeParentEl.clientWidth;
        let lastHeight = resizeParentEl.clientHeight;

        this.resizeObserver = new ResizeObserver((entries) => {
          if (!entries || !entries[0]) return;
          this.resizeObserverEvents = (this.resizeObserverEvents || 0) + 1;
          const { width, height } = entries[0].contentRect;
          
          // Ignore small changes under 2px to prevent feedback loops in Shopify Editor
          if (Math.abs(width - lastWidth) <= 2 && Math.abs(height - lastHeight) <= 2) {
            return;
          }
          
          lastWidth = width;
          lastHeight = height;

          if (resizeTimeout) clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            if (this.renderer) {
              this.onResize();
            }
          }, 60);
        });
        this.resizeObserver.observe(resizeParentEl);
      }

      // The first stable composite is rendered by warmupAndReveal. Keeping a
      // second RAF loop alive during loading only competes with asset decode.
      this.clock = new THREE.Clock();

      // Set section element loading status
      this.container.classList.add('is-loading');
      this.container.classList.remove('is-ready');

      Promise.all(activeLoadPromises).then(() => {
        if (!this.container || !this.renderer) return;

        this.logTimelineEvent('all assets ready');

        // Force a synchronous clear and render pass for the stable first frame
        this.onResize();
        this.logTimelineEvent('renderer aspect synchronized');

        // GPU Warm-up compilation
        try {
          this.logTimelineEvent('GPU compilation warmup started');
          if (this.renderer.compileAsync) {
            this.renderer.compileAsync(this.scene, this.camera).then(() => {
              this.logTimelineEvent('GPU compileAsync finished');
              this.warmupAndReveal(poster);
            });
          } else {
            // Three r128's synchronous compiler crashes on mixed transparent,
            // instanced, and line materials. The hidden render passes below warm
            // the exact production pipeline without touching that broken path.
            this.logTimelineEvent('sync compiler skipped; hidden render warmup active');
            this.warmupAndReveal(poster);
          }
        } catch (e) {
          console.warn("[Moztech] Compile failed, fallback reveal:", e);
          this.warmupAndReveal(poster);
        }
      });
    }

    warmupAndReveal(poster) {
      // Compile the transparent depth layers across separate frames. The poster
      // remains visible while the browser avoids one long blocking GPU warm-up.
      const warmupStages = [
        ...this.bubbles.map((_, index) => `orb:${index}`),
        'logo'
      ];
      let warmupIndex = 0;
      this.logTimelineEvent('staged warmup started');

      const runWarmupStage = () => {
        this.warmupFrameId = null;
        if (!this.renderer || !this.section || !document.body.contains(this.section)) return;
        if (performance.now() - this.lastScrollActivity < 180) {
          this.warmupFrameId = requestAnimationFrame(runWarmupStage);
          return;
        }
        this.renderPasses(warmupStages[warmupIndex]);
        warmupIndex += 1;
        if (warmupIndex < warmupStages.length) {
          this.warmupFrameId = requestAnimationFrame(runWarmupStage);
          return;
        }

        this.logTimelineEvent('staged warmup finished');
        // All shaders are compiled now; build one complete hidden composite so
        // reveal never exposes only the last warm-up layer for a frame.
        this.renderPasses();
        this.isFirstFrameRendered = true;
        this.isSceneReady = true;

        if (this.textMeshPrepass) this.textMeshPrepass.visible = true;

        if (this.shouldRunIntro()) {
          this.replayIntro();
        } else {
          this.introActive = false;
          this.introProgress = 1;
          this.introPhase = 'complete';
          if (this.container) this.container.dataset.mbsIntro = 'complete';
        }

        this.warmupFrameId = requestAnimationFrame(() => {
          this.warmupFrameId = null;
          if (!this.renderer || !this.section || !document.body.contains(this.section)) return;
          this.heightAfterReady = this.section.clientHeight || 0;
          if (this.container) {
            this.container.classList.remove('is-loading');
            this.container.classList.add('is-ready');
          }
          if (this.renderer && this.renderer.domElement) {
            this.renderer.domElement.style.opacity = '1';
            this.renderer.domElement.style.pointerEvents = 'auto';
          }
          if (poster) {
            poster.style.opacity = '0';
            poster.style.pointerEvents = 'none';
          }

          this.logTimelineEvent('canvas opacity changed to 1');
          this.logTimelineEvent('fallback opacity changed to 0');
          this.logTimelineEvent('scene ready');
          console.table(this.timelineLogs);

          this.updateDebugDashboard();
          this.startRenderLoop();
          this.startDeferredAssetLoads();
        });
      };

      this.warmupFrameId = requestAnimationFrame(runWarmupStage);
    }

    startRenderLoop() {
      if (!this.renderer || this.animFrameId || document.hidden) return;
      this.animFrameId = requestAnimationFrame(this.render);
    }

    startDeferredAssetLoads() {
      if (this._destroyed || !this.renderer || !this.deferredAssetLoaders.length || this.deferredAssetHandle != null) return;
      const run = (deadline) => {
        this.deferredAssetHandle = null;

        if (this._destroyed || !this.renderer || !this.visible || document.hidden) return;

        const recentlyScrolled = performance.now() - this.lastScrollActivity < 260;
        const needsMoreIdleTime = deadline && !deadline.didTimeout && deadline.timeRemaining() < 8;
        if (recentlyScrolled || needsMoreIdleTime) {
          this.startDeferredAssetLoads();
          return;
        }

        // Upload one carousel texture per idle window. Uploading all three at
        // once produced a multi-second texImage2D spike while users scrolled
        // from the hero toward the product grid.
        const load = this.deferredAssetLoaders.shift();
        Promise.resolve()
          .then(() => {
            if (!this._destroyed && this.renderer) return load();
          })
          .catch(() => {})
          .finally(() => {
            if (!this._destroyed && this.renderer) this.startDeferredAssetLoads();
          });
      };

      if ('requestIdleCallback' in window) {
        this.deferredAssetHandle = window.requestIdleCallback(run, { timeout: 3600 });
      } else {
        this.deferredAssetHandle = window.setTimeout(run, 900);
      }
    }

    updatePointerCoordinates(event) {
      const rect = this.renderer.domElement.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      this.pointerSurfaceActive = true;
      this.mouse2D.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      this.mouse2D.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      this.pointerX = this.mouse2D.x;
      this.pointerY = this.mouse2D.y;

      const now = performance.now();
      if (this.lastPointerSample && now - this.lastPointerSample.time < 120) {
        const elapsed = Math.max(1, now - this.lastPointerSample.time);
        const nextX = (event.clientX - this.lastPointerSample.x) / elapsed;
        const nextY = -(event.clientY - this.lastPointerSample.y) / elapsed;
        const blend = 1 - Math.exp(-elapsed / 28);
        this.pointerVelocity.x = THREE.MathUtils.lerp(this.pointerVelocity.x, nextX, blend);
        this.pointerVelocity.y = THREE.MathUtils.lerp(this.pointerVelocity.y, nextY, blend);
      } else {
        this.pointerVelocity.x = 0;
        this.pointerVelocity.y = 0;
      }
      this.lastPointerSample = { x: event.clientX, y: event.clientY, time: now };
    }

    getPointerHit() {
      this.raycaster.setFromCamera(this.mouse2D, this.camera);
      const ray = this.raycaster.ray;
      const toCenter = this._pointerHitVector || (this._pointerHitVector = new THREE.Vector3());
      let closestBubble = null;
      let closestDistance = Infinity;

      // The hero interaction target is a sphere, so testing all product planes,
      // glass triangles and decorative meshes on every pointermove is wasted CPU.
      for (let i = 0; i < this.bubbles.length; i++) {
        const bubble = this.bubbles[i];
        toCenter.subVectors(bubble.group.position, ray.origin);
        const projectedDistance = toCenter.dot(ray.direction);
        if (projectedDistance < 0) continue;
        const radius = bubble.baseRadius
          * bubble.group.scale.x
          * this.getBubbleSilhouetteMultiplier(bubble);
        const centerDistanceSq = Math.max(
          0,
          toCenter.lengthSq() - projectedDistance * projectedDistance
        );
        const radiusSq = radius * radius;
        if (centerDistanceSq > radiusSq) continue;
        const hitDistance = projectedDistance - Math.sqrt(radiusSq - centerDistanceSq);
        if (hitDistance >= 0 && hitDistance < closestDistance) {
          closestDistance = hitDistance;
          closestBubble = bubble;
        }
      }

      this.pointerHitBubble = closestBubble;
      this.pointerHitDistance = closestDistance;
      if (closestBubble) {
        const worldHit = this.pointerHitWorld || (this.pointerHitWorld = new THREE.Vector3());
        ray.at(closestDistance, worldHit);
      }
      return closestBubble;
    }

    setBubblePointerSurface(bubble, worldHit) {
      const state = bubble.interactionState;
      const localHit = state.localPointerHit || (state.localPointerHit = new THREE.Vector3());
      // Physics can move/rotate the group since the last renderer traversal.
      // Refresh its matrix before turning the actual sphere hit into a unit
      // direction. Whole-canvas NDC is not a direction on an off-center orb.
      bubble.group.updateWorldMatrix(true, false);
      bubble.group.worldToLocal(localHit.copy(worldHit));
      const lengthSq = localHit.lengthSq();
      state.hasPointerHit = lengthSq > 0.00000001;
      if (state.hasPointerHit) localHit.multiplyScalar(1 / Math.sqrt(lengthSq));
    }

    updateLocalPointerHits() {
      for (let i = 0; i < this.bubbles.length; i++) {
        const bubble = this.bubbles[i];
        bubble.interactionState.hasPointerHit = Boolean(
          bubble === this.touchFocusBubble && bubble.interactionState.localPointerHit
        );
      }
      if (!this.pointerSurfaceActive || !this.enabled || this.introActive) return;
      const hitBubble = this.getPointerHit();
      const visualTarget = this.pointerIsDown && this.draggedBubble ? this.draggedBubble : hitBubble;
      this.mouseHoveredBubble = hitBubble;
      for (let i = 0; i < this.bubbles.length; i++) {
        this.bubbles[i].interactionState.hoverTarget = this.bubbles[i] === visualTarget ? 1 : 0;
      }
      if (!visualTarget) return;
      if (hitBubble === visualTarget) {
        this.setBubblePointerSurface(visualTarget, this.pointerHitWorld);
      } else if (this.dragConstraint) {
        // A spring-dragged orb can lag behind the pointer. Keep its deformation
        // directed toward the pointer's grab plane rather than another orb.
        const projectedHit = this.dragPointerSurface || (this.dragPointerSurface = new THREE.Vector3());
        if (this.raycaster.ray.intersectPlane(this.dragConstraint.plane, projectedHit)) {
          this.setBubblePointerSurface(visualTarget, projectedHit);
        }
      }
    }

    applyPointerImpulse(bubble, strength) {
      if (!bubble) return;

      const velocity = new THREE.Vector2(this.pointerVelocity.x, this.pointerVelocity.y);
      velocity.clampLength(0, 3);
      if (velocity.lengthSq() < 0.0004) return;

      const impulse = this.cameraRight.clone()
        .multiplyScalar(velocity.x)
        .addScaledVector(this.cameraUp, velocity.y);
      const displacement = bubble.group.position.distanceTo(bubble.targetPos);
      const returnInfluence = Math.max(0.2, 1 - displacement / 1.25);
      const interactionStrength = bubble.interaction_intensity || 1;
      const impulseScale = 1.18 * strength * interactionStrength * returnInfluence / Math.max(0.5, bubble.mass);

      bubble.velocity.addScaledVector(impulse, impulseScale);
      bubble.angularVelocity.x += -velocity.y * 2.1 * strength;
      bubble.angularVelocity.y += velocity.x * 2.1 * strength;
      bubble.angularVelocity.z += (velocity.x - velocity.y) * 0.38 * strength;
      this.physicsReleaseUntil = performance.now() + 2200;
      this.physicsAwake = true;
    }

    clearTouchHold() {
      if (this.touchHoldTimer !== null) window.clearTimeout(this.touchHoldTimer);
      this.touchHoldTimer = null;
    }

    beginPointerDrag(event, bubble) {
      if (!bubble || !this.renderer || this.introActive) return;
      this.clearTouchHold();
      this.updatePointerCoordinates(event);
      this.raycaster.setFromCamera(this.mouse2D, this.camera);
      const normal = this.camera.getWorldDirection(new THREE.Vector3());
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, bubble.group.position);
      const hit = this.raycaster.ray.intersectPlane(plane, new THREE.Vector3());
      if (!hit) return;

      this.dragConstraint = {
        pointerType: event.pointerType,
        plane,
        grabOffset: bubble.group.position.clone().sub(hit),
        target: bubble.group.position.clone(),
        hit: new THREE.Vector3(),
        previousTarget: bubble.group.position.clone(),
        velocity: new THREE.Vector3(),
        sampledAt: performance.now()
      };
      this.draggedBubble = bubble;
      this.pointerIsDown = true;
      this.activePointerId = event.pointerId;
      this.physicsAwake = true;
      this.touchFocusBubble = null;
      this.touchFocusUntil = 0;
      this.bubbles.forEach((item) => {
        item.interactionState.selectedTarget = item === bubble ? 1 : 0;
        item.interactionState.hoverTarget = item === bubble ? 1 : 0;
      });
      try { this.renderer.domElement.setPointerCapture(event.pointerId); } catch (error) {}
      this.renderer.domElement.style.cursor = 'grabbing';
      this.requestFrame();
    }

    updateDragTarget() {
      const drag = this.dragConstraint;
      if (!drag || !this.draggedBubble) return;
      this.raycaster.setFromCamera(this.mouse2D, this.camera);
      if (!this.raycaster.ray.intersectPlane(drag.plane, drag.hit)) return;
      drag.target.copy(drag.hit).add(drag.grabOffset);
      // Pointer capture may continue outside the viewport. Bound that reach,
      // not the spring, so a lost/remote pointer sample cannot eject a body.
      drag.hit.subVectors(drag.target, this.draggedBubble.targetPos)
        .clampLength(0, Math.max(2.4, this.basePlaneWidth * 0.65));
      drag.target.copy(this.draggedBubble.targetPos).add(drag.hit);
      const now = performance.now();
      const elapsed = Math.max(0.001, (now - drag.sampledAt) / 1000);
      drag.hit.subVectors(drag.target, drag.previousTarget).divideScalar(elapsed).clampLength(0, 8);
      drag.velocity.lerp(drag.hit, 1 - Math.exp(-elapsed * 24));
      drag.previousTarget.copy(drag.target);
      drag.sampledAt = now;
    }

    onPointerDown(event) {
      if (!this.enabled || this.introActive) return;
      if (event.isPrimary === false) {
        if (event.pointerType === 'touch') this.onPointerUp();
        return;
      }
      if (event.button !== undefined && event.button !== 0) return;
      if (this.activePointerId !== null || this.touchTapCandidate) return;
      this.lastPointerSample = null;
      this.updatePointerCoordinates(event);
      const hitBubble = this.getPointerHit();

      // Swipes remain native. A stationary 280 ms hold is an explicit opt-in
      // to dragging; a short stationary tap retains the existing focus action.
      if (event.pointerType === 'touch') {
        this.pointerSurfaceActive = false;
        this.mouseHoveredBubble = null;
        this.touchTapCandidate = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          lastX: event.clientX,
          lastY: event.clientY,
          startedAt: performance.now(),
          moved: false,
          bubble: hitBubble
        };
        if (hitBubble) {
          this.touchHoldTimer = window.setTimeout(() => {
            this.touchHoldTimer = null;
            const candidate = this.touchTapCandidate;
            if (!candidate || candidate.moved || !this.visible || !this.renderer) return;
            this.beginPointerDrag({
              pointerId: candidate.pointerId,
              pointerType: 'touch',
              clientX: candidate.lastX,
              clientY: candidate.lastY
            }, candidate.bubble);
          }, 280);
        }
        return;
      }

      // Blank canvas space is not an interaction target. Capturing it would
      // suppress native browser gestures without moving an object.
      if (!hitBubble) {
        this.pointerIsDown = false;
        this.activePointerId = null;
        this.lastPointerSample = null;
        this.bubbles.forEach((bubble) => {
          bubble.interactionState.selectedTarget = 0;
        });
        this.renderer.domElement.style.cursor = 'default';
        return;
      }

      this.beginPointerDrag(event, hitBubble);
      if (event.cancelable) event.preventDefault();
    }

    onPointerMove(event) {
      if (!this.enabled || this.introActive) return;
      if (event.isPrimary === false) return;
      if (this.activePointerId !== null && event.pointerId !== this.activePointerId) return;
      if (this.pointerIsDown && event.pointerType !== 'touch' && event.buttons === 0) {
        this.onPointerUp(event);
        return;
      }
      if (event.pointerType === 'touch') {
        const candidate = this.touchTapCandidate;
        if (candidate && candidate.pointerId === event.pointerId) {
          candidate.lastX = event.clientX;
          candidate.lastY = event.clientY;
          candidate.moved = candidate.moved || Math.hypot(
            event.clientX - candidate.startX,
            event.clientY - candidate.startY
          ) > 8;
          if (candidate.moved && !this.dragConstraint) this.clearTouchHold();
        }
        if (!this.dragConstraint) return;
      }

      this.updatePointerCoordinates(event);
      this.requestFrame();

      const previousHover = this.mouseHoveredBubble;
      const hitBubble = this.getPointerHit();
      this.mouseHoveredBubble = hitBubble;
      const visualTarget = this.pointerIsDown && this.draggedBubble ? this.draggedBubble : hitBubble;

      this.bubbles.forEach((bubble) => {
        bubble.interactionState.hoverTarget = bubble === visualTarget ? 1 : 0;
        bubble.interactionState.pointerX = this.pointerX;
        bubble.interactionState.pointerY = this.pointerY;
      });

      if (!this.pointerIsDown && hitBubble && hitBubble !== previousHover) {
        this.applyPointerImpulse(hitBubble, 0.22);
      }

      if (this.pointerIsDown && this.draggedBubble) {
        // Move one spring target, never add one impulse per browser event.
        // This keeps the same drag response on 60/120 Hz displays and mice.
        this.updateDragTarget();
        this.renderer.domElement.style.cursor = 'grabbing';
        if (event.cancelable) event.preventDefault();
      } else {
        this.renderer.domElement.style.cursor = hitBubble ? 'grab' : 'default';
      }
    }

    onPointerUp(event) {
      if (event && this.activePointerId !== null && event.pointerId !== this.activePointerId) return;
      if (event && this.activePointerId === null && this.touchTapCandidate
        && event.pointerId !== this.touchTapCandidate.pointerId) return;
      this.clearTouchHold();
      const cancelled = !event || event.type === 'pointercancel' || event.type === 'lostpointercapture';
      if (event && event.pointerType === 'touch' && !this.dragConstraint) {
        const candidate = this.touchTapCandidate;
        const elapsed = candidate ? performance.now() - candidate.startedAt : Infinity;
        const isTap = Boolean(
          candidate &&
          candidate.pointerId === event.pointerId &&
          !cancelled &&
          !candidate.moved &&
          elapsed <= 350
        );

        if (isTap) {
          this.updatePointerCoordinates(event);
          const hitBubble = this.getPointerHit();
          if (hitBubble) this.setBubblePointerSurface(hitBubble, this.pointerHitWorld);
          this.touchFocusBubble = hitBubble;
          this.touchFocusUntil = hitBubble ? performance.now() + 1800 : 0;
          this.bubbles.forEach((bubble) => {
            bubble.interactionState.hoverTarget = 0;
            bubble.interactionState.selectedTarget = bubble === hitBubble ? 1 : 0;
          });
          this.requestFrame();
        }

        this.touchTapCandidate = null;
        this.pointerIsDown = false;
        this.activePointerId = null;
        this.draggedBubble = null;
        this.lastPointerSample = null;
        this.pointerSurfaceActive = false;
        return;
      }

      const releasedBubble = this.draggedBubble;
      this.pointerIsDown = false;
      this.draggedBubble = null;
      this.dragConstraint = null;
      this.touchTapCandidate = null;
      if (releasedBubble) {
        this.physicsReleaseUntil = Math.max(this.physicsReleaseUntil, performance.now() + 2200);
        this.physicsAwake = true;
        if (cancelled) releasedBubble.velocity.multiplyScalar(0.3);
      }
      if (cancelled || (event && event.pointerType === 'touch')) {
        this.mouseHoveredBubble = null;
        this.pointerSurfaceActive = false;
      }
      this.bubbles.forEach((bubble) => {
        bubble.interactionState.selectedTarget = 0;
        bubble.interactionState.hoverTarget = bubble === this.mouseHoveredBubble ? 1 : 0;
      });

      const pointerId = this.activePointerId;
      this.activePointerId = null;
      if (this.renderer && this.renderer.domElement.releasePointerCapture && pointerId !== null) {
        try {
          this.renderer.domElement.releasePointerCapture(pointerId);
        } catch (error) {}
      }
      this.lastPointerSample = null;
      this.pointerVelocity.x = 0;
      this.pointerVelocity.y = 0;

      if (releasedBubble && this.debugMode === 'interaction') {
        console.log("[BMS Debug] Released force target:", releasedBubble.title);
      }
      this.renderer.domElement.style.cursor = this.mouseHoveredBubble ? 'grab' : 'default';
    }

    onPointerLeave(event) {
      if (this.pointerIsDown) return;
      this.pointerSurfaceActive = false;
      if (event && event.pointerType === 'touch') {
        this.clearTouchHold();
        this.touchTapCandidate = null;
        return;
      }
      this.mouseHoveredBubble = null;
      this.lastPointerSample = null;
      this.pointerVelocity.x = 0;
      this.pointerVelocity.y = 0;
      this.bubbles.forEach((bubble) => {
        bubble.interactionState.hoverTarget = 0;
        bubble.interactionState.selectedTarget = 0;
      });
      this.renderer.domElement.style.cursor = 'default';
    }

    updateContactDepthLayers() {
      const towardViewer = this.cameraTowardViewer;
      if (!towardViewer) return;
      const depthRange = Math.max(0.0001, this.orbitMaxCameraDepth || 1);
      this._depthSortedBubbles = this._depthSortedBubbles || [];
      this._depthSortedBubbles.length = 0;
      for (const bubble of this.bubbles) {
        const position = bubble.group.position;
        const target = bubble.targetPos || position;
        const displacementDepth = (position.x - target.x) * towardViewer.x
          + (position.y - target.y) * towardViewer.y
          + (position.z - target.z) * towardViewer.z;
        const targetDepth = Number.isFinite(bubble.depthNorm)
          ? bubble.depthNorm : target.dot(towardViewer) / depthRange;
        bubble.renderDepthNorm = targetDepth + displacementDepth / depthRange;
        // Preserve the authored crossing/entry/exit at rest. Only actual contact
        // displacement can move a body into the other wordmark pass.
        if (Math.abs(displacementDepth) > 0.00001) {
          if (bubble.renderDepthNorm > 0.04) bubble.isForeground = true;
          else if (bubble.renderDepthNorm < -0.04) bubble.isForeground = false;
        }
        bubble.cameraDepth = position.dot(towardViewer);
        this._depthSortedBubbles.push(bubble);
      }
      this._depthSortedBubbles.sort((a, b) => a.cameraDepth - b.cameraDepth);
      this._depthSortedBubbles.forEach((bubble, rank) => {
        // Composite each complete orb from back to front; never interleave one
        // orb's core with every other orb's glass. Nested Groups must share the
        // rank because Three.js otherwise resets groupOrder inside the asset.
        const layer = 100 + rank * 16;
        bubble.group.renderOrder = layer;
        (bubble.renderGroups || []).forEach((group) => { group.renderOrder = layer; });
        (bubble.renderables || []).forEach((child) => {
          const local = child.userData.mbsLocalRenderOrder
            || child.material?.userData?.mbsLocalRenderOrder || 0;
          child.renderOrder = layer + local;
        });
      });
    }

    stepInteractionPhysics(frameDt, now) {
      // Use elapsed render time here: the visual animation clock deliberately
      // caps dt at 30 ms, which otherwise slows physics on a 30 Hz device.
      const elapsed = this.physicsUpdatedAt === undefined ? frameDt : (now - this.physicsUpdatedAt) / 1000;
      this.physicsUpdatedAt = now;
      const dt = Math.min(0.05, Math.max(0.001, elapsed));
      const canInteract = !this.introActive && this.heroExitProgress < 0.02;
      const active = canInteract && (this.pointerIsDown || this.physicsAwake
        || now < this.physicsReleaseUntil || this.hasDesktopOrbitContact());
      this.physicsActive = active;
      const scratch = this.physicsScratch || (this.physicsScratch = {
        delta: new THREE.Vector3(),
        relative: new THREE.Vector3(),
        tangent: new THREE.Vector3(),
        torque: new THREE.Vector3(),
        rotation: new THREE.Quaternion()
      });

      this.bubbles.forEach((bubble) => {
        const state = bubble.physicsState || (bubble.physicsState = {
          previousTarget: bubble.targetPos.clone(),
          targetVelocity: new THREE.Vector3()
        });
        if (!bubble.restOrientation) bubble.restOrientation = bubble.group.quaternion.clone();
        state.targetVelocity.subVectors(bubble.targetPos, state.previousTarget);
        // Store interaction velocity relative to the moving belt. Carrying its
        // displacement keeps idle bodies on the exact authored 3D trajectory.
        if (active) bubble.group.position.add(state.targetVelocity);
        state.targetVelocity.divideScalar(dt);
        state.previousTarget.copy(bubble.targetPos);
        bubble.isCollidingBoundary = false;
        if (!active) {
          bubble.group.position.copy(bubble.targetPos);
          bubble.velocity.set(0, 0, 0);
          bubble.force.set(0, 0, 0);
          bubble.angularVelocity.multiplyScalar(Math.exp(-4.2 * dt));
        }
      });
      if (!active) {
        this.physicsAwake = false;
        return;
      }

      // Real integration substeps, not repeated positional correction after
      // one large step. At most six tiny steps keeps low-FPS recovery bounded.
      const steps = Math.max(1, Math.ceil(dt / (1 / 120) - 0.000001));
      const h = dt / steps;
      for (let step = 0; step < steps; step++) {
        this.bubbles.forEach((bubble) => {
          const mass = Math.max(0.35, bubble.mass);
          const dragging = bubble === this.draggedBubble && this.dragConstraint;
          const stiffness = dragging ? 190 : 20;
          const damping = 2 * Math.sqrt(stiffness * mass) * (dragging ? 0.78 : 0.62);
          const target = dragging ? this.dragConstraint.target : bubble.targetPos;
          bubble.force.subVectors(target, bubble.group.position).multiplyScalar(stiffness);
          bubble.force.addScaledVector(bubble.velocity, -damping);
          if (dragging) {
            const sampleAge = Math.max(0, (now - this.dragConstraint.sampledAt) / 1000);
            bubble.force.addScaledVector(this.dragConstraint.velocity, damping * 0.22 * Math.exp(-sampleAge * 24));
            bubble.force.addScaledVector(bubble.physicsState.targetVelocity, -damping);
            // A dragged surface rolls with the spring's lateral velocity.
            // Integrate per substep, not per pointer event, to keep 30/120 Hz
            // devices equivalent. The camera-facing contact gives a stable
            // world-space torque without rotating the entire scene.
            scratch.torque.crossVectors(this.cameraTowardViewer || this.camera.position,
              bubble.velocity).multiplyScalar(0.2 / Math.max(0.15, this.getCollisionRadius(bubble)));
            scratch.torque.clampLength(0, 1.6);
            bubble.angularVelocity.lerp(scratch.torque, 1 - Math.exp(-h * 10));
          }
          bubble.velocity.addScaledVector(bubble.force, h / mass).clampLength(0, 8);
          bubble.group.position.addScaledVector(bubble.velocity, h);
        });

        for (let iteration = 0; iteration < 2; iteration++) {
          for (let i = 0; i < this.bubbles.length; i++) {
            for (let j = i + 1; j < this.bubbles.length; j++) {
              const first = this.bubbles[i];
              const second = this.bubbles[j];
              const firstRadius = this.getCollisionRadius(first);
              const secondRadius = this.getCollisionRadius(second);
              // Desktop allows projected overlap, not interpenetrating bodies.
              // Touch layouts retain the existing contact/target relationship.
              const minimumDistance = this.usesProjectedOrbitSpacing()
                ? Math.min(firstRadius + secondRadius, first.targetPos.distanceTo(second.targetPos))
                : firstRadius + secondRadius;
              scratch.delta.subVectors(first.group.position, second.group.position);
              let distance = scratch.delta.length();
              if (distance >= minimumDistance || minimumDistance <= 0.0001) continue;
              if (distance < 0.0001) {
                scratch.delta.subVectors(first.targetPos, second.targetPos).normalize();
                if (scratch.delta.lengthSq() < 0.0001) scratch.delta.set(1, 0, 0);
                distance = 0;
              } else {
                scratch.delta.divideScalar(distance);
              }
              const inverseMassFirst = 1 / Math.max(0.35, first.mass);
              const inverseMassSecond = 1 / Math.max(0.35, second.mass);
              const inverseMassTotal = inverseMassFirst + inverseMassSecond;
              const correction = Math.max(0, minimumDistance - distance - 0.001) * 0.72 / inverseMassTotal;
              first.group.position.addScaledVector(scratch.delta, correction * inverseMassFirst);
              second.group.position.addScaledVector(scratch.delta, -correction * inverseMassSecond);
              first.isCollidingBoundary = true;
              second.isCollidingBoundary = true;

              scratch.relative.subVectors(first.velocity, second.velocity)
                .add(first.physicsState.targetVelocity).sub(second.physicsState.targetVelocity);
              const speed = scratch.relative.dot(scratch.delta);
              if (speed >= 0) continue;
              // Resting contacts do not bounce. Faster impacts retain enough
              // energy to make the neighboring body's mass visibly matter.
              const restitution = speed < -0.18 ? 0.62 : 0;
              const impulse = -(1 + restitution) * speed / inverseMassTotal;
              first.velocity.addScaledVector(scratch.delta, impulse * inverseMassFirst);
              second.velocity.addScaledVector(scratch.delta, -impulse * inverseMassSecond);
              scratch.tangent.copy(scratch.relative).addScaledVector(scratch.delta, -speed);
              const tangentialSpeed = scratch.tangent.length();
              if (tangentialSpeed > 0.0001) {
                scratch.tangent.divideScalar(tangentialSpeed);
                const friction = Math.min(impulse * 0.16, tangentialSpeed / inverseMassTotal);
                first.velocity.addScaledVector(scratch.tangent, -friction * inverseMassFirst);
                second.velocity.addScaledVector(scratch.tangent, friction * inverseMassSecond);
                scratch.torque.crossVectors(scratch.delta, scratch.tangent).multiplyScalar(friction * 0.5);
                first.angularVelocity.addScaledVector(scratch.torque, inverseMassFirst / Math.max(0.1, firstRadius));
                second.angularVelocity.addScaledVector(scratch.torque, inverseMassSecond / Math.max(0.1, secondRadius));
              }
            }
          }
        }
      }

      let unsettled = false;
      this.bubbles.forEach((bubble) => {
        const angularSpeed = bubble.angularVelocity.length();
        if (angularSpeed > 0.0001) {
          scratch.delta.copy(bubble.angularVelocity).divideScalar(angularSpeed);
          scratch.rotation.setFromAxisAngle(scratch.delta, Math.min(angularSpeed, 2.5) * dt);
          bubble.group.quaternion.premultiply(scratch.rotation).normalize();
        }
        bubble.angularVelocity.multiplyScalar(Math.exp(-2.8 * dt));
        if (bubble !== this.draggedBubble) {
          // Impact roll is temporary. Preserve each object's independent
          // child animation while restoring the authored parent orientation.
          bubble.group.quaternion.slerp(bubble.restOrientation, 1 - Math.exp(-dt * 1.65));
        }
        if (bubble.group.position.distanceToSquared(bubble.targetPos) > 0.000025
          || bubble.velocity.lengthSq() > 0.0001
          || bubble.angularVelocity.lengthSq() > 0.0001
          || bubble.group.quaternion.angleTo(bubble.restOrientation) > 0.002) {
          unsettled = true;
        }
      });
      // A timer is not a settling criterion: never snap a still-moving body to
      // its orbit when the old 2.2-second interaction window expires.
      this.physicsAwake = Boolean(this.pointerIsDown || unsettled);
    }

    onResize() {
      const canvas = this.renderer.domElement;
      const parentEl = canvas.parentElement || this.container;
      const width = (parentEl && parentEl.clientWidth) || canvas.clientWidth || window.innerWidth || 800;
      const height = (parentEl && parentEl.clientHeight) || canvas.clientHeight || window.innerHeight || 600;

      this.viewportWidth = width;
      this.viewportHeight = height;
      const frame = this.getResponsiveLayoutProfile();
      this.camera.aspect = width / height;
      this.camera.fov = THREE.MathUtils.radToDeg(2 * Math.atan(
        frame.sceneWidth / (2 * this.camera.position.z * this.camera.aspect)
      ));
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height, false);
      
      this.viewportWidth = width;
      this.viewportHeight = height;
      const sectionRect = this.section.getBoundingClientRect();
      this.sectionDocumentTop = sectionRect.top + (window.scrollY || window.pageYOffset || 0);
      this.sectionDocumentHeight = Math.max(sectionRect.height, this.section.offsetHeight, height);
      this.documentScrollRange = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      this.isMobile = width < HERO_LAYOUT.phoneToTabletWidth.start;
      this.isTablet = width >= HERO_LAYOUT.phoneToTabletWidth.start && width < HERO_LAYOUT.tabletToDesktopWidth.end;
      this.isDesktop = width >= HERO_LAYOUT.tabletToDesktopWidth.end;
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.getPixelRatioCap()));

      if (this.debugOverlayCanvas) {
        this.debugOverlayCanvas.width = width;
        this.debugOverlayCanvas.height = height;
      }

      // Safe area padding percentage based on viewport device category
      if (this.isMobile) {
        this.safePaddingPercent = 0.15; // 15% mobile padding
      } else if (this.isTablet) {
        this.safePaddingPercent = 0.12; // 12% tablet padding
      } else {
        this.safePaddingPercent = 0.08; // 8% desktop padding
      }

      const frustumHeight = 2 * Math.tan((this.camera.fov * Math.PI) / 360) * this.camera.position.z;
      const frustumWidth = frustumHeight * this.camera.aspect;
      this.frustumHeight = frustumHeight;
      this.frustumWidth = frustumWidth;
      this.refreshOrbitMetrics(frustumWidth);
      const logoWidthFraction = this.getWordmarkWidthFraction();
      const targetPlaneWidth = frustumWidth * logoWidthFraction;
      this.objectLayoutScale = this.getResponsiveObjectScale();
      
      this.logoWidth = targetPlaneWidth;
      this.logoHeight = targetPlaneWidth / 3.125;

      if (this.textMeshVisual && this.textMeshPrepass && this.basePlaneWidth) {
        const scale = targetPlaneWidth / this.basePlaneWidth;
        this.wordmarkBaseScale = scale;
        this.textMeshVisual.scale.set(scale, scale, 1);
        this.textMeshPrepass.scale.set(scale, scale, 1);
      }

      this.bubbles.forEach((bubble) => {
        bubble.group.traverse((child) => {
          if (!child.isMesh || !child.material || !child.material.uniforms) return;
          if (child.material.uniforms.uWordmarkSize) {
            child.material.uniforms.uWordmarkSize.value.set(this.logoWidth, this.logoHeight);
          }
        });
      });

      this.updateSpacingBudget();
      this._spacingSolution = null;

      if (this.safeAreaOutline) {
        const dist0 = Math.abs(this.camera.position.z);
        const fHeight0 = 2 * Math.tan((this.camera.fov * Math.PI) / 360) * dist0;
        const fWidth0 = fHeight0 * this.camera.aspect;
        const safePaddingRatio = this.safePaddingPercent || 0.08;
        const paddingX0 = fWidth0 * safePaddingRatio;
        const paddingY0 = fHeight0 * safePaddingRatio;
        const limitX0 = (fWidth0 / 2) - paddingX0;
        const limitY0 = (fHeight0 / 2) - paddingY0;

        const tempV1 = new THREE.Vector3(-limitX0, limitY0, 0).project(this.camera);
        const tempV2 = new THREE.Vector3(limitX0, -limitY0, 0).project(this.camera);

        const x1 = (tempV1.x * 0.5 + 0.5) * width;
        const y1 = (tempV1.y * -0.5 + 0.5) * height;
        const x2 = (tempV2.x * 0.5 + 0.5) * width;
        const y2 = (tempV2.y * -0.5 + 0.5) * height;

        const safeBoxW = x2 - x1;
        const safeBoxH = y2 - y1;

        this.safeAreaOutline.style.left = `${x1}px`;
        this.safeAreaOutline.style.top = `${y1}px`;
        this.safeAreaOutline.style.width = `${safeBoxW}px`;
        this.safeAreaOutline.style.height = `${safeBoxH}px`;
      }

      this.requestFrame();
    }

    onScroll() {
      if (!this.enabled || !this.visible) return;
      this.lastScrollActivity = performance.now();

      const scrollTop = window.scrollY || window.pageYOffset || 0;
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      let sectionTop;
      let sectionHeight;
      if (Number.isFinite(this.sectionDocumentTop) && this.sectionDocumentHeight > 0) {
        sectionTop = this.sectionDocumentTop - scrollTop;
        sectionHeight = this.sectionDocumentHeight;
      } else {
        const rect = this.section.getBoundingClientRect();
        sectionTop = rect.top;
        sectionHeight = rect.height;
      }
      const progress = 1 - Math.max(-1, Math.min(1, (sectionTop - viewport * 0.22) / Math.max(sectionHeight, viewport)));
      this.scroll = Math.max(0, Math.min(1, progress));
      this.targetScroll = scrollTop;
      const stickyRunway = Math.max(1, sectionHeight - viewport);
      const exitDistance = this.section.classList.contains('layout-cinematic')
        ? Math.max(viewport * 0.24, stickyRunway * 0.92)
        : Math.max(viewport * 0.78, sectionHeight * 0.46);
      this.heroExitTarget = THREE.MathUtils.clamp(-sectionTop / exitDistance, 0, 1);
      this.requestFrame();
    }

    requestFrame() {
      if (!this.visible || this.raf) return;
      this.raf = window.requestAnimationFrame(this.applyFrame);
    }

    applyFrame() {
      this.raf = null;
      this.section.style.setProperty('--mbs-pointer-x', this.pointerX.toFixed(4));
      this.section.style.setProperty('--mbs-pointer-y', this.pointerY.toFixed(4));
      this.section.style.setProperty('--mbs-scroll', this.scroll.toFixed(4));
    }

    playVideos() {
      this.videos.forEach((video) => {
        if (!video.dataset.mbsVideoBound) {
          video.dataset.mbsVideoBound = 'true';
          video.muted = true;
          video.loop = true;
          video.playsInline = true;
        }
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      });
    }

    render() {
      this.animFrameId = null;
      if (!this.section || !document.body.contains(this.section)) {
        this.destroy();
        if (window.moztechBubblesInstances) {
          window.moztechBubblesInstances.delete(this);
        }
        return;
      }
      const now = performance.now();
      if (document.hidden || (!this.visible && this.isSceneReady && !this.introActive)) {
        this._lastFrameTime = now;
        return;
      }
      const targetFrameInterval = this.getTargetFrameInterval();
      if (
        this._lastRenderedAt &&
        now - this._lastRenderedAt < targetFrameInterval - 1
      ) {
        this.startRenderLoop();
        return;
      }
      this._lastRenderedAt = now;
      if (this.introActive && this.introStartTime !== null) {
        const introElapsed = (now - this.introStartTime) / 1000;
        const logoReveal = this.smoothstep(0.05, 0.34, introElapsed);
        const logoUnfold = this.smoothstep(0.52, 0.82, introElapsed);
        this.logoIntroOpacity = logoReveal;
        this.logoIntroScaleX = THREE.MathUtils.lerp(0.095, 1, logoUnfold);
      } else {
        this.logoIntroOpacity = 1;
        this.logoIntroScaleX = 1;
      }
      if (!this._lastFrameTime) {
        this._lastFrameTime = now;
      }
      let dt = (now - this._lastFrameTime) / 1000;
      this._lastFrameTime = now;

      // Fallback and clamping to prevent extreme jumps or simulation freezes
      if (dt <= 0 || isNaN(dt)) {
        dt = 0.016;
      }
      dt = Math.min(dt, 0.03);
      window.lastMbsDt = dt;
      window.mbsFrameCount = (window.mbsFrameCount || 0) + 1;

      if (!this._startTime) {
        this._startTime = now;
      }
      const time = (now - this._startTime) / 1000;
      const exitSmoothing = 1 - Math.exp(-dt * 5.4);
      this.heroExitProgress += (this.heroExitTarget - this.heroExitProgress) * exitSmoothing;
      const heroExitEase = this.heroExitProgress * this.heroExitProgress * (3 - 2 * this.heroExitProgress);
      this.orbExitProgress = this.smoothstep(0.01, 0.94, heroExitEase);
      this.orbExitOpacity = 1;
      const logoExitCollapse = this.smoothstep(0.66, 0.94, heroExitEase);
      this.logoExitScaleX = THREE.MathUtils.lerp(1, 0.095, logoExitCollapse);
      this.logoExitOpacity = 1 - this.smoothstep(0.82, 1, heroExitEase);
      const wordmarkBaseScale = this.wordmarkBaseScale || 1;
      const wordmarkScaleX = wordmarkBaseScale * this.logoIntroScaleX * this.logoExitScaleX;
      if (this.textMeshVisual) {
        this.textMeshVisual.scale.set(wordmarkScaleX, wordmarkBaseScale, 1);
      }
      if (this.textMeshPrepass) {
        this.textMeshPrepass.scale.set(wordmarkScaleX, wordmarkBaseScale, 1);
      }
      const exitCssValue = this.heroExitProgress.toFixed(4);
      if (exitCssValue !== this.lastExitCssValue) {
        this.section.style.setProperty('--mbs-exit', exitCssValue);
        this.lastExitCssValue = exitCssValue;
      }

      // Smooth scroll tracking
      this.currentScroll += (this.targetScroll - this.currentScroll) * 0.08;
      this.scrollProgress = this.currentScroll / this.documentScrollRange;

      // Camera is static for this hero, so reuse the basis calculated on resize.
      const cameraRight = this.cameraRight;
      const cameraUp = this.cameraUp;
      const cameraTowardViewer = this.cameraTowardViewer;

      const wordmarkCenter = this.wordmarkCenter || (this.wordmarkCenter = new THREE.Vector3());

      // The shared orbit changes slowly. Reuse each spacing solution briefly
      // and advance its angles at the common speed instead of rebuilding the
      // complete projection/packing model on every animation frame.
      const resolvedAngles = [];
      const bubbleOverlappingFlags = new Array(this.bubbles.length).fill(false);
      let sortedProj = [];

      const wordmarkWidth = this.logoWidth || this.basePlaneWidth || 5.0;
      const wordmarkHeight = this.logoHeight || (this.basePlaneWidth / 3.125) || 1.6;

      const isStandaloneLab = (
        this.debugMode === 'static-composition' ||
        this.debugMode === 'prototype-3' ||
        this.debugMode === 'object-style-sheet' ||
        this.debugMode === 'mozcare-transmission-lab' ||
        this.debugMode === 'starburst-tentacle-lab' ||
        this.debugMode === 'two-orb-interaction-lab' ||
        this.debugMode === 'grid-mozcare-lab'
      );
      const useSpacingSolver = !isStandaloneLab && this.bubbles.length > 1
        && this.usesProjectedOrbitSpacing();
      let layoutScale = (this.objectLayoutScale || 1) * (this.spacingPackScale || 1);
      const canReuseSpacing = useSpacingSolver
        && this._spacingSolution
        && now - this._spacingSolution.solvedAt < 220;

      const getMinGap = (radiusA, radiusB) => {
        const containsAccent = Math.min(radiusA, radiusB) < (this.isMobile ? 14 : 20);
        if (containsAccent) return this.isMobile ? 4 : 6;
        return this.getSpacingGapPixels();
      };

      if (canReuseSpacing) {
        const elapsed = time - this._spacingSolution.time;
        this._spacingSolution.angles.forEach((angle, idx) => {
          resolvedAngles[idx] = angle + elapsed * this.bubbles[idx].orbitSpeed;
        });
        layoutScale = this._spacingSolution.layoutScale;
        this._spacingSolution.overlappingFlags.forEach((flag, idx) => {
          bubbleOverlappingFlags[idx] = flag;
        });
        this.spacingSolverIterations = 0;
      } else if (useSpacingSolver) {
        if (this.debugMode === 'orbit-spacing') {
          const canvas = this.renderer.domElement;
          console.log(`[Frame Log] time=${time.toFixed(2)} canvas=${canvas.clientWidth}x${canvas.clientHeight} bubbles=${this.bubbles.length}`);
        }
        const canvas = this.renderer.domElement;
        const circumference = this.orbitCircumferencePixels || 1;
        const curveSafety = this.spacingCurveSafety || 1.06;
        const normalizeAngle = (angle) => ((angle % TAU) + TAU) % TAU;
        const projectBubble = (b, idx, angle) => {
          const sample = this.getOrbitPoint(angle);
          const depthNorm = sample.depthNorm;
          const depthScale = this.getOrbitScale(angle);
          const visualScale = b.baseScale * depthScale * layoutScale;
          const radius3D = b.baseRadius * visualScale * this.getBubbleSilhouetteMultiplier(b);
          const tempV = new THREE.Vector3().copy(sample.position);
          tempV.project(this.camera);
          const screenX = (tempV.x * 0.5 + 0.5) * canvas.clientWidth;
          const screenY = (-tempV.y * 0.5 + 0.5) * canvas.clientHeight;
          const dist = this.camera.position.distanceTo(sample.position);
          const frustumH = 2 * Math.tan((this.camera.fov * Math.PI) / 360) * dist;
          const radiusPixels = radius3D * (canvas.clientHeight / frustumH);
          return {
            idx,
            bubble: b,
            angle,
            normAngle: normalizeAngle(angle),
            screenPos: new THREE.Vector2(screenX, screenY),
            radiusPixels,
            packingRadiusPixels: radiusPixels * this.getProjectedPackingFactor(depthNorm),
            visualScale,
            depthScale,
            depthNorm,
            position: sample.position
          };
        };

        this.bubbles.forEach((bubble) => {
          resolvedAngles.push(time * bubble.orbitSpeed + bubble.startAngle);
        });

        // Keep the authored object order, then allocate every adjacent pair a
        // complete segment of the closed projected orbit. Unlike pairwise
        // pushing, the final-to-first seam is part of the same equation and
        // cannot collect unresolved overlap around MOZ or TECH.
        const orderedIndices = this.bubbles
          .map((bubble, idx) => ({ idx, angle: normalizeAngle(bubble.startAngle) }))
          .sort((a, b) => a.angle - b.angle)
          .map((item) => item.idx);
        const authoredGapWeights = orderedIndices.map((idx, orderIndex) => {
          const nextIdx = orderedIndices[(orderIndex + 1) % orderedIndices.length];
          const current = normalizeAngle(this.bubbles[idx].startAngle);
          const next = normalizeAngle(this.bubbles[nextIdx].startAngle);
          return ((next - current) + TAU) % TAU;
        });
        const totalAuthoredGap = authoredGapWeights.reduce((sum, value) => sum + value, 0) || TAU;
        const anchorIdx = orderedIndices[0];
        const anchorAngle = time * this.bubbles[anchorIdx].orbitSpeed + this.bubbles[anchorIdx].startAngle;
        const anchorArc = this.getOrbitArcAtAngle(anchorAngle);
        let packingPasses = 0;

        const minimumLayoutScale = (this.objectLayoutScale || 1) * 0.84;
        for (let pass = 0; pass < 3; pass++) {
          packingPasses = pass + 1;
          const projections = this.bubbles.map((bubble, idx) => projectBubble(bubble, idx, resolvedAngles[idx]));
          let radiusSum = 0;
          let gapSum = 0;
          orderedIndices.forEach((idx, orderIndex) => {
            const nextIdx = orderedIndices[(orderIndex + 1) % orderedIndices.length];
            radiusSum += projections[idx].packingRadiusPixels;
            gapSum += getMinGap(projections[idx].radiusPixels, projections[nextIdx].radiusPixels);
          });

          const requiredTotal = curveSafety * ((radiusSum * 2) + gapSum);
          if (requiredTotal > circumference + 0.5 && layoutScale > minimumLayoutScale + 0.001) {
            const targetRadiusSum = Math.max(1, ((circumference / curveSafety) - gapSum) * 0.5);
            layoutScale = Math.max(
              minimumLayoutScale,
              layoutScale * THREE.MathUtils.clamp(targetRadiusSum / Math.max(1, radiusSum), 0.65, 1)
            );
            continue;
          }

          const pairDistances = orderedIndices.map((idx, orderIndex) => {
            const nextIdx = orderedIndices[(orderIndex + 1) % orderedIndices.length];
            return curveSafety * (
              projections[idx].packingRadiusPixels
              + projections[nextIdx].packingRadiusPixels
              + getMinGap(projections[idx].radiusPixels, projections[nextIdx].radiusPixels)
            );
          });
          const pairDistanceTotal = pairDistances.reduce((sum, value) => sum + value, 0);
          const pairCompression = Math.min(1, circumference / Math.max(1, pairDistanceTotal));
          const remainingArc = Math.max(0, circumference - pairDistanceTotal * pairCompression);
          let runningArc = anchorArc;
          resolvedAngles[anchorIdx] = this.getOrbitAngleAtArc(runningArc);
          for (let orderIndex = 0; orderIndex < orderedIndices.length - 1; orderIndex++) {
            const idx = orderedIndices[orderIndex];
            const nextIdx = orderedIndices[orderIndex + 1];
            const authoredShare = authoredGapWeights[orderIndex] / totalAuthoredGap;
            runningArc += pairDistances[orderIndex] * pairCompression + remainingArc * authoredShare;
            resolvedAngles[nextIdx] = this.getOrbitAngleAtArc(runningArc);
          }
        }
        this.spacingSolverIterations = packingPasses;
        this.spacingFrameScale = layoutScale / Math.max(
          0.0001,
          (this.objectLayoutScale || 1) * (this.spacingPackScale || 1)
        );

        // Restore authored phase angles continuously across the tablet→desktop
        // blend, rather than switching an entire packed arrangement at 1280.
        const authoredPhaseBlend = this.getResponsiveLayoutProfile().tabletToDesktop;
        if (authoredPhaseBlend > 0) {
          resolvedAngles.forEach((angle, idx) => {
            const authored = time * this.bubbles[idx].orbitSpeed + this.bubbles[idx].startAngle;
            const shortestDelta = Math.atan2(Math.sin(authored - angle), Math.cos(authored - angle));
            resolvedAngles[idx] = angle + shortestDelta * authoredPhaseBlend;
          });
        }

        const finalProj = this.bubbles.map((b, idx) => projectBubble(b, idx, resolvedAngles[idx]));

        sortedProj = [...finalProj].sort((a, b) => a.normAngle - b.normAngle);
        
        for (let i = 0; i < sortedProj.length; i++) {
          const itemA = sortedProj[i];
          const itemB = sortedProj[(i + 1) % sortedProj.length];
          
          const distPixels = itemA.screenPos.distanceTo(itemB.screenPos);
          const rA = itemA.radiusPixels;
          const rB = itemB.radiusPixels;
          const minGap = getMinGap(rA, rB);
          const screenGap = distPixels - rA - rB;
          
          if (screenGap < minGap) {
            bubbleOverlappingFlags[itemA.idx] = true;
            bubbleOverlappingFlags[itemB.idx] = true;
            
          }
        }
        this._spacingSolution = {
          solvedAt: now,
          time,
          angles: [...resolvedAngles],
          layoutScale,
          overlappingFlags: [...bubbleOverlappingFlags]
        };
      }
      
      this.sortedProj = sortedProj;
      this.bubbleOverlappingFlags = bubbleOverlappingFlags;

      // 1. Calculate slow mathematically constrained, tilted 3D elliptical orbits
      this.bubbles.forEach((b, idx) => {
        // Retrieve wordmark dimensions
        const wordmarkWidth = this.logoWidth || this.basePlaneWidth || 5.0;
        const wordmarkHeight = this.logoHeight || (this.basePlaneWidth / 3.125) || 1.6;

        // Center Offset: Left-Front offset in camera space
        const centerOffset = cameraRight.clone()
          .multiplyScalar(-wordmarkWidth * 0.04)
          .add(cameraTowardViewer.clone().multiplyScalar(wordmarkWidth * 0.05));

        const orbitCenter = wordmarkCenter.clone().add(centerOffset);

        // Constrained Anchor Offsets (restricted to maximum per-object offsets)
        const maxOffsetX = wordmarkWidth * 0.08;
        const maxOffsetY = wordmarkHeight * 0.16;
        const maxOffsetZ = wordmarkWidth * 0.03;

        const clampedAnchorX = Math.max(-maxOffsetX, Math.min(maxOffsetX, b.anchorX || 0));
        const clampedAnchorY = Math.max(-maxOffsetY, Math.min(maxOffsetY, b.anchorY || 0));
        const clampedAnchorZ = Math.max(-maxOffsetZ, Math.min(maxOffsetZ, b.anchorZ || 0));
        const anchorOffset = new THREE.Vector3(clampedAnchorX, clampedAnchorY, clampedAnchorZ);

        const radiusMajor = wordmarkWidth * (b.orbitRadiusXMult || 0.46);
        const radiusMinorY = wordmarkHeight * (b.orbitRadiusYMult || 0.65);
        const radiusMinor = wordmarkWidth * (b.orbitRadiusZMult || 0.08); // depthAmplitude

        let localOrbitX = 0;
        let localOrbitY = 0;
        let localOrbitZ = 0;
        let depthNorm = 0;
        let depthScale = 1.0;

        if (
          this.debugMode !== 'static-composition' &&
          this.debugMode !== 'prototype-3' &&
          this.debugMode !== 'object-style-sheet' &&
          this.debugMode !== 'mozcare-transmission-lab' &&
          this.debugMode !== 'starburst-tentacle-lab' &&
          this.debugMode !== 'two-orb-interaction-lab' &&
          this.debugMode !== 'grid-mozcare-lab'
        ) {
          const idleTheta = useSpacingSolver ? resolvedAngles[idx] : (time * b.orbitSpeed + b.startAngle);
          const sample = this.getOrbitPoint(idleTheta);

          // No per-object anchor or scroll offset may move an idle orb away from
          // the shared orbit. Interaction is applied only while a pointer drag is
          // active, then the spring settles back to this exact target.
          const orbitTarget = sample.position.clone();
          const introTransform = this.getIntroTransform(orbitTarget, idleTheta, idx, now);
          if (this.orbExitProgress > 0.0001 && b.exitBaseAngle === undefined) {
            b.exitBaseAngle = idleTheta;
          } else if (this.orbExitProgress <= 0.0001) {
            b.exitBaseAngle = undefined;
          }
          const exitBaseAngle = b.exitBaseAngle === undefined ? idleTheta : b.exitBaseAngle;
          const exitTransform = this.getExitTransform(
            orbitTarget,
            exitBaseAngle,
            this.orbExitProgress
          );
          const isExiting = this.orbExitProgress > 0.0001;
          const totalTarget = isExiting
            ? exitTransform.position.clone()
            : introTransform.position.clone();
          const motionAngle = isExiting ? exitTransform.angle : idleTheta;
          depthNorm = isExiting ? exitTransform.depthNorm : sample.depthNorm;
          b.currentAngle = motionAngle; // store the actual transition/orbit angle for debug telemetry

          localOrbitX = totalTarget.x;
          localOrbitY = totalTarget.y;
          localOrbitZ = totalTarget.z;

          b.orbitCenter = wordmarkCenter.clone();
          b.anchorOffset = new THREE.Vector3(0, 0, 0);
          b.orbitOffset = new THREE.Vector3(localOrbitX, localOrbitY, localOrbitZ).sub(b.orbitCenter).sub(b.anchorOffset);
          b.pureTarget = orbitTarget;
          b.introScale = introTransform.scale;
          b.depthNorm = depthNorm; // store for telemetry

          // Side points shrink while the front and rear arcs remain full-size.
          depthScale = this.getOrbitScale(motionAngle);
          
          const presentationScale = this.getPresentationScale(depthNorm);
          b.group.scale.setScalar(
            b.baseScale
            * depthScale
            * layoutScale
            * introTransform.scale
            * presentationScale
            * exitTransform.scale
          );
          b.scaleByPhase = b.baseScale * depthScale * layoutScale; // store for telemetry

          // Dynamic occlusion & opacity & renderOrder
          // Near the logo plane, use the side of the ellipse as a deterministic
          // tie-breaker: the right/H crossing is front, the left crossing is back.
          const isForeground = depthNorm > 0.04 || (
            Math.abs(depthNorm) <= 0.04 && Math.cos(motionAngle) >= 0
          );
          b.isForeground = isForeground;
          b.group.renderOrder = isForeground ? 3 : 1;

          // Rear objects stay full-size and materially legible. Occlusion provides
          // the depth cue; only a restrained tonal falloff distinguishes the rear.
          let brightnessFactor = 1.0;
          let roughnessFactor = 0.0;
          let opacityFactor = 1.0;

          if (depthNorm < 0.0) {
            opacityFactor = THREE.MathUtils.lerp(1.0, 0.88, -depthNorm);
            brightnessFactor = THREE.MathUtils.lerp(1.0, 0.8, -depthNorm);
            roughnessFactor = -depthNorm * 0.18;
          }

          (b.renderables || []).forEach((child) => {
            if (child.isMesh || child.isPoints || child.isLine) {
              const localRenderOrder = child.userData.mbsLocalRenderOrder
                || (child.material.userData && child.material.userData.mbsLocalRenderOrder)
                || 0;
              child.renderOrder = (isForeground ? 300 : 100) + localRenderOrder;
              if (child.material) {
                const forceOpaque = child.material.userData.mbsForceOpaque === true;
                child.material.transparent = !forceOpaque;
                if (child.material.userData.mbsBaseOpacity === undefined) {
                  child.material.userData.mbsBaseOpacity = child.material.opacity;
                }
                child.material.opacity = forceOpaque
                  ? 1
                  : child.material.userData.mbsBaseOpacity
                    * opacityFactor
                    * introTransform.opacity
                    * exitTransform.opacity;

                if (child.material.uniforms && child.material.uniforms.depthFade) {
                  child.material.uniforms.depthFade.value = brightnessFactor;
                }
                if (child.material.uniforms && child.material.uniforms.uDepthFade) {
                  child.material.uniforms.uDepthFade.value = brightnessFactor;
                }

                // Apply brightness dimming
                if (child.material.color) {
                  if (!child.material.baseColor) {
                    child.material.baseColor = child.material.color.clone();
                  }
                  child.material.color.copy(child.material.baseColor).multiplyScalar(brightnessFactor);
                }

                // Reduce highlights and contrast for standard materials
                if (child.material.roughness !== undefined) {
                  if (child.material.baseRoughness === undefined) {
                    child.material.baseRoughness = child.material.roughness;
                  }
                  child.material.roughness = THREE.MathUtils.lerp(
                    child.material.baseRoughness,
                    Math.min(1, child.material.baseRoughness + 0.18),
                    roughnessFactor
                  );
                }
              }
            }
          });

          // Update helper orbit paths dynamically based on camera space orientation
          if (b.pathLine) {
            const positions = b.pathLine.geometry.attributes.position.array;
            const segments = 128;
            for (let i = 0; i <= segments; i++) {
              const pTheta = (i / segments) * Math.PI * 2;
              const pSample = this.getOrbitPoint(pTheta);
              
              let pt;
              if (this.debugMode === 'single-orbit-layout' || this.debugMode === 'single-orbit-belt' || this.debugMode === 'depth-staging' || this.debugMode === 'depth-occlusion') {
                pt = pSample.position.clone();
              } else {
                const smallOffset = b.smallAnchorOffset || new THREE.Vector3(clampedAnchorX, clampedAnchorY, clampedAnchorZ);
                pt = pSample.position.clone().add(smallOffset);
              }

              positions[i * 3] = pt.x;
              positions[i * 3 + 1] = pt.y;
              positions[i * 3 + 2] = pt.z;
            }
            b.pathLine.geometry.attributes.position.needsUpdate = true;
          }
        } else {
          b.group.scale.setScalar(b.baseScale || 1.0);
          if (
            this.debugMode === 'mozcare-transmission-lab' ||
            this.debugMode === 'starburst-tentacle-lab' ||
            this.debugMode === 'two-orb-interaction-lab' ||
            this.debugMode === 'grid-mozcare-lab'
          ) {
            const isPairedLab = (
              this.debugMode === 'two-orb-interaction-lab' ||
              this.debugMode === 'grid-mozcare-lab'
            );
            localOrbitX = isPairedLab && !this.isMobile ? (idx === 0 ? -1.35 : 1.35) : 0;
            localOrbitY = isPairedLab && this.isMobile ? (idx === 0 ? 0.82 : -0.82) : 0;
            localOrbitZ = b.baseRadius * 1.38;
            b.depthNorm = 1;
            b.scaleByPhase = b.baseScale || 1;
            b.isForeground = true;
            b.orbitCenter = wordmarkCenter.clone();
            b.anchorOffset = new THREE.Vector3(0, 0, 0);
            b.orbitOffset = new THREE.Vector3(localOrbitX, localOrbitY, localOrbitZ);
            b.pureTarget = new THREE.Vector3(localOrbitX, localOrbitY, localOrbitZ);
            b.group.renderOrder = 300;

            b.group.traverse((child) => {
              if ((!child.isMesh && !child.isPoints && !child.isLine) || !child.material) return;
              const localRenderOrder = child.userData.mbsLocalRenderOrder
                || (child.material.userData && child.material.userData.mbsLocalRenderOrder)
                || 0;
              child.renderOrder = 300 + localRenderOrder;
              if (child.material.uniforms && child.material.uniforms.uDepthFade) {
                child.material.uniforms.uDepthFade.value = 1;
              }
            });
          } else if (this.debugMode === 'prototype-3' || this.debugMode === 'object-style-sheet') {
            const col = idx % 4;
            const row = Math.floor(idx / 4);
            const colSpacing = (this.frustumWidth * 0.8) / 3.0;
            const rowSpacing = (this.frustumHeight * 0.7) / 2.0;
            localOrbitX = (col - 1.5) * colSpacing;
            localOrbitY = (1.0 - row) * rowSpacing;
            localOrbitZ = 0.0;
            // A material sheet must composite the same internal layers as a
            // foreground orb. Default Three.js sorting can otherwise paint an
            // inner core over its glass shell and misrepresent the material.
            b.depthNorm = 1;
            b.isForeground = true;
            b.group.renderOrder = 300;
            (b.renderables || []).forEach((child) => {
              if (!child.material) return;
              const localRenderOrder = child.userData.mbsLocalRenderOrder
                ?? child.material.userData?.mbsLocalRenderOrder
                ?? 0;
              child.renderOrder = 300 + localRenderOrder;
              if (child.material.uniforms?.depthFade) child.material.uniforms.depthFade.value = 1;
              if (child.material.uniforms?.uDepthFade) child.material.uniforms.uDepthFade.value = 1;
            });
          } else {
            localOrbitX = b.anchorX;
            localOrbitY = b.anchorY;
            localOrbitZ = b.anchorZ;
          }
        }

        // Apply Scroll Parallax displacement
        const scrollOffsetY = 0;
        const scrollOffsetX = 0;

        b.targetPos.set(
          localOrbitX + scrollOffsetX,
          localOrbitY + scrollOffsetY,
          localOrbitZ
        );

        // Reset forces
        b.force.set(0, 0, 0);
      });

      this.stepInteractionPhysics(dt, now);
      this.updateContactDepthLayers();

      // Update static slot marker positions relative to camera basis/logo center in single-orbit-belt mode
      if (this.slotMarkers && (this.debugMode === 'single-orbit-layout' || this.debugMode === 'single-orbit-belt' || this.debugMode === 'depth-staging' || this.debugMode === 'depth-occlusion')) {
        const slotPhaseDeg = [96, 64, 31, 357, 324, 294, 264, 228, 193, 162, 132];
        this.slotMarkers.forEach((marker, i) => {
          const theta = THREE.MathUtils.degToRad(slotPhaseDeg[i]);
          const sample = this.getOrbitPoint(theta);
          marker.position.copy(sample.position);
        });
      }

      if (this.touchFocusBubble && now >= this.touchFocusUntil) {
        this.touchFocusBubble.interactionState.selectedTarget = 0;
        this.touchFocusBubble = null;
        this.touchFocusUntil = 0;
      }

      // Reproject after orbit + physics, including a stationary cursor while
      // a rotating orb moves underneath it. The cached vectors are reused.
      this.updateLocalPointerHits();

      // Local mesh updates (canvas text rendering, spike scaling, etc.) and marker updates
      this.bubbles.forEach((b) => {
        const interaction = b.interactionState;
        interaction.hover += (interaction.hoverTarget - interaction.hover) * Math.min(1, dt * 14);
        interaction.selected += (interaction.selectedTarget - interaction.selected) * Math.min(1, dt * 12);
        const focus = Math.max(interaction.hover, interaction.selected);
        (b.renderables || []).forEach((child) => {
          if ((!child.isMesh && !child.isPoints && !child.isLine) || !child.material || !child.material.uniforms) return;
          if (child.material.uniforms.hover) child.material.uniforms.hover.value = focus;
          if (child.material.uniforms.time) child.material.uniforms.time.value = time;
        });
        if (b.haloMesh) {
          b.haloMesh.scale.setScalar(1 + focus * 0.018);
        }
        if (b.update) b.update(time, focus, interaction.pointerX, interaction.pointerY,
          interaction.hasPointerHit ? interaction.localPointerHit : null);
        if (b.centerMarker) {
          b.centerMarker.position.copy(b.group.position);
        }
      });
      // Update visible debug dashboard parameters (throttled to every 10 frames to optimize main thread layout performance)
      if (this.debugDashboard) {
        this.dashboardFrameCount = (this.dashboardFrameCount || 0) + 1;
        if (this.dashboardFrameCount % 10 === 0) {
          this.updateDebugDashboard();
        }
      }

      // Project 3D coordinate parameters dynamically to HTML DOM overlays if object-labels mode is enabled
      const debugLabelsContainer = document.getElementById('mbs-debug-labels');
      if (debugLabelsContainer && (this.debugMode === 'object-labels' || this.debugMode === 'orbit-paths' || this.debugMode === 'prototype-1' || this.debugMode === 'prototype-2' || this.debugMode === 'prototype-3' || this.debugMode === 'object-style-sheet' || this.debugMode === 'physics-bounds' || this.debugMode === 'prototype-orbit' || this.debugMode === 'prototype-orbit-angle' || this.debugMode === 'orbit-angle' || this.debugMode === 'orbit-calibration' || this.debugMode === 'orbit-members' || this.debugMode === 'orbit-belt-calibration' || this.debugMode === 'scale-check' || this.debugMode === 'orbit-no-physics' || this.debugMode === 'orbit-system-reset' || this.debugMode === 'orbit-minimal' || this.debugMode === 'orbit-trace' || this.debugMode === 'orbit-scale-curve' || this.debugMode === 'single-orbit-belt' || this.debugMode === 'depth-staging' || this.debugMode === 'single-orbit-layout' || this.debugMode === 'depth-occlusion' || this.debugMode === 'orbit-depth-phase' || this.debugMode === 'orbit-depth-phase-12' || this.debugMode === 'orbit-spacing' || this.debugMode === 'orbit-spacing-depth')) {
        const canvas = this.renderer.domElement;
        const tempV = new THREE.Vector3();
        
        // Retrieve wordmark dimensions for label calculations
        const wordmarkWidth = this.logoWidth || this.basePlaneWidth || 5.0;
        const wordmarkHeight = this.logoHeight || (this.basePlaneWidth / 3.125) || 1.6;
        const inclination = THREE.MathUtils.degToRad(28);

        this.bubbles.forEach((b, idx) => {
          const div = document.getElementById(`mbs-debug-label-${idx}`);
          if (div) {
            tempV.copy(b.group.position);
            tempV.project(this.camera);

            const x = (tempV.x * 0.5 + 0.5) * canvas.clientWidth;
            const y = (tempV.y * -0.5 + 0.5) * canvas.clientHeight;

            if (!this._loggedLabelCoords) {
               this._loggedLabelCoords = true;
               console.log("[BMS Coords Debug] First label coords:", b.title, "x:", x, "y:", y, "clientWidth:", canvas.clientWidth, "clientHeight:", canvas.clientHeight);
            }

            // Update label innerHTML with parameters
            const radiusInPixels = b.baseRadius * (canvas.clientHeight / (this.frustumHeight || 6.0));
            div.style.left = `${x}px`;
            div.style.top = `${y - radiusInPixels - 16}px`; // position 16px above the top edge of the orb
            
            if (this.debugMode === 'physics-bounds') {
              const diam = radiusInPixels * 2;
              div.innerHTML = `
                <div style="position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: ${diam}px; height: ${diam}px; border: 1.5px dashed ${b.isCollidingBoundary ? '#ff2bed' : '#00ffcc'}; border-radius: 50%; box-shadow: 0 0 10px ${b.isCollidingBoundary ? 'rgba(255, 43, 237, 0.4)' : 'rgba(0, 255, 204, 0.2)'}; pointer-events: none; box-sizing: border-box;"></div>
                <div style="font-family: monospace; font-size: 10px; background: rgba(8, 12, 22, 0.95); color: #00ffcc; border: 1px solid #2bb9ed; padding: 6px 8px; border-radius: 4px; pointer-events: none; white-space: nowrap; line-height: 1.35; box-shadow: 0 0 10px rgba(43, 185, 237, 0.3); text-align: center;">
                  <strong style="color: #ffffff; font-size: 11px;">${b.title}</strong><br/>
                  <span style="color: #888888; font-size: 9px;">Vel: ${b.velocity.length().toFixed(2)} | Mass: ${b.mass.toFixed(1)}</span><br/>
                  <span style="color: ${b.isCollidingBoundary ? '#ff2bed' : '#00ffcc'}; font-size: 9px; font-weight: bold;">
                    Status: ${b.isCollidingBoundary ? 'COLLIDING BOUND' : 'INSIDE BOUNDS'}
                  </span>
                </div>
              `;
            } else if (this.debugMode === 'prototype-3' || this.debugMode === 'object-style-sheet') {
              div.innerHTML = `
                <div style="font-family: monospace; font-size: 10px; background: rgba(8, 12, 22, 0.95); color: #00ffcc; border: 1px solid #2bb9ed; padding: 6px 8px; border-radius: 4px; pointer-events: none; white-space: nowrap; line-height: 1.35; box-shadow: 0 0 10px rgba(43, 185, 237, 0.3); text-align: center;">
                  <strong style="color: #ffffff; font-size: 11px;">${b.title}</strong><br/>
                  <span style="color: #888888; font-size: 9px;">Type: ${b.orb_type}</span><br/>
                  <span style="color: #2bb9ed; font-size: 9px; max-width: 180px; display: inline-block; white-space: normal;">${b.description}</span>
                </div>
              `;
            } else if (this.debugMode === 'orbit-members') {
              const orbWorldPos = new THREE.Vector3();
              b.group.getWorldPosition(orbWorldPos);
              const orbCamPos = orbWorldPos.applyMatrix4(this.camera.matrixWorldInverse);
              const logoCamPosLoc = new THREE.Vector3(0, 0, 0).applyMatrix4(this.camera.matrixWorldInverse);
              const isForeground = (orbCamPos.z - logoCamPosLoc.z) > -b.baseRadius;

              const radiusMajor = wordmarkWidth * (b.orbitRadiusXMult || 0.43);
              const radiusMinor = wordmarkWidth * (b.orbitRadiusZMult || 0.15) / Math.sin(inclination);
              const zAmp = radiusMinor * Math.sin(inclination);
              const orbitEnabled = b.orbitEnabled !== false;

              div.style.display = 'block';

              div.innerHTML = `
                <div style="font-family: monospace; font-size: 10px; background: rgba(8, 12, 22, 0.95); color: #00ffcc; border: 1px solid #2bb9ed; padding: 6px 8px; border-radius: 4px; pointer-events: none; white-space: nowrap; line-height: 1.35; box-shadow: 0 0 10px rgba(43, 185, 237, 0.3);">
                  <strong style="color: #ffffff; font-size: 11px;">${b.title}</strong><br/>
                  OrbitEnabled: ${orbitEnabled ? '<span style="color:#00ffcc; font-weight:bold;">TRUE</span>' : '<span style="color:#ff3333; font-weight:bold;">FALSE</span>'}<br/>
                  Pos: [${b.group.position.x.toFixed(1)}, ${b.group.position.y.toFixed(1)}, ${b.group.position.z.toFixed(1)}]<br/>
                  Phase: ${b.startAngle.toFixed(2)} rad<br/>
                  Speed: ${b.orbitSpeed.toFixed(3)} rad/s<br/>
                  RadiusX: ${radiusMajor.toFixed(1)}<br/>
                  RadiusY: ${radiusMinor.toFixed(1)}<br/>
                  zAmplitude: ${zAmp.toFixed(2)}<br/>
                  ScreenRoll: 15°<br/>
                  DepthInclination: 30°<br/>
                  State: ${isForeground ? '<span style="color:#00ff00;">FRONT</span>' : '<span style="color:#ffaa00;">BACK</span>'}
                </div>
              `;
            } else if (this.debugMode === 'single-orbit-belt' || this.debugMode === 'single-orbit-layout') {
              div.style.display = 'block';
              const theta = b.currentAngle || 0;
              const expected = this.getOrbitPoint(theta).position;
              const actual = b.group.position;
              const distanceToOrbitCurve = actual.distanceTo(expected);
              const depthNormVal = b.depthNorm !== undefined ? b.depthNorm : 0;
              const scaleVal = b.scaleByPhase !== undefined ? b.scaleByPhase : 1.0;
              const isLayout = this.debugMode === 'single-orbit-layout';
              
              const isOverwritten = false; 

              let stateStr = '<span style="color:#ffaa00;">SIDE</span>';
              if (depthNormVal > 0.25) {
                stateStr = '<span style="color:#00ff00; font-weight:bold;">FRONT</span>';
              } else if (depthNormVal < -0.25) {
                stateStr = '<span style="color:#0088ff; font-weight:bold;">BACK</span>';
              }

              const hasSharedCenter = b.orbitCenter && (
                isLayout
                  ? (Math.abs(b.orbitCenter.x - (wordmarkWidth * 0.04)) < 0.001 && Math.abs(b.orbitCenter.y - (wordmarkHeight * 0.08)) < 0.001 && Math.abs(b.orbitCenter.z - (-0.25)) < 0.001)
                  : (Math.abs(b.orbitCenter.x) < 0.001 && Math.abs(b.orbitCenter.y) < 0.001 && Math.abs(b.orbitCenter.z) < 0.001)
              );
              const distColor = distanceToOrbitCurve > (wordmarkWidth * 0.02) ? '#ff3366' : '#00ffcc';
              const centerStr = isLayout
                ? `[${(wordmarkWidth * 0.04).toFixed(3)}, ${(wordmarkHeight * 0.08).toFixed(3)}, -0.250]`
                : `[0.000, 0.000, 0.000]`;

              div.innerHTML = `
                <div style="font-family: monospace; font-size: 9px; background: rgba(8, 12, 22, 0.95); color: #00ffcc; border: 1px solid #2bb9ed; padding: 6px 8px; border-radius: 4px; pointer-events: none; white-space: nowrap; line-height: 1.35; box-shadow: 0 0 10px rgba(43, 185, 237, 0.3);">
                  <strong style="color: #ffffff; font-size: 10px;">${b.title} (${isLayout ? 'Layout' : 'Belt'})</strong><br/>
                  phase: ${b.startAngle.toFixed(3)} rad<br/>
                  theta: ${theta.toFixed(3)} rad<br/>
                  orbitCenter: ${centerStr}<br/>
                  pos: [${actual.x.toFixed(2)}, ${actual.y.toFixed(2)}, ${actual.z.toFixed(2)}]<br/>
                  depthNorm: ${depthNormVal.toFixed(3)}<br/>
                  scaleByPhase: ${scaleVal.toFixed(3)}x<br/>
                  state: ${stateStr}<br/>
                  distanceToOrbitCurve: <span class="${distanceToOrbitCurve > (wordmarkWidth * 0.02) ? 'red' : ''}" style="color:${distColor}; font-weight:bold;">${distanceToOrbitCurve.toFixed(5)}</span><br/>
                  finalPositionWasOverwritten: <span style="color:#00ffcc; font-weight:bold;">false</span><br/>
                  sharedOrbitCenter: <span style="color:${hasSharedCenter ? '#00ffcc' : '#ff3366'}; font-weight:bold;">${hasSharedCenter ? 'true' : 'false'}</span><br/>
                  orbitEnabled: <span style="color:#00ffcc; font-weight:bold;">true</span>
                </div>
              `;
            } else if (this.debugMode === 'depth-staging') {
              div.style.display = 'block';
              const theta = b.currentAngle || 0;
              const actual = b.group.position;
              const depthNormVal = b.depthNorm !== undefined ? b.depthNorm : 0;
              const scaleVal = b.scaleByPhase !== undefined ? b.scaleByPhase : 1.0;
              
              let stateStr = '<span style="color:#ffaa00;">SIDE</span>';
              if (depthNormVal > 0.25) {
                stateStr = '<span style="color:#00ff00; font-weight:bold;">FRONT</span>';
              } else if (depthNormVal < -0.25) {
                stateStr = '<span style="color:#0088ff; font-weight:bold;">BACK</span>';
              }

              const wordmarkLocation = actual.z > -0.1 ? '<span style="color:#00ff00; font-weight:bold;">FRONT (In front of wordmark)</span>' : '<span style="color:#ff8800; font-weight:bold;">BACK (Behind wordmark)</span>';

              div.innerHTML = `
                <div style="font-family: monospace; font-size: 9px; background: rgba(8, 12, 22, 0.95); color: #00ffcc; border: 1px solid #2bb9ed; padding: 6px 8px; border-radius: 4px; pointer-events: none; white-space: nowrap; line-height: 1.35; box-shadow: 0 0 10px rgba(43, 185, 237, 0.3);">
                  <strong style="color: #ffffff; font-size: 10px;">${b.title} (Depth Staging)</strong><br/>
                  phase: ${b.startAngle.toFixed(3)} rad<br/>
                  theta: ${theta.toFixed(3)} rad<br/>
                  pos: [${actual.x.toFixed(2)}, ${actual.y.toFixed(2)}, ${actual.z.toFixed(2)}]<br/>
                  depthNorm: ${depthNormVal.toFixed(3)}<br/>
                  scaleByPhase: ${scaleVal.toFixed(3)}x<br/>
                  state: ${stateStr}<br/>
                  wordmarkLocation: ${wordmarkLocation}<br/>
                  orbitPlaneDirection: X+, Y+, Z- tilted<br/>
                  depthAmplitude: ${(wordmarkWidth * 0.17).toFixed(3)}<br/>
                  screenRollDeg: 12°
                </div>
              `;
            } else if (this.debugMode === 'depth-occlusion') {
              div.style.display = 'block';
              const depthNormVal = b.depthNorm !== undefined ? b.depthNorm : 0;
              const scaleVal = b.scaleByPhase !== undefined ? b.scaleByPhase : 1.0;
              const zDepth = b.group.position.z;
              const renderOrder = b.group.renderOrder !== undefined ? b.group.renderOrder : 0;
              const occluded = depthNormVal <= 0.25;

              div.innerHTML = `
                <div style="font-family: monospace; font-size: 9px; background: rgba(8, 12, 22, 0.95); color: #00ffcc; border: 1px solid #2bb9ed; padding: 6px 8px; border-radius: 4px; pointer-events: none; white-space: nowrap; line-height: 1.35; box-shadow: 0 0 10px rgba(43, 185, 237, 0.3);">
                  <strong style="color: #ffffff; font-size: 10px;">${b.title} (Depth Occlusion)</strong><br/>
                  depthNorm: ${depthNormVal.toFixed(3)}<br/>
                  scaleByPhase: ${scaleVal.toFixed(3)}x<br/>
                  z-depth: ${zDepth.toFixed(3)}<br/>
                  renderOrder: ${renderOrder}<br/>
                  occluded: <span style="color:${occluded ? '#ffaa00' : '#00ff00'}; font-weight:bold;">${occluded}</span>
                </div>
              `;
            } else if (this.debugMode === 'orbit-scale-curve') {
              div.style.display = 'block';
              const orbitCenter = b.orbitCenter || new THREE.Vector3(0, 0, 0);
              const depthNormVal = b.depthNorm !== undefined ? b.depthNorm : 0;
              const scaleVal = b.scaleByPhase !== undefined ? b.scaleByPhase : 1.0;
              const pureTarget = b.pureTarget || b.group.position;
              
              let stateStr = '<span style="color:#ffaa00;">SIDE</span>';
              if (depthNormVal > 0.25) {
                stateStr = '<span style="color:#00ff00; font-weight:bold;">FRONT</span>';
              } else if (depthNormVal < -0.25) {
                stateStr = '<span style="color:#0088ff; font-weight:bold;">BACK</span>';
              }
              
              const isOverwritten = b.group.position.distanceTo(pureTarget) > 0.05;

              div.innerHTML = `
                <div style="font-family: monospace; font-size: 9px; background: rgba(8, 12, 22, 0.95); color: #00ffcc; border: 1px solid #2bb9ed; padding: 6px 8px; border-radius: 4px; pointer-events: none; white-space: nowrap; line-height: 1.35; box-shadow: 0 0 10px rgba(43, 185, 237, 0.3);">
                  <strong style="color: #ffffff; font-size: 10px;">${b.title} (Scale Curve)</strong><br/>
                  Shared Center: [${orbitCenter.x.toFixed(2)}, ${orbitCenter.y.toFixed(2)}, ${orbitCenter.z.toFixed(2)}]<br/>
                  Roll / Period: 12° / 60s<br/>
                  Radii / DepthAmp: [${radiusMajor.toFixed(2)}, ${radiusMinorY.toFixed(2)}, ${radiusMinor.toFixed(2)}]<br/>
                  Phase / depthNorm: [${(b.currentAngle || 0).toFixed(2)} rad, ${depthNormVal.toFixed(2)}]<br/>
                  ScaleByPhase: ${scaleVal.toFixed(3)}x<br/>
                  FinalPos: [${b.group.position.x.toFixed(2)}, ${b.group.position.y.toFixed(2)}, ${b.group.position.z.toFixed(2)}]<br/>
                  State: ${stateStr}<br/>
                  SharedCenterUsed: <span style="color:#00ffcc; font-weight:bold;">TRUE</span><br/>
                  Overwritten: <span style="color:${isOverwritten ? '#ff3366' : '#00ffcc'}; font-weight:bold;">${isOverwritten ? 'YES' : 'NO'}</span>
                </div>
              `;
            } else if (this.debugMode === 'orbit-minimal') {
              div.style.display = 'block';
              div.innerHTML = `
                <div style="font-family: monospace; font-size: 10px; background: rgba(8, 12, 22, 0.95); color: #00ffcc; border: 1px solid #2bb9ed; padding: 6px 8px; border-radius: 4px; pointer-events: none; white-space: nowrap; line-height: 1.35; box-shadow: 0 0 10px rgba(43, 185, 237, 0.3); text-align: center;">
                  <strong style="color: #ffffff; font-size: 11px;">${b.title} (Minimal)</strong><br/>
                  Pos: [${b.group.position.x.toFixed(2)}, ${b.group.position.y.toFixed(2)}, ${b.group.position.z.toFixed(2)}]<br/>
                  Shared Center: [0.00, 0.00, 0.00]<br/>
                  Orbit Path: Unified Shallow Belt
                </div>
              `;
            } else if (this.debugMode === 'orbit-depth-phase' || this.debugMode === 'orbit-depth-phase-12') {
              div.style.display = 'block';
              const theta = b.currentAngle || 0;
              const depthNormVal = b.depthNorm !== undefined ? b.depthNorm : 0;
              const scaleVal = b.scaleByPhase !== undefined ? b.scaleByPhase : 1.0;
              const actual = b.group.position;
              
              let phaseStr = '<span style="color:#ffaa00;">SIDE</span>';
              if (depthNormVal > 0.25) {
                phaseStr = '<span style="color:#00ff00; font-weight:bold;">FRONT</span>';
              } else if (depthNormVal < -0.25) {
                phaseStr = '<span style="color:#0088ff; font-weight:bold;">BACK</span>';
              } else {
                const center = b.orbitCenter || new THREE.Vector3(0, 0, 0);
                if (actual.x >= center.x) {
                  phaseStr = '<span style="color:#ffff00; font-weight:bold;">SIDE_RIGHT</span>';
                } else {
                  phaseStr = '<span style="color:#ff8800; font-weight:bold;">SIDE_LEFT</span>';
                }
              }

              const wordmarkLocation = actual.z > -0.1 ? '<span style="color:#00ff00; font-weight:bold;">FRONT (In front of wordmark)</span>' : '<span style="color:#ff8800; font-weight:bold;">BACK (Behind wordmark)</span>';
              const isForeground = depthNormVal > 0.25;
              const renderOrder = b.group.renderOrder !== undefined ? b.group.renderOrder : (isForeground ? 3 : 1);
              
              let materialOpacity = 0.85;
              b.group.traverse((child) => {
                if (child.isMesh && child.material) {
                  materialOpacity = child.material.opacity;
                }
              });

              div.innerHTML = `
                <div style="font-family: monospace; font-size: 9px; background: rgba(8, 12, 22, 0.95); color: #00ffcc; border: 1px solid #2bb9ed; padding: 6px 8px; border-radius: 4px; pointer-events: none; white-space: nowrap; line-height: 1.35; box-shadow: 0 0 10px rgba(43, 185, 237, 0.3);">
                  <strong style="color: #ffffff; font-size: 10px;">${b.title} (${this.debugMode === 'orbit-depth-phase' ? 'Depth Phase' : '12-Phase'})</strong><br/>
                  target theta: ${theta.toFixed(3)} rad<br/>
                  depthNorm: ${depthNormVal.toFixed(3)}<br/>
                  scaleByPhase: ${scaleVal.toFixed(3)}x<br/>
                  depth phase: ${phaseStr}<br/>
                  wordmarkLocation: ${wordmarkLocation}<br/>
                  isForeground: ${isForeground}<br/>
                  renderOrder: ${renderOrder}<br/>
                  opacity: ${materialOpacity.toFixed(3)}
                </div>
              `;
            } else if (this.debugMode === 'orbit-trace') {
              const pureTarget = b.pureTarget || b.group.position;
              const orbitCenter = b.orbitCenter || new THREE.Vector3(0, 0, 0);
              const anchorOffset = b.anchorOffset || new THREE.Vector3(0, 0, 0);
              const orbitOffset = b.orbitOffset || new THREE.Vector3(0, 0, 0);
              
              // Physics displacement
              const physicsOffset = new THREE.Vector3().subVectors(b.group.position, b.targetPos || b.group.position);
              
              // Overwritten check: does final position deviate from pure mathematical orbit target?
              const isOverwritten = b.group.position.distanceTo(pureTarget) > 0.05;
              const borderColor = isOverwritten ? '#ff3366' : '#2bb9ed';
              const titleColor = isOverwritten ? '#ff3366' : '#ffffff';
              
              div.style.display = 'block';
              div.innerHTML = `
                <div style="font-family: monospace; font-size: 9px; background: rgba(8, 12, 22, 0.95); color: #00ffcc; border: 1px solid ${borderColor}; padding: 6px 8px; border-radius: 4px; pointer-events: none; white-space: nowrap; line-height: 1.35; box-shadow: 0 0 10px ${isOverwritten ? 'rgba(255, 51, 102, 0.4)' : 'rgba(43, 185, 237, 0.3)'};">
                  <strong style="color: ${titleColor}; font-size: 10px;">${b.title}</strong><br/>
                  Center: [${orbitCenter.x.toFixed(2)}, ${orbitCenter.y.toFixed(2)}, ${orbitCenter.z.toFixed(2)}]<br/>
                  AnchorOffset: [${anchorOffset.x.toFixed(2)}, ${anchorOffset.y.toFixed(2)}, ${anchorOffset.z.toFixed(2)}]<br/>
                  OrbitOffset: [${orbitOffset.x.toFixed(2)}, ${orbitOffset.y.toFixed(2)}, ${orbitOffset.z.toFixed(2)}]<br/>
                  PhysicsOffset: [${physicsOffset.x.toFixed(2)}, ${physicsOffset.y.toFixed(2)}, ${physicsOffset.z.toFixed(2)}]<br/>
                  FinalPos: [${b.group.position.x.toFixed(2)}, ${b.group.position.y.toFixed(2)}, ${b.group.position.z.toFixed(2)}]<br/>
                  OrbitEnabled: TRUE<br/>
                  PhysicsEnabled: TRUE<br/>
                  BoundaryEnabled: TRUE<br/>
                  Overwritten: <span style="color:${isOverwritten ? '#ff3366' : '#00ffcc'}; font-weight:bold;">${isOverwritten ? 'YES' : 'NO'}</span>
                </div>
              `;
            } else if (this.debugMode === 'orbit-belt-calibration' || this.debugMode === 'orbit-system-reset') {
              const labelSuffix = this.debugMode === 'orbit-system-reset' ? 'Reset' : 'Calibration';
              div.style.display = 'block';
              div.innerHTML = `
                <div style="font-family: monospace; font-size: 10px; background: rgba(8, 12, 22, 0.95); color: #00ffcc; border: 1px solid #2bb9ed; padding: 6px 8px; border-radius: 4px; pointer-events: none; white-space: nowrap; line-height: 1.35; box-shadow: 0 0 10px rgba(43, 185, 237, 0.3); text-align: center;">
                  <strong style="color: #ffffff; font-size: 11px;">${b.title} (${labelSuffix})</strong><br/>
                  Pos: [${b.group.position.x.toFixed(2)}, ${b.group.position.y.toFixed(2)}, ${b.group.position.z.toFixed(2)}]<br/>
                  Screen Width/Height Ratio: 1.00<br/>
                  Orbit Path: Shared Shallow Belt
                </div>
              `;
            } else if (this.debugMode === 'scale-check') {
              const localScale = b.group.scale;
              const worldScale = new THREE.Vector3();
              b.group.getWorldScale(worldScale);
              const parentScale = b.group.parent ? b.group.parent.scale : new THREE.Vector3(1, 1, 1);
              
              const projWidth = radiusInPixels * 2;
              const projHeight = radiusInPixels * 2;
              const ratio = 1.00;

              div.style.display = 'block';
              div.innerHTML = `
                <div style="font-family: monospace; font-size: 10px; background: rgba(8, 12, 22, 0.95); color: #00ffcc; border: 1px solid #2bb9ed; padding: 6px 8px; border-radius: 4px; pointer-events: none; white-space: nowrap; line-height: 1.35; box-shadow: 0 0 10px rgba(43, 185, 237, 0.3);">
                  <strong style="color: #ffffff; font-size: 11px;">${b.title} (Scale Check)</strong><br/>
                  Local Scale: [${localScale.x.toFixed(2)}, ${localScale.y.toFixed(2)}, ${localScale.z.toFixed(2)}]<br/>
                  World Scale: [${worldScale.x.toFixed(2)}, ${worldScale.y.toFixed(2)}, ${worldScale.z.toFixed(2)}]<br/>
                  Parent Scale: [${parentScale.x.toFixed(2)}, ${parentScale.y.toFixed(2)}, ${parentScale.z.toFixed(2)}]<br/>
                  Geometry Radius: ${b.baseRadius.toFixed(2)} units<br/>
                  Projected Size: ${projWidth.toFixed(0)}x${projHeight.toFixed(0)}px<br/>
                  Aspect Ratio: ${ratio.toFixed(2)}
                </div>
              `;
            } else {
              const orbWorldPos = new THREE.Vector3();
              b.group.getWorldPosition(orbWorldPos);
              const orbCamPos = orbWorldPos.applyMatrix4(this.camera.matrixWorldInverse);
              const logoCamPosLoc = new THREE.Vector3(0, 0, 0).applyMatrix4(this.camera.matrixWorldInverse);
              const isForeground = (orbCamPos.z - logoCamPosLoc.z) > -b.baseRadius;

              // Calculate overlap with logo bounding box for occlusion check
              const logoHalfW = (this.logoWidth || this.basePlaneWidth || 5.0) / 2;
              const logoHalfH = ((this.logoHeight || (this.basePlaneWidth / 3.125)) || 1.6) / 2;
              const overlapsLogo = (Math.abs(b.group.position.x) < (logoHalfW + b.baseRadius)) && (Math.abs(b.group.position.y) < (logoHalfH + b.baseRadius));
              const isOccluded = !isForeground && overlapsLogo;
              const currentAngleDeg = (((b.currentAngle || 0) * 180 / Math.PI) % 360).toFixed(1);

              const radiusMajor = wordmarkWidth * (b.orbitRadiusXMult || 0.43);
              const radiusMinor = wordmarkWidth * (b.orbitRadiusZMult || 0.15) / Math.sin(inclination);
              const scaleVal = b.group.scale.x;

              div.style.display = 'block';

              div.innerHTML = `
                <div style="font-family: monospace; font-size: 10px; background: rgba(8, 12, 22, 0.9); color: #00ffcc; border: 1px solid #2bb9ed; padding: 4px 6px; border-radius: 4px; pointer-events: none; white-space: nowrap; line-height: 1.35; box-shadow: 0 0 10px rgba(43, 185, 237, 0.3);">
                  <strong>${b.title}</strong> (${b.orb_type})<br/>
                  World Pos: [${b.group.position.x.toFixed(1)}, ${b.group.position.y.toFixed(1)}, ${b.group.position.z.toFixed(1)}]<br/>
                  Z-depth: ${b.group.position.z.toFixed(2)}<br/>
                  Orbit-R: ${radiusMajor.toFixed(1)} x ${radiusMinor.toFixed(1)}<br/>
                  Orbit-Speed: ${b.orbitSpeed.toFixed(3)} rad/s<br/>
                  Orbit-Angle: ${currentAngleDeg}°<br/>
                  Scale: ${scaleVal.toFixed(2)}x<br/>
                  In Front of Wordmark: ${isForeground ? '<span style="color:#00ff00;">YES</span>' : '<span style="color:#ffaa00;">NO (BEHIND)</span>'}<br/>
                  Occluded by Logo: ${isOccluded ? '<span style="color:#ff3333; font-weight:bold;">YES (BEHIND MASK)</span>' : '<span style="color:#00ff00;">NO</span>'}<br/>
                  Hit: ${this.mouseHoveredBubble === b ? '<span style="color:#00ff00;">HOVERED</span>' : 'FALSE'}<br/>
                  Pointer force: ${b === this.draggedBubble ? '<span style="color:#ff007f;">ACTIVE</span>' : 'IDLE'}
                </div>
              `;
            }
          }
        });
      }

      // 5. Custom Depth Layer Rendering with Debug query overrides
      if (this.isSceneReady) {
        this.renderPasses();
      }

      if (this.visible || this.introActive || !this.isSceneReady) {
        this.startRenderLoop();
      }
    }

    renderPasses(warmupStage = '') {
      if (!this.renderer || !this.scene || !this.camera) return;

      // DEBUG MODE: dom-logo-only -> Show HTML backing and hide WebGL canvas
      if (this.debugMode === 'dom-logo-only') {
        const htmlHeading = this.container.querySelector('.mbs-canvas-heading');
        if (htmlHeading) htmlHeading.style.opacity = '1';
        this.renderer.domElement.style.display = 'none';
        return;
      } else {
        this.renderer.domElement.style.display = 'block';
      }

      const orbExitOpacity = this.orbExitOpacity ?? 1;
      const logoExitOpacity = this.logoExitOpacity ?? 1;
      const showOrbs = (
        orbExitOpacity > 0.012 &&
        this.debugMode !== 'disable-orbs' &&
        this.debugMode !== 'logo-visual-only' &&
        this.debugMode !== 'logo-depth-mask-only'
      );
      const showVisualLogo = (
        logoExitOpacity > 0.012 &&
        this.debugMode !== 'logo-depth-mask-only' &&
        this.debugMode !== 'prototype-2' &&
        this.debugMode !== 'prototype-3' &&
        this.debugMode !== 'object-style-sheet'
      );

      // Set visibility of elements in the scene
      this.bubbles.forEach(b => {
        b.group.visible = showOrbs;
      });

      const isOcclusionEnabled = (this.debugMode !== 'disable-logo-occlusion');
      if (this.textMeshPrepass) {
        this.textMeshPrepass.visible = isOcclusionEnabled && logoExitOpacity > 0.012;
        this.textMeshPrepass.renderOrder = 0;
      }

      if (this.textMeshVisual) {
        this.textMeshVisual.visible = showVisualLogo;
        if (showVisualLogo) {
           this.textMeshVisual.material.transparent = true;
           this.textMeshVisual.material.depthWrite = false; // No clipping box artifacts
           // The visual wordmark is composited in its own pass. Enabling depth
           // testing here only flips material state twice per frame.
           this.textMeshVisual.material.depthTest = false;
           this.textMeshVisual.material.alphaTest = 0.01;
           this.textMeshVisual.material.opacity = logoExitOpacity * (this.logoIntroOpacity ?? 1);
           this.textMeshVisual.renderOrder = 2;
         }
      }

      // Explicit compositing is stable for transparent WebGL objects. Three.js
      // transparency sorting alone cannot guarantee that rear orbs stay behind
      // the wordmark while front orbs pass over it.
      const backOrbs = this.bubbles.filter((bubble) => !bubble.isForeground);
      const frontOrbs = this.bubbles.filter((bubble) => bubble.isForeground);
      const setVisibleOrbs = (orbs) => {
        const visible = new Set(orbs);
        this.bubbles.forEach((bubble) => {
          bubble.group.visible = showOrbs && visible.has(bubble);
        });
      };

      if (warmupStage.startsWith('orb:')) {
        const index = Number(warmupStage.split(':')[1]);
        const bubble = this.bubbles[index];
        this.renderer.clear(true, true, true);
        if (this.textMeshPrepass) this.textMeshPrepass.visible = false;
        if (this.textMeshVisual) this.textMeshVisual.visible = false;
        setVisibleOrbs(bubble ? [bubble] : []);
        this.renderer.render(this.scene, this.camera);
        return;
      }

      if (warmupStage === 'back') {
        this.renderer.clear(true, true, true);
        this.textMeshPrepass.visible = false;
        setVisibleOrbs(backOrbs);
        if (this.textMeshVisual) this.textMeshVisual.visible = false;
        this.renderer.render(this.scene, this.camera);
        return;
      }

      if (warmupStage === 'logo') {
        setVisibleOrbs([]);
        if (this.textMeshVisual) {
          this.textMeshVisual.visible = showVisualLogo;
          this.textMeshVisual.material.depthTest = false;
          this.textMeshVisual.material.depthWrite = false;
        }
        this.renderer.clearDepth();
        this.renderer.render(this.scene, this.camera);
        return;
      }

      if (warmupStage === 'front') {
        setVisibleOrbs(frontOrbs);
        if (this.textMeshVisual) this.textMeshVisual.visible = false;
        this.renderer.clearDepth();
        this.renderer.render(this.scene, this.camera);
        return;
      }

      this.renderer.clear(true, true, true);
      this.textMeshPrepass.visible = false;

      setVisibleOrbs(backOrbs);
      if (this.textMeshVisual) this.textMeshVisual.visible = false;
      this.renderer.render(this.scene, this.camera);

      setVisibleOrbs([]);
      if (this.textMeshVisual) {
        this.textMeshVisual.visible = showVisualLogo;
        this.textMeshVisual.material.depthTest = false;
        this.textMeshVisual.material.depthWrite = false;
      }
      this.renderer.clearDepth();
      this.renderer.render(this.scene, this.camera);

      setVisibleOrbs(frontOrbs);
      if (this.textMeshVisual) this.textMeshVisual.visible = false;
      this.renderer.clearDepth();
      this.renderer.render(this.scene, this.camera);

      // Draw 2D Canvas debug overlay
      if (this.debugOverlayCanvas && this.debugOverlayCtx) {
        const ctx = this.debugOverlayCtx;
        ctx.clearRect(0, 0, this.debugOverlayCanvas.width, this.debugOverlayCanvas.height);

        if (this.debugMode === 'orbit-spacing' || this.debugMode === 'orbit-spacing-depth') {
          // Draw orbit path line on canvas
          ctx.beginPath();
          for (let i = 0; i <= 100; i++) {
            const angle = (i / 100) * Math.PI * 2;
            const pt = this.getOrbitPoint(angle);
            const tempV = new THREE.Vector3().copy(pt.position);
            tempV.project(this.camera);
            const canvas = this.renderer.domElement;
            const x = (tempV.x * 0.5 + 0.5) * canvas.clientWidth;
            const y = (-tempV.y * 0.5 + 0.5) * canvas.clientHeight;
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.strokeStyle = 'rgba(43, 185, 237, 0.35)';
          ctx.lineWidth = 1.5;
          ctx.setLineDash([5, 5]);
          ctx.stroke();
          ctx.setLineDash([]);

          if (this.sortedProj) {
            const getMinGap = (bA, bB) => {
              const isAccentA = (bA.baseScale <= 0.26);
              const isAccentB = (bB.baseScale <= 0.26);
              if (isAccentA || isAccentB) return 16;
              return 30;
            };

            for (let i = 0; i < this.sortedProj.length; i++) {
              const itemA = this.sortedProj[i];
              const itemB = this.sortedProj[(i + 1) % this.sortedProj.length];
              
              const posA = itemA.screenPos;
              const posB = itemB.screenPos;
              
              const distPixels = posA.distanceTo(posB);
              const rA = itemA.radiusPixels;
              const rB = itemB.radiusPixels;
              const minGap = getMinGap(itemA.bubble, itemB.bubble);
              const screenGap = distPixels - rA - rB;
              
              const dir = new THREE.Vector2().subVectors(posB, posA).normalize();
              const edgePtA = new THREE.Vector2().copy(posA).addScaledVector(dir, rA);
              const edgePtB = new THREE.Vector2().copy(posB).addScaledVector(dir, -rB);
              
              const hasOverlap = screenGap < minGap;
              
              ctx.beginPath();
              ctx.moveTo(edgePtA.x, edgePtA.y);
              ctx.lineTo(edgePtB.x, edgePtB.y);
              ctx.strokeStyle = hasOverlap ? 'rgba(255, 75, 75, 0.85)' : 'rgba(0, 255, 204, 0.6)';
              ctx.lineWidth = hasOverlap ? 2.0 : 1.2;
              ctx.stroke();
              
              // Draw gap value label in the middle of the line
              const mid = new THREE.Vector2().addVectors(edgePtA, edgePtB).multiplyScalar(0.5);
              ctx.font = '10px monospace';
              ctx.fillStyle = hasOverlap ? '#ff4b4b' : '#00ffcc';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'bottom';
              ctx.fillText(`gap: ${Math.round(screenGap)}px`, mid.x, mid.y - 2);
            }

            // 2. Draw bubble bounding circles, centers, and metadata
            this.sortedProj.forEach((item) => {
              const { screenPos, radiusPixels, bubble, idx, depthNorm, visualScale } = item;
              const hasOverlap = this.bubbleOverlappingFlags ? this.bubbleOverlappingFlags[idx] : false;
            
            // Bounding circle
            ctx.beginPath();
            ctx.arc(screenPos.x, screenPos.y, radiusPixels, 0, Math.PI * 2);
            ctx.strokeStyle = hasOverlap ? 'rgba(255, 75, 75, 0.9)' : 'rgba(0, 255, 204, 0.7)';
            ctx.lineWidth = hasOverlap ? 2.5 : 1.5;
            ctx.stroke();
            
            // Fill semi-transparent if overlapped to highlight collision
            if (hasOverlap) {
              ctx.fillStyle = 'rgba(255, 75, 75, 0.12)';
              ctx.fill();
            }
            
            // Center point
            ctx.beginPath();
            ctx.arc(screenPos.x, screenPos.y, 3, 0, Math.PI * 2);
            ctx.fillStyle = hasOverlap ? '#ff4b4b' : '#00ffcc';
            ctx.fill();
            
            // Metadata label
            ctx.font = '9px monospace';
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            
            const labelText1 = `[#${idx}] ${bubble.name || 'Orb'}`;
            const labelText2 = `r: ${Math.round(radiusPixels)}px scale: ${visualScale.toFixed(2)}`;
            const labelText3 = `depthNorm: ${depthNorm.toFixed(2)}`;
            
            ctx.fillText(labelText1, screenPos.x, screenPos.y + 6);
            ctx.fillText(labelText2, screenPos.x, screenPos.y + 16);
            ctx.fillText(labelText3, screenPos.x, screenPos.y + 26);
          });
        }
      }
    }
  }

    updateDebugDashboard() {
      if (!this.debugDashboard) return;

      const canvas = this.renderer ? this.renderer.domElement : null;
      const width = canvas ? canvas.clientWidth : 0;
      const height = canvas ? canvas.clientHeight : 0;
      const frustumHeight = this.camera ? 2 * Math.tan((this.camera.fov * Math.PI) / 360) * this.camera.position.z : 0;
      const frustumWidth = this.camera ? frustumHeight * this.camera.aspect : 0;

      let content = `
        <div style="border-bottom: 1px solid #2bb9ed; padding-bottom: 6px; margin-bottom: 8px; font-weight: bold; color: #ffffff;">MOZTECH SYSTEM MONITOR</div>
        <strong>Source Branch:</strong> ${BUILD_SOURCE_BRANCH}<br/>
        <strong>Base Commit:</strong> ${BUILD_BASE_COMMIT}<br/>
        <strong>JS Version:</strong> V${BUILD_VERSION}<br/>
        <strong>CSS CDN Revision:</strong> ${CSS_ASSET_REVISION}<br/>
        <strong>Theme ID:</strong> ${Number(window.Shopify?.theme?.id) || 'local preview'}<br/>
        <strong>Section ID:</strong> ${this.sectionId}<br/>
        <strong>WebGL Contexts:</strong> ${window.moztechWebGLContextCount || 0}<br/>
        <strong>Debug Mode:</strong> ${this.debugMode || 'NONE'}<br/>
        <strong>Build Date:</strong> 2026-09-24<br/>
        <strong>Intro:</strong> ${this.introPhase} (${this.introProgress.toFixed(2)})<br/>
        <strong>Design Mode:</strong> ${!!(window.Shopify && window.Shopify.designMode)}<br/>
        <strong>Active Instances:</strong> ${typeof instances !== 'undefined' ? instances.size : 0}<br/>
        <strong>Disposed Count:</strong> ${window.moztechDisposedCount || 0}<br/>
        <strong>Last Destroy:</strong> ${window.moztechLastDestroyTime ? (window.moztechLastDestroyTime / 1000).toFixed(2) + 's' : 'NONE'}<br/>
        <strong>Last Init:</strong> ${window.moztechLastInitTime ? (window.moztechLastInitTime / 1000).toFixed(2) + 's' : 'NONE'}<br/>
        <div style="font-size: 8px; color: #888888; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 260px; margin-top: 4px;">
          <strong>Script:</strong> ${document.querySelector('script[src*="moztech-floating-bubbles.js"]')?.src.split('/').pop() || 'UNKNOWN'}<br/>
          <strong>Style:</strong> ${document.querySelector('link[href*="moztech-floating-bubbles.css"]')?.href.split('/').pop() || 'UNKNOWN'}
        </div>
      `;

      if (this.debugMode === 'mozcare-transmission-lab') {
        const transmissionOrb = this.bubbles.find((bubble) => bubble.orb_type === 'ribbon_wrapped');
        let transmissionMaterial = null;
        if (transmissionOrb) {
          transmissionOrb.group.traverse((child) => {
            if (
              !transmissionMaterial &&
              child.isMesh &&
              child.material &&
              child.material.userData.mbsPremiumTransmission
            ) {
              transmissionMaterial = child.material;
            }
          });
        }
        const uniforms = transmissionMaterial ? transmissionMaterial.uniforms : null;
        content += `
          <strong>Material Lab:</strong> MOZ Care+ Transmission<br/>
          <strong>Transmission Shader:</strong> ${transmissionMaterial ? '<span style="color:#00ffcc;">ACTIVE</span>' : '<span style="color:#ff3333;">MISSING</span>'}<br/>
          <strong>Opaque Inner Core:</strong> <span style="color:#00ffcc;">REMOVED</span><br/>
          <strong>Wordmark Buffer:</strong> ${uniforms ? '<span style="color:#00ffcc;">ACTIVE</span>' : 'UNAVAILABLE'}<br/>
          <strong>IOR:</strong> ${uniforms ? uniforms.uIor.value.toFixed(2) : '0.00'}<br/>
          <strong>Refraction Strength:</strong> ${uniforms ? uniforms.uRefractionStrength.value.toFixed(3) : '0.000'}<br/>
          <strong>Thickness:</strong> ${uniforms ? uniforms.uThickness.value.toFixed(3) : '0.000'}<br/>
          <strong>Roughness:</strong> ${uniforms ? uniforms.uRoughness.value.toFixed(3) : '0.000'}<br/>
          <strong>Chromatic Aberration:</strong> ${uniforms ? uniforms.uChromaticAberration.value.toFixed(3) : '0.000'}<br/>
          <strong>Studio Environment:</strong> ${uniforms ? '<span style="color:#00ffcc;">ACTIVE</span>' : 'UNAVAILABLE'}<br/>
          <strong>Depth-only Belt Occlusion:</strong> <span style="color:#00ffcc;">ACTIVE</span><br/>
          <strong>Drag:</strong> ${this.draggedBubble ? '<span style="color:#ff79c6;">ACTIVE</span>' : 'READY'}<br/>
        `;
      } else if (this.debugMode === 'starburst-tentacle-lab') {
        const starburstOrb = this.bubbles.find((bubble) => (
          bubble.orb_type === 'starburst' || bubble.orb_type === 'energy_starburst'
        ));
        let tentacleMaterial = null;
        let filamentCount = 0;
        let filamentSegments = 0;
        if (starburstOrb) {
          starburstOrb.group.traverse((child) => {
            if (
              !tentacleMaterial &&
              child.isMesh &&
              child.material &&
              child.material.userData.mbsStarburstTentacles
            ) {
              tentacleMaterial = child.material;
              filamentCount = child.geometry.instanceCount || 0;
              filamentSegments = Math.max(0, ((child.geometry.getAttribute('uv')?.count || 2) / 2) - 1);
            }
          });
        }
        content += `
          <strong>Material Lab:</strong> Reactive Starburst<br/>
          <strong>Tentacle Shader:</strong> ${tentacleMaterial ? '<span style="color:#00ffcc;">ACTIVE</span>' : '<span style="color:#ff3333;">MISSING</span>'}<br/>
          <strong>Coarse Cone Meshes:</strong> <span style="color:#00ffcc;">REMOVED</span><br/>
          <strong>Filament Count:</strong> ${filamentCount}<br/>
          <strong>Segments / Filament:</strong> ${filamentSegments}<br/>
          <strong>Draw Calls:</strong> 1 filament mesh<br/>
          <strong>Traveling Pulse:</strong> ${tentacleMaterial ? '<span style="color:#00ffcc;">ACTIVE</span>' : 'UNAVAILABLE'}<br/>
          <strong>Cursor Attraction:</strong> ${tentacleMaterial ? '<span style="color:#00ffcc;">ACTIVE</span>' : 'UNAVAILABLE'}<br/>
          <strong>Drag:</strong> ${this.draggedBubble ? '<span style="color:#ff79c6;">ACTIVE</span>' : 'READY'}<br/>
        `;
      } else if (this.debugMode === 'loading') {
        const failedUrlsStr = this.failedTextureUrls.length > 0
          ? `<span style="color:#ff3333;">${this.failedTextureUrls.join(', ')}</span>`
          : 'NONE';
        const canvasEl = this.renderer ? this.renderer.domElement : null;
        const isCanvasVisible = canvasEl && canvasEl.style.opacity === '1';
        const posterEl = this.container.querySelector('.mbs-canvas-poster');
        const isPosterVisible = posterEl && posterEl.style.opacity !== '0';
        const activeInstances = typeof instances !== 'undefined' ? instances.size : 0;
        const activeRafCount = window.moztechBubblesInstances ? window.moztechBubblesInstances.size : 1;
        const duration = this.loadingStartTime ? ((performance.now() - this.loadingStartTime) / 1000).toFixed(2) : '0.00';
        const shiftSourcesStr = this.layoutShiftSources && this.layoutShiftSources.length > 0
          ? `<span style="color:#ffcc00; font-size:10px;">${Array.from(new Set(this.layoutShiftSources)).join(', ')}</span>`
          : 'NONE';

        let logLines = '';
        if (this.timelineLogs && this.timelineLogs.length > 0) {
          logLines = '<div style="margin-top: 8px; border-top: 1px solid rgba(43,185,237,0.3); padding-top: 4px; font-size: 8px; color: #aaa; max-height: 120px; overflow-y: auto; text-align: left;">';
          this.timelineLogs.forEach(entry => {
            logLines += `[${entry.timestamp_sec.toFixed(3)}s] ${entry.event}<br/>`;
          });
          logLines += '</div>';
        }

        content += `
          <strong>Loading State:</strong> ${this.isSceneReady ? '<span style="color:#00ffcc; font-weight:bold;">READY</span>' : '<span style="color:#ffcc00;">LOADING</span>'} (${duration}s)<br/>
          <strong>Assets Loaded:</strong> ${this.loadedAssetsCount} / ${this.totalAssetsCount}<br/>
          <strong>Failed Textures:</strong> ${failedUrlsStr}<br/>
          <strong>CanvasTexture Ready:</strong> <span style="color:#00ffcc;">TRUE</span><br/>
          <strong>SVGTexture Ready:</strong> ${this.isSvgLogoReady ? '<span style="color:#00ffcc;">TRUE</span>' : '<span style="color:#ff3333;">FALSE</span>'}<br/>
          <strong>ImageTexture Ready:</strong> ${this.isSceneReady ? '<span style="color:#00ffcc;">TRUE</span>' : '<span style="color:#ffcc00;">LOADING</span>'}<br/>
          <strong>First Frame Rendered:</strong> ${this.isFirstFrameRendered ? '<span style="color:#00ffcc;">TRUE</span>' : '<span style="color:#ff3333;">FALSE</span>'}<br/>
          <strong>Scene Ready:</strong> ${this.isSceneReady ? '<span style="color:#00ffcc;">TRUE</span>' : '<span style="color:#ff3333;">FALSE</span>'}<br/>
          <strong>Canvas Visible:</strong> ${isCanvasVisible ? '<span style="color:#00ffcc;">TRUE (opacity=1)</span>' : '<span style="color:#ff3333;">FALSE (opacity=0)</span>'}<br/>
          <strong>Fallback Visible:</strong> ${isPosterVisible ? '<span style="color:#ffcc00;">TRUE</span>' : '<span style="color:#888888;">FALSE</span>'}<br/>
          <strong>WebGL Context Count:</strong> ${window.moztechWebGLContextCount || 1}<br/>
          <strong>Active Instance Count:</strong> ${activeInstances}<br/>
          <strong>Active RAF Count:</strong> ${activeRafCount}<br/>
          <strong>ResizeObserver Events:</strong> ${this.resizeObserverEvents || 0}<br/>
          <strong>Height Before Init:</strong> ${this.heightBeforeInit || 0}px<br/>
          <strong>Height After Init:</strong> ${this.heightAfterInit || 0}px<br/>
          <strong>Height After Ready:</strong> ${this.heightAfterReady || 0}px<br/>
          <strong>Layout Shift Count:</strong> <span style="color:${this.layoutShiftCount > 0 ? '#ff3333' : '#00ffcc'}; font-weight:bold;">${this.layoutShiftCount || 0}</span><br/>
          <strong>Layout Shift Sources:</strong> ${shiftSourcesStr}<br/>
          ${logLines}
        `;
      } else if (this.debugMode === 'responsive-bounds') {
        content += `
          <strong>Canvas Size:</strong> ${width}x${height}px<br/>
          <strong>Renderer Size:</strong> ${width}x${height}px<br/>
          <strong>Pixel Ratio:</strong> ${this.renderer ? this.renderer.getPixelRatio() : 1}<br/>
          <strong>Camera Aspect:</strong> ${this.camera ? this.camera.aspect.toFixed(3) : 1.0}<br/>
          <strong>Frustum Width:</strong> ${frustumWidth.toFixed(2)} units<br/>
          <strong>Frustum Height:</strong> ${frustumHeight.toFixed(2)} units<br/>
          <strong>Safe Area Width:</strong> ${(frustumWidth * (this.isMobile ? 0.76 : 0.52)).toFixed(2)} units<br/>
          <strong>Logo Bounds:</strong> ${this.logoWidth ? this.logoWidth.toFixed(2) : 0}x${this.logoHeight ? this.logoHeight.toFixed(2) : 0} units<br/>
          <strong>Clipping Status:</strong> NO_CLIPPING_DETECTED (Full Screen breakout active)<br/>
        `;
      } else if (this.debugMode === 'physics-bounds') {
        let collidingCount = 0;
        this.bubbles.forEach(b => {
          if (b.isCollidingBoundary) collidingCount++;
        });
        content += `
          <strong>Safe Padding:</strong> ${(this.safePaddingPercent * 100).toFixed(0)}%<br/>
          <strong>Viewport Category:</strong> ${this.isMobile ? 'Mobile' : this.isTablet ? 'Tablet' : 'Desktop'}<br/>
          <strong>Camera Aspect:</strong> ${this.camera ? this.camera.aspect.toFixed(3) : 1.0}<br/>
          <strong>Frustum Size (Z=0):</strong> ${frustumWidth.toFixed(2)}x${frustumHeight.toFixed(2)} units<br/>
          <strong>Active Colliders:</strong> ${collidingCount} / ${this.bubbles.length}<br/>
          <strong>Damping (c):</strong> 5.2 units/s<br/>
          <strong>Restitution (e):</strong> 0.34<br/>
          <strong>Boundary Force:</strong> DISABLED DURING ORBIT<br/>
          <strong>Return Spring:</strong> ACTIVE AFTER POINTER FORCE<br/>
        `;
      } else if (this.debugMode === 'interaction' || this.debugMode === 'physics-colliders' || this.debugMode === 'raycast' || this.debugMode === 'prototype-2') {
        let velocityStr = "0.00";
        let massStr = "1.0";
        let collisionRadius = "0.0";
        
        let dragName = "NONE";
        if (this.draggedBubble) {
          dragName = this.draggedBubble.title;
          velocityStr = this.draggedBubble.velocity.length().toFixed(2);
          massStr = this.draggedBubble.mass.toFixed(1);
          collisionRadius = this.getCollisionRadius(this.draggedBubble).toFixed(2);
        }
        
        let hoverName = "NONE";
        if (this.mouseHoveredBubble) {
          hoverName = this.mouseHoveredBubble.title;
        }

        const isThemeEditor = window.Shopify && window.Shopify.designMode;
        content += `
          <strong>Hovered Object:</strong> <span style="color:#00ff00;">${hoverName}</span><br/>
          <strong>Pointer Force Target:</strong> <span style="color:#ff007f;">${dragName}</span><br/>
          <strong>Raycast Hit Result:</strong> ${this.draggedBubble ? 'SUCCESS' : (this.mouseHoveredBubble ? 'HOVER_SUCCESS' : 'NONE')}<br/>
          <strong>Collision Radius:</strong> ${collisionRadius} units<br/>
          <strong>Object Velocity:</strong> ${velocityStr} units/s<br/>
          <strong>Object Mass:</strong> ${massStr}<br/>
          <strong>Physics Active:</strong> ${this.physicsActive ? 'TRUE' : 'FALSE'} (Spring constraint, mass, collision, damping)<br/>
          <strong>Pointer Event Status:</strong> ${this.draggedBubble ? 'ACTIVE' : 'IDLE'}<br/>
          <strong>Editor Interception:</strong> ${isThemeEditor ? 'YES (OVERLAY BLOCKED)' : 'NO'}<br/>
        `;
      } else if (this.debugMode === 'orbit-angle' || this.debugMode === 'prototype-orbit-angle' || this.debugMode === 'orbit-calibration') {
        content += `
          <strong>Orbit Center:</strong> [0.0, 0.0, 0.0] (Logo Center)<br/>
          <strong>Screen Roll Angle:</strong> 19°<br/>
          <strong>Depth Inclination:</strong> 29°<br/>
          <strong>Orbit Period:</strong> ${(TAU / REFERENCE_ORBIT.angularSpeed).toFixed(0)}s<br/>
          <strong>Orbit Model:</strong> Single Rotated XZ Ellipse<br/>
          <strong>Active Orbs:</strong> ${this.bubbles.length}<br/>
        `;
      } else if (this.debugMode === 'orbit-members') {
        const allOrbiting = this.bubbles.every(b => b.orbitEnabled !== false);
        content += `
          <strong>Orbit Model:</strong> Single Rotated XZ Ellipse<br/>
          <strong>Screen Roll Angle:</strong> 19°<br/>
          <strong>Depth Inclination:</strong> 29°<br/>
          <strong>Active Members:</strong> ${this.bubbles.length}<br/>
          <strong>Orbit Enabled Status:</strong> ${allOrbiting ? '<span style="color:#00ffcc; font-weight:bold;">ALL OK (TRUE)</span>' : '<span style="color:#ff3333; font-weight:bold;">MISSING ORBIT (FALSE)</span>'}<br/>
        `;
      } else if (this.debugMode === 'orbit-belt-calibration') {
        content += `
          <strong>Orbit Center:</strong> [0.0, 0.0, 0.0] (Logo Center)<br/>
          <strong>Screen Roll Angle:</strong> 15° (shallow tilt)<br/>
          <strong>Depth Inclination:</strong> 28° (shallow Z-depth)<br/>
          <strong>Active Spheres:</strong> ${this.bubbles.length} (gray placeholders)<br/>
          <strong>Physics State:</strong> DISABLED (calibration tracking)<br/>
        `;
      } else if (this.debugMode === 'orbit-no-physics') {
        content += `
          <strong>Orbit Model:</strong> Pure Kinematic Trajectory<br/>
          <strong>Screen Roll Angle:</strong> 15°<br/>
          <strong>Depth Inclination:</strong> 28°<br/>
          <strong>Active Members:</strong> ${this.bubbles.length}<br/>
          <strong>Physics Solver:</strong> BYPASSED (No collisions/pointer force)<br/>
        `;
      } else if (this.debugMode === 'orbit-system-reset') {
        const wordmarkW = this.logoWidth || 5.0;
        const centerOffsetX = -wordmarkW * 0.04;
        const centerOffsetZ = wordmarkW * 0.05;
        content += `
          <strong>Wordmark Center:</strong> [0.000, 0.000, 0.000]<br/>
          <strong>Orbit Center:</strong> [${centerOffsetX.toFixed(3)}, 0.000, ${centerOffsetZ.toFixed(3)}]<br/>
          <strong>Shared Orbit Check:</strong> <span style="color:#00ffcc; font-weight:bold;">PASSED (ALL MATCHED)</span><br/>
          <strong>Screen Roll Angle:</strong> 15° (shallow tilt)<br/>
          <strong>Depth Inclination:</strong> 28° (shallow Z-depth)<br/>
          <strong>Active Spheres:</strong> ${this.bubbles.length} / 6 (gray placeholders)<br/>
          <strong>Physics Solver:</strong> BYPASSED (system reset verification)<br/>
        `;
      } else if (this.debugMode === 'single-orbit-layout' || this.debugMode === 'single-orbit-belt') {
        const wordmarkW = this.logoWidth || 5.0;
        const isLayout = this.debugMode === 'single-orbit-layout';
        const allShared = this.bubbles.every(b => {
          if (isLayout) {
            const wordmarkH = this.logoHeight || (wordmarkW / 3.125) || 1.6;
            return b.orbitCenter &&
                   Math.abs(b.orbitCenter.x - (wordmarkW * 0.04)) < 0.001 &&
                   Math.abs(b.orbitCenter.y - (wordmarkH * 0.08)) < 0.001 &&
                   Math.abs(b.orbitCenter.z - (-0.25)) < 0.001;
          } else {
            return b.orbitCenter && Math.abs(b.orbitCenter.x) < 0.001 && Math.abs(b.orbitCenter.y) < 0.001 && Math.abs(b.orbitCenter.z) < 0.001;
          }
        });
        const wordmarkH = this.logoHeight || (wordmarkW / 3.125) || 1.6;
        const centerStr = isLayout
          ? `[${(wordmarkW * 0.04).toFixed(3)}, ${(wordmarkH * 0.08).toFixed(3)}, -0.250] (Tilted Orbit Center)`
          : `[0.000, 0.000, 0.000] (Logo Center)`;
        content += `
          <strong>Orbit Model:</strong> ${isLayout ? 'Single Orbit Layout Alignment' : 'Single Parametric Orbit Belt'}<br/>
          <strong>wordmarkCenter:</strong> [0.000, 0.000, 0.000]<br/>
          <strong>orbitCenter:</strong> ${centerStr}<br/>
          <strong>sharedOrbitCenter:</strong> <span style="color:${allShared ? '#00ffcc' : '#ff3366'}; font-weight:bold;">${allShared ? 'true' : 'false'}</span><br/>
          <strong>Spheres Center Check:</strong> <span style="color:${allShared ? '#00ffcc' : '#ff3366'}; font-weight:bold;">${allShared ? 'PASSED (ALL MATCHED)' : 'FAILED (DEVIATED)'}</span><br/>
          <strong>Screen Roll Angle:</strong> 19°<br/>
          <strong>Depth Inclination:</strong> 29°<br/>
          <strong>Orbit Period:</strong> ${(TAU / REFERENCE_ORBIT.angularSpeed).toFixed(0)}s<br/>
          <strong>Base Scales:</strong> Front: 1.00 | Side: 0.50 | Back: 1.00<br/>
          <strong>Active Spheres:</strong> ${this.bubbles.length} / 11 (gray placeholders)<br/>
          <strong>Physics Solver:</strong> BYPASSED (${isLayout ? 'layout' : 'single orbit'} verification)<br/>
        `;
      } else if (this.debugMode === 'depth-staging') {
        const wordmarkW = this.logoWidth || 5.0;
        const wordmarkH = this.logoHeight || (wordmarkW / 3.125) || 1.6;
        const depthAmpVal = wordmarkW * 0.17;
        const allShared = this.bubbles.every(b => b.orbitCenter && Math.abs(b.orbitCenter.x - (wordmarkW * 0.04)) < 0.001 && Math.abs(b.orbitCenter.y - (wordmarkH * 0.08)) < 0.001 && Math.abs(b.orbitCenter.z - (-0.25)) < 0.001);
        content += `
          <strong>Orbit Model:</strong> strict Depth-Staging Alignment<br/>
          <strong>wordmarkCenter:</strong> [0.000, 0.000, 0.000]<br/>
          <strong>orbitCenter:</strong> [${(wordmarkW * 0.04).toFixed(3)}, ${(wordmarkH * 0.08).toFixed(3)}, -0.250] (Tilted Orbit Center)<br/>
          <strong>sharedOrbitCenter:</strong> <span style="color:${allShared ? '#00ffcc' : '#ff3366'}; font-weight:bold;">${allShared ? 'true' : 'false'}</span><br/>
          <strong>depthAmplitude:</strong> ${depthAmpVal.toFixed(3)} (Z-depth shift)<br/>
          <strong>screenRollAngle:</strong> 12° (微微上揚)<br/>
          <strong>orbitPlaneDirection:</strong> X+, Y+, Z- tilted<br/>
          <strong>Base Scales:</strong> Front: 1.00 | Side: 0.50 | Back: 1.00<br/>
          <strong>Active Spheres:</strong> ${this.bubbles.length} / 11 (gray placeholders)<br/>
          <strong>Physics Solver:</strong> BYPASSED (depth staging verification)<br/>
        `;
      } else if (this.debugMode === 'depth-occlusion') {
        const wordmarkW = this.logoWidth || 5.0;
        const wordmarkH = this.logoHeight || (wordmarkW / 3.125) || 1.6;
        const allShared = this.bubbles.every(b => b.orbitCenter && Math.abs(b.orbitCenter.x - (wordmarkW * 0.04)) < 0.001 && Math.abs(b.orbitCenter.y - (wordmarkH * 0.08)) < 0.001 && Math.abs(b.orbitCenter.z - (-0.25)) < 0.001);
        content += `
          <strong>Orbit Model:</strong> Three.js renderOrder Occlusion<br/>
          <strong>wordmarkCenter:</strong> [0.000, 0.000, 0.000]<br/>
          <strong>orbitCenter:</strong> [${(wordmarkW * 0.04).toFixed(3)}, ${(wordmarkH * 0.08).toFixed(3)}, -0.250] (Tilted Orbit Center)<br/>
          <strong>sharedOrbitCenter:</strong> <span style="color:${allShared ? '#00ffcc' : '#ff3366'}; font-weight:bold;">${allShared ? 'true' : 'false'}</span><br/>
          <strong>occlusionSorting:</strong> dynamic depth-based occlusion<br/>
          <strong>renderOrderLayers:</strong> Front: 3 | Logo: 2 | Back: 1<br/>
          <strong>screenRollAngle:</strong> 12° (微微上揚)<br/>
          <strong>Active Spheres:</strong> ${this.bubbles.length} / 11 (gray placeholders)<br/>
          <strong>Physics Solver:</strong> BYPASSED (depth occlusion verification)<br/>
        `;
      } else if (this.debugMode === 'orbit-scale-curve') {
        const wordmarkW = this.logoWidth || 5.0;
        const centerOffsetX = -wordmarkW * 0.04;
        const centerOffsetZ = wordmarkW * 0.05;
        content += `
          <strong>Orbit Model:</strong> Shopify Editions Scale Curve<br/>
          <strong>Orbit Center:</strong> [${centerOffsetX.toFixed(3)}, 0.000, ${centerOffsetZ.toFixed(3)}] (Logo Center)<br/>
          <strong>Shared Orbit Check:</strong> <span style="color:#00ffcc; font-weight:bold;">PASSED (ALL MATCHED)</span><br/>
          <strong>Screen Roll Angle:</strong> 19°<br/>
          <strong>Orbit Period:</strong> ${(TAU / REFERENCE_ORBIT.angularSpeed).toFixed(0)}s<br/>
          <strong>Base Scales:</strong> Front: 1.00 | Side: 0.50 | Back: 1.00<br/>
          <strong>Active Spheres:</strong> ${this.bubbles.length} / 5 (gray placeholders)<br/>
          <strong>Physics Solver:</strong> BYPASSED (scale curve verification)<br/>
        `;
      } else if (this.debugMode === 'orbit-depth-phase' || this.debugMode === 'orbit-depth-phase-12') {
        const wordmarkW = this.logoWidth || 5.0;
        const wordmarkH = this.logoHeight || (wordmarkW / 3.125) || 1.6;
        const allShared = this.bubbles.every(b => b.orbitCenter && Math.abs(b.orbitCenter.x - (wordmarkW * 0.04)) < 0.001 && Math.abs(b.orbitCenter.y - (wordmarkH * 0.08)) < 0.001 && Math.abs(b.orbitCenter.z - (-0.25)) < 0.001);
        
        let hoverName = "NONE";
        if (this.mouseHoveredBubble) {
          hoverName = this.mouseHoveredBubble.title;
        }
        const fps = this.fpsEstimate || 60;
        
        content += `
          <strong>Orbit Model:</strong> strict Shallow Tilted 3D Belt<br/>
          <strong>wordmarkCenter:</strong> [0.000, 0.000, 0.000]<br/>
          <strong>orbitCenter:</strong> [${(wordmarkW * 0.04).toFixed(3)}, ${(wordmarkH * 0.08).toFixed(3)}, -0.250] (Tilted Orbit Center)<br/>
          <strong>sharedOrbitCenter:</strong> <span style="color:${allShared ? '#00ffcc' : '#ff3366'}; font-weight:bold;">${allShared ? 'true' : 'false'}</span><br/>
          <strong>Object Count:</strong> ${this.bubbles.length}<br/>
          <strong>Orbit Active:</strong> <span style="color:#00ffcc; font-weight:bold;">true</span><br/>
          <strong>Interaction Active:</strong> <span style="color:#ff3333; font-weight:bold;">false</span><br/>
          <strong>Physics Active:</strong> <span style="color:#ff3333; font-weight:bold;">false</span><br/>
          <strong>Animation Enabled:</strong> <span style="color:#00ffcc; font-weight:bold;">true</span><br/>
          <strong>Pixel Ratio:</strong> ${this.renderer ? this.renderer.getPixelRatio() : 1}<br/>
          <strong>FPS Estimate:</strong> ~${Math.round(fps)}<br/>
          <strong>Current Hovered Object:</strong> <span style="color:#00ff00;">${hoverName}</span><br/>
          <strong>Current Pointer Target:</strong> <span style="color:#ff3333;">NONE</span><br/>
        `;
      } else if (this.debugMode === 'scale-check') {
        const checkPassed = this.bubbles.every(b => {
          const aspect = 1.00;
          return Math.abs(aspect - 1.0) < 0.01;
        });
        content += `
          <strong>Canvas CSS Size:</strong> ${canvas ? canvas.clientWidth : 0}x${canvas ? canvas.clientHeight : 0}px<br/>
          <strong>Renderer Size:</strong> ${width}x${height}px<br/>
          <strong>Camera Aspect:</strong> ${this.camera ? this.camera.aspect.toFixed(3) : 1.0}<br/>
          <strong>Scale Match State:</strong> ${checkPassed ? '<span style="color:#00ffcc; font-weight:bold;">PASSED (1.00 SPHERICAL)</span>' : '<span style="color:#ff3333; font-weight:bold;">FAILED (DISTORTED)</span>'}<br/>
        `;
      } else {
        content += `
          <strong>Animation:</strong> ${this.enabled ? 'ENABLED' : 'DISABLED'}<br/>
          <strong>Active Orbs:</strong> ${this.bubbles.length}<br/>
          <strong>Orbit Motion:</strong> TILTED_3D_ORBITAL_ACTIVE<br/>
          <strong>Pointer Physics:</strong> SPRING_RAYCAST_ACTIVE<br/>
          <strong>Collisions:</strong> ELASTIC_IMPULSE_ACTIVE
        `;
      }

      const isThemeEditor = window.Shopify && window.Shopify.designMode;
      if (isThemeEditor) {
        content += `<div style="margin-top: 10px; padding: 6px; background: rgba(255, 60, 100, 0.22); border: 1px solid #ff3c64; border-radius: 4px; color: #ff99aa; font-weight: bold; line-height: 1.3;">Pointer-force physics active.<br/>Please test on the real preview page because the theme editor can intercept pointer events.</div>`;
      }

      this.debugDashboard.innerHTML = content;
    }

    pauseVideos() {
      this.videos.forEach((video) => video.pause());
    }

    destroy() {
      if (this._destroyed) return;
      this._destroyed = true;
      const disposedResources = new Set();
      this.clearTouchHold();
      this.touchTapCandidate = null;
      if (this.renderer) this.onPointerUp();
      window.moztechLastDestroyTime = performance.now();
      window.moztechDisposedCount = (window.moztechDisposedCount || 0) + 1;
      if (this.sectionId && instances.get(this.sectionId) === this) {
        instances.delete(this.sectionId);
      }
      if (window.moztechBubblesInstances) {
        window.moztechBubblesInstances.delete(this);
      }
      if (this.section && this.section._bubblesInstance === this) {
        this.section._bubblesInstance = null;
      }
      window.moztechWebGLContextCount = Math.max(0, (window.moztechWebGLContextCount || 1) - 1);
      if (this.performanceObserver) {
        try {
          this.performanceObserver.disconnect();
        } catch (e) {}
      }
      try {
        if (this.animFrameId) {
          cancelAnimationFrame(this.animFrameId);
        }
        if (this.raf) {
          cancelAnimationFrame(this.raf);
        }
        if (this.warmupFrameId) {
          cancelAnimationFrame(this.warmupFrameId);
          this.warmupFrameId = null;
        }
        if (this.deferredAssetHandle != null) {
          if ('cancelIdleCallback' in window) {
            window.cancelIdleCallback(this.deferredAssetHandle);
          } else {
            clearTimeout(this.deferredAssetHandle);
          }
        }
      } catch (err) {}
      // A loader already in flight can settle after Shopify unloads this section.
      // Release the remaining closures and prevent its finalizer from reviving work.
      this.deferredAssetHandle = null;
      this.deferredAssetLoaders = [];

      try {
        const canvas = this.renderer.domElement;
        canvas.removeEventListener('pointerdown', this.pointerDownHandler);
        canvas.removeEventListener('pointermove', this.pointerMoveHandler);
        canvas.removeEventListener('pointerup', this.pointerUpHandler);
        canvas.removeEventListener('pointercancel', this.pointerUpHandler);
        canvas.removeEventListener('pointerleave', this.pointerLeaveHandler);
        canvas.removeEventListener('lostpointercapture', this.pointerCaptureLostHandler);
        canvas.removeEventListener('touchmove', this.touchMoveHandler);
        window.removeEventListener('blur', this.pointerBlurHandler);
      } catch (err) {}

      try {
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('scroll', this.onScroll);
        document.removeEventListener('visibilitychange', this.visibilityHandler);
        if (this.observer) {
          this.observer.disconnect();
        }
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
        if (this.safeAreaOutline && this.safeAreaOutline.parentNode) {
          this.safeAreaOutline.parentNode.removeChild(this.safeAreaOutline);
        }
      } catch (err) {}

      try {
        this.scene.traverse((obj) => {
          try {
            if (obj.geometry) obj.geometry.dispose();
            if (obj.material) {
              if (Array.isArray(obj.material)) {
                obj.material.forEach(m => this.disposeMaterial(m, disposedResources));
              } else {
                this.disposeMaterial(obj.material, disposedResources);
              }
            }
          } catch (e) {}
        });
      } catch (err) {}

      try {
        (this.sharedTextures || []).forEach((texture) => {
          if (!disposedResources.has(texture)) {
            texture.dispose();
            disposedResources.add(texture);
          }
        });
        this.sharedTextures = [];
      } catch (err) {}

      try {
        this.renderer.dispose();
      } catch (err) {}

      this.scene = null;
      this.camera = null;
      this.renderer = null;
      this.bubbles = [];
    }

    disposeMaterial(mat, disposedResources = new Set()) {
      try {
        if (disposedResources.has(mat)) return;
        disposedResources.add(mat);
        mat.dispose();
        const disposeTexture = (texture) => {
          if (texture?.isTexture && !disposedResources.has(texture)) {
            disposedResources.add(texture);
            texture.dispose();
          }
        };
        Object.values(mat).forEach(disposeTexture);
        Object.values(mat.uniforms || {}).forEach((uniform) => {
          if (Array.isArray(uniform.value)) uniform.value.forEach(disposeTexture);
          else disposeTexture(uniform.value);
        });
      } catch (err) {}
    }
  }

  function initSection(root) {
    const sections = root.matches && root.matches('.moztech-bubbles-section')
      ? [root]
      : Array.from(root.querySelectorAll ? root.querySelectorAll('.moztech-bubbles-section') : []);

    sections.forEach((section) => {
      const sectionId = section.dataset.sectionId;
      if (!sectionId) return;
      if (instances.has(sectionId) || section._bubblesInstance) {
        const old = instances.get(sectionId) || section._bubblesInstance;
        try {
          old.destroy();
        } catch (e) {}
        instances.delete(sectionId);
      }
      const inst = new MoztechFloatingBubbles(section);
      instances.set(sectionId, inst);
      section._bubblesInstance = inst;
    });
  }

  function destroySection(sectionRoot) {
    const sections = sectionRoot.matches && sectionRoot.matches('.moztech-bubbles-section')
      ? [sectionRoot]
      : Array.from(sectionRoot.querySelectorAll ? sectionRoot.querySelectorAll('.moztech-bubbles-section') : []);

    sections.forEach((section) => {
      const sectionId = section.dataset.sectionId;
      if (!sectionId) return;
      const inst = instances.get(sectionId);
      if (inst) {
        inst.destroy();
        instances.delete(sectionId);
      }
    });
  }

  function checkThree() {
    if (window.THREE && window.THREE.GLTFLoader && window.THREE.DRACOLoader) {
      initSection(document);
    } else {
      setTimeout(checkThree, 50);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSection(document));
  } else {
    initSection(document);
  }

  document.addEventListener('shopify:section:load', (event) => initSection(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroySection(event.target));
})();
